import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
//  For the specialty breakdown panel only. It has to render outside the filter
//  row, which is an `overflow: auto` scrollport that clipped it to a 34px strip —
//  see the note above SpecialtyChip.
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  //  The clinical review's mark, on the button beside the back arrow. A
  //  stethoscope rather than a spark or a robot: what it does is read the
  //  medicine against the diagnosis, and that is the gesture it should suggest.
  Stethoscope,
  //  The dismiss on the "only flagged claims" chip.
  X,
  //  "Reviewed, and nothing was questioned" on the clinical review button. A tick
  //  rather than a zero — a 0 in a count badge reads as "none yet", which is the
  //  opposite of what it means here.
  Check,
  Download, Layers, Trash2, Search, ChevronLeft, Home as HomeIcon,
  ChevronDown, ChevronUp, ChevronRight, Wrench, SquarePen, Building2, ShieldAlert,
  //  The medication review's own icon. A warning that looks like every other
  //  paragraph on the pane is a warning nobody stops at.
  AlertTriangle,
  //  The date-range group's mark, so the control is identifiable at a glance
  //  rather than by reading its label.
  CalendarRange,
  //  The error report's mark, beside the date range it reports on.
  FileText,
  //  "this becomes that" on an update mapping row. The arrow is the whole grammar of
  //  that row, so it is drawn rather than typed as a character.
  ArrowRight,
  //  The spinner shown while the Advanced Filter reads the month. Spun by the
  //  `.mf-spin` keyframe declared alongside it — lucide ships the glyph, not the
  //  animation.
  Loader2,
} from 'lucide-react';
import {
  claimsdetails, claimsDetailsFacets,
  //  Deletes ONE claim on either model, and decides what goes with it. Replaces
  //  the old /deleteclaim call, which could only reach claim_information — which
  //  is why an imported claim used to be refused here.
  claimStoreDeleteClaim,
  claimsMultiVetPreview, claimsMultiVet, claimsMultiVetSelection,
  //  The SAME controlled vocabularies the single-claim editor uses for a
  //  prescription. Multi Vet must not invent its own list of frequencies.
  claimStoreMedicationCatalogue, claimStoreDiagnosisTariff, claimsXmlExport,
  //  The other three catalogues, so the composer offers what the single-claim
  //  editor offers — the same endpoints, so the same rows and the same prices.
  claimStoreProcedureTariff, claimStoreInvestigationTariff,
  //  Putting a claim's service back in agreement with its own rows. Dry run first;
  //  see repairSelected below.
  claimStoreRepair,
  //  Used by "Treat as out-patient" in the medication review: moving the discharge
  //  date back to the visit date is what flips the claim to a same-day attendance,
  //  and updateBio is the one place that rule lives.
  claimStoreBio,
  //  A whole routine in one pass, and the saved routines that fill it.
  claimsMultiVetBatch, claimsMultiVetRoutines,
  //  The service catalogue, from the same endpoint the single-claim editor's
  //  "Select Service" reads — so a service offered here is one that editor accepts.
  claimStoreServices,
  //  The NHIA format corrections behind Fix all on those tiles. Preview first,
  //  always — two of them change a patient's name and a membership number across
  //  a whole month.
  nhiaFixPreview, nhiaFixApply,
} from '../BaseURL';
//  The catalogue cache: the same fetch the single-claim editor uses, so a list
//  already pulled for a claim is not pulled again for the composer.
import { cachedGet } from './catalogueCache';
//  The brand one-way message — the same chrome the claim editor uses, so a refusal
//  here looks like a refusal there rather than like a browser alert.
import { notifyClaim } from './claimsDialog';
//  The ⓘ on a filter row: what the database really holds for that code, and the
//  one place it can be corrected at the root and on the claims already written.
import CatalogueEntryDialog from './CatalogueEntryDialog';
//  Setting the G-DRG on admission diagnoses that carry an out-patient one — the
//  other half of ipd_on_opd_service, answered once per diagnosis.
import GdrgRecodeDialog from './GdrgRecodeDialog';
import { BRAND } from '../../brand';
import ClaimEditor from './ClaimEditor';
//  Multi Vet's sixth act: one condition template onto every ticked claim it suits.
//  Its own file because its answer is a verdict PER CLAIM, which the basket's shared
//  aggregate cannot express — see MV_OWN_APPLY below and that file's own header.
import MultiVetTemplate from './MultiVetTemplate';
import RejectReasonDialog from './RejectReasonDialog';
import DoseUnitFixDialog from './DoseUnitFixDialog';
//  The clinical review: does the treatment on each claim fit the condition
//  billed? Its own file because the run is a job with a progress interface of its
//  own — see that file's header and backend/aiClaimReview.js.
import ClinicalReviewDialog from './ClinicalReviewDialog';
//  The month's errors as a printable, exportable report. It takes the claims ALREADY
//  on screen and groups them — no endpoint of its own, deliberately, so a report
//  handed to a manager cannot say something different from the table it was printed
//  from. See that file's header.
import ClaimsErrorReport from './ClaimsErrorReport';
//  Keeps a dialog open when the backdrop is clicked — see dialogGuard.js.
import useDialogGuard from './dialogGuard';

// ============================================================================
//  ClaimsMenu — one month's claims, for both claim models
// ============================================================================
//
//  Replaces the old drill-down table. Two things were wrong with that screen and
//  only one of them was cosmetic.
//
//  THE BUG. It read /claimsdetails, which queried claim_information only. A month
//  whose claims arrived as an NHIA XML batch therefore rendered "No claims found"
//  underneath a header that correctly said 2,383 — the count came from the
//  dashboard, the rows from a table that held none of them. /claimsdetails now
//  unions both models (see claimStore.claimsList), so this screen shows a
//  facility's typed-in claims and its imported ones in one list.
//
//  Its IPD/OPD pills were also derived from `dat = d_dat`, which contradicts the
//  stored in_out column on 221 of one month's 565 rows and drops NULL-dat claims
//  from both branches — so the two pills never summed to the total. Both now come
//  from the stored classification, and reconcile.
//
//  THE LAYOUT. Filtering is client-side over the whole month, which is why the
//  search box claims to update instantly and can honestly say so: the month is
//  already in memory. That holds at a few thousand rows. Past roughly 50,000 this
//  needs a server-side page, and the honest place to notice will be this comment.
//
//  BUTTON STATE, deliberately explicit: Export, Import, Multi Task and Toolbox
//  are present and inert, awaiting their own work. They are rendered disabled
//  with a tooltip rather than looking live and doing nothing, because a button
//  that silently no-ops is worse than one that says it is not ready. Bulk Delete
//  IS wired.

const MONTH_TITLE = (m) => String(m || '').toUpperCase();

// ── What went wrong, in words an officer can act on ─────────────────────────
//
//  "The claim was not deleted — Request failed with status code 404."
//
//  That is what the delete dialog said, and it is axios's own boilerplate: the
//  code read `err.response.data.message || err.message`, and when Express serves
//  its DEFAULT 404 the body is an HTML page, so `.message` is undefined and the
//  fallback is the HTTP code. A claims officer cannot do anything with that, and
//  it is indistinguishable from a real "this claim is not there".
//
//  THE TWO 404s MEAN OPPOSITE THINGS and need opposite actions:
//
//    404 WITH a JSON message   the server ran the request and the claim is not
//                              there. Refresh the month.
//    404 WITHOUT one           the server has never heard of this endpoint —
//                              the backend has not been updated. Nothing is
//                              wrong with the claim at all.
//
//  The second was exactly the case: the frontend had been rebuilt and deployed
//  and server.js / claimStoreRoutes.js had not, so the browser was calling a
//  route the running server does not have.
const apiError = (err, what = 'That did not go through') => {
  //  The server's own words first, wherever it sent any. Every handler in
  //  claimStoreRoutes answers with { success: false, message }.
  const said = err?.response?.data?.message;
  if (typeof said === 'string' && said.trim()) return said;

  const status = err?.response?.status;

  //  No response at all: the request never arrived.
  if (!err?.response) {
    return 'The server could not be reached. Check the connection and try again.';
  }
  //  A body that is not JSON is a framework page, not an answer from the app.
  //  On 404 that means the route is not registered on the running server.
  if (status === 404) {
    return 'This server does not have that feature yet — its backend needs '
         + 'updating. Nothing is wrong with the claim.';
  }
  if (status === 401 || status === 403) {
    return 'You are not signed in with permission to do that.';
  }
  if (status === 413) return 'That was too large for the server to accept.';
  if (status >= 500) {
    return 'The server hit an error carrying that out. Nothing was changed — '
         + 'send this to IT if it keeps happening.';
  }
  return `${what} (HTTP ${status}).`;
};

const fmtDate = (v) => {
  if (!v) return '—';
  // Already 'YYYY-MM-DD' from the server for both models; shown as-is so it
  // matches what the claim actually carries rather than a locale guess.
  return String(v).slice(0, 10);
};

// "2010-11-10 (15y, 5m)" — age at the claim date, not today's age. A claim is a
// historical document and the age that matters is the age on the day.
const dobWithAge = (dob, onDate) => {
  if (!dob) return '—';
  const d = new Date(dob);
  const ref = onDate ? new Date(onDate) : new Date();
  if (Number.isNaN(d.getTime()) || Number.isNaN(ref.getTime())) return fmtDate(dob);
  let months = (ref.getFullYear() - d.getFullYear()) * 12 + (ref.getMonth() - d.getMonth());
  if (ref.getDate() < d.getDate()) months -= 1;
  if (months < 0) return fmtDate(dob);
  return `${fmtDate(dob)} (${Math.floor(months / 12)}y, ${months % 12}m)`;
};

const money = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}));

// ── Shared styles ───────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100%', background: BRAND.canvas, padding: '14px 16px 28px',
    fontFamily: 'inherit', color: BRAND.ink,
  },
  crumbs: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    fontSize: 12.5, color: BRAND.inkMuted, marginBottom: 10,
  },
  crumbLink: { color: BRAND.inkMuted, cursor: 'pointer', textDecoration: 'none' },
  crumbHere: { color: BRAND.ink, fontWeight: 700 },

  searchWrap: { position: 'relative', flex: '1 1 300px', maxWidth: 420, minWidth: 200 },
  search: {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 31px',
    fontSize: 12.5, font: 'inherit', fontFamily: 'inherit',
    border: `1px solid ${BRAND.line}`, borderRadius: 7,
    background: BRAND.surface, color: BRAND.ink, outline: 'none',
  },
  searchIcon: {
    position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
    color: BRAND.inkFaint, pointerEvents: 'none',
  },
  hint: { fontSize: 11.5, color: BRAND.inkMuted },

  // 19px, down from 25. The month and scheme still read as the page's subject
  // without a heading tall enough to push the table below the fold.
  title: { fontSize: 19, fontWeight: 800, letterSpacing: '-.3px', lineHeight: 1.2, margin: 0 },
  subtitle: { fontSize: 12, color: BRAND.inkMuted, marginTop: 2 },

  // Compact: 7px/13px padding rather than 10px/16px. Four buttons plus Toolbox
  // fit one row at laptop width instead of forcing the Toolbox onto its own line.
  btnBase: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 13px', borderRadius: 8, border: '1px solid transparent',
    font: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'filter .15s ease, opacity .15s ease',
  },

  // ── The filter bar ────────────────────────────────────────────────────────
  //  One row, replacing two stacked pill rows that each carried their own "ALL".
  //  Two ALLs meant two different things — all attendances, and all specialties —
  //  and nothing on screen said which was which.
  //
  //  Attendance became a SEGMENTED control: All / OPD / IPD are one choice, so
  //  they read as one control with three positions, and there is exactly one ALL.
  //  Specialty lost its ALL entirely; no chip lit means no specialty filter, and
  //  a Clear appears only once one is active.
  filterBar: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '8px 12px', marginBottom: 10,
    background: BRAND.surface, border: `1px solid ${BRAND.line}`, borderRadius: 9,
  },
  segment: {
    display: 'inline-flex', padding: 2, gap: 2,
    background: BRAND.canvas, border: `1px solid ${BRAND.line}`, borderRadius: 8,
  },
  segBtn: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '5px 12px', borderRadius: 6, border: 0, font: 'inherit',
    fontSize: 12, fontWeight: 800, letterSpacing: '.02em', cursor: 'pointer',
    background: active ? BRAND.green : 'transparent',
    color: active ? '#fff' : BRAND.inkMuted,
    transition: 'background .14s ease, color .14s ease',
  }),
  divider: { width: 1, alignSelf: 'stretch', background: BRAND.line, margin: '0 2px' },
  metaLabel: {
    fontSize: 10, fontWeight: 800, letterSpacing: '.08em',
    textTransform: 'uppercase', color: BRAND.inkFaint, whiteSpace: 'nowrap',
  },
  // Scrolls sideways rather than wrapping. Eight specialties wrapping to a second
  // line is what made this section grow — a facility with twenty would have
  // pushed the table off the screen entirely.
  chipScroll: {
    display: 'flex', gap: 6, alignItems: 'center',
    flex: '1 1 240px', minWidth: 0, overflowX: 'auto',
    paddingBottom: 1, scrollbarWidth: 'thin',
  },

  // ── The table ─────────────────────────────────────────────────────────────
  //  The wrapper scrolls in BOTH directions, and that is deliberate rather than
  //  incidental. `overflow-x: auto` alone computes overflow-y to `auto` as well,
  //  creating a scrollport that never scrolls — and `position: sticky` on the
  //  header then silently does nothing, because it sticks to a container that has
  //  no scroll of its own. Giving the wrapper a real max-height makes it the
  //  vertical scroller, so the header genuinely sticks and the page stops growing
  //  without bound at 2,383 rows.
  tableWrap: {
    background: BRAND.surface,
    border: `1px solid ${BRAND.line}`,
    borderRadius: 9,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 230px)',
    scrollbarWidth: 'thin',
  },
  table: {
    width: '100%', minWidth: 1520, borderCollapse: 'separate', borderSpacing: 0,
    fontSize: 12.5,
  },
  th: {
    textAlign: 'left', padding: '9px 11px', fontSize: 9.5, fontWeight: 800,
    letterSpacing: '.08em', textTransform: 'uppercase', color: BRAND.inkMuted,
    borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
    // Opaque, because rows scroll underneath it.
    background: '#F7F9FC',
    position: 'sticky', top: 0, zIndex: 2,
  },
  // 9px rows rather than 13px. At 25 visible rows that is 200px of reclaimed
  // height — more claims on screen without scrolling, which is the point.
  td: {
    padding: '9px 11px', borderBottom: `1px solid ${BRAND.line}`,
    verticalAlign: 'middle', color: BRAND.ink,
  },
  num: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  mono: {
    fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace',
    fontSize: 11.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  // Pinned right so the edit and delete controls stay reachable at 14 columns
  // without scrolling back. Its background has to be set per row state or the
  // striping shows through the pinned cell.
  stickyRight: { position: 'sticky', right: 0, zIndex: 1 },
  iconBtn: (colour) => ({
    display: 'grid', placeItems: 'center', width: 27, height: 27,
    border: `1px solid transparent`, borderRadius: 7, background: 'none',
    color: colour, cursor: 'pointer', padding: 0,
    transition: 'background .14s ease, border-color .14s ease',
  }),
};

const btn = (kind, disabled) => {
  const map = {
    green: { background: BRAND.green, color: '#fff', borderColor: BRAND.green },
    slate: { background: '#5C6E88', color: '#fff', borderColor: '#5C6E88' },
    ghost: {
      background: BRAND.surface, color: BRAND.green,
      borderColor: BRAND.green, borderWidth: 1.5,
    },
  };
  return {
    ...S.btnBase, ...map[kind],
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
};

// ── THE THIRTEEN SPECIALTY GROUPS ───────────────────────────────────────────
//
//  The same list as claimRules.MDC_GROUPS on the server, which is itself the distinct
//  `mdc_code` values in the tariff data this application ships. Restated here because
//  the browser cannot require the backend module, and pinned by a test that reads both
//  so the two cannot drift.
//
//  ── WHAT IT IS FOR ──────────────────────────────────────────────────────────
//  Telling a specialty from something that is merely sitting in the specialty column.
//  One month's import carried D001 on 414 claims, plus OPD0, OOBG and ODPC — a
//  department number, a truncation, and transpositions of OPDC and OBGY. They came
//  straight out of the file's specialtyAttended field and read, in the chip row, exactly
//  like the real ones.
const MDC_GROUPS = new Set([
  'ASUR', 'DENT', 'ENTH', 'INVE', 'MEDI', 'OBGY', 'OPDC',
  'OPHT', 'ORTH', 'PAED', 'PSUR', 'RSUR', 'ZOOM',
]);

// ── Specialty chip ──────────────────────────────────────────────────────────
//  Deliberately has no "all" variant. An unlit row of chips already means "no
//  specialty filter", and a second ALL sitting next to the attendance control's
//  ALL was ambiguous — two identical buttons scoping different things.
function Chip({ label, count, active, onClick, disabled, tone, title }) {
  //  `tone: 'danger'` marks a chip whose VALUE is wrong, not one whose count is zero.
  //  A specialty that is not one of the thirteen MDC groups is the case it exists for:
  //  D001 sat in this row reading exactly like OPDC, on 414 claims.
  const bad = tone === 'danger' && !disabled;
  const edge = bad ? BRAND.danger : (active ? BRAND.green : BRAND.line);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={title || (
        disabled ? `No ${label} claims in the current selection`
          : active ? `Showing ${label} only — click to clear`
          : `Show only ${label}`
      )}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0,
        padding: '5px 11px', borderRadius: 7, font: 'inherit', fontSize: 11.5,
        fontWeight: 800, letterSpacing: '.02em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${edge}`,
        background: active ? (bad ? BRAND.danger : BRAND.green)
          : (bad ? BRAND.dangerSoft : BRAND.surface),
        color: active ? '#fff'
          : (disabled ? BRAND.inkFaint : (bad ? BRAND.danger : BRAND.ink)),
        // A chip that would return nothing is dimmed rather than hidden: it still
        // tells you the specialty exists in this month, it just has none under the
        // attendance you have selected. Hiding it would look like data loss.
        opacity: disabled ? 0.45 : 1,
        textDecoration: disabled ? 'line-through' : 'none',
        transition: 'background .14s ease, border-color .14s ease, opacity .14s ease',
      }}
    >
      <span>{label}</span>
      <span style={{
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: active ? 'rgba(255,255,255,.85)' : BRAND.inkFaint,
      }}>
        {Number(count || 0).toLocaleString()}
      </span>
    </button>
  );
}

//  ── A SPECIALTY CHIP, WITH THE CODES INSIDE IT ──────────────────────────────
//
//  "So I can just click the OPDC, or I can just click the OBGY. But when I want to
//  filter, I can click on the ZOOM, then it'll drop down, and I'll filter specific
//  ZOOM."
//
//  TWO CONTROLS, ONE CHIP. The chip body still filters the whole specialty exactly
//  as it did — nothing that worked before needs relearning — and the caret beside
//  it opens the breakdown. Making the whole chip open a menu would have taken the
//  one-click specialty filter away to add a two-click one.
//
//  The caret appears only where there is more than one code to choose between: a
//  specialty billed on a single G-DRG has no breakdown, and a caret that opens a
//  list of one is a control that cannot do anything.
//
//  ── WHY THE PANEL IS A PORTAL AND NOT A CHILD OF THE CHIP ───────────────────
//
//  Reported: "when you click on the drop-down it opens, but that place is too
//  small, you can't even see anything."
//
//  It was `position: absolute` inside the chip, and the chip lives in
//  `S.chipScroll` — `overflowX: 'auto'`. THE SAME TRAP THIS FILE ALREADY
//  DOCUMENTS eight lines under that style, for the sticky table header:
//  `overflow-x: auto` computes overflow-y to `auto` as well. So the chip row is a
//  scrollport in BOTH axes and about 34px tall, and an absolutely-positioned
//  child was clipped to that strip — a 34px letterbox with a scrollbar, exactly
//  as described.
//
//  Neither escape works from inside. `overflow: visible` on the row would give
//  the panel its space and take away the sideways scrolling that keeps twenty
//  specialties from pushing the table off the screen. A taller row would leave a
//  permanent gap under the chips for a panel that is shut most of the time.
//
//  So the panel is rendered into `document.body` and positioned from the chip's
//  own rectangle. A portal has no clipping ancestor to be trapped by, which also
//  makes it immune to the next `overflow` added anywhere above this row.
function SpecialtyChip({
  code, count, active, disabled, tone, title, onClick,
  codes, open, onToggle, chosenCode, onPickCode,
}) {
  const bad = tone === 'danger' && !disabled;
  const canBreak = (codes || []).length > 1;
  const ref = useRef(null);
  //  The panel is no longer inside `ref`, so the outside-click test has to know
  //  about it separately or the first click inside the list would close it.
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  //  WHERE THE PANEL GOES, in viewport coordinates.
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const PAD = 10;
    //  Wide enough for "Detention for Observation and Treatment - Adult", and
    //  never wider than the viewport allows on a phone.
    const width = Math.max(240, Math.min(430, window.innerWidth - PAD * 2));
    //  LEFT-ALIGNED TO THE CHIP, then pulled back on-screen. ASUR is the last of
    //  ten chips and sits at the right-hand edge; aligned naively its panel would
    //  hang off the window.
    const left = Math.max(PAD, Math.min(r.left, window.innerWidth - width - PAD));
    const below = window.innerHeight - r.bottom - PAD;
    const above = r.top - PAD;
    //  OPENS UPWARD when there is more room there. The filter row can sit low on a
    //  short window, and a panel with 40px of height is the bug this replaces.
    const up = below < 200 && above > below;
    return setPos({
      left,
      width,
      maxHeight: Math.min(340, Math.max(140, up ? above : below)),
      top: up ? undefined : Math.round(r.bottom + 5),
      bottom: up ? Math.round(window.innerHeight - r.top + 5) : undefined,
    });
  }, []);

  //  CLOSE ON OUTSIDE CLICK AND ON ESCAPE, and follow the chip while open. The row
  //  scrolls sideways and the page scrolls under it, so a fixed panel that does not
  //  re-measure would drift away from the chip it belongs to. `capture: true`
  //  because the scroll happens on the chip row, not on the window.
  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    const away = (e) => {
      const inChip = ref.current && ref.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inChip && !inPanel) onToggle(null);
    };
    const esc = (e) => { if (e.key === 'Escape') onToggle(null); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, onToggle, place]);

  const label = code === 'UNSPECIFIED' ? 'Unspecified' : code;
  const edge = bad ? BRAND.danger : (active ? BRAND.green : BRAND.line);
  //  A chip narrowed to one code is still ACTIVE, and says which — otherwise the
  //  row reads "OPDC" while the table shows 3,384 of 20,053 claims, with the only
  //  evidence in a panel that has closed.
  const narrowed = active && chosenCode && chosenCode !== 'all';

  return (
    //  No `position: relative` here any more. It was what an absolutely-positioned
    //  panel anchored to, and the panel is a portal now — leaving it would suggest
    //  something still hangs off this element.
    <div ref={ref} style={{ flexShrink: 0, display: 'inline-flex' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'stretch', flexShrink: 0,
        border: `1px solid ${edge}`, borderRadius: 7, overflow: 'hidden',
        background: active ? (bad ? BRAND.danger : BRAND.green)
          : (bad ? BRAND.dangerSoft : BRAND.surface),
        opacity: disabled ? 0.45 : 1,
        transition: 'background .14s ease, border-color .14s ease, opacity .14s ease',
      }}>
        <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active}
          title={title || (
            disabled ? `No ${label} claims in the current selection`
              : active ? `Showing ${label} only — click to clear`
                : `Show only ${label}`
          )}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '5px 10px', border: 'none', background: 'none',
            font: 'inherit', fontSize: 11.5, fontWeight: 800, letterSpacing: '.02em',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: active ? '#fff' : (disabled ? BRAND.inkFaint : (bad ? BRAND.danger : BRAND.ink)),
            textDecoration: disabled ? 'line-through' : 'none',
          }}>
          <span>{label}</span>
          {narrowed && (
            <span style={{
              ...S.mono, fontSize: 10, fontWeight: 800, padding: '1px 5px',
              borderRadius: 4, background: 'rgba(255,255,255,.22)', color: '#fff',
            }}>{chosenCode}</span>
          )}
          <span style={{
            fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            color: active ? 'rgba(255,255,255,.85)' : BRAND.inkFaint,
          }}>
            {Number(count || 0).toLocaleString()}
          </span>
        </button>

        {canBreak && (
          <button type="button"
            onClick={() => onToggle(open ? null : code)}
            aria-expanded={open}
            aria-label={`Break ${label} down by G-DRG`}
            title={`${label} is billed on ${codes.length} different G-DRG codes. `
              + 'Open to see each one and filter to it.'}
            style={{
              display: 'inline-flex', alignItems: 'center', padding: '0 7px',
              border: 'none', borderLeft: `1px solid ${active ? 'rgba(255,255,255,.28)' : edge}`,
              background: open ? (active ? 'rgba(255,255,255,.18)' : BRAND.head) : 'none',
              cursor: 'pointer',
              color: active ? 'rgba(255,255,255,.9)' : BRAND.inkMuted,
            }}>
            <ChevronDown size={12} style={{
              transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .14s ease',
            }} />
          </button>
        )}
      </div>

      {/*  INTO document.body, not into this chip. See the note above the component:
           the chip's own row is an `overflow: auto` scrollport 34px tall, and it
           clipped the panel to a letterbox. `position: fixed` with coordinates
           measured off the chip, so there is no ancestor left to clip it. */}
      {open && pos && createPortal(
        <div role="menu" ref={panelRef}
          style={{
            position: 'fixed',
            left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width,
            maxHeight: pos.maxHeight, overflowY: 'auto',
            //  ABOVE EVERYTHING IN THIS SCREEN, BELOW EVERY DIALOG. A portal is a
            //  child of <body>, so it no longer inherits a stacking context from
            //  the filter row and has to name its own layer. The highest z-index in
            //  this file is 80; the lowest in the Claims dialogs is 999. 900 sits
            //  between, so the panel covers the sticky table header it overlaps and
            //  a confirmation still covers the panel.
            zIndex: 900,
            background: BRAND.surface, border: `1px solid ${BRAND.line}`,
            borderRadius: 9, boxShadow: '0 12px 34px rgba(15,23,42,.20)',
            padding: 5,
          }}>
          {/*  NAMES ITS CHIP. The panel no longer sits visually attached inside the
               row, so the heading is what ties it to the chip that opened it. */}
          <div style={{
            padding: '5px 8px 6px', fontSize: 10, fontWeight: 800,
            letterSpacing: '.06em', textTransform: 'uppercase', color: BRAND.inkFaint,
          }}>
            {label} · {codes.length} G-DRG codes
          </div>
          {/*  ALL OF THEM, first. Having opened the panel to narrow the specialty,
               the way back out of a code has to be in the same panel — not only on
               the Reset button at the far end of the row. */}
          <button type="button" role="menuitem"
            onClick={() => { onPickCode('all'); onToggle(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '6px 8px', borderRadius: 6, border: 'none', font: 'inherit',
              fontSize: 11.5, textAlign: 'left', cursor: 'pointer',
              background: (!chosenCode || chosenCode === 'all') ? BRAND.greenSoft : 'none',
              color: (!chosenCode || chosenCode === 'all') ? BRAND.green : BRAND.ink,
              fontWeight: (!chosenCode || chosenCode === 'all') ? 800 : 650,
            }}>
            <span style={{ flex: 1 }}>All of {label}</span>
            <span style={{ ...S.mono, fontSize: 10.5, color: BRAND.inkFaint }}>
              {Number(count || 0).toLocaleString()}
            </span>
          </button>
          <div style={{ height: 1, background: BRAND.line, margin: '5px 2px' }} />
          {codes.map((c) => {
            const on = chosenCode === c.code;
            //  A CODE FROM ANOTHER GROUP, sitting under this specialty. Not an
            //  error this panel can fix, and not noise either: 447 OPDC claims are
            //  billed on ZOOM05A, which is exactly what `specialty_not_the_service`
            //  reports. Marked, so the panel does not look like it has mixed the
            //  groups up itself.
            const foreign = code !== 'UNSPECIFIED' && c.code.slice(0, 4) !== code;
            return (
              <button key={c.code} type="button" role="menuitem"
                onClick={() => { onPickCode(c.code); onToggle(null); }}
                title={foreign
                  ? `${c.n.toLocaleString()} ${label} claim(s) are billed on ${c.code}, `
                    + `which belongs to ${c.code.slice(0, 4)} — not to ${label}.`
                  : `Show only the ${c.n.toLocaleString()} claim(s) billed on ${c.code}`}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 8, width: '100%',
                  padding: '6px 8px', borderRadius: 6, border: 'none', font: 'inherit',
                  fontSize: 11.5, textAlign: 'left', cursor: 'pointer',
                  background: on ? BRAND.greenSoft : 'none',
                  color: on ? BRAND.green : BRAND.ink,
                }}>
                <span style={{
                  ...S.mono, fontSize: 11, fontWeight: 800, flexShrink: 0,
                  color: on ? BRAND.green : (foreign ? BRAND.danger : BRAND.ink),
                }}>{c.code}</span>
                {/*  THE NAME, after a dash. "OPDC06A — General OPD - Adult": the
                     code is what NHIA reads and the name is what a person reads,
                     and neither alone is enough to pick the right row. */}
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 11, lineHeight: 1.45,
                  color: on ? BRAND.green : BRAND.inkMuted,
                }}>
                  {c.name ? `— ${c.name}` : ''}
                  {foreign && (
                    <span style={{
                      marginLeft: 5, padding: '0 4px', borderRadius: 3, fontSize: 9,
                      fontWeight: 800, background: BRAND.dangerSoft, color: BRAND.danger,
                    }}>{c.code.slice(0, 4)}</span>
                  )}
                </span>
                <span style={{
                  ...S.mono, fontSize: 10.5, flexShrink: 0,
                  color: on ? BRAND.green : BRAND.inkFaint,
                }}>{c.n.toLocaleString()}</span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

const TypeBadge = ({ value }) => {
  const ipd = value === 'IPD';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 5,
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.03em',
      background: ipd ? '#EDE4FF' : '#E1EEFA',
      color: ipd ? '#6D28D9' : '#1B5E97',
      border: `1px solid ${ipd ? '#D6C4FA' : '#BFDBF2'}`,
    }}>{value}</span>
  );
};

// Outcome carries clinical weight — a death or a transfer-out is not the same
// event as a discharge, and an officer scanning 2,383 rows should see that
// without reading. Colour encodes it; the word is still there for anyone who
// cannot rely on colour.
const STATUS_TONE = {
  Discharged:      { bg: '#E7F5EC', fg: '#1B6631', bd: '#BFE3CC' },
  Died:            { bg: '#FBE9E9', fg: '#A62828', bd: '#F0C4C4' },
  Transferred_out: { bg: '#FDF2E3', fg: '#8A5510', bd: '#F2DCB8' },
  Pending:         { bg: '#EEF1F6', fg: '#5A6B87', bd: '#DDE5EF' },
};

const StatusPill = ({ value }) => {
  if (!value) return <span style={{ color: BRAND.inkFaint }}>—</span>;
  const tone = STATUS_TONE[value] || STATUS_TONE.Pending;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 20,
      fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap',
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.bd}`,
    }}>
      {String(value).replace(/_/g, ' ')}
    </span>
  );
};

// ── The claim will be rejected ──────────────────────────────────────────────
//  Sits beside the outcome, because that column is where the eye already goes to
//  judge a row. Deliberately LOUD: this was invisible for the whole life of the
//  rule that produces it, and 207 claims went out on it.
//
//  It only appears on a claim that HAS a problem, and only where the check could
//  actually run — a typed-in claim keeps its diagnoses in another table these
//  predicates do not read, so `issues_checked` false means NOT CHECKED and draws
//  nothing rather than an all-clear.
//
//  ERRORS ONLY, and that is a deliberate threshold rather than a shortcut.
//
//  The importer sets is_principal on NO row it writes — all 3,529 of them are 0 —
//  so `no_principal_dx` is true of very nearly every imported claim without a
//  procedure. Badging those would put an amber pill on almost every line of the
//  table, and a badge that is always on is a badge nobody reads, including on the
//  one row that says the claim comes back rejected.
//
//  So the badge means exactly one thing: NHIA will reject this. The warnings are
//  still counted in the Advanced Filter, still listed in the editor's banner, and
//  still repairable in bulk — they just do not compete with the errors here.
//
//  The names come from the catalogue the server sent, so this component holds no
//  second copy of the rule titles. An unrecognised key is treated as an error and
//  still renders: a badge that silently disappears when the backend adds a rule is
//  worse than one that reads oddly for a release.
//  CLICKABLE, and it has to LOOK it. As a bare <span> it carried a title
//  attribute and nothing else — no cursor, no hover, no focus ring, and nothing
//  a screen reader would announce as an action. The tooltip named the rules but
//  only after a hover nobody knew to try, and it could not say WHICH value on
//  this claim failed. A <button> gets keyboard and assistive-tech behaviour for
//  free; the aria-label spells out what opening it does.
//
//  stopPropagation because the row itself is clickable — opening the reasons must
//  not also toggle the editor open underneath the dialog.
const IssueBadge = ({ codes, catalogue, onOpen }) => {
  if (!codes || !codes.length) return null;
  const known = (k) => (catalogue || []).find((c) => c.code === k);
  const errors = codes.filter((k) => (known(k)?.severity || 'error') === 'error');
  if (!errors.length) return null;

  const named = errors.map((k) => known(k)?.name || k);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(); }}
      title={`${named.join('\n')}\n\nClick for details`}
      aria-label={`Rejected by ${errors.length} rule${errors.length === 1 ? '' : 's'}: `
        + `${named.join(', ')}. Open the reasons.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6,
        padding: '2px 7px', borderRadius: 20, verticalAlign: 'middle',
        font: 'inherit', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap',
        background: '#FBE9E9', color: '#A62828', border: '1px solid #F0C4C4',
        cursor: 'pointer',
        //  The underline is what says "this opens something" without colour,
        //  which matters on a pill that is already red beside a red status.
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: 2,
        transition: 'background-color .15s ease, border-color .15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#F7D9D9';
        e.currentTarget.style.borderColor = '#E0A5A5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#FBE9E9';
        e.currentTarget.style.borderColor = '#F0C4C4';
      }}
    >
      <ShieldAlert size={11} />
      {/*  The word, not the icon and colour alone — this row already carries a
           coloured outcome pill beside it, and red-on-red is not a distinction a
           red-green colourblind officer can make at a glance.  */}
      REJECT{errors.length > 1 ? ` ${errors.length}` : ''}
    </button>
  );
};

// ── A row of tick-boxes with their counts ───────────────────────────────────
//  Used by BOTH check groups in the Advanced Filter — what the claim is missing
//  and what it gets wrong. Extracted when the second group arrived rather than
//  copied: the two are the same control asking different questions, and two
//  copies would have drifted the first time either was restyled.
//
//  Straight checkboxes, not a searchable list: each set is fixed and short, and
//  every entry is a question you either want asked or do not. Ticking several
//  means "any of these" — the same OR as several diagnoses in one panel. ANDing
//  them would ask for the one claim wrong in every way at once.
//
//  FLEX-WRAP, not a nested auto-fit grid. As a grid inside a spanning grid item
//  this box computed a one-row height, and `overflow: hidden` then silently ate
//  four of the seven checkboxes while the next group heading drew on top of it.
//  Wrapping flex items cannot collapse that way, and nothing here clips.
//  Each check is a CARD, not a line of text.
//
//  These used to be transparent-bordered buttons on a white card, which meant an
//  unticked check had no edge, no background and nothing to separate it from its
//  neighbours — seven options read as one grey paragraph, and the count on the
//  far right of a stretched row had drifted so far from its label that they no
//  longer looked related. Long names were truncated on top of that, so
//  "Admission billed on an out-patient service" ended at an ellipsis.
//
//  Now: a tinted well holding white cards, every one with a visible border, an
//  actual checkbox, a label allowed to wrap, and the count as a pill anchored to
//  the label rather than to the far edge of the row.
//  `actionFor(check)` may return a small button to sit in a tile's corner, for a
//  check that has a bulk remedy. Optional: a check with no remedy renders exactly
//  as before.
const CheckList = ({ items, chosen, loaded, onSet, empty, actionFor }) => {
  const picked = chosen || [];
  const list = items || [];
  return (
    <div style={{
      gridColumn: '1 / -1', border: `1px solid ${BRAND.line}`,
      borderRadius: 10,
      //  Tinted, so the white cards inside it have something to sit ON. On a
      //  white card on a white panel the only thing separating one check from
      //  the next was 2px of gap.
      background: BRAND.canvas,
      padding: 8,
    }}>
      {/*  FLEX-WRAP, not a nested auto-fit grid — as a grid inside a spanning
           grid item this box once computed a one-row height and `overflow:
           hidden` silently ate four of the seven checkboxes. Wrapping flex items
           cannot collapse that way.

           FOUR to a row. The basis is a PERCENTAGE with a matching maxWidth,
           which is what keeps the columns even — with a fixed pixel basis the
           last row's leftovers stretch to fill the width, so a row holding two
           checks drew them at twice the size of the four above.

           Four rather than two because this list only grows: seven checks at
           two-up is four rows of tall cards, most of it empty space, and every
           check added later pushes the groups below it further off screen.
           `minWidth` steps it down to three, two and finally one column as the
           dialog narrows, so nothing is ever crushed.  */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {list.map((g) => {
          const on = picked.includes(g.code);
          const reject = g.severity === 'error';
          const none = !g.claims;
          //  A per-check FIX action, where one exists. Some of these checks name a
          //  fault with a bulk remedy — "medication without a dose unit" is one
          //  correction per DRUG, not per claim — and the tile is where the
          //  officer already is when they find out.
          //
          //  Rendered as a sibling in a positioned wrapper, never inside the
          //  toggle: a button within a button is invalid, and the browser's own
          //  behaviour for it is undefined.
          const action = none ? null : (actionFor ? actionFor(g) : null);
          return (
            <div key={g.code} style={{
              position: 'relative',
              //  The tile's own flex basis moves out here so the wrapper is what
              //  sits in the row and the button fills it.
              flex: '1 1 calc(25% - 4.5px)',
              maxWidth: 'calc(25% - 4.5px)',
              minWidth: 178,
              display: 'flex',
            }}>
            <button type="button"
              onClick={() => onSet(on
                ? picked.filter((k) => k !== g.code)
                : [...picked, g.code])}
              aria-pressed={on}
              //  A count of 0 stays clickable. "Nothing is wrong" is the answer an
              //  auditor wants confirmed, and greying it out would read as the
              //  filter being broken.
              title={none ? 'No claims match this in the month' : undefined}
              style={{
                //  Fills the wrapper, which carries the row's flex basis now.
                flex: 1, minWidth: 0,
                boxSizing: 'border-box',
                display: 'flex', alignItems: 'flex-start', gap: 8,
                //  Room for the Fix button in the corner, so a long check name
                //  wraps clear of it rather than running underneath.
                padding: action ? '8px 10px 26px' : '8px 10px',
                borderRadius: 8, cursor: 'pointer',
                textAlign: 'left', font: 'inherit', fontSize: 12,
                //  Always a real border. This is the whole point: an unticked
                //  option has to be as findable as a ticked one.
                border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                boxShadow: on
                  ? `0 0 0 1px ${BRAND.greenDark}, 0 1px 2px rgba(15,31,61,.10)`
                  : '0 1px 2px rgba(15,31,61,.05)',
                background: on ? BRAND.greenSoft : BRAND.surface,
                //  A check with nothing to find recedes. It stays CLICKABLE — see
                //  the note above; "nothing is wrong" is an answer an auditor
                //  wants to confirm — but at full strength a dozen empty tiles
                //  competed with the two or three that have claims behind them,
                //  which is the opposite of what this list is for.
                opacity: none && !on ? .62 : 1,
                transition: 'border-color .12s ease, box-shadow .12s ease, opacity .12s ease',
              }}>
              {/*  16px rather than 13. At 13 with a 1.5px border the tick was
                   three pixels of white on green and read as a filled square. */}
              <span aria-hidden="true" style={{
                width: 15, height: 15, borderRadius: 4, flexShrink: 0, marginTop: 1,
                display: 'grid', placeItems: 'center',
                border: `1.5px solid ${on ? BRAND.greenDark : '#B6C2D4'}`,
                background: on ? BRAND.greenDark : '#fff',
                color: '#fff', fontSize: 10, fontWeight: 900, lineHeight: 1,
              }}>{on ? '✓' : ''}</span>

              {/*  The label and its count FLOW together as inline content rather
                   than sitting on two stacked rows. On a short name the count
                   lands on the same line and the card is one line tall; on a
                   long one it follows the wrapped text. Stacking them cost a
                   fixed ~20px of height on every card, which is most of the
                   empty space this panel was carrying.  */}
              <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                {/*  WRAPS. Truncating the name of a check is the one thing this
                     list cannot afford — "Admission billed on an out-patient
                     service" and "…with no principal diagnosis" both begin
                     "Admission billed on an", so an ellipsis makes them
                     indistinguishable. */}
                <span style={{
                  color: on ? BRAND.greenDark : BRAND.ink,
                  fontWeight: on ? 700 : 600,
                }}>
                  {g.name}
                </span>
                {' '}
                {/*  The count, attached to its own label rather than floating at
                     the far right of a stretched row. Says what the number IS,
                     too — a bare "18" next to "No medication" could be read as
                     eighteen medications. */}
                <span style={{
                  display: 'inline-block', whiteSpace: 'nowrap',
                  fontSize: 10.5, fontWeight: 800, lineHeight: 1,
                  padding: '3px 6px', borderRadius: 20,
                  background: none ? '#EEF2F7' : (on ? '#fff' : BRAND.navySoft),
                  color: none ? BRAND.inkFaint : BRAND.navy,
                  border: `1px solid ${none ? '#E2E8F0' : 'transparent'}`,
                }}>
                  {none ? 'none' : `${Number(g.claims).toLocaleString()} claim${g.claims === 1 ? '' : 's'}`}
                </span>
                {/*  Severity in words. An error means the claim comes back from
                     NHIA; a warning means it is worth a look. Colour alone would
                     not carry that, and this list is scanned quickly.  */}
                {/*  Two badges, not one. A check that makes NHIA send the claim
                     back and a check that is merely worth a look are different
                     jobs, and colour alone would not carry that — so each says
                     which it is in words, in its own colour.

                     REJECTS is red and means the claim comes back. CHECK is
                     AMBER and means the claim is payable but something on it is
                     worth an eye: medication that stops before the patient goes
                     home is the case this was added for. Red for both would have
                     made a worklist of 1,306 amber rows look like 1,306
                     rejections. */}
                {(reject || g.severity === 'warning') && (
                  <>
                    {' '}
                    <span style={{
                      display: 'inline-block', whiteSpace: 'nowrap',
                      fontSize: 9, fontWeight: 800, letterSpacing: .3,
                      padding: '3px 5px', borderRadius: 4, lineHeight: 1,
                      background: reject ? BRAND.dangerSoft : '#FFF4E5',
                      color: reject ? '#A62828' : '#9A5B00',
                      border: `1px solid ${reject ? 'rgba(166,40,40,.22)' : 'rgba(154,91,0,.22)'}`,
                    }}>
                      {reject ? 'REJECTS' : 'CHECK'}
                    </span>
                  </>
                )}
              </span>
            </button>

            {action}
            </div>
          );
        })}
        {/*  ── STILL LOADING ────────────────────────────────────────────────
             Skeleton cards, not an empty box. `loaded` false used to render
             nothing at all, so this well drew as a bare grey strip — which reads
             as "no checks apply to this month", the opposite of the truth. The
             placeholders also hold the panel's height, so the groups below do not
             jump when the real checks arrive.  */}
        {!loaded && Array.from({ length: 8 }).map((_, i) => (
          <div key={`sk-${i}`} aria-hidden="true" style={{
            flex: '1 1 calc(25% - 4.5px)', maxWidth: 'calc(25% - 4.5px)',
            minWidth: 178, boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 8,
            border: `1px solid ${BRAND.line}`, background: BRAND.surface,
          }}>
            <div style={{
              width: 15, height: 15, borderRadius: 4, flexShrink: 0,
              background: '#E7EDF6',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                height: 9, borderRadius: 5, background: '#E7EDF6',
                //  Varied widths so it reads as text loading rather than as a
                //  row of identical placeholder bars.
                width: `${[78, 62, 88, 54, 70, 84, 60, 74][i % 8]}%`,
              }} />
            </div>
          </div>
        ))}

        {loaded && list.length === 0 && (
          <div style={{
            flex: '1 1 100%', padding: '12px 11px', fontSize: 12,
            color: BRAND.inkMuted, textAlign: 'center',
          }}>
            {empty}
          </div>
        )}
      </div>
    </div>
  );
};

// Nights between two dates. Shown only for a genuine multi-day stay: an officer
// checking whether an inpatient claim's length of stay looks right should not
// have to subtract two dates in their head, 2,383 times.
const nights = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(from); const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const d = Math.round((b - a) / 86400000);
  return d > 0 ? d : null;
};

export default function ClaimsMenu({
  monthData,
  selectedYear,
  onBack,
  // The schemes ticked on the month card. [] means every scheme in the month.
  // A facility runs several at once, and which ones are on screen must never be
  // something the officer has to infer — so the applied list is printed in the
  // heading, and it comes back from the SERVER rather than being echoed from what
  // this component sent.
  insurances = [],
  // ── Facility scope, inherited from the dashboard ─────────────────────────
  //  'all', 'own' (typed in here) or a client_facilities id. Deliberately NOT
  //  re-selectable on this screen: the scope is chosen once, on the dashboard,
  //  and carried in. Every action here — select-first-N, Multi Task, Bulk Delete —
  //  works from the rows this component fetched, so a scoped fetch is what makes
  //  all of them facility-safe. A second selector here would be a second place for
  //  the two to disagree.
  facility = 'all',
  facilityLabel = 'All facilities',
}) {
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({ all: 0, ipd: 0, opd: 0, specialties: {} });
  const [sources, setSources] = useState([]);
  // What the SERVER says the rows belong to, not what we asked for.
  const [appliedFacility, setAppliedFacility] = useState('all');
  const [facilitiesInView, setFacilitiesInView] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [typeTab, setTypeTab] = useState('all');       // all | opd | ipd
  const [specialty, setSpecialty] = useState('all');
  //  ── ONE SPECIFIC G-DRG WITHIN A SPECIALTY ─────────────────────────────────
  //
  //  "We have so many of the OPDC — OPDC06A, OPDC06C. We have ZOOM02, ZOOM04. So I
  //  want all the specialties to now break down. There is a little drop-down on it,
  //  you click on it and you see the specification, and when I click, it can filter."
  //
  //  A SUB-FILTER OF THE SPECIALTY, not a fourth independent dimension: OPDC06A
  //  only means anything under OPDC. So picking a code sets its specialty too, and
  //  picking a different specialty clears the code — OBGY together with OPDC06A is
  //  an empty table nobody asked for.
  const [serviceCode, setServiceCode] = useState('all');
  //  Which chip's breakdown is open. ONE at a time: ten open lists of thirty codes
  //  is the scrolling this row exists to avoid.
  const [openSpec, setOpenSpec] = useState(null);
  const [q, setQ] = useState('');
  //  NOTE: `dateRange` is declared further down, beside `dateDraft` and
  //  `applyDates` — the draft/applied pair for the visit-date window. Nothing to
  //  add here; a second declaration is a redeclaration error, which is what
  //  happened when it looked like the state was missing.

  // ── Advanced Filter ───────────────────────────────────────────────────────
  //  Filters on what is INSIDE a claim rather than on its header: show me the
  //  claims carrying this diagnosis, this procedure, this drug. Two pieces of
  //  state on purpose — the DRAFT the dialog edits, and the APPLIED set the fetch
  //  uses. Typing in the dialog must not re-query on every keystroke across 1,095
  //  claims; Search is what commits it.
  //  `service` is the claim's specialty G-DRG — a HEADER field, so it goes through
  //  the endpoint's existing which_code parameter rather than a child EXISTS.
  //  ARRAYS, not strings. Several diagnoses can be ticked at once — "Malaria or
  //  Typhoid" — and the server ORs within a filter and ANDs across them.
  const EMPTY_ADV = {
    //  `investigation` is here because the Advanced Filter now has a Lab / Scan
    //  panel. The server has always served the facet and always accepted the
    //  filter — CHILD_FILTERS.investigation — so this was a gap on the screen
    //  rather than in the API: a test billed across 1,205 claims could be filtered
    //  in the composer and not here.
    diagnosis: [], procedure: [], investigation: [], medication: [],
    service: [], type: 'all',
    //  Completeness checks — what the claim is MISSING. Keys, not codes; see
    //  GAP_FILTERS in claimStore.js. Several ticked means "any of these".
    gaps: [],
    //  Consistency checks — what the claim gets WRONG. Keys from
    //  claimRules.ISSUE_SQL. Separate from `gaps` because they are acted on
    //  differently: a missing medication is something you go and find, an
    //  admission billed on an OPD tariff is something the claim can repair from
    //  its own rows.
    issues: [],
    //  WHO the patient was, in years, at the visit. Empty string means "no
    //  bound", which is why these are strings and not numbers — 0 is a real age
    //  (under-1s are 908 claims here) and `Number('') === 0` would have made an
    //  empty box mean "from birth".
    //
    //  The only advanced filter that works on BOTH claim models: it is computed
    //  from a date of birth and a visit date, and typed-in claims carry both.
    ageFrom: '', ageTo: '',
    //  ── GENDER ──────────────────────────────────────────────────────────────
    //  An array, like the code filters, because more than one can be wanted and
    //  because "male AND not-recorded" is a real cohort — the claims NHIA will
    //  query for a missing gender, plus the ones it will accept.
    //
    //  No facet list for it: there are only ever three values, so the panel is a
    //  fixed row of three, the same reasoning as the attendance row. A facet query
    //  would also have covered imported claims only, while the filter itself works
    //  on both stores.
    gender: [],
    //  ── HOW LONG THEY STAYED ────────────────────────────────────────────────
    //  An array of day counts — '1', '2', '3' … and '10+' for the tail — because
    //  "two or three days" is one cohort to an officer and two filters to a computer.
    //
    //  '1' means SAME DAY: admitted and discharged on one date, never a night on the
    //  ward. That is the distinction this filter exists to make visible. 19 of KYEBI's
    //  321 May in-patients are same-day, and on a table showing only the two dates
    //  they look no different from the ones that stayed.
    //
    //  Strings, not numbers, because '10+' is one of the values and a mixed array is
    //  worse than a consistent one. The server parses both forms.
    stayDays: [],
  };
  //  ── NHIA XML export ───────────────────────────────────────────────────────
  //  Scoped by the facility and month already on screen, narrowed by an optional
  //  date range and attendance. Downloaded as a blob rather than a plain link so a
  //  server-side error arrives as a MESSAGE instead of a browser-saved file
  //  containing JSON — which is what a naive window.open would produce.
  //  Takes the choices from the export dialog. Swal's own form controls were the
  //  wrong tool — `swal2-input` forces its own width and margins, which is what
  //  put "Attendance" and "Facility type" on the same line.
  //  No `force` option any more: it is always on. It existed so the 409 handler
  //  could call this back with the officer's override, and there is no 409 handler.
  const runExport = async (r) => {
    const monthName = monthData?.month || '';
    //  `ids` names specific claims and the server then ignores every other filter,
    //  so one claim can be exported straight from its row — and so can a ticked
    //  selection — without also having to satisfy the month and specialty the
    //  screen happens to be showing.
    //  ── THE REFS CARRY THEIR MODEL, AND THAT IS THE WHOLE POINT ─────────────
    //
    //  THE BUG THIS REPLACES. This read `r.ids.map(Number).filter(Boolean)` and
    //  posted the result. The export matches by id against ONE model's table, and
    //  a typed-in claim's row id is a plain integer too — so hospital claims were
    //  sent as if they were imported ones. Two outcomes, both wrong: no overlap
    //  gave "No claims match that selection" on 617 claims plainly on screen, and
    //  an overlap would have put a DIFFERENT patient's claim into an NHIA
    //  submission.
    //
    //  Both models are exported now, so the refs are not split here — they are
    //  passed through TAGGED and the server decides which table each one means.
    //  An untagged value is a legacy link, and those only ever named imported
    //  claims; the server reads them that way.
    const only = (r.ids || []).length
      ? (r.ids || []).map((v) => {
        const s = String(v);
        return s.includes(':') ? s : `imported:${s}`;
      })
      : null;

    const { from, to, attendance, kind, specialties } = r;

    Swal.fire({ title: 'Generating the XML…', allowOutsideClick: false,
      didOpen: () => Swal.showLoading() });
    try {
      const args = {
        year: selectedYear, month: monthName,
        //  The facility already chosen on this screen. Never widened here — a
        //  submission carrying another facility's claims is the one mistake this
        //  export must not be able to make. It applies even alongside `ids`, on
        //  the server, for the same reason.
        facility,
        from: from || undefined, to: to || undefined,
        attendance, kind,
        specialty: specialties?.length ? specialties.join(',') : undefined,
          //  ── ALWAYS FORCED. THE EXPORT NEVER REFUSES. ──────────────────────
          //  The server still judges every claim and still answers 409 with the
          //  offenders when it is not forced — that contract is unchanged and
          //  other callers keep it. This screen simply does not ask.
          //
          //  It used to. On a month where 2,637 of 2,637 claims were billed
          //  out-patient on OPDC, the refusal listed fifteen of them, said "and
          //  2,622 more", and offered Export anyway / Stop and fix them. A gate
          //  that stops everything stops nothing: it cannot be acted on — none of
          //  those can be repaired automatically, each needs a service choosing —
          //  so the only real answer was Export anyway, every time, in front of a
          //  list nobody could use.
          //
          //  The faults are not hidden by this. They are on the claims list as
          //  REJECT badges, countable on the Advanced Filter, explained per claim
          //  in the reject dialog, and repairable in bulk where the claim's own
          //  rows allow it. Those are the places to act; the export is not, and
        //  making it the last line of defence only made it the place where work
        //  stopped.
        force: 1,
      };

      //  ── POST WHEN THE CLAIMS ARE NAMED, GET OTHERWISE ────────────────────
      //
      //  A GET puts `ids` in the URL, and a ticked month does not fit: 3,064
      //  selected claims is about 21,000 characters of query string, several
      //  times what Node accepts on a request line and past what a proxy in
      //  front of it allows. That fails as a 431 or a dropped connection, which
      //  on screen is indistinguishable from the export being broken.
      //
      //  So a named selection goes in a JSON body. The GET is kept for
      //  everything else — one claim from its row, and any saved link.
      const { data, headers } = only
        ? await axios.post(claimsXmlExport, { ...args, ids: only }, { responseType: 'blob' })
        : await axios.get(claimsXmlExport, { params: args, responseType: 'blob' });
      //  A 404 "nothing matches" comes back as JSON with a blob responseType, so
      //  it has to be read as text before it can be reported.
      if (data.type && data.type.includes('json')) {
        const msg = JSON.parse(await data.text())?.error || 'Nothing to export.';
        Swal.fire({ icon: 'info', title: 'Nothing to export', text: msg });
        return;
      }
      const count = headers['x-claim-count'] || '';
      const match = /filename="([^"]+)"/.exec(headers['content-disposition'] || '');
      const name = match ? match[1] : `NHIA_Claims_${selectedYear}.xml`;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      //  ── AND ANY NAMED CLAIM THAT DID NOT REACH THE FILE ───────────────────
      //  Read from the server's own count rather than inferred here. Both models
      //  are exported now, so a shortfall means a row could not be read or the
      //  flat load was refused — not "the wrong kind of claim". Saying nothing
      //  would leave a file with fewer claims than were ticked and no
      //  explanation.
      const skipped = Number(headers['x-typed-in-skipped'] || 0);
      const incomplete = headers['x-typed-in-incomplete'] || '';
      Swal.fire({
        icon: skipped ? 'warning' : 'success',
        title: `${count} claim(s) exported`,
        html: `<div style="text-align:left;font-size:13.5px;line-height:1.6">${name}`
          + `${skipped
            ? `<p style="margin-top:10px;color:#8A5510"><b>`
              + `${skipped.toLocaleString()} typed-in claim(s) named were not in this `
              + `file.</b><br><span style="color:#6B7280">`
              + `${incomplete || 'Their rows could not be read.'}</span></p>`
            : ''}</div>`,
        confirmButtonText: 'Done',
      });
    } catch (e) {
      //  A server error arrives as a Blob because responseType is 'blob', so the
      //  message has to be read out of it before it can be shown. `body` is no
      //  longer kept — the only thing that read it was the 409 offender list.
      let msg = e.message;
      try {
        if (e.response?.data instanceof Blob) {
          msg = JSON.parse(await e.response.data.text())?.error || msg;
        }
      } catch { /* keep the original message */ }

      //  ── NO REFUSAL DIALOG ────────────────────────────────────────────────
      //  There was one here: a 409 handler that listed the offending claims and
      //  offered "Export anyway" / "Stop and fix them". It is gone, and the
      //  request above is always forced, so the 409 can no longer arrive.
      //
      //  It was the right idea in the wrong place. On a month where 2,637 of 2,637
      //  claims were billed out-patient on OPDC it named fifteen, said "and 2,622
      //  more", and reported that none could be repaired automatically. Every
      //  route out of it but one was a dead end, so it was a confirmation step
      //  with a single real answer — friction, not a safeguard.
      //
      //  Nothing about the checking changed. The server still judges every claim
      //  and the faults are still visible where they can be acted on: REJECT
      //  badges on the list, counts on the Advanced Filter, a per-claim
      //  explanation in the reject dialog, and Fix all where the claim's own rows
      //  can settle it. The export is a download; it is not the place to litigate
      //  the month.
      //
      //  Anything that DOES reach here is a genuine failure — the file could not
      //  be produced — and is reported as one.
      Swal.fire({ icon: 'error', title: 'The export failed', text: msg });
    }
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [vetOpen, setVetOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [advDraft, setAdvDraft] = useState(EMPTY_ADV);
  const [adv, setAdv] = useState(EMPTY_ADV);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filtersUnsupportedFor, setFiltersUnsupportedFor] = useState([]);
  //  What each key in a row's `issues` array means. Sent with the list itself, so
  //  the badge can name a rule without this component holding its own copy of the
  //  titles — the copy that would go stale the first time a rule is added.
  const [issueTypes, setIssueTypes] = useState([]);
  //  The GAP catalogue — what a claim can be MISSING, as opposed to what it gets
  //  wrong. Carried beside issueTypes because the error report needs both and had
  //  only ever been given the second: an officer could filter to "No medication ·
  //  735 claims" and find no mention of it anywhere in the report.
  const [gapTypes, setGapTypes] = useState([]);
  //  How many are set, for the button's badge — the whole point is that a filtered
  //  list must never look like an unfiltered one.
  //  Counts VALUES, not panels: three diagnoses plus a drug is four things narrowing
  //  the list, and the badge should say four.
  const advCount = ['diagnosis', 'procedure', 'medication', 'service', 'gaps', 'issues', 'gender']
    .reduce((n, k) => n + (adv[k]?.length || 0), 0)
    + (adv.type !== 'all' ? 1 : 0);
  const [selected, setSelected] = useState(() => new Set());
  //  ── The visit-date range ──────────────────────────────────────────────────
  //  TWO pieces of state, not one. The boxes are typed into freely (`dateDraft`)
  //  and only `dateRange` narrows the table — because a date is entered a digit
  //  at a time, and filtering on each keystroke would empty the screen while the
  //  year is still being typed. The search box beside it filters as you type,
  //  which is right for a name and wrong for a date.
  const [dateDraft, setDateDraft] = useState({ from: '', to: '' });
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  //  Reversed ends are swapped rather than refused — "the 30th to the 1st" can
  //  only mean one thing, and an empty table with no explanation is worse.
  const applyDates = () => {
    const a = dateDraft.from;
    const b = dateDraft.to;
    setDateRange(a && b && a > b ? { from: b, to: a } : { from: a, to: b });
  };

  const [selectFirst, setSelectFirst] = useState(50);
  const [sortBy, setSortBy] = useState(null);          // member_no | folder_no
  const [sortDir, setSortDir] = useState('asc');

  //  How many rows are actually put in the DOM at once. 100 by default: a screen
  //  holds roughly 25, so a page is four screens of scrolling — enough to work
  //  through without the pager becoming the job, and two orders of magnitude off
  //  the 6,974 that were being rendered before.
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);

  // The claim whose editor is open, as `${source}-${id}`. One at a time: two open
  // editors on screen is two claims you could confuse, and this panel is a page's
  // worth of fields. Clicking the pencil on another row moves it there.
  const [openRow, setOpenRow] = useState(null);

  //  The claim whose REJECT badge was clicked. Holds the row, not just an id, so
  //  the dialog's header can name the patient before the detail request lands —
  //  a dialog that opens blank and fills in a beat later reads as broken.
  const [rejectFor, setRejectFor] = useState(null);

  //  The bulk dose-unit fix, opened from its own tile in the Advanced Filter.
  //  Owned here rather than inside that dialog so it survives the filter closing.
  const [doseFixOpen, setDoseFixOpen] = useState(false);
  //  The G-DRG re-code, opened from the ipd_on_opd_service tile. Owned here for the
  //  same reason as the dose fix: the Advanced Filter closes behind it.
  const [recodeOpen, setRecodeOpen] = useState(false);

  //  The clinical review — the scan that asks whether the treatment on each claim
  //  fits the condition billed. Owned here because the button that opens it sits
  //  in the header and the count it produces is read by the table.
  const [aiOpen, setAiOpen] = useState(false);
  //  Show only the claims it flagged. A plain boolean rather than a member of the
  //  Advanced Filter's issue list: those are answered by SQL over claimRules'
  //  catalogue, and this one is answered by a stored opinion in another table.
  //
  //  ── IT SURVIVES A RELOAD ─────────────────────────────────────────────────
  //
  //  ASKED FOR: "I can click and I can filter and I can still quickly go through
  //   — what happens if there are so many that I have to review them one after
  //   the other?"
  //
  //  Working through 58 flagged claims is not one sitting. Closing the browser
  //  and coming back to the whole month again, with the narrowing to be found and
  //  re-applied, is how a list of 58 becomes a list nobody finishes. sessionStorage
  //  rather than localStorage: it should outlive a reload and a navigation away,
  //  not a week.
  const [aiOnly, setAiOnly] = useState(() => {
    try { return sessionStorage.getItem('mefims.claims.aiOnly') === '1'; }
    catch { return false; }                                  // private mode
  });
  useEffect(() => {
    try {
      if (aiOnly) sessionStorage.setItem('mefims.claims.aiOnly', '1');
      else sessionStorage.removeItem('mefims.claims.aiOnly');
    } catch { /* private mode */ }
  }, [aiOnly]);

  // ── A fixed claim stops being flagged, on the same click ───────────────────
  //  The row's `issues` array is computed server-side when the MONTH is fetched,
  //  so it is a snapshot. Correct a patient's surname in the editor and the
  //  editor agrees the claim is clean — while the red REJECT badge on the row
  //  behind it stays put, because nothing told the list anything had changed.
  //  `onChanged` was declared on ClaimEditor and never passed, so every write
  //  path was calling into nothing.
  //
  //  This patches the ONE row from the claim the editor just re-read, rather
  //  than refetching the month. A reload would be a second round trip, would
  //  collapse the open editor and would throw the officer back to the top of a
  //  1,095-row table — for information already sitting in the response.
  //
  //  `fresh` is the claimDetail payload: its `issues` are the full rule set, of
  //  which the row shows the SQL-backed subset. Taking them wholesale is right —
  //  the badge filters to errors itself, and a rule the list cannot compute is
  //  still true of the claim.
  const applyFreshClaim = useCallback((fresh) => {
    const h = fresh?.header;
    if (!h?.id) return;
    setClaims((prev) => prev.map((c) => {
      if (String(c.id) !== String(h.id) || c.source !== fresh.source) return c;
      return {
        ...c,
        issues: (fresh.issues || []).map((i) => i.code),
        //  The columns the badge sits beside, so the row stops disagreeing with
        //  the panel that is open on top of it. A name corrected in the editor
        //  and still blank in the table is the same fault as the stale badge.
        name: `${h.surname || ''} ${h.other_names || ''}`.trim() || c.name,
        dob: h.date_of_birth ?? c.dob,
        member_no: h.member_no ?? c.member_no,
        ccc: h.ccc ?? c.ccc,
        folder_no: h.folder_no ?? c.folder_no,
        attendance: h.specialty_code ?? c.attendance,
        service_code: h.service_code ?? c.service_code,
        service_name: h.service_name ?? c.service_name,
        service: h.service_name
          ? `${h.service_name}${h.service_code ? ` (${h.service_code})` : ''}`
          : c.service,
        discharge_date: h.discharge_date ?? c.discharge_date,
        type: h.attendance_type
          ? (h.attendance_type === 'inpatient' ? 'IPD' : 'OPD')
          : c.type,
      };
    }));
  }, []);

  // ── Opening an editor takes over the viewport ──────────────────────────────
  //  Whichever row you click, the editor scrolls to the top of the table's own
  //  scroll area so it gets the whole visible height. Clicking a claim 40 rows
  //  down and then having to scroll to find the form it opened is the thing this
  //  avoids.
  //
  //  The scroll position at the moment of opening is remembered, and restored on
  //  close, so the row you were working through is exactly where you left it.
  const wrapRef = useRef(null);
  const returnToRef = useRef(null);

  // The last claim that was open, kept after the panel closes so the row stays
  // marked. Working a month means finishing one claim and moving to the next, and
  // without this the officer lands back in an undifferentiated list of 2,383 rows
  // with no sign of where they had got to.
  const [lastEdited, setLastEdited] = useState(null);

  // How tall the table's sticky column header is. The editor's own identity bar
  // sticks directly BENEATH it, so this has to be measured rather than guessed —
  // hardcoding 30px would tuck the bar under the header the first time the header
  // wraps to two lines at a narrow width.
  const [headerH, setHeaderH] = useState(0);

  const openEditor = (rowKey) => {
    if (openRow === rowKey) { closeEditor(); return; }
    // Only remember the position on the FIRST open. Jumping from one open editor
    // straight to another should still return you to where you originally were,
    // not to the previous editor's position.
    if (openRow === null && wrapRef.current) {
      returnToRef.current = wrapRef.current.scrollTop;
    }
    setOpenRow(rowKey);
  };

  const closeEditor = () => {
    const closing = openRow;
    const back = returnToRef.current;
    returnToRef.current = null;
    setLastEdited(closing);
    setOpenRow(null);

    const wrap = wrapRef.current;
    if (!wrap) return;
    // TWO frames. The first lets the panel unmount; the second lets the container
    // shrink back to its non-editing max-height. Acting in between would be
    // clamped by a height that is about to change.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Land on the row just finished, not on a remembered scroll offset. After
        // stepping through several claims with Next, the position you started at
        // is no longer where you are — the row you last worked is.
        const row = closing && wrap.querySelector(`[data-claim-row="${closing}"]`);
        if (row) {
          const delta = row.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
          const head = wrap.querySelector('thead');
          const headH = head ? head.getBoundingClientRect().height : 0;
          // A third of the way down, so the next few claims are visible under it.
          wrap.scrollTo({
            top: Math.max(0, wrap.scrollTop + delta - headH - wrap.clientHeight / 3),
            behavior: 'smooth',
          });
        } else if (back != null) {
          const max = wrap.scrollHeight - wrap.clientHeight;
          wrap.scrollTop = Math.max(0, Math.min(back, max));
        }
      });
    });
  };

  // NOTE: the Prev/Next stepping helpers live BELOW `visible`, not here. A
  // useMemo's dependency array is evaluated where it is written, so referencing
  // `visible` before its own declaration is a temporal-dead-zone ReferenceError on
  // every render — which is what happened when they were first written up here.

  // ── Putting the editor at the top ─────────────────────────────────────────
  //  Two things made a naive version land short, leaving a screenful of rows
  //  above the panel instead of the panel at the top:
  //
  //  1. CLAMPING. On mount the editor is still a one-line "Loading…" placeholder,
  //     so the container has barely more scrollable height than before and the
  //     browser clamps scrollTop to whatever fits. By the time the real panel
  //     renders — 900px of cards — nothing scrolls again. So this runs a second
  //     time once the editor reports it has content (`scrollOpenEditor` is called
  //     from the onReady callback below).
  //
  //  2. offsetTop is measured against the nearest POSITIONED ancestor, which for
  //     a <tr> inside a table is not necessarily the scroll container. Rects are
  //     measured against the viewport, so the delta between two of them is exact
  //     regardless of what is positioned where.
  const scrollOpenEditor = useCallback((behavior = 'smooth') => {
    const wrap = wrapRef.current;
    if (!wrap || !openRow) return;
    const row = wrap.querySelector(`[data-editor-row="${openRow}"]`);
    if (!row) return;
    // The sticky thead sits at the top of the scrollport, so its height has to
    // come off or the first card lands underneath it.
    const head = wrap.querySelector('thead');
    const headH = head ? head.getBoundingClientRect().height : 0;
    const delta = row.getBoundingClientRect().top
                - wrap.getBoundingClientRect().top
                - headH;
    if (Math.abs(delta) < 2) return;
    wrap.scrollTo({ top: wrap.scrollTop + delta, behavior });
  }, [openRow]);

  useEffect(() => {
    if (!openRow) return;
    // First attempt on mount. Usually clamped, and that is fine — onReady runs it
    // again with the panel at full height.
    const id = requestAnimationFrame(() => scrollOpenEditor('auto'));
    return () => cancelAnimationFrame(id);
  }, [openRow, scrollOpenEditor]);

  // Keep the measured header height current. Re-measured on open and on resize,
  // because the header can wrap at narrow widths and the editor's sticky bar is
  // positioned off this number.
  useEffect(() => {
    const measure = () => {
      const head = wrapRef.current?.querySelector('thead');
      if (head) setHeaderH(Math.round(head.getBoundingClientRect().height));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // `claims`, not `visible` — this sits above `visible`'s declaration, and a
    // dependency array is evaluated where it is written. The thead's height does
    // not depend on the filtered subset anyway.
  }, [openRow, claims.length]);

  // What the SERVER says it filtered by, not what we asked for. If the two ever
  // disagree, the heading tells the truth about the rows on screen.
  const [applied, setApplied] = useState([]);

  const schemeLabel = applied.length
    ? applied.join(' + ')
    : 'All Insurance Types';

  // Serialised for the dependency array — an array literal is a new reference on
  // every render and would re-fetch forever.
  const schemeKey = insurances.join('|');

  // ── Load ──────────────────────────────────────────────────────────────────
  //  The whole month, unfiltered by type/specialty — those are applied here so a
  //  pill click is instant and the pill counts stay stable. The server still
  //  scopes by year, month and scheme, which is what keeps this to one month.
  const load = useCallback(async () => {
    if (!monthData?.month || !selectedYear) return;
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(claimsdetails, {
        params: {
          year: selectedYear,
          month: monthData.month,
          // Comma-separated; the endpoint accepts one name, several, or 'all'.
          insurance_type: insurances.length ? insurances.join(',') : 'all',
          // The scope, applied server-side to the rows AND to the pill counts, so
          // nothing on this screen can reach another facility's claims.
          facility,
          //  Advanced Filter. Sent as-is; the server matches each against a code
          //  OR a name and echoes back what it honoured. Blank means "not set" and
          //  is dropped rather than sent as an empty match-everything.
          //  Comma-joined rather than repeated params: the server splits on commas
          //  as well as accepting an array, and one string keeps the URL readable
          //  in a network log.
          diagnosis: adv.diagnosis.length ? adv.diagnosis.join(',') : undefined,
          procedure: adv.procedure.length ? adv.procedure.join(',') : undefined,
          medication: adv.medication.length ? adv.medication.join(',') : undefined,
          investigation: adv.investigation?.length
            ? adv.investigation.join(',') : undefined,
          //  The attendance filter lives in the dialog too, but it is a HEADER
          //  field, so the server has always been able to apply it.
          type: adv.type !== 'all' ? adv.type : undefined,
          //  The specialty G-DRG. The endpoint already had this parameter for the
          //  pill row, so the dialog reuses it rather than adding a second way to
          //  ask the same question.
          which_code: adv.service.length ? adv.service.join(',') : undefined,
          //  Completeness. Sent as keys the server whitelists, so an unknown one
          //  is dropped rather than widening or breaking the query.
          gaps: adv.gaps?.length ? adv.gaps.join(',') : undefined,
          //  Consistency. Same discipline, its own whitelist — claimRules.ISSUE_SQL.
          issues: adv.issues?.length ? adv.issues.join(',') : undefined,
          //  Age at the visit. Either bound may travel alone; a blank one is
          //  dropped rather than sent as an empty string, which the server would
          //  have to decide the meaning of.
          gender: adv.gender?.length ? adv.gender.join(',') : undefined,
          ageFrom: adv.ageFrom !== '' && adv.ageFrom != null ? adv.ageFrom : undefined,
          ageTo: adv.ageTo !== '' && adv.ageTo != null ? adv.ageTo : undefined,
          //  Length of stay, as the comma list the server's parser reads — day counts
          //  and the '10+' tail token in one parameter, so "2 or 3 days or a fortnight"
          //  is one filter rather than three.
          stayDays: adv.stayDays?.length ? adv.stayDays.join(',') : undefined,
        },
      });
      if (data?.success) {
        setClaims(data.claims || []);
        setStats(data.stats || { all: 0, ipd: 0, opd: 0, specialties: {} });
        setSources(data.sources || []);
        setApplied(data.appliedInsurances || []);
        setAppliedFacility(data.appliedFacility || 'all');
        setFacilitiesInView(data.facilitiesInView || []);
        setAppliedFilters(data.appliedFilters || []);
        setFiltersUnsupportedFor(data.filtersUnsupportedFor || []);
        setIssueTypes(data.issueTypes || []);
        setGapTypes(data.gapTypes || []);
        //  ── RETURNS THE ROWS IT LOADED ──────────────────────────────────────
        //  So a caller that re-read AFTER writing can count them. `claims` in a
        //  handler's scope is the value from its own render and does not update
        //  when this calls setClaims — a delete handler comparing against it
        //  would read a stale length and always conclude nothing else moved.
        //  That is exactly the comparison the delete confirmation needs to be
        //  right about, since it is telling the officer whether a row that
        //  vanished was deleted or merely filtered out.
        return data.claims || [];
      }
      setError(data?.message || 'Failed to load claims');
      setClaims([]);
      return null;
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setClaims([]);
      return null;
    } finally {
      setLoading(false);
    }
    //  `adv` is a dep: pressing Search replaces it, which re-runs this. It is the
    //  APPLIED set, never the draft, so typing in the dialog does not re-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthData?.month, selectedYear, schemeKey, facility, adv]);

  useEffect(() => { load(); }, [load]);
  //  A filter change can empty the current selection's rows out from under it, so
  //  the selection is cleared alongside the other narrowing controls.
  useEffect(() => { setSelected(new Set()); }, [typeTab, specialty, q, adv]);

  // ── Client-side filtering ─────────────────────────────────────────────────
  //  specKey normalises a NULL attendance to 'UNSPECIFIED' — the same key the
  //  facet counter uses. Without that agreement an "Unspecified" chip counts rows
  //  it can never match, because the rows hold NULL and the predicate compared
  //  against the literal string.
  const specKey = (c) => (c.attendance ? String(c.attendance).toUpperCase() : 'UNSPECIFIED');

  const matchesSearch = (c, term) => !term || [c.member_no, c.folder_no, c.name, c.ccc]
    .some((v) => String(v || '').toLowerCase().includes(term));

  //  ── The visit date, between two dates ──────────────────────────────────────
  //  Compared as 'YYYY-MM-DD' STRINGS, not as Date objects. Both ends and the
  //  claim's own date are already in that form, and lexical order on it is
  //  chronological order — so this needs no parsing, and cannot be caught by the
  //  timezone shift that turns a midnight Date into the previous day. That shift
  //  is what once read a date in this project as the year 2001.
  //
  //  Inclusive at both ends: "from the 1st to the 30th" plainly includes both.
  //  Either end may stand alone — a "from" with no "to" is everything since.
  const inDateRange = (c, from, to) => {
    if (!from && !to) return true;
    const d = String(c.visit_date || c.claim_date || '').slice(0, 10);
    if (!d) return false;          // no date cannot be shown to be inside a range
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  //  The G-DRG a claim is billed on, normalised. `service_code` is the resolved
  //  PRINCIPAL — see LIST_SOURCES in claimStore — which is the code the specialty
  //  chips break down into.
  const codeKey = (c) => String(c.service_code || '').trim().toUpperCase();

  //  The code's PUBLISHED NAME — "OPDC06A · General OPD - Adult". Resolved by the
  //  backend out of insured_services, diagnosis_tarf and procedure_tarf; 157 of the
  //  213 codes in this year's claims have one.
  //
  //  NULL WHEN IT IS ONLY THE CODE AGAIN. `service_name` falls back to the code
  //  itself where no catalogue knows it, and rendering "D001A — D001A" reads as a
  //  bug rather than as a code nobody published a name for. So the name is dropped
  //  and the row shows the bare code, which is the honest answer.
  const nameOfCode = (c) => {
    const code = codeKey(c);
    const raw = String(c.service_name || c.service || '').trim();
    if (!raw) return null;
    return raw.toUpperCase() === code ? null : raw;
  };

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();

    // ── THE CLAIM YOU ARE WORKING ON DOES NOT SLIP OUT FROM UNDER YOU ────────
    //
    //  REPORTED: "I've saved it, and after that you just close everything and I
    //   can't even find the claim again. Who told you I'm done with that claim?
    //   Don't close it after saving — leave it open and let the changes take
    //   effect. Mostly it goes and hides and I don't see where it is any more."
    //
    //  Nothing closes the editor. The editor is rendered INSIDE the claim's row,
    //  so when the row leaves this list the panel goes with it — and every filter
    //  below reads the row's CURRENT values, which saving is precisely what
    //  changes. Re-bill a claim from PAED onto MEDI while the PAED pill is held,
    //  or let a discharge date turn an OPD claim into an IPD one while the OPD tab
    //  is open, and the row stops matching. From the officer's side the claim they
    //  were halfway through vanished, and finding it again meant clearing the
    //  pills and searching for the patient by name.
    //
    //  So the OPEN claim, and the one most recently closed, are exempt from the
    //  filters. Two rows at most, and only ever rows the month already holds —
    //  this adds nothing that was not fetched, it declines to REMOVE something
    //  while it is being worked on. The pills' own counts come from `claims` and
    //  are unaffected.
    //
    //  ── KEPT IN PLACE, NOT APPENDED ─────────────────────────────────────────
    //
    //  The first version of this filtered normally and then pushed the held rows
    //  onto the end. That fixed nothing: only one PAGE of `visible` is put in the
    //  DOM, so a row appended to a 600-row list landed on page 12 while the
    //  officer was on page 1, and the editor was still not rendered. The row has
    //  to stay at the index it already occupied, which means the exemption belongs
    //  in the predicate rather than after it.
    const held = new Set([openRow, lastEdited].filter(Boolean));
    const isHeld = (c) => held.has(`${c.source}-${c.id}`);
    const matches = (c) => {
      //  "We will also be able to filter only these claims, for the person to
      //   really sit on it and then look at it." Narrowest test first: when this
      //   is on it is the whole point of the view.
      if (aiOnly && !(c.ai_findings > 0)) return false;
      if (typeTab !== 'all' && c.type !== typeTab.toUpperCase()) return false;
      if (specialty !== 'all' && specKey(c) !== specialty) return false;
      if (serviceCode !== 'all' && codeKey(c) !== serviceCode) return false;
      if (term && !matchesSearch(c, term)) return false;
      if ((dateRange.from || dateRange.to)
        && !inDateRange(c, dateRange.from, dateRange.to)) return false;
      return true;
    };

    //  `_pinned` travels with the row so the table can say why it is still there;
    //  without that it reads as a filter that does not work.
    let rows = claims
      .filter((c) => matches(c) || isHeld(c))
      .map((c) => (matches(c) ? c : { ...c, _pinned: true }));

    if (sortBy) {
      rows = [...rows].sort((a, b) => {
        const x = String(a[sortBy] || ''); const y = String(b[sortBy] || '');
        return sortDir === 'asc' ? x.localeCompare(y, undefined, { numeric: true })
                                 : y.localeCompare(x, undefined, { numeric: true });
      });
    }
    return rows;
  }, [claims, typeTab, specialty, serviceCode, q, sortBy, sortDir, dateRange,
    openRow, lastEdited, aiOnly]);

  //  How many claims in this month the clinical review flagged, and how many it
  //  has looked at. Counted from `claims` rather than `visible`, so the number on
  //  the button is about the MONTH and does not change as the pills narrow the
  //  table — a count that moved when you clicked OPD would read as a bug.
  const aiFlagged = useMemo(
    () => claims.filter((c) => c.ai_findings > 0).length, [claims]);
  const aiReviewed = useMemo(
    () => claims.filter((c) => c.ai_reviewed).length, [claims]);

  // ── THE ERROR REPORT ──────────────────────────────────────────────────────
  //  Opened from the toolbar and fed `visible`, not `claims`: it reports on what
  //  is ON SCREEN. A report printed while a date range or a specialty is in force
  //  has to describe that selection, or it is a different document from the one
  //  the officer was reading when they pressed the button.
  const [reportOpen, setReportOpen] = useState(false);

  //  How many of those NHIA would send back — shown on the button so "is there
  //  anything in it" is answered before the report is opened.
  //
  //  Counted from `issueTypes`' own severities rather than from a list of error
  //  codes kept here. The two format rules carry a severity a facility can change
  //  on Claims ▸ Claim Rules, so a hard-coded copy would go stale the first time
  //  one was downgraded to a warning while a batch was cleaned up.
  const rejectableCount = useMemo(() => {
    const errs = new Set(
      (issueTypes || []).filter((t) => t.severity === 'error').map((t) => t.code)
    );
    if (!errs.size) return 0;
    return visible.filter((c) => c.issues_checked
      && (c.issues || []).some((k) => errs.has(k))).length;
  }, [visible, issueTypes]);

  // ── Live facet counts ─────────────────────────────────────────────────────
  //  Each dimension is counted with the OTHER filters applied, never with its own.
  //  This is the fix for the filters that "did not work": in this data every
  //  specialty is exclusively OPD or exclusively IPD — OPDC/ENTH/DENT/OPHT are
  //  outpatient-only, MEDI/PAED/OBGY inpatient-only. So with OPD selected,
  //  clicking MEDI returned nothing while the chip still advertised 318. The
  //  control was working; its label was lying about what it would do.
  //
  //  Counting each facet within the current selection makes a dead end read as 0
  //  BEFORE it is clicked, and the chip is then dimmed and unclickable.
  //
  //  Computed from the month already in memory, so this stays instant.
  const facets = useMemo(() => {
    const term = q.trim().toLowerCase();
    //  The date range narrows the pool the pills are counted from, exactly as the
    //  search box does. That is what makes OPD/IPD and every specialty follow the
    //  dates rather than going on advertising the whole month — a chip reading
    //  318 that returns nothing once clicked is the failure this file already
    //  fixed once for attendance, and a date filter counted anywhere else would
    //  reintroduce it.
    let searched = term ? claims.filter((c) => matchesSearch(c, term)) : claims;
    if (dateRange.from || dateRange.to) {
      searched = searched.filter((c) => inDateRange(c, dateRange.from, dateRange.to));
    }

    // Attendance counts honour specialty + search, and ignore attendance.
    //  AND THE CODE, because it is a narrowing of the specialty and not a dimension
    //  of its own: with OPDC06A chosen, OPD/IPD must read what pressing them would
    //  actually show.
    let forType = specialty === 'all'
      ? searched
      : searched.filter((c) => specKey(c) === specialty);
    if (serviceCode !== 'all') forType = forType.filter((c) => codeKey(c) === serviceCode);
    const type = { all: forType.length, opd: 0, ipd: 0 };
    for (const c of forType) { if (c.type === 'IPD') type.ipd += 1; else type.opd += 1; }

    // Specialty counts honour attendance + search, and ignore specialty.
    //  AND IGNORE THE CODE, for the same reason they ignore the specialty: this row
    //  is how you choose which specialty, and picking one clears the code. Counting
    //  OPDC06A into these would leave every other chip reading 0 and dimmed —
    //  ten dead ends, and no way back except Reset.
    const forSpec = typeTab === 'all'
      ? searched
      : searched.filter((c) => c.type === typeTab.toUpperCase());
    const spec = {};
    //  ── AND THE CODES INSIDE EACH SPECIALTY ─────────────────────────────────
    //  Counted in the SAME pool as the chip above it, so a breakdown can never add
    //  up to more or less than the chip it hangs off — which is the one thing that
    //  would make the drop-down worth less than reading the table.
    const codes = {};
    for (const c of forSpec) {
      const k = specKey(c);
      spec[k] = (spec[k] || 0) + 1;
      const code = codeKey(c);
      if (!code) continue;          // no code at all belongs to no breakdown row
      if (!codes[k]) codes[k] = new Map();
      const at = codes[k].get(code);
      if (at) {
        at.n += 1;
        if (!at.name) at.name = nameOfCode(c);
      } else {
        codes[k].set(code, { code, n: 1, name: nameOfCode(c) });
      }
    }
    //  Sorted by COUNT, commonest first. OPDC holds 32 codes and OPDC06A is 13,945
    //  of them; alphabetical would put OPDC02A at the top and bury the one row
    //  almost every click wants.
    const codeList = {};
    for (const k of Object.keys(codes)) {
      codeList[k] = [...codes[k].values()].sort((a, b) => b.n - a.n || a.code.localeCompare(b.code));
    }
    return { type, spec, codes: codeList };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, q, typeTab, specialty, serviceCode, dateRange]);

  //  ── THE REJECT WORKLIST, AS ONE NUMBER ────────────────────────────────────
  //  How many claims carry at least one ERROR rule — everything NHIA will send
  //  back — and which rules make up that set.
  //
  //  Counted HERE rather than taken from the server's facet, and the difference
  //  matters: this pool has already been narrowed by the search box, the date
  //  range, the attendance tab and the specialty chip, exactly as the OPD/IPD
  //  pills beside it are. A server count scoped only to the month would advertise
  //  318 and hand back nothing once a date filter was on — the precise failure the
  //  attendance pills in this file were already fixed for once.
  //
  //  NOT the sum of the per-rule counts in the Advanced Filter. A claim with a bad
  //  membership number AND a missing check code is in two of those, so adding them
  //  gives 4,245 where the real worklist is 2,628.
  //
  //  The severities come from `issueTypes`, which the list endpoint sends with the
  //  rows. No copy of them lives on this side: a local list would go stale the
  //  first time a rule is added, and a worklist that quietly stops including a
  //  whole class of rejection is worse than no worklist.
  const rejectFacet = useMemo(() => {
    const errorCodes = (issueTypes || [])
      .filter((t) => (t.severity || 'error') === 'error')
      .map((t) => t.code);
    if (!errorCodes.length) return { codes: [], count: 0 };
    const isError = new Set(errorCodes);

    //  The same pool the pills count from — search and dates applied, attendance
    //  and specialty applied, so the number matches what pressing it will show.
    const term = q.trim().toLowerCase();
    let pool = term ? claims.filter((c) => matchesSearch(c, term)) : claims;
    if (dateRange.from || dateRange.to) {
      pool = pool.filter((c) => inDateRange(c, dateRange.from, dateRange.to));
    }
    if (typeTab !== 'all') pool = pool.filter((c) => c.type === typeTab.toUpperCase());
    if (specialty !== 'all') pool = pool.filter((c) => specKey(c) === specialty);
    //  The chosen G-DRG narrows this too, or "Will be rejected 12" would count
    //  rejects from the whole specialty while the table beneath shows one code.
    if (serviceCode !== 'all') pool = pool.filter((c) => codeKey(c) === serviceCode);

    let count = 0;
    for (const c of pool) {
      if ((c.issues || []).some((k) => isError.has(k))) count += 1;
    }
    //  Only the codes actually present, so the filter it sends cannot name a rule
    //  this month has none of — which the server would drop anyway, but which
    //  would make the applied-filter chip list read as sixteen things.
    const present = new Set();
    for (const c of pool) {
      for (const k of c.issues || []) if (isError.has(k)) present.add(k);
    }
    return { codes: [...present], count };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, issueTypes, q, dateRange, typeTab, specialty, serviceCode]);

  // Every specialty present in the month, in a stable order, so chips never jump
  // about or vanish as filters change — only their counts and enabled state move.
  const specialtyCodes = useMemo(() => {
    const m = new Map();
    for (const c of claims) {
      const k = specKey(c);
      m.set(k, (m.get(k) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [claims]);

  // ── Step through claims without closing ───────────────────────────────────
  //  A month is worked in order. Closing the panel, finding the next row and
  //  clicking its pencil is three actions for something that should be one.
  //
  //  Deliberately AFTER `visible`: this walks the filtered, sorted order the
  //  officer is actually looking at, so Next respects the active pills and search
  //  rather than jumping to a claim that is not on screen. It also has to come
  //  after it — see the note above scrollOpenEditor.
  const orderedKeys = useMemo(
    () => visible.map((c) => `${c.source}-${c.id}`), [visible]);

  const openIndex = openRow ? orderedKeys.indexOf(openRow) : -1;

  // ── Only a page of rows is put in the DOM ──────────────────────────────────
  //  The month is still fetched and filtered whole — that is what keeps the pill
  //  counts stable and a pill click instant, and it is worth keeping.
  //
  //  What was not affordable was RENDERING it whole. Each row is 37 elements —
  //  fifteen cells, nine spans, three action buttons, three icons — so a 6,974
  //  claim month asked React and the browser for roughly 258,000 elements on
  //  every keystroke in the filter box, every sort, every checkbox. That is the
  //  slowness; the query behind it returns in under a second.
  //
  //  Everything that reasons about the month still uses `visible`: Prev/Next
  //  steps through the whole filtered set, the header checkbox selects all of it,
  //  and "select first N" counts from its start. Only the <tr>s are windowed.
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  //  Clamped rather than stored blindly: deleting rows or tightening a filter can
  //  leave `page` past the end, and a page past the end renders nothing at all.
  const pageSafe = Math.min(page, pageCount);
  const pageStart = (pageSafe - 1) * pageSize;
  const pageRows = useMemo(
    () => visible.slice(pageStart, pageStart + pageSize),
    [visible, pageStart, pageSize]);

  //  Back to the first page whenever the set beneath it changes. Without this a
  //  search that narrows 6,974 rows to 12 would leave you on page 40, looking at
  //  an empty table and concluding the search found nothing.
  //  `serviceCode` belongs in here as much as `specialty` does, and more sharply:
  //  it is the narrowest filter on the row, cutting 20,053 OPDC claims to 3,384 or
  //  to 2, so it is the one most likely to leave you on a page that no longer
  //  exists.
  useEffect(() => { setPage(1); },
    [typeTab, specialty, serviceCode, q, adv, sortBy, sortDir, pageSize, dateRange]);

  const step = (delta) => {
    if (openIndex < 0) return;
    const nextIndex = openIndex + delta;
    const next = orderedKeys[nextIndex];
    if (!next) return;
    setLastEdited(openRow);   // mark the one being left behind
    setOpenRow(next);
    //  Stepping is over the whole filtered set, so the next claim may be on
    //  another page. Follow it, or Next would open a row that is not rendered and
    //  the panel would appear to close.
    setPage(Math.floor(nextIndex / pageSize) + 1);
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };

  const allShown = visible.length > 0 && visible.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(allShown ? new Set() : new Set(visible.map((c) => c.id)));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const takeFirst = () => {
    const n = Math.max(1, Number(selectFirst) || 0);
    setSelected(new Set(visible.slice(0, n).map((c) => c.id)));
  };

  // ── Bulk delete ───────────────────────────────────────────────────────────
  //  BOTH MODELS NOW. This said "only claims the flat model owns can be removed
  //  here", because /deleteclaim targets claim_information by id — so an imported
  //  claim was refused and an officer had to delete a whole batch from IT / ICT to
  //  remove one row. The delete goes through claimStore.deleteClaim now, which
  //  knows both models and what belongs to a claim on each.
  //
  //  THE SPLIT IS STILL COMPUTED, and only to describe what is about to happen:
  //  an imported claim's diagnoses and medicines go with it, a hospital claim's
  //  are ward records and stay. Neither group is filtered out any more — the old
  //  code silently skipped the imported rows in a mixed selection, so ticking 40
  //  claims deleted 28.
  const chosen = visible.filter((c) => selected.has(c.id));
  const removable = chosen.filter((c) => c.source === 'facility');
  const imported = chosen.filter((c) => c.source === 'imported');

  //  Repair runs in two halves with a dialog between them: a DRY RUN that writes
  //  nothing and returns the plan, then the write, confirmed against that plan.
  //  `repairResult` being set is what turns the dialog from a preview into an
  //  outcome, so the officer never gets a second box stacked on the first.
  const [repairPlan, setRepairPlan] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);

  //  Which row's trash button is mid-delete. THE ID, not a boolean, so only that
  //  row goes quiet and the rest of the table stays usable — and so a double-click
  //  cannot open the confirmation twice. Declared up here with the other state
  //  rather than beside deleteOne: a hook's position must not depend on anything
  //  above it. See deleteOne.
  const [deletingId, setDeletingId] = useState(null);

  //  How many of the ticked rows have something the rules can act on. Read off the
  //  same per-row data the badge uses, so this needs no extra request.
  //
  //  WIDER than the badge on purpose. The badge shows errors only — see IssueBadge
  //  for why — but setting a principal that the importer never set is a real,
  //  wanted repair, and doing it in bulk is most of the point of this button. So a
  //  claim with only warnings counts here even though its row carries no pill.
  //  The dry run then shows exactly what that means before anything is written.
  const repairable = chosen.filter((c) => c.source === 'imported' && c.issues?.length);

  // ── Repair the selection ──────────────────────────────────────────────────
  //  DRY RUN FIRST, ALWAYS. The run is shown as a before/after per claim and
  //  nothing is written until it is confirmed a second time. This acts on a whole
  //  selection, it touches the column NHIA validates against, and there is no
  //  undo — so the preview is not a courtesy, it is the safety.
  //
  //  It also has to be honest about the claims it CANNOT fix. A claim whose every
  //  G-DRG is an out-patient code carries nothing to bill an admission on; the
  //  server reports those as `unfixable` and they are counted separately here
  //  rather than folded into a total that would overstate what was done.
  const repairSelected = async () => {
    if (!repairable.length) return;
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const { data } = await axios.post(claimStoreRepair, {
        ids: repairable.map((c) => Number(c.id)), dryRun: true,
      });
      setRepairPlan(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not check these claims',
        text: err?.response?.data?.message || err.message });
    } finally {
      setRepairBusy(false);
    }
  };

  // ── EVERY CLAIM ON SCREEN WHOSE SPECIALTY CAN BE SETTLED ──────────────────
  //
  //  "The moment you load up the claims, you show it at the top. This can be auto
  //   fixed... fix it once and for all."
  //
  //  Counted off the rows already on screen — no extra request — so the number in
  //  the banner is the number of rows the officer is looking at, and it follows
  //  every filter above the table. Filter to one specialty and the banner narrows
  //  with it.
  //
  //  IMPORTED ONLY, and that is not an omission. `repairClaims` reads the
  //  normalised tables and cannot act on a typed-in claim; those are corrected by
  //  claimStore.settleSpecialty the moment the claim is opened, which needs no
  //  button. A banner offering to fix claims the endpoint would refuse is worse
  //  than no banner.
  const autoFixIds = useMemo(() => visible
    .filter((c) => c.source === 'imported'
      && (c.issues || []).some((k) => AUTO_FIX_ISSUES.has(k)))
    .map((c) => Number(c.id)), [visible]);

  //  Straight into the SAME dry-run-then-confirm dialog the Repair button uses.
  //  This writes the column NHIA validates against, across a whole month, with no
  //  undo — a "fix all" that skipped the preview would be a different and much
  //  worse feature wearing the same label.
  const autoFixAll = async () => {
    if (!autoFixIds.length) return;
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const { data } = await axios.post(claimStoreRepair, {
        ids: autoFixIds, dryRun: true,
      });
      setRepairPlan(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not check these claims',
        text: apiError(err, 'Could not check these claims') });
    } finally {
      setRepairBusy(false);
    }
  };

  //  ── The same repair, driven by a RULE instead of a selection ──────────────
  //  "Fix all" on an Advanced Filter tile. There is no selection to work from: the
  //  tile counted every claim in the month carrying that rule, and most of them are
  //  on pages nobody has opened — so the ids are resolved on the server, from the
  //  same query the tile's own count came from.
  //
  //  It goes through the identical dry-run-then-confirm path as the table's Repair
  //  button, and lands in the same dialog. That matters more than the convenience:
  //  this writes the column NHIA validates against, across a whole month, with no
  //  undo. A "fix all" that skipped the preview would be a different and much worse
  //  feature wearing the same label.
  const repairIssue = async (code) => {
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const { data } = await axios.post(claimStoreRepair, {
        issue: code, dryRun: true,
        year: selectedYear, month: monthData?.month,
        facility, insurance_type: insurances?.length ? insurances.join(',') : 'all',
      });
      if (!data?.results?.length) {
        Swal.fire({ icon: 'info', title: 'Nothing to fix',
          text: data?.message || 'No claims in this month carry that fault.' });
        return;
      }
      setRepairPlan(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not check these claims',
        text: err?.response?.data?.message || err.message });
    } finally {
      setRepairBusy(false);
    }
  };

  //  ── Fix all, for the NHIA FORMAT rules ────────────────────────────────────
  //  A different engine from repairIssue above — those settle a claim from its own
  //  rows; these correct a header field NHIA validates. Same discipline though:
  //  PLAN, show it, confirm, then write. Two of these alter a patient's name and a
  //  membership number across a whole month with no undo, so the preview is not
  //  optional politeness.
  //
  //  The plan is not scoped to the month. It cannot be: these are header format
  //  faults, the fix is the same wherever the claim sits, and the tile's own count
  //  is month-wide only because the tile is. The confirmation says the real number
  //  rather than letting the officer infer it from the tile.
  const fixNhiaIssue = async (ruleCode, fixKey) => {
    setRepairBusy(true);
    //  The SAME scope the tile counted with. Without it a tile reading "15 claims"
    //  had a button that would have corrected 285 across every month, and the
    //  confirmation would have said 285 — the officer would have been right to
    //  distrust the screen.
    const scope = {
      year: selectedYear,
      month: monthData?.month,
      facility,
      insurance: insurances?.length ? insurances.join(',') : 'all',
    };
    try {
      //  ── NEVER FROM CACHE ──────────────────────────────────────────────────
      //  This is a GET whose URL and query are identical every time it is pressed, so
      //  the browser is entitled to replay a previous response — and it did. A 400 from
      //  a server that had not yet been restarted came back on every subsequent press,
      //  long after the server was correct, and read as "you fixed nothing". Three
      //  verifications against the live server said the endpoint was fine while the
      //  screen kept showing the old failure.
      //
      //  A preview is a question about data that changes, and a stale answer is never
      //  the right one. `_t` defeats the URL cache; the headers defeat a proxy's.
      const { data } = await axios.get(nhiaFixPreview, {
        params: { rule: fixKey, ...scope, _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const rows = data?.rows || [];
      const changing = rows.filter((r) => r.action === 'change');
      const refused = rows.filter((r) => r.action === 'refuse');

      if (!changing.length) {
        Swal.fire({ icon: 'info', title: 'Nothing can be corrected automatically',
          html: refused.length
            ? `${refused.length} claim(s) carry this fault and every one needs a `
              + `person:<br><br><span style="font-size:12px">`
              + refused.slice(0, 6).map((r) => `${r.ref}: <code>${r.from}</code> — ${r.why}`)
                .join('<br>') + '</span>'
            : 'No claims carry this fault any more.' });
        return;
      }

      //  What it would do, grouped by KIND — one rule can make more than one
      //  correction, and "224 claims" would hide that some are being MOVED to a
      //  different field while others have a character trimmed.
      const byKind = {};
      changing.forEach((r) => {
        const k = r.kind || r.field || 'changed';
        byKind[k] = (byKind[k] || 0) + 1;
      });
      const KIND_WORDS = {
        move_card: 'Ghana Card number moved out of the membership field into the '
                 + 'Ghana Card field',
        letter_o: 'letter O corrected to a zero',
        //  "from the end" was wrong about most of them: of the 42 in the live
        //  data, the majority carry the stray character at the FRONT — "-22914762",
        //  "-34467187" — and only some at the back. Either end is trimmed, and
        //  saying "the end" made an officer check the wrong side of the number.
        edge_punctuation: 'stray character trimmed from the front or the back '
                        + '(no digit is changed)',
        other_names: 'name with the digit spelled out, so twins stay apart',
        includesPharmacy: 'pharmacy flag cleared',
      };
      const summary = Object.entries(byKind)
        .map(([k, n]) => `<b>${n}</b> &mdash; ${KIND_WORDS[k] || k}`).join('<br>');
      //  ── A MOVE IS NOT AN EDIT, AND MUST NOT BE DRAWN AS ONE ──────────────
      //  Every row rendered as `from → to`. On a Ghana Card the value does not
      //  change at all — it is the FIELD that changes — so the line read
      //
      //      GHA-718665705-5 → GHA-718665705-5
      //
      //  which is indistinguishable from "this fix does nothing", and that is
      //  exactly how it was read: "those things that are remaining when you try to
      //  fix it". Sixty-four of the hundred and two rows in that dialog looked
      //  like no-ops.
      //
      //  A move now names both fields and shows the membership number being
      //  emptied, which is the part that actually happens to the claim.
      const esc = (v) => String(v ?? '').replace(/[&<>]/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      const line = (r) => (r.kind === 'move_card'
        ? `${esc(r.ref)}: <code>${esc(r.from)}</code> out of the membership `
          + 'field &rarr; into the <b>Ghana Card</b> field '
          + '<span style="opacity:.75">(membership number left blank &mdash; NHIA '
          + 'takes either)</span>'
        : `${esc(r.ref)}: <code>${esc(r.from ?? '(blank)')}</code> &rarr; `
          + `<code>${esc(r.to)}</code>`);
      //  ── EXAMPLES OF EACH KIND, NOT THE FIRST SIX ROWS ────────────────────
      //  One rule makes several different corrections, and the rows arrive in
      //  claim order — so the first six were all Ghana Cards and the officer never
      //  saw what a trimmed number looks like before authorising 38 of them.
      const perKind = Object.keys(byKind).map((k) => {
        const of = changing.filter((r) => (r.kind || r.field || 'changed') === k);
        return of.slice(0, 3).map(line).join('<br>')
          + (of.length > 3
            ? `<br><span style="opacity:.7">&hellip; and ${of.length - 3} more like `
              + 'this</span>' : '');
      });
      const examples = perKind.join('<div style="height:7px"></div>');

      const ok = await Swal.fire({
        icon: 'question',
        title: `Correct ${changing.length} claim${changing.length === 1 ? '' : 's'}?`,
        html: `<div style="text-align:left;font-size:13px;line-height:1.6">${summary}`
          //  No overall "… and N more" tail: each kind now carries its own, and
          //  the two together read as two different remainders.
          + `<div style="margin-top:10px;font-size:12px;color:#42536E">${examples}`
          + '</div>'
          + (refused.length
            ? `<div style="margin-top:10px;font-size:12px;color:#7A4A00">`
              + `${refused.length} more carry this fault and are left alone — they `
              + 'need a person.</div>'
            : '')
          + '<div style="margin-top:10px;font-size:12px;color:#A62828">'
          + 'There is no undo.</div></div>',
        showCancelButton: true,
        confirmButtonText: `Correct ${changing.length}`,
        cancelButtonText: 'Cancel',
      });
      if (!ok.isConfirmed) return;

      //  No claimIds: the server re-plans and applies every row its own plan
      //  marks as a change, so a stale preview cannot write an out-of-date value.
      //  The same scope goes with it, or the apply would reach past the preview.
      const res = await axios.post(nhiaFixApply, { rule: fixKey, ...scope });
      const r = res.data || {};
      await Swal.fire({
        icon: r.failed ? 'warning' : 'success',
        title: `${r.changed} claim${r.changed === 1 ? '' : 's'} corrected`,
        html: '<div style="text-align:left;font-size:13px;line-height:1.6">'
          + Object.entries(r.byKind || {})
            .map(([k, n]) => `<b>${n}</b> &mdash; ${KIND_WORDS[k] || k}`).join('<br>')
          + (r.refused ? `<div style="margin-top:8px;font-size:12px;color:#7A4A00">`
            + `${r.refused} left for a person.</div>` : '')
          + (r.failed ? `<div style="margin-top:8px;font-size:12px;color:#A62828">`
            + `${r.failed} failed.</div>` : '')
          + '</div>',
      });
      //  The counts on the tiles are stale now.
      await load();
    } catch (err) {
      //  ── SAY WHICH CALL FAILED, AND WHERE IT WENT ─────────────────────────
      //  "invalid_specialty_code is not a rule that can be fixed mechanically" was
      //  reported three times while the endpoint, verified live, answered correctly —
      //  and the message gave no way to tell a stale server from a stale cache from a
      //  wrong address. The status and the URL are what turn the next one into a
      //  five-second answer instead of an hour.
      const status = err?.response?.status;
      const url = err?.config?.url || '';
      Swal.fire({
        icon: 'error',
        title: 'Could not correct these claims',
        html: `<div style="text-align:left;font-size:13px;line-height:1.6">`
          + `${String(err?.response?.data?.error || err?.response?.data?.message
            || err.message).replace(/</g, '&lt;')}`
          + `<div style="margin-top:10px;font-size:11.5px;color:#64748B">`
          + `${status ? `HTTP ${status}` : 'no response'} · ${String(url).replace(/</g, '&lt;')}`
          //  A 400 naming a rule the server does not know, from a URL that does know
          //  it, is a stale bundle or a stale cache — not a missing feature.
          + (status === 400
            ? '<br>If the server has been restarted since this last worked, reload the '
              + 'page with Ctrl+Shift+R.'
            : '')
          + '</div></div>',
      });
    } finally {
      setRepairBusy(false);
    }
  };

  //  The second half, run from the dialog once the plan has been read. Only the
  //  ids the dry run said it WOULD fix are sent — never the whole selection — so a
  //  claim that was ambiguous or stuck when previewed cannot be swept in by a
  //  change of state between the two calls.
  //  ── The other reading of a thin-charted stay ──────────────────────────────
  //  "Two nights, one day of drugs, billed as detention" is either a mis-coded
  //  ADMISSION — repair it — or a correctly-coded DETENTION with the wrong dates.
  //  The software cannot tell which; the officer can.
  //
  //  This is the second answer. It collapses the stay to the visit date, which
  //  makes the claim a same-day attendance — and ZOOM02A, which was wrong on an
  //  admission, becomes exactly right. Nothing about the coding is touched: only
  //  the discharge date moves.
  //
  //  Done through updateBio, not a new endpoint, because updateBio already DERIVES
  //  the attendance from the discharge date — "same day out, later in" — so setting
  //  them equal flips the claim to out-patient through the one piece of code that
  //  owns that rule. A second place deciding it is a second place to get it wrong.
  //
  //  Confirmed first. It shortens a recorded stay, which is a clinical claim about
  //  the patient, and it is the one action in this dialog that is not a repair.
  const treatAsOutpatient = async (row) => {
    const name = claims.find((c) => String(c.id) === String(row.id))?.name || `Claim ${row.id}`;
    const m = row.medication || {};
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Record this as a same-day attendance?',
      html: `<div style="text-align:left;font-size:13px;line-height:1.6">`
          + `<b>${name}</b> is recorded as ${m.nights} night(s), `
          + `${m.med_lines ? `with drugs on ${m.med_days} day(s)` : 'with no drugs at all'}.`
          + `<br><br>This sets the discharge date back to the visit date `
          + `(<b>${m.admission || '—'}</b>), which makes it an out-patient attendance. `
          + `Its detention code then becomes correct and it drops out of this repair.`
          + `<br><br>The diagnoses, procedures and medicines are not touched.`
          + `<br><br><b>Only do this if the patient really was not admitted.</b></div>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, same-day',
      cancelButtonText: 'Keep as an admission',
      focusCancel: true,
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.put(claimStoreBio, { discharge_date: m.admission },
        { params: { id: row.id, source: 'imported' } });
      //  Re-run the dry run rather than patching the plan by hand: the claim has
      //  changed, and so has whether it is repairable at all.
      await load();
      const { data } = await axios.post(claimStoreRepair, {
        ids: (repairPlan?.results || []).map((r) => Number(r.id)).filter((n) => n !== Number(row.id)),
        dryRun: true,
      });
      setRepairPlan(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not change the dates',
        text: err?.response?.data?.message || err.message });
    }
  };

  //  `held` is the set of claim ids the officer has un-ticked in the medication
  //  review — claims the plan CAN fix but which they want to look at first, because
  //  the drug chart does not cover the stay. They are dropped from the write and
  //  left exactly as they were, so the tile still counts them and they can be come
  //  back to. Excluding them here rather than filtering the plan keeps the dialog
  //  honest about what it checked versus what it wrote.
  const repairConfirm = async (held) => {
    const skip = held instanceof Set ? held : new Set();
    const would = (repairPlan?.results || [])
      .filter((r) => r.status === 'would_fix' && !skip.has(Number(r.id)));
    if (!would.length) return;
    setRepairBusy(true);
    try {
      const { data } = await axios.post(claimStoreRepair,
        { ids: would.map((r) => r.id), dryRun: false });
      const fixed = (data.results || []).filter((r) => r.status === 'fixed');
      const failed = (data.results || []).filter((r) => r.status === 'failed');
      //  Anything the repair did NOT clear. A claim can be fixed on one rule and
      //  still fail another, and reporting "23 repaired" beside a row whose badge
      //  is still red is how a screen loses its officer.
      setRepairResult({
        fixed, failed, left: fixed.filter((r) => (r.remaining || []).length),
      });
      //  The list behind the dialog is refreshed while it is still open, so the
      //  badges are already gone when it is dismissed.
      await load();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'The repair did not go through',
        text: err?.response?.data?.message || err.message });
    } finally {
      setRepairBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (!chosen.length) return;

    //  ── EVERY SELECTED CLAIM IS DELETED, WHICHEVER MODEL IT IS ──────────────
    //
    //  This used to refuse an all-imported selection outright and, on a mixed one,
    //  silently SKIP the imported rows — the officer ticked 40 and 12 were quietly
    //  left behind with a note. Both are gone: see deleteOne for why the import
    //  record was never the obstacle it was described as.
    //
    //  `removable` and `imported` are still computed, but only to SAY what is
    //  about to happen. Nothing is filtered out any more.
    const note = imported.length
      ? `<p style="color:#8A5510;margin-top:10px">
           ${imported.length} of these came from an XML import, so their
           <b>diagnoses, procedures, medicines and investigations go with them</b>.
           ${removable.length
    ? `The other ${removable.length} ${removable.length === 1 ? 'is a' : 'are'}
              hospital claim${removable.length === 1 ? '' : 's'} — those keep their
              ward records.`
    : ''}</p>`
      : `<p style="margin-top:8px;color:#166534">
           These are hospital claims. Prescriptions, diagnoses, procedures and lab
           requests are <b>not</b> deleted — those are ward records.</p>`;

    // Whose claims, named in the confirmation. The list is scoped server-side, so
    // this cannot reach another facility — but a dialog that says only "delete 40
    // claims" is one an officer working four facilities can confirm on the wrong
    // screen. Named from the ROWS being deleted, not from the filter.
    // The server names every row, including this facility's own claims (which
    // it labels with the configured facility name). This fallback only fires if
    // a row somehow arrives unnamed.
    //  Named from EVERY row being deleted now, not just the facility ones — an
    //  imported claim carries the sending facility's name and that is exactly the
    //  thing an officer working four facilities needs to see before confirming.
    const owners = [...new Set(chosen.map((c) => c.facility_name || 'This facility'))];
    const whose = owners.length === 1
      ? `<p style="margin-top:8px">Facility: <b>${owners[0]}</b></p>`
      : `<p style="color:${BRAND.warning};margin-top:8px">
           These claims span <b>${owners.length} facilities</b>: ${owners.join(', ')}.
           Filter to one facility on the dashboard if that is not what you meant.</p>`;

    const res = await Swal.fire({
      icon: 'warning',
      title: `Delete ${chosen.length} claim(s)?`,
      html: `<div style="text-align:left;font-size:13.5px;line-height:1.6">
        This permanently removes ${chosen.length} claim(s) from
        ${MONTH_TITLE(monthData?.month)} ${selectedYear}. It cannot be undone.
        ${whose}${note}</div>`,
      showCancelButton: true,
      confirmButtonText: `Delete ${chosen.length}`,
      cancelButtonText: 'Keep',
      focusCancel: true,
    });
    if (!res.isConfirmed) return;

    let done = 0; const failed = [];
    //  How many rows were on screen before, so the confirmation can account for
    //  every one that disappears — see deleteOne for why that matters.
    const before = claims.length;
    //  `chosen`, not `removable`. Every ticked claim is deleted through the
    //  model-aware endpoint, which decides what goes with each one.
    for (const c of chosen) {
      try {
        await axios.delete(claimStoreDeleteClaim,
          { params: { id: c.id, source: c.source } });
        done++;
      } catch (err) {
        //  ── STOP AFTER THE FIRST ONE THAT CANNOT WORK ──────────────────────
        //  A 404 with no JSON body means the server does not have the endpoint.
        //  That is true of the NEXT claim too, so grinding through 400 of them
        //  produces 400 identical failures and 400 round trips before saying so.
        //  Reported once, with what is left untouched.
        const missing = err?.response?.status === 404
          && !err?.response?.data?.message;
        failed.push(`${c.member_no || c.id}: ${apiError(err)}`);
        if (missing) {
          failed.push(`Stopped here — the remaining `
            + `${chosen.length - done - 1} were not attempted.`);
          break;
        }
      }
    }
    setSelected(new Set());
    const after = await load();

    //  ── AND WHAT ELSE LEFT THE VIEW WITHOUT BEING DELETED ───────────────────
    //  Same accounting as the single-row delete: under a filter, removing a claim
    //  can stop OTHER claims matching — delete one of a duplicate pair and the
    //  survivor is no longer a duplicate. Those rows disappear having not been
    //  touched, and the officer is entitled to be told so rather than counting
    //  the table and concluding the worst.
    const alsoLeft = Array.isArray(after)
      ? Math.max(0, before - after.length - done)
      : 0;
    const leftNote = alsoLeft > 0
      ? `<p style="margin-top:10px;color:#8A5510"><b>${alsoLeft} other claim`
        + `${alsoLeft === 1 ? '' : 's'} left this view and `
        + `${alsoLeft === 1 ? 'was' : 'were'} NOT deleted.</b><br>`
        + '<span style="color:#6B7280">A filter is in force and they no longer '
        + 'match it. Clear the filter to see them.</span></p>'
      : '';

    if (failed.length) {
      Swal.fire({
        icon: done ? 'warning' : 'error',
        title: done ? `${done} deleted, ${failed.length} failed` : 'Nothing was deleted',
        html: `<div style="text-align:left;font-size:12.5px">${failed.slice(0, 8).join('<br>')}`
          + `${leftNote}</div>`,
      });
    } else if (alsoLeft > 0) {
      Swal.fire({
        icon: 'success',
        title: `${done} claim${done === 1 ? '' : 's'} deleted`,
        html: `<div style="text-align:left;font-size:13px;line-height:1.6">${leftNote}</div>`,
        confirmButtonText: 'OK',
      });
    } else {
      Swal.fire({ icon: 'success', title: `${done} claim(s) deleted`, timer: 1800,
        showConfirmButton: false });
    }
  };

  // ── ONE ROW, DELETED FROM ITS OWN TRASH BUTTON ────────────────────────────
  //
  //  ASKED FOR: "I want you to enable the delete button so that we can delete a
  //  specific row as we want. And I want you to add a message — are you sure you
  //  want to delete the specific selected row? If the person say yes,
  //  automatically deleted. And then it's just gone like that."
  //
  //  The button was already there and already red, and all it did was TICK THE
  //  ROW — `setSelected(new Set([c.id]))`. Its own tooltip said "Select this claim
  //  for deletion", so the officer had to find Bulk Delete afterwards to finish a
  //  job the trash icon looked like it had started. On a duplicate claim, which is
  //  what this is for, that is three clicks and a second dialog to remove one row.
  //
  //  ── WHAT IS ACTUALLY REMOVED, WHICH IS THE PART WORTH KNOWING ─────────────
  //
  //  /deleteclaim deletes the claim_information ROW AND NOTHING ELSE. On a
  //  whole-hospital install that matters enormously: `medication`,
  //  `doctors_analysis`, `doctors_procedure_` and `investigation` are the
  //  hospital's own clinical records, matched to a claim only by patient and date
  //  and shared with the ward and OPD screens. Deleting the claim removes the
  //  CLAIM; the prescription, the diagnosis and the lab request stay exactly where
  //  the clinician left them. The dialog says so, because "delete this claim" on a
  //  hospital system reads like "delete this visit" and it is not that.
  //
  //  So a claim deleted here can be raised again from the same visit. That is
  //  precisely what makes it the right button for a duplicate.
  const deleteOne = async (c) => {
    if (!c || deletingId) return;

    //  ── THE IMPORTED-CLAIM REFUSAL IS GONE ──────────────────────────────────
    //
    //  It used to stop here and send the officer to IT / ICT ▸ Claims XML Import,
    //  because "deleting it on its own would leave that record reporting a claim
    //  that no longer exists."
    //
    //  That premise did not hold. `claim_import_jobs` is a log of what a RUN did,
    //  stamped when it ran — "this file held 1,290 claims and 1,285 were inserted
    //  on 3 February" stays true whatever happens to a claim in March. It is
    //  history, not an inventory. So the refusal was protecting a consistency
    //  that was never at risk, and charging an officer a whole batch to remove one
    //  duplicate for it.
    //
    //  Both models now delete from here. WHAT GOES WITH THE CLAIM DIFFERS, and the
    //  dialog below says which, because the difference is the officer's to know:
    //  an imported claim's diagnoses and medicines are the claim and go with it; a
    //  hospital claim's are ward records and stay.
    const imported = c.source !== 'facility';

    //  NAMED, not counted. "Are you sure?" over a table of 50 near-identical
    //  discharged rows is a question nobody can answer — the whole reason this is
    //  being used is that two rows look the same. So the dialog names the patient,
    //  the membership number and the visit date: the three things that tell the
    //  officer WHICH of the duplicates they are about to remove.
    const who = c.name || '(no name on this claim)';
    const res = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure you want to delete this claim?',
      html: `<div style="text-align:left;font-size:13.5px;line-height:1.6">
        <table style="font-size:13px;border-collapse:collapse">
          <tr><td style="padding:2px 10px 2px 0;color:#6B7280">Patient</td>
              <td style="font-weight:700">${who}</td></tr>
          <tr><td style="padding:2px 10px 2px 0;color:#6B7280">Membership no.</td>
              <td style="font-weight:700">${c.member_no || '—'}</td></tr>
          <tr><td style="padding:2px 10px 2px 0;color:#6B7280">Folder no.</td>
              <td style="font-weight:700">${c.folder_no || '—'}</td></tr>
          <tr><td style="padding:2px 10px 2px 0;color:#6B7280">Visit</td>
              <td style="font-weight:700">${fmtDate(c.visit_date) || '—'}</td></tr>
        </table>
        <p style="margin-top:10px">This removes the claim from
          ${MONTH_TITLE(monthData?.month)} ${selectedYear} and
          <b>cannot be undone</b>.</p>
        ${imported
    //  ── AN IMPORTED CLAIM TAKES ITS OWN ROWS WITH IT ──────────────────
    //  They are not ward records: the import wrote them, nothing else reads
    //  them, and they cascade. Said in amber rather than green because this
    //  one really is deleting clinical detail — and the file it came from is
    //  named, since re-importing it is how the claim comes back.
    //  The batch id is NOT on the list row — the union does not project
    //  source_batch_id — so it is not named here. The server returns it and the
    //  confirmation after the delete names it. A branch on a field the row never
    //  carries would have been dead code that read as a feature.
    ? `<p style="margin-top:8px;color:#8A5510">
            This claim came from an XML import, so its <b>diagnoses, procedures,
            medicines and investigations are part of the claim</b> and are removed
            with it. Re-importing the batch it came from would bring it back.</p>
          <p style="margin-top:6px;color:#6B7280;font-size:12.5px">
            The import record for that batch is a log of what the run did and is
            left as it is — it is history, not a list of what is still here.</p>`
    : `<p style="margin-top:8px;color:#166534">
            The patient&rsquo;s prescriptions, diagnoses, procedures and lab requests
            are <b>not</b> deleted — those are ward records, not part of the claim.
            The claim can be raised again from the same visit.</p>`}
      </div>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, keep it',
      //  THE CANCEL BUTTON HOLDS FOCUS. An officer working down a list of
      //  duplicates presses Enter without reading; on a question that cannot be
      //  undone, the default answer is "no".
      focusCancel: true,
    });
    if (!res.isConfirmed) return;

    setDeletingId(c.id);
    //  ── HOW MANY ROWS WERE ON SCREEN BEFORE ─────────────────────────────────
    //  Kept so the confirmation can account for every row that disappears. See
    //  the note below: a filtered list can drop claims that were NOT deleted, and
    //  an officer watching two rows vanish after deleting one is right to
    //  conclude the system destroyed both.
    const before = claims.length;
    try {
      //  The MODEL-AWARE endpoint, not /deleteclaim — that one targets
      //  claim_information by id and can only ever act on a hospital claim, which
      //  is why an imported one had to be refused before. `source` is sent so the
      //  server does not have to guess from the shape of the id.
      const { data: out } = await axios.delete(claimStoreDeleteClaim,
        { params: { id: c.id, source: c.source } });

      //  The editor cannot stay open on a claim that is gone — it would re-fetch
      //  it and show "Could not load this claim" where the row used to be.
      if (openRow === `${c.source}-${c.id}`) closeEditor();
      //  And it must not stay TICKED, or the next Bulk Delete counts a row that no
      //  longer exists and reports a failure for it.
      setSelected((prev) => {
        if (!prev.has(c.id)) return prev;
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });

      //  The rows AS RELOADED, returned by load() rather than read off `claims` —
      //  that binding belongs to this handler's render and will not have changed.
      const after = await load();
      //  WHAT WENT, from the server's own count rather than from what this screen
      //  assumed. On an imported claim it names the rows the cascade took; on a
      //  hospital claim it says the ward records are still there, which is the
      //  reassurance an officer wants immediately after pressing delete.
      const withIt = Object.entries((out && out.removedWith) || {})
        .map(([k, n]) => `${n} ${k}`).join(', ');

      //  ── AND ACCOUNT FOR EVERY ROW THAT DISAPPEARED ────────────────────────
      //
      //  REPORTED AS DATA LOSS, and it was not. Deleting one half of a duplicate
      //  pair under the "possible duplicate" filter made BOTH rows vanish, so the
      //  officer concluded the delete had removed two claims. Only one was
      //  removed: `load()` re-applies the Advanced Filter server-side, and the
      //  SURVIVOR no longer matched it — with its partner gone it was no longer a
      //  duplicate of anything, so the server correctly excluded it.
      //
      //  The filter was doing exactly what it says and the screen said nothing,
      //  which is the part that was wrong. Any row that leaves the view without
      //  being deleted is now named as such, with the count.
      //
      //  Counted rather than reasoned about, so it holds for EVERY filter — a
      //  specialty pill, a date range, an issue tile — not just this one.
      //
      //  `after` is null when the reload itself failed. Then nothing is claimed
      //  about what else moved: an unknown count must not be reported as zero.
      const removed = (out && out.rows) || 1;
      const alsoLeft = Array.isArray(after)
        ? Math.max(0, before - after.length - removed)
        : 0;

      Swal.fire({
        icon: 'success',
        title: `${removed} claim deleted`,
        html: `<div style="text-align:left;font-size:13px;line-height:1.6">`
          + `<b>${who}</b>${withIt ? ` — and ${withIt}` : ''}`
          + `${out && out.keptClinicalRecords
            ? '<br><span style="color:#166534">Ward records kept.</span>' : ''}`
          //  The batch, named from the server's answer. This is the one place it
          //  can be named: it is not on the list row.
          + `${out && out.claim && out.claim.batch
            ? `<br><span style="color:#6B7280">From batch ${out.claim.batch}.</span>` : ''}`
          + `${alsoLeft > 0
            ? `<p style="margin-top:10px;color:#8A5510">`
              + `<b>${alsoLeft} other claim${alsoLeft === 1 ? '' : 's'} left this `
              + `view and ${alsoLeft === 1 ? 'was' : 'were'} NOT deleted.</b><br>`
              + `<span style="color:#6B7280">A filter is in force, and `
              + `${alsoLeft === 1 ? 'it no longer matches' : 'they no longer match'} `
              + `it — removing one of a duplicate pair leaves the other one no `
              + `longer a duplicate. Clear the filter to see `
              + `${alsoLeft === 1 ? 'it' : 'them'}.</span></p>`
            : ''}`
          + '</div>',
        confirmButtonText: 'OK',
        //  NO TIMER when something needs reading. The old dialog auto-closed after
        //  2.4 seconds, which is fine for "deleted" and useless for a paragraph
        //  explaining that a row on screen was not deleted.
        timer: alsoLeft > 0 ? undefined : 2400,
        showConfirmButton: alsoLeft > 0,
      });
    } catch (err) {
      //  Said plainly and NOT cleared from the screen. A delete that failed leaves
      //  the row where it was, which is the truth about it — the officer needs to
      //  know it is still there rather than assume the list is stale.
      //
      //  THROUGH apiError, not `err.message`. This showed "Request failed with
      //  status code 404" — axios boilerplate — on a server whose backend simply
      //  had not been updated. See apiError for why the two kinds of 404 must not
      //  read the same.
      Swal.fire({
        icon: 'error',
        title: 'The claim was not deleted',
        text: apiError(err, 'The claim was not deleted'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const notReady = (name) => Swal.fire({
    icon: 'info', title: `${name} is not wired up yet`,
    text: 'The button is in place; the behaviour behind it is the next piece of work.',
    confirmButtonText: 'OK',
  });

  // The server's stats.specialties is no longer used to build the chips — those
  // come from specialtyCodes + facets.spec, which stay in step with the other
  // active filters. stats.all is still what the heading counts.

  const sortIcon = (key) => (sortBy !== key
    ? <ChevronDown size={13} style={{ opacity: .35 }} />
    : sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />);

  return (
    <div style={S.page}>
      {/* ══ ROW 1 — breadcrumb and quick filter, sharing one line ═══════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        marginBottom: 10,
      }}>
        <nav style={{ ...S.crumbs, marginBottom: 0 }} aria-label="Breadcrumb">
          <HomeIcon size={12} />
          <span style={S.crumbLink} onClick={onBack} role="link" tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onBack?.()}>Dashboard</span>
          <span aria-hidden="true">/</span>
          <span style={S.crumbLink} onClick={onBack} role="link" tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onBack?.()}>Claims</span>
          <span aria-hidden="true">/</span>
          <span style={S.crumbHere} aria-current="page">Claims Menu</span>
        </nav>

        <div style={S.searchWrap}>
          <Search size={13} style={S.searchIcon} />
          <input
            style={S.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by member no, CCC, folder no or name"
            aria-label="Quick filter claims"
            // The "filtering is client-side and instant" note was a whole line of
            // its own explaining behaviour nobody had questioned. It is a tooltip
            // now; the row it occupied went to the table.
            title="Filters the loaded month client-side — updates as you type"
          />
        </div>

        {/*  ── VISIT DATE, BETWEEN TWO DATES ──────────────────────────────────
             Beside the quick filter because it answers the same kind of
             question — "narrow what I am looking at" — and unlike the Advanced
             Filter it needs no dialog and no round trip.

             Applied on the BUTTON, not as you type. A date is entered a digit at
             a time and filtering on each keystroke empties the table while the
             year is still being typed. The name box next to it is the opposite
             case and stays live.

             Narrowing happens in the same place the search box narrows, so the
             OPD/IPD tabs and every specialty chip recount against the dates
             rather than going on advertising the whole month. */}
        {/*  ONE CONTROL, not five loose ones. The first version put a label, two
             bare date inputs, a button and a link on the row as separate
             elements: five different heights, three borders that did not line up
             with the search field beside them, and a browser date input at its
             default size, which is taller than everything else on the row.

             It is a segmented group now — calendar mark, two fields divided by a
             hairline, then the action — sharing ONE border and ONE radius with
             the search box, at the same 12.5px and the same 7px radius, so the
             two read as a matched pair of controls rather than as a control and
             some leftovers.

             `dirty` drives the whole group: the action is only live when
             pressing it would change something, and it turns green at that
             moment so the row itself says there is something to apply. */}
        {(() => {
          const dirty = dateDraft.from !== dateRange.from || dateDraft.to !== dateRange.to;
          const active = Boolean(dateRange.from || dateRange.to);
          const field = {
            border: 0, outline: 'none', background: 'transparent',
            font: 'inherit', fontFamily: 'inherit', fontSize: 12.5,
            color: BRAND.ink, padding: '7px 8px', width: 132,
            //  The browser's own clear affordance is a second, differently-styled
            //  control inside ours; this group already has one.
            colorScheme: 'light',
          };
          return (
            <div style={{
              display: 'inline-flex', alignItems: 'stretch',
              border: `1px solid ${active ? BRAND.greenDark : BRAND.line}`,
              borderRadius: 7, background: BRAND.surface, overflow: 'hidden',
              boxShadow: active ? `0 0 0 1px ${BRAND.greenDark}22` : 'none',
            }}>
              <span style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '0 8px 0 10px', fontSize: 11.5, fontWeight: 700,
                color: active ? BRAND.greenDark : BRAND.inkMuted,
                borderRight: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
              }}>
                <CalendarRange size={13} />
                Visit date
              </span>

              <input type="date" value={dateDraft.from} style={field}
                onChange={(e) => setDateDraft({ ...dateDraft, from: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyDates(); }}
                aria-label="Visit date from" title="From this date" />

              <span aria-hidden="true" style={{
                alignSelf: 'center', color: BRAND.inkFaint, fontSize: 11.5,
                padding: '0 2px',
              }}>–</span>

              <input type="date" value={dateDraft.to} style={field}
                onChange={(e) => setDateDraft({ ...dateDraft, to: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyDates(); }}
                aria-label="Visit date to" title="Up to and including this date" />

              {active && (
                <button type="button"
                  onClick={() => { setDateDraft({ from: '', to: '' }); setDateRange({ from: '', to: '' }); }}
                  aria-label="Clear the date range" title="Show the whole month again"
                  style={{
                    border: 0, borderLeft: `1px solid ${BRAND.line}`,
                    background: 'transparent', cursor: 'pointer',
                    padding: '0 9px', fontSize: 13, lineHeight: 1,
                    color: BRAND.inkMuted, fontFamily: 'inherit',
                  }}>✕</button>
              )}

              <button type="button" onClick={applyDates} disabled={!dirty}
                title={dirty
                  ? 'Filter the month to this range of visit dates'
                  : 'Nothing to apply — the range shown is already in force'}
                style={{
                  border: 0, borderLeft: `1px solid ${dirty ? BRAND.greenDark : BRAND.line}`,
                  padding: '0 14px', fontSize: 12.5, fontWeight: 700,
                  fontFamily: 'inherit', whiteSpace: 'nowrap',
                  cursor: dirty ? 'pointer' : 'default',
                  background: dirty ? BRAND.green : '#F1F5F9',
                  color: dirty ? '#fff' : BRAND.inkFaint,
                }}>
                Filter
              </button>
            </div>
          );
        })()}

        {/*  ── THE REPORT, IMMEDIATELY AFTER THE DATE FILTER ──────────────────
             "You should give me a report after the date filter. That's all I want."

             IT SAT AT THE FAR RIGHT OF THIS ROW AND WAS NOT FOUND. It was pushed
             to the corner with `marginLeft: 'auto'` on the strength of the word
             "corner" in the first request — six hundred pixels from the Filter
             button, on a breadcrumb row nobody scans, where it read as page
             furniture rather than as the control that had just been asked for.
             The follow-up was "I can't see any report button near the date
             filter", which is the same instruction said twice.

             So it sits HERE: the next thing after Filter, in normal flow, in the
             same visual group as the control it reports on. A control is found
             where the thing it relates to is, not where there happens to be room.

             Same green as Filter, deliberately — those two are the pair that act
             on this row, and giving the report its own navy made it read as
             belonging to the toolbar below instead. */}
        <button type="button" onClick={() => setReportOpen(true)}
          disabled={loading || !!error}
          title={loading
            ? 'Wait for the month to finish loading'
            : 'A printable report of every error in the claims now on screen, '
              + 'with an Excel export'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 14px', borderRadius: 7, whiteSpace: 'nowrap',
            font: 'inherit', fontSize: 12.5, fontWeight: 700,
            cursor: loading || error ? 'default' : 'pointer',
            border: `1px solid ${loading || error ? BRAND.line : BRAND.greenDark}`,
            background: loading || error ? '#F1F5F9' : BRAND.green,
            color: loading || error ? BRAND.inkFaint : '#fff',
            boxShadow: loading || error ? 'none' : `0 1px 3px ${BRAND.green}44`,
          }}>
          <FileText size={14} />
          Error report
          {/*  The count of rejectable claims, on the button. The whole reason to
               open this is to find out whether there is anything in it, and a
               button that answers that before it is pressed saves the trip. */}
          {rejectableCount > 0 && (
            <span style={{
              padding: '1px 7px', borderRadius: 20, fontSize: 11, fontWeight: 800,
              background: 'rgba(255,255,255,.22)', color: '#fff',
              fontVariantNumeric: 'tabular-nums',
            }}>{rejectableCount.toLocaleString()}</span>
          )}
        </button>

        {/*  One line for whatever is narrowing the table, rather than one per
             filter — with both set they were two counts of the same number.
             A pill rather than loose grey text: it sits next to two bordered
             controls, and unbounded text beside them is what made the row look
             assembled rather than designed. */}
        {(q || dateRange.from || dateRange.to) && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', borderRadius: 20, fontSize: 11.5,
            background: BRAND.greenSoft, color: BRAND.greenDark,
            border: `1px solid ${BRAND.greenDark}33`, whiteSpace: 'nowrap',
          }}>
            <strong>{visible.length.toLocaleString()}</strong>
            of {claims.length.toLocaleString()}
            {(dateRange.from || dateRange.to) && (
              <span style={{ opacity: .85 }}>
                ·{' '}
                {dateRange.from && dateRange.to
                  ? `${dateRange.from} → ${dateRange.to}`
                  : (dateRange.from ? `from ${dateRange.from}` : `up to ${dateRange.to}`)}
              </span>
            )}
          </span>
        )}

      </div>

      {/* ══ ROW 2 — title, what is applied, and the actions ═════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        marginBottom: 10,
      }}>
        <button
          type="button" onClick={onBack} aria-label="Back to dashboard"
          title="Back to the dashboard"
          style={{
            display: 'grid', placeItems: 'center', width: 26, height: 26,
            flexShrink: 0, border: 0, background: 'none', cursor: 'pointer',
            color: BRAND.inkMuted, borderRadius: 6,
          }}
        >
          <ChevronLeft size={19} />
        </button>

        {/* ── THE CLINICAL REVIEW ────────────────────────────────────────────
            ASKED FOR: "put a button near where the arrow is — there is a space
            after the arrow — and then we can synchronise the whole system to
            figure out these mismatches."

            Here rather than in the toolbar on the right, which is where the
            actions that WRITE live: Export, Repair, Bulk Delete. This one reads
            the month and reports; grouping it with the destructive buttons would
            be the wrong neighbourhood for it and the wrong first impression.

            It carries its own count once a scan has run, so the answer is on the
            button that produced it rather than somewhere the officer has to go
            looking. */}
        {/*  ── THREE STATES, NOT TWO ────────────────────────────────────────
             "I went back home on the application and I came back and it didn't
              show me back the errors again."

             The results had not gone anywhere — the officer had moved to another
             FACILITY, which has never been scanned. But the button looked
             identical in both cases, so "this hospital was never reviewed" and
             "this hospital was reviewed and is clean" were impossible to tell
             apart, and the only reading left was that the answers had been lost.

             So the button says which of the three it is: never scanned (plain),
             scanned and clean (a quiet green tick), scanned and flagged (amber
             with the count). */}
        <button
          type="button"
          onClick={() => setAiOpen(true)}
          aria-label="Review this month for clinical mismatches"
          title={aiFlagged > 0
            ? `${aiFlagged} claim${aiFlagged === 1 ? '' : 's'} in this month carry a clinical warning`
            : aiReviewed > 0
              ? `All ${aiReviewed.toLocaleString()} claims here have been reviewed — nothing was questioned`
              : 'This month has not been reviewed yet. Check every claim: do the '
                + 'medicines, labs and course lengths fit what was diagnosed?'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            padding: '5px 11px', borderRadius: 8, cursor: 'pointer',
            font: 'inherit', fontSize: 12, fontWeight: 800,
            border: `1px solid ${aiFlagged > 0 ? '#E08B2F66'
              : aiReviewed > 0 ? `${BRAND.green}55` : BRAND.line}`,
            background: aiFlagged > 0 ? '#FFF8EC'
              : aiReviewed > 0 ? BRAND.greenSoft : '#fff',
            color: aiFlagged > 0 ? '#8A5510'
              : aiReviewed > 0 ? BRAND.greenDark : BRAND.inkMuted,
          }}
        >
          <Stethoscope size={13} />
          Clinical review
          {aiFlagged > 0 ? (
            <span style={{
              padding: '1px 6px', borderRadius: 5, fontSize: 11, fontWeight: 900,
              background: '#E08B2F', color: '#fff',
            }}>{aiFlagged}</span>
          ) : aiReviewed > 0 ? (
            //  Reviewed, nothing found. A tick rather than a "0" — a zero in a
            //  count badge reads as "none yet", which is the opposite of what
            //  this means.
            <Check size={12} />
          ) : null}
        </button>

        {/*  Showing only what the review flagged. A chip rather than another
             toggle in the filter row: it is a temporary way of looking at the
             month, it is obvious while it is on, and one click puts it back. */}
        {aiOnly && (
          <button
            type="button"
            onClick={() => setAiOnly(false)}
            title="Show every claim again"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
              font: 'inherit', fontSize: 12, fontWeight: 800,
              border: '1px solid rgba(224,139,47,.45)', background: '#E08B2F',
              color: '#fff',
            }}
          >
            Only flagged claims
            <X size={12} />
          </button>
        )}

        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          {/* The facility leads the title when there is exactly one, because it is
              the coarsest fact about what is on screen — the month and the scheme
              are both narrowings WITHIN it. "APRIL 2026 — NHIS" is the same
              heading whichever of four facilities you opened. */}
          <h1 style={S.title}>
            {facilitiesInView.length === 1 && facilitiesInView[0].name && (
              <>
                <span>{facilitiesInView[0].name}</span>
                <span style={{ color: BRAND.inkFaint, fontWeight: 700 }}> · </span>
              </>
            )}
            {MONTH_TITLE(monthData?.month)} {selectedYear} — {schemeLabel}
          </h1>
          {/* One meta line instead of three stacked blocks: the total, which
              schemes were applied, and whether the rows span both claim models.
              The scheme names stay in the h1 as well — a facility running four
              schemes must never have to infer which one is on screen — but they no
              longer need a chip row of their own to say it twice. */}
          <div style={S.subtitle}>
            <strong style={{ color: BRAND.ink, fontWeight: 800 }}>
              {Number(stats.all || 0).toLocaleString()}
            </strong>{' claims'}
            {applied.length > 0
              ? <> · filtered to <strong style={{ color: BRAND.greenDark, fontWeight: 800 }}>
                  {applied.join(' + ')}
                </strong></>
              : (!loading && <> · <span style={{ color: BRAND.inkFaint }}>all schemes</span></>)}
            {sources.length > 1 && (
              <span style={{ color: BRAND.inkFaint }}> · typed-in + imported</span>
            )}
          </div>

          {/* ── Whose claims these are ────────────────────────────────────────
              Stated on its own line, from the SERVER's answer rather than the
              prop this component sent. Every button in the toolbar acts on this
              list, so "which facility am I about to bulk-delete from" must never
              be something the officer has to remember from the previous screen.

              Two shapes, because they carry different risk. One facility: named,
              in green, settled. Several mixed together: named with counts, in
              amber — that is the state in which a bulk action needs a second
              thought, and the only honest thing to do is say so. */}
          {/* ONE facility in view — whether it was explicitly filtered or is simply
              the only one there is. The second case is what left this silent: all
              2,383 claims from one facility with no filter applied fell through
              both branches, so the officer editing them was told nothing. */}
          {facilitiesInView.length === 1 ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
              padding: '3px 9px', borderRadius: 7,
              background: BRAND.greenSoft, border: `1px solid ${BRAND.green}44`,
            }}>
              <Building2 size={13} style={{ color: BRAND.greenDark, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 800, color: BRAND.ink }}>
                {facilitiesInView[0].name || facilityLabel}
              </span>
              {facilitiesInView[0].facilityId != null && (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: BRAND.inkMuted }}>
                  #{facilitiesInView[0].facilityId}
                </span>
              )}
              <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
                {appliedFacility !== 'all'
                  ? "— only this facility's claims"
                  : '— every claim below belongs to this facility'}
              </span>
            </div>
          ) : null}

          {/*  ── A FILTER THAT WENT UNANSWERED ────────────────────────────────
               The server echoes back every filter it HONOURED. If something was
               asked for and is missing from that echo, the request reached a
               server that does not know the parameter — which is what a deploy
               of the frontend without the backend looks like from here.

               Express ignores query parameters it does not destructure, so the
               month comes back complete and the screen shows every claim. The
               filter appears to do nothing, with nothing anywhere saying why:
               the exact failure this line exists to name. It costs one
               comparison and turns a silent no-op into a sentence. */}
          {(() => {
            const asked = [];
            if (adv.ageFrom !== '' || adv.ageTo !== '') asked.push('age');
            if (adv.gaps?.length) asked.push('gaps');
            if (adv.issues?.length) asked.push('issues');
            if (adv.diagnosis?.length) asked.push('diagnosis');
            if (adv.procedure?.length) asked.push('procedure');
            if (adv.medication?.length) asked.push('medication');
            if (adv.investigation?.length) asked.push('investigation');
            //  'stay' is the field the server echoes for a length-of-stay filter. Named
            //  here so a frontend deployed ahead of its backend says so, instead of
            //  showing the whole month as though nothing had been ticked.
            if (adv.stayDays?.length) asked.push('stay');
            const honoured = new Set(appliedFilters.map((f) => f.field));
            const ignored = asked.filter((f) => !honoured.has(f));
            if (!ignored.length || loading || error) return null;
            return (
              <div style={{
                marginTop: 8, padding: '9px 12px', borderRadius: 8,
                background: '#FFF4E5', border: '1px solid rgba(154,91,0,.28)',
                color: '#7A4A00', fontSize: 12, lineHeight: 1.5,
              }}>
                <strong>
                  This server did not apply the {ignored.join(' and ')} filter
                  {ignored.length > 1 ? 's' : ''}.
                </strong>{' '}
                The list below is NOT narrowed by
                {ignored.length > 1 ? ' them' : ' it'} — every claim in the month is
                shown. The screen is newer than the server it is talking to; the
                backend needs the matching update.
              </div>
            );
          })()}

          {/*  What the Advanced Filter narrowed this list to, from the SERVER's
               answer. The count in the heading above is already filtered, so
               without this line 12 claims out of 1,095 look like a month with
               twelve claims in it. */}
          {appliedFilters.length > 0 && (
            <div style={{
              display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center',
            }}>
              {/*  One chip per filter, listing its values. Several ticked codes are
                   one narrowing — "diagnosis: B54, A09" — so they belong in one
                   chip rather than looking like two unrelated filters. */}
              {appliedFilters.map((f) => (
                <span key={f.field} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 9px', borderRadius: 20, fontSize: 11.5,
                  background: BRAND.greenSoft, border: `1px solid ${BRAND.greenDark}44`,
                  color: BRAND.ink, maxWidth: '46ch',
                }}>
                  <span style={{ color: BRAND.inkMuted, flexShrink: 0 }}>{f.label}</span>
                  <strong style={{
                    fontWeight: 800, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {(f.values || []).join(', ')}
                  </strong>
                  {(f.values || []).length > 1 && (
                    <span style={{ color: BRAND.inkMuted, flexShrink: 0 }}>
                      ({f.values.length})
                    </span>
                  )}
                </span>
              ))}
              {filtersUnsupportedFor.includes('facility') && (
                <span style={{ fontSize: 11, color: '#8A5510' }}>
                  · typed-in claims not searched
                </span>
              )}
              <button type="button" onClick={() => { setAdv(EMPTY_ADV); setAdvDraft(EMPTY_ADV); }}
                style={{
                  border: 0, background: 'none', cursor: 'pointer', padding: '2px 4px',
                  fontSize: 11.5, fontWeight: 700, color: BRAND.inkMuted,
                  textDecoration: 'underline', fontFamily: 'inherit',
                }}>clear</button>
            </div>
          )}

          {facilitiesInView.length > 1 ? (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6,
              padding: '5px 9px', borderRadius: 7, maxWidth: '72ch',
              background: '#FDF6EC', border: `1px solid ${BRAND.warning}55`,
            }}>
              <Building2 size={13} style={{ color: '#8A5510', flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 11.5, color: '#8A5510', lineHeight: 1.55 }}>
                <strong style={{ fontWeight: 800 }}>
                  {facilitiesInView.length} facilities
                </strong>{' in this list — '}
                {facilitiesInView.map((f, i) => (
                  <span key={String(f.id)}>
                    {i > 0 && ', '}
                    {f.name} <strong>{f.total.toLocaleString()}</strong>
                  </span>
                ))}
                {'. Pick one on the dashboard before deleting or exporting.'}
              </span>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          {/*  NHIA XML, from the model this screen actually edits, scoped to the
               facility already selected above.

               IT USED TO EXPORT THE FILTERED MONTH AND ONLY THE FILTERED MONTH,
               on the reasoning that "a submission is a period, not a hand-picked
               set". That is true of a monthly submission and false of everything
               else an officer does with this button — a resubmission of the claims
               NHIA queried, one specialty, one G-DRG. Ticking 3,064 rows and
               getting a file of 3,725 was the report.

               The dialog now asks, and defaults to the ticked rows when there are
               any. The count is on the button so the answer is visible before it
               is opened. */}
          <button type="button" style={btn('green')}
            title={chosen.length
              ? `Export — ${chosen.length.toLocaleString()} claim(s) ticked. You can `
                + 'choose those or the whole month.'
              : 'Generate the NHIA e-Claims XML for this facility and month'}
            onClick={() => setExportOpen(true)}>
            <Download size={14} /> Export
            {chosen.length > 0 && (
              <span style={{
                fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                padding: '1px 6px', borderRadius: 999, fontSize: 10.5,
                background: 'rgba(255,255,255,.22)',
              }}>{chosen.length.toLocaleString()}</span>
            )}
          </button>
          {/*  No Import button here. Importing a batch is IT / ICT ▸ Claims XML
               Import, which owns the facility picker, the parse preview and the
               import record — a second entry point on this screen could only be a
               worse copy of it, and a disabled one just invites the question. */}
          {/*  MULTI VET: the claim editor's own work — diagnoses, G-DRG, lab, scan,
               medications — applied to every ticked claim at once. Not a status
               workflow; "vet" here means the same editing you do on one claim.
               Acts on the TICKED rows, not the whole filter: Advanced Filter
               narrows the month, the checkboxes say which of the result you meant. */}
          {/*  ── ALWAYS OPENS ────────────────────────────────────────────────
               It used to be disabled until claims were ticked, so the only way to
               find out what Multi Vet even does was to go and select something
               first. That is backwards: you open a tool to see what it offers, then
               choose what to point it at.

               The dialog itself now says when nothing is selected and what to do
               about it — which is the right place for that, because it can explain,
               and a greyed-out button cannot.

               ── AND THE TICKS ARE HONOURED ──────────────────────────────────
               With rows ticked, the dialog opens ON THEM: they are the group, every
               one already ticked, straight to choosing what to do. With nothing
               ticked it opens on the pattern and goes looking.

               The count on this badge is therefore a promise about what happens
               next. It used to be decoration — the ticks were counted here and then
               ignored, and the officer who had filtered the month down to fifteen
               patients had to describe those same fifteen again inside the dialog. */}
          <button type="button"
            style={btn('slate')}
            title={chosen.length
              ? `Vet the ${chosen.length} selected claim(s)`
              : 'Vet many claims at once — describe a group inside'}
            onClick={() => setVetOpen(true)}>
            <Layers size={14} /> Multi Vet
            {chosen.length > 0 && (
              <span style={{
                marginLeft: 2, padding: '0 6px', borderRadius: 20,
                background: '#fff', color: BRAND.ink, fontSize: 10.5, fontWeight: 800,
              }}>{chosen.length}</span>
            )}
          </button>
          {/*  REPAIR: put the ticked claims' service back in agreement with their
               own rows. Sits beside Multi Vet because it is the same kind of act —
               one operation across a selection — and because the claims that need
               it are found the same way, by ticking what the Advanced Filter's
               "What is wrong" group turned up.

               Enabled only when a ticked row actually has something to fix, so the
               button says by its own state whether there is work here. It always
               previews before it writes.  */}
          <button type="button"
            style={btn('slate', repairable.length === 0)}
            disabled={repairable.length === 0}
            title={repairable.length
              ? `Preview a repair of the ${repairable.length} ticked claim(s) that need one`
              : (chosen.length
                ? 'None of the ticked claims has anything to repair'
                : 'Tick some claims first')}
            onClick={repairSelected}>
            <Wrench size={14} /> Repair
            {repairable.length > 0 && (
              <span style={{
                marginLeft: 2, padding: '0 6px', borderRadius: 20,
                background: '#fff', color: BRAND.ink, fontSize: 10.5, fontWeight: 800,
              }}>{repairable.length}</span>
            )}
          </button>
          <button
            type="button"
            style={btn('slate', chosen.length === 0)}
            disabled={chosen.length === 0}
            onClick={bulkDelete}
            title={chosen.length ? `Delete ${chosen.length} selected` : 'Select claims first'}
          >
            <Trash2 size={14} /> Bulk Delete
          </button>
          {/*  Advanced Filter, which is what the Toolbox slot was always going to
               become. Live, not disabled — and it carries a count when anything is
               applied, because the one thing a filtered list must never do is look
               like an unfiltered one. */}
          <button type="button"
            style={{
              ...btn('ghost'),
              ...(advCount ? {
                borderColor: BRAND.greenDark, color: BRAND.greenDark,
                background: BRAND.greenSoft, fontWeight: 800,
              } : null),
            }}
            title={advCount
              ? `${advCount} filter${advCount === 1 ? '' : 's'} applied — click to change`
              : 'Filter by diagnosis, procedure, medication or attendance'}
            onClick={() => { setAdvDraft(adv); setAdvOpen(true); }}>
            <Wrench size={14} /> Advanced Filter
            {advCount > 0 && (
              <span style={{
                marginLeft: 2, padding: '0 6px', borderRadius: 20,
                background: BRAND.greenDark, color: '#fff',
                fontSize: 10.5, fontWeight: 800,
              }}>{advCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* While an editor is open the filter bar folds away to a single line. The
          filters are not what you are working on at that moment, and the ~46px
          they occupy is a card and a half of the claim you ARE working on. */}
      {openRow && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '6px 12px', marginBottom: 8, fontSize: 11.5,
          background: BRAND.navySoft, border: `1px solid ${BRAND.line}`,
          borderRadius: 8, color: BRAND.navy,
        }}>
          <SquarePen size={13} />
          <strong>Editing a claim</strong>
          <span style={{ color: BRAND.inkMuted }}>
            filters and totals are folded away — {visible.length.toLocaleString()} claim(s)
            still loaded behind this panel
          </span>
          <button type="button" onClick={closeEditor}
            style={{ ...S.btnBase, marginLeft: 'auto', padding: '4px 12px', fontSize: 11.5,
              background: BRAND.surface, color: BRAND.ink, borderColor: BRAND.line }}>
            Close editor
          </button>
        </div>
      )}

      {/* ══ ROW 3 — one filter bar ══════════════════════════════════════════
          Attendance is a segmented control, so All/OPD/IPD read as one choice
          with three positions and there is exactly ONE "All" on the screen.
          Specialty has no All of its own: no chip lit means no specialty filter,
          and a Clear appears only once one is active. */}
      <div style={{ ...S.filterBar, display: openRow ? 'none' : S.filterBar.display }}>
        <div style={S.segment} role="group" aria-label="Attendance type">
          {[['all', 'All', facets.type.all],
            ['opd', 'OPD', facets.type.opd],
            ['ipd', 'IPD', facets.type.ipd]].map(([key, label, n]) => {
            // Zero and not currently selected means selecting it shows nothing, so
            // it is disabled. The active one stays clickable — you must always be
            // able to get back out of a filter you are already in.
            const dead = n === 0 && typeTab !== key;
            return (
              <button key={key} type="button"
                style={{ ...S.segBtn(typeTab === key),
                  opacity: dead ? .4 : 1, cursor: dead ? 'not-allowed' : 'pointer' }}
                disabled={dead}
                aria-pressed={typeTab === key}
                title={dead
                  ? `No ${label} claims in the current selection`
                  : `Show ${label} claims`}
                onClick={() => setTypeTab(key)}>
                {label}
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  opacity: typeTab === key ? .85 : .7 }}>
                  {Number(n || 0).toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>

        {/*  ── EVERYTHING NHIA WILL SEND BACK, IN ONE CLICK ─────────────────
             The Advanced Filter has a tile per rule, which is right when you
             want one rule. It is the wrong shape for the question actually being
             asked most mornings — "show me what is going to bounce" — because
             answering it there means ticking sixteen boxes and knowing which
             sixteen.

             The count is the UNION, not the sum of those tiles. A claim with a
             bad membership number and a missing check code is in two of them, so
             adding them up says 4,245 where the real worklist is 2,628. The
             server computes it in the same pass as the per-rule counts.

             The rule codes come from the server too, and are not listed here:
             severities live in claimRules, and a copy on this side would go
             stale the first time a rule is added — which is exactly how a
             worklist quietly stops including a whole class of rejection. */}
        {(rejectFacet.codes.length > 0) && (() => {
          const { codes, count: n } = rejectFacet;
          //  On when every error rule is selected — the state this button sets.
          //  Compared as sets so a reordering by the server does not read as off.
          const on = adv.issues?.length === codes.length
            && codes.every((c) => adv.issues.includes(c));
          const dead = n === 0 && !on;
          return (
            <>
              <div style={S.divider} />
              <button type="button"
                disabled={dead}
                aria-pressed={on}
                title={dead
                  ? 'Nothing in this selection will be rejected'
                  : on
                    ? `Showing the ${n.toLocaleString()} claims NHIA will reject — `
                      + 'click to show everything again'
                    : `Show only the ${n.toLocaleString()} claims NHIA will reject, `
                      + `across all ${codes.length} rules. Each row says which rule `
                      + 'in its REJECT badge.'}
                onClick={() => {
                  //  A toggle, not a one-way trip. The point of a worklist filter
                  //  is working through it and coming back out.
                  setAdv((a) => ({ ...a, issues: on ? [] : codes }));
                  setPage(1);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 11px', borderRadius: 7, flexShrink: 0,
                  font: 'inherit', fontSize: 12, fontWeight: 800,
                  cursor: dead ? 'not-allowed' : 'pointer',
                  opacity: dead ? .45 : 1,
                  //  Red when active, because this is the reject worklist and the
                  //  REJECT badges in the rows beneath it are red. An officer
                  //  filtered down to rejections should see the screen agree.
                  background: on ? '#A62828' : BRAND.dangerSoft,
                  color: on ? '#fff' : '#A62828',
                  border: `1px solid ${on ? '#A62828' : 'rgba(166,40,40,.28)'}`,
                }}>
                <ShieldAlert size={12} />
                Will be rejected
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  opacity: .9 }}>
                  {n.toLocaleString()}
                </span>
              </button>
            </>
          );
        })()}

        {specialtyCodes.length > 0 && <div style={S.divider} />}

        {specialtyCodes.length > 0 && (
          <>
            <span style={S.metaLabel}>Specialty</span>
            <div style={S.chipScroll}>
              {specialtyCodes.map((code) => {
                const n = facets.spec[code] || 0;
                const active = specialty === code;
                //  ── A CHIP THAT IS NOT A SPECIALTY ────────────────────────
                //  D001 sat here reading like OPDC and MEDI, on 414 claims. It is not
                //  a specialty: it came out of the imported file's specialtyAttended
                //  and is a department number. OPD0, OOBG and ODPC are the same story —
                //  a truncation and two transpositions.
                //
                //  So the chip says so, and CARRIES THE REPAIR. The fault was visible
                //  here all along and the only way to act on it was three screens away
                //  in the Advanced Filter, which is why it read as something nothing
                //  could be done about.
                //
                //  UNSPECIFIED is excluded: no specialty at all is a different and far
                //  commoner state, and colouring 13 claims red beside 414 real faults
                //  would bury them.
                const broken = code !== 'UNSPECIFIED' && !MDC_GROUPS.has(code);
                return (
                  <SpecialtyChip key={code} code={code} count={n} active={active}
                    disabled={n === 0 && !active}
                    tone={broken ? 'danger' : undefined}
                    title={broken
                      ? `"${code}" is not one of the 13 specialty groups — it came from `
                        + `the imported file. Click to see the ${n} claim(s); use Repair `
                        + 'to set each one to its own service\'s group.'
                      : undefined}
                    // Clicking the active chip clears it — the same control both
                    // sets and unsets, so no separate All is needed.
                    //
                    //  AND THE CODE GOES WITH IT, both ways. Leaving OPDC06A set
                    //  while moving to OBGY gives an empty table and two filters to
                    //  work out; leaving it set while clearing the specialty
                    //  silently keeps filtering the whole month by one OPDC code.
                    onClick={() => {
                      setSpecialty(active ? 'all' : code);
                      setServiceCode('all');
                      setOpenSpec(null);
                    }}
                    codes={facets.codes?.[code] || []}
                    open={openSpec === code}
                    onToggle={setOpenSpec}
                    chosenCode={active ? serviceCode : 'all'}
                    //  PICKING A CODE PICKS ITS SPECIALTY. OPDC06A means nothing on
                    //  its own, and the officer opened the panel from the OPDC chip,
                    //  so that is the specialty they asked for.
                    onPickCode={(picked) => {
                      setSpecialty(code);
                      setServiceCode(picked);
                    }} />
                );
              })}
            </div>
            {/*  ── THE REPAIR, WHERE THE FAULT IS VISIBLE ─────────────────────
                 The fix for a bad specialty already existed, on a tile three screens
                 away in the Advanced Filter. So the fault was on this row, in plain
                 sight, on 414 claims — and the row said nothing could be done about it.
                 The officer's words were "I don't even see it. Where do I fix it?"

                 This is that button, beside the chips it is about. Same engine as the
                 tile — fixNhiaIssue — so there is one code path and one confirmation,
                 and it previews every change before writing anything. */}
            {(() => {
              const bad = specialtyCodes.filter((code) => code !== 'UNSPECIFIED'
                && !MDC_GROUPS.has(code) && (facets.spec[code] || 0) > 0);
              if (!bad.length) return null;
              const n = bad.reduce((t, code) => t + (facets.spec[code] || 0), 0);
              return (
                <button type="button"
                  disabled={repairBusy}
                  onClick={() => fixNhiaIssue('specialty_not_a_group', 'invalid_specialty_code')}
                  title={`${bad.join(', ')} ${bad.length === 1 ? 'is' : 'are'} not `
                    + `${bad.length === 1 ? 'a specialty' : 'specialties'}. This sets each `
                    + `of the ${n.toLocaleString()} claim(s) to the group its own service `
                    + 'belongs to — MEDI28A becomes MEDI. You see every change before '
                    + 'anything is written, and a specialty that IS a real group is never '
                    + 'touched.'}
                  style={{
                    ...S.btnBase, flexShrink: 0, padding: '5px 11px', fontSize: 11.5,
                    fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: BRAND.dangerSoft, color: BRAND.danger,
                    borderColor: `${BRAND.danger}66`,
                    cursor: repairBusy ? 'wait' : 'pointer',
                  }}>
                  <Wrench size={12} />
                  {repairBusy ? 'Repairing…' : `Repair ${n.toLocaleString()}`}
                </button>
              );
            })()}
            {(specialty !== 'all' || serviceCode !== 'all' || typeTab !== 'all' || q) && (
              <button type="button"
                onClick={() => {
                  setSpecialty('all'); setTypeTab('all'); setQ('');
                  setServiceCode('all'); setOpenSpec(null);
                }}
                title="Clear every filter"
                style={{
                  ...S.btnBase, flexShrink: 0, padding: '5px 10px', fontSize: 11.5,
                  background: 'none', color: BRAND.inkMuted,
                  borderColor: BRAND.line,
                }}>
                Reset
              </button>
            )}
          </>
        )}
      </div>

      {/* When a combination genuinely has nothing, say so where the filters are —
          not only as an empty table twelve rows down. In this data every specialty
          is exclusively OPD or exclusively IPD, so this is reachable in one click
          and used to look like a broken button. */}
      {!loading && !error && claims.length > 0 && visible.length === 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '8px 12px', marginBottom: 10, fontSize: 12,
          background: '#FDF2E3', border: '1px solid #F2DCB8', borderRadius: 8,
          color: '#8A5510',
        }}>
          <strong>No claims match this combination.</strong>
          {/*  THE CODE IS NAMED FIRST when one is chosen. It is the narrowest of the
               filters and the likeliest cause, and it is the one whose control has
               closed — so an officer reading only the chips would see OPDC active,
               with 20,053 beside it, and an empty table. */}
          {serviceCode !== 'all' ? (
            <span>
              {specialty === 'all' ? 'Nothing' : specialty} is billed on{' '}
              <strong>{serviceCode}</strong>
              {typeTab !== 'all' ? ` in no ${typeTab.toUpperCase()} claim` : ' in none of these claims'}
              . Open the specialty and pick <strong>All of {specialty}</strong> to widen it.
            </span>
          ) : specialty !== 'all' && typeTab !== 'all' && (
            <span>
              {specialty} has no {typeTab.toUpperCase()} claims in this month —
              every specialty here is either outpatient or inpatient, not both.
            </span>
          )}
          <button type="button"
            onClick={() => {
              setSpecialty('all'); setTypeTab('all'); setQ('');
              setServiceCode('all'); setOpenSpec(null);
            }}
            style={{ ...S.btnBase, padding: '4px 11px', fontSize: 11.5,
              background: BRAND.surface, color: BRAND.ink, borderColor: '#F2DCB8' }}>
            Reset filters
          </button>
        </div>
      )}

      {/* ══ CAN BE FIXED AUTOMATICALLY ═══════════════════════════════════════
           "The moment you load up the claims, you show it at the top. This can
            be auto fixed... fix it once and for all."

           ABOVE THE TABLE and not inside a dialog, because the whole complaint
           was that the fix existed somewhere the officer never went. It states
           what it will do, in the officer's terms — the specialty follows the
           G-DRG the claim is billed on — and it previews before it writes.

           Shown only when there is something to do, so it is never a permanent
           strip that stops being read. */}
      {autoFixIds.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap', padding: '9px 13px', marginBottom: 10,
          background: BRAND.greenSoft, border: `1px solid ${BRAND.greenDark}44`,
          borderRadius: 9,
        }}>
          <span style={{
            display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
            fontSize: 12.5, color: BRAND.ink,
          }}>
            <Wrench size={14} style={{ color: BRAND.greenDark, alignSelf: 'center' }} />
            <b>
              {autoFixIds.length.toLocaleString()} claim
              {autoFixIds.length === 1 ? '' : 's'} can be fixed automatically
            </b>
            <span style={{ color: BRAND.inkMuted }}>
              — the specialty does not match the G-DRG the claim is billed on, and
              the code itself says what it should be. MEDI28A is MEDI.
            </span>
          </span>
          <button
            type="button"
            onClick={autoFixAll}
            disabled={repairBusy}
            title={`Check all ${autoFixIds.length} and show what would change before writing`}
            style={{
              ...S.btnBase, padding: '6px 13px', fontSize: 12, fontWeight: 800,
              background: repairBusy ? BRAND.canvas : BRAND.greenDark,
              color: repairBusy ? BRAND.inkMuted : '#fff',
              borderColor: repairBusy ? BRAND.line : BRAND.greenDark,
              cursor: repairBusy ? 'default' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            }}>
            {repairBusy
              ? <><Loader2 size={13} className="mf-autofix-spin" /> Checking…</>
              : <><Wrench size={13} /> Fix all {autoFixIds.length.toLocaleString()}</>}
          </button>
          {/*  ITS OWN KEYFRAME, declared beside the only thing that uses it. The
               first version reached for `.mf-cm-spin`, which does not exist — and
               `.mf-spin` is declared inside a dialog further down this file, so it
               is only defined while that dialog is mounted. A spinner that does
               not spin reads as a frozen screen. */}
          <style>{'@keyframes mfautofixspin{to{transform:rotate(360deg)}}'
            + '.mf-autofix-spin{animation:mfautofixspin .9s linear infinite}'}</style>
        </div>
      )}

      {/* ── Selection bar ───────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', padding: '7px 12px',
        background: BRAND.navySoft, border: `1px solid ${BRAND.line}`,
        borderRadius: '9px 9px 0 0', borderBottom: 0, fontSize: 12,
      }}>
        <span style={{ fontWeight: 700, color: chosen.length ? BRAND.ink : BRAND.inkMuted }}>
          {chosen.length ? `${chosen.length} selected` : 'None selected'}
          {/*  WAS "delete those from the import screen", which stopped being true
               when the delete was taught both models — an imported claim is
               deleted from here now, and what differs is what goes WITH it: its
               own diagnoses and medicines, where a hospital claim's are ward
               records and stay. Said plainly instead of sending them away. */}
          {chosen.length > 0 && imported.length > 0 && (
            <span style={{ fontWeight: 500, color: BRAND.inkMuted }}>
              {' '}({imported.length} imported — deleting those removes their
              diagnoses and medicines too)
            </span>
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="cm-first" style={{ color: BRAND.inkMuted }}>Select first</label>
          <input
            id="cm-first" type="number" min={1} value={selectFirst}
            onChange={(e) => setSelectFirst(e.target.value)}
            style={{
              width: 76, padding: '6px 9px', font: 'inherit', fontSize: 12.5,
              border: `1px solid ${BRAND.line}`, borderRadius: 7,
              background: BRAND.surface, color: BRAND.ink,
            }}
          />
          <button type="button" onClick={takeFirst}
            style={{ ...S.btnBase, padding: '5px 15px', background: BRAND.green,
              color: '#fff', borderColor: BRAND.green }}>
            Go
          </button>
        </span>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div
        ref={wrapRef}
        style={{
          ...S.tableWrap, borderRadius: '0 0 9px 9px',
          // Taller while editing, because the filter bar above has folded away
          // and the whole point is to hand that space to the claim.
          maxHeight: openRow ? 'calc(100vh - 150px)' : S.tableWrap.maxHeight,
        }}
      >
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 38, paddingLeft: 13 }}>
                <input type="checkbox" checked={allShown} onChange={toggleAll}
                  aria-label="Select all shown claims"
                  title="Select every claim currently shown"
                  style={{ cursor: 'pointer', accentColor: BRAND.green }} />
              </th>
              {[
                ['visit_date',  'Visit'],
                ['discharge_date', 'Discharge'],
                ['member_no',   'Member No'],
                ['ccc',         'CCC'],
                ['folder_no',   'Folder No'],
                ['name',        'Name'],
              ].map(([key, label]) => (
                <th key={key} style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort(key)}
                  title={`Sort by ${label}`}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {label} {sortIcon(key)}
                  </span>
                </th>
              ))}
              <th style={S.th}>DOB / Age</th>
              <th style={S.th}>Gender</th>
              <th style={S.th}>Type</th>
              <th style={S.th}>Attendance</th>
              <th style={S.th}>Service</th>
              <th style={S.th}>Status</th>
              <th style={{
                ...S.th, ...S.stickyRight, zIndex: 3, textAlign: 'right',
                width: 84, boxShadow: `inset 1px 0 0 ${BRAND.line}`,
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td style={{ ...S.td, textAlign: 'center', padding: 46 }} colSpan={14}>
                Loading claims…
              </td></tr>
            )}

            {!loading && error && (
              <tr><td style={{ ...S.td, textAlign: 'center', padding: 46, color: BRAND.danger }} colSpan={14}>
                {error}
                <div><button type="button" onClick={load}
                  style={{ ...S.btnBase, marginTop: 12, background: BRAND.surface,
                    color: BRAND.ink, borderColor: BRAND.line }}>Try again</button></div>
              </td></tr>
            )}

            {!loading && !error && visible.length === 0 && (
              <tr><td style={{ ...S.td, textAlign: 'center', padding: 52 }} colSpan={14}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>No claims match these filters</div>
                <div style={{ fontSize: 12.5, color: BRAND.inkMuted, marginTop: 5 }}>
                  {claims.length > 0
                    ? `${claims.length.toLocaleString()} claim(s) in this month — clear the search, or choose All.`
                    : 'This month has no claims in either the typed-in or the imported tables.'}
                </div>
              </td></tr>
            )}

            {/*  pageRows, not visible — only this page is put in the DOM. The
                 index is the ABSOLUTE one so the zebra striping does not restart
                 mid-table when you turn a page. */}
            {!loading && !error && pageRows.flatMap((c, indexOnPage) => {
              const rowIndex = pageStart + indexOnPage;
              const on = selected.has(c.id);
              const rowKey = `${c.source}-${c.id}`;
              const editing = openRow === rowKey;
              const stay = nights(c.visit_date, c.discharge_date);
              // Zebra striping, but a selected row always wins. The pinned Actions
              // cell has to repeat this background explicitly or the rows scrolling
              // beneath it show through.
              // An open editor tints its own row, so it stays findable once the
              // panel below it has pushed everything else down the screen. A row
              // just finished keeps an amber mark, so after closing the panel the
              // officer can see where they had got to in 2,383 rows.
              const done = !editing && lastEdited === rowKey;
              const rowBg = editing ? BRAND.navySoft
                : done ? '#FDF6E9'
                : on ? BRAND.greenSoft
                : (rowIndex % 2 ? '#FBFCFE' : BRAND.surface);
              const cell = { ...S.td, background: rowBg };
              const locked = on || editing || done;

              return [(
                <tr
                  key={rowKey}
                  data-claim-row={rowKey}
                  style={{ background: rowBg }}
                  onMouseEnter={(e) => {
                    if (!locked) e.currentTarget.style.background = '#F2F7FC';
                  }}
                  onMouseLeave={(e) => {
                    if (!locked) e.currentTarget.style.background = rowBg;
                  }}
                >
                  <td style={{
                    ...cell, paddingLeft: 13, position: 'relative',
                    // The accent sits inside the cell rather than on the row, so it
                    // does not shift the row's width when selection changes.
                    boxShadow: editing ? `inset 3px 0 0 ${BRAND.navy}`
                      : done ? 'inset 3px 0 0 #E08B2F'
                      : on ? `inset 3px 0 0 ${BRAND.green}` : 'none',
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggleOne(c.id)}
                      aria-label={`Select the claim for ${c.name || c.member_no || c.id}`}
                      style={{ cursor: 'pointer', accentColor: BRAND.green }} />
                  </td>

                  {/* Visit and discharge, separately. The nights badge saves an
                      officer subtracting two dates to sanity-check a stay. */}
                  <td style={{ ...cell, ...S.num, fontWeight: 700 }}>
                    {fmtDate(c.visit_date)}
                    {/*  KEPT, THOUGH IT NO LONGER MATCHES. Saving can change the
                         very fields the pills filter on, and the row would leave
                         the list mid-edit. It is held instead — and told, because
                         a row sitting outside its own filter with no explanation
                         reads as the filter being broken. */}
                    {/*  What the clinical review said about this claim. Amber and
                         never red: it is a model's opinion that the treatment does
                         not fit the condition, which is a reason to look rather
                         than a rejection. Sits beside the visit date because that
                         is the first cell the eye lands on when scanning rows. */}
                    {c.ai_findings > 0 && (
                      <span
                        title={`The clinical review raised ${c.ai_findings} warning`
                          + `${c.ai_findings === 1 ? '' : 's'} on this claim — open it to read them`}
                        style={{
                          marginLeft: 6, padding: '1px 5px', borderRadius: 4,
                          fontSize: 9.5, fontWeight: 800, verticalAlign: 'middle',
                          background: '#FFF8EC', color: '#8A5510',
                          border: '1px solid rgba(224,139,47,.35)',
                        }}>
                        ⚕ {c.ai_findings}
                      </span>
                    )}
                    {c._pinned && (
                      <span
                        title={'This claim no longer matches the filters above, but it is '
                          + 'being kept here while you work on it. It will go on the next reload.'}
                        style={{
                          marginLeft: 6, padding: '1px 5px', borderRadius: 4,
                          fontSize: 9.5, fontWeight: 800, verticalAlign: 'middle',
                          background: '#FDF6E9', color: '#8A5510',
                          border: '1px solid rgba(224,139,47,.35)',
                        }}>
                        KEPT
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, ...S.num }}>
                    {c.discharge_date ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {fmtDate(c.discharge_date)}
                        {stay && (
                          <span
                            title={`${stay} night${stay === 1 ? '' : 's'} between visit and discharge`}
                            style={{
                              padding: '1px 6px', borderRadius: 4, fontSize: 10,
                              fontWeight: 800, background: '#EDE4FF', color: '#6D28D9',
                            }}>
                            {stay}n
                          </span>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: BRAND.inkFaint }} title="No discharge date recorded">
                        not recorded
                      </span>
                    )}
                  </td>

                  <td style={{ ...cell, ...S.mono }}>{c.member_no || '—'}</td>
                  <td style={{ ...cell, ...S.mono }}>
                    {c.ccc || <span style={{ color: BRAND.inkFaint }}>—</span>}
                  </td>
                  <td style={{ ...cell, ...S.mono }}>{c.folder_no || '—'}</td>
                  <td style={{ ...cell, fontWeight: 700, color: BRAND.ink }}>
                    {c.name || '—'}
                    {done && (
                      <span
                        title="You were last working on this claim"
                        style={{
                          marginLeft: 8, padding: '1px 7px', borderRadius: 4,
                          fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em',
                          background: '#F6E4C4', color: '#8A5510', whiteSpace: 'nowrap',
                        }}>
                        LAST EDITED
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, ...S.num, color: BRAND.inkMuted }}>
                    {dobWithAge(c.dob, c.visit_date || c.claim_date)}
                  </td>
                  <td style={{ ...cell, color: BRAND.inkMuted }}>{c.gender || '—'}</td>
                  <td style={cell}><TypeBadge value={c.type} /></td>
                  <td style={{ ...cell, fontWeight: 800, letterSpacing: '.02em' }}>
                    {c.attendance || '—'}
                  </td>
                  <td style={{ ...cell, maxWidth: 280 }} title={c.service || ''}>
                    {c.service_name ? (
                      <>
                        <span style={{ color: BRAND.ink }}>{c.service_name}</span>
                        <span style={{ ...S.mono, color: BRAND.inkFaint, marginLeft: 6 }}>
                          {c.service_code}
                        </span>
                      </>
                    ) : (
                      <span style={{ color: BRAND.inkFaint }}>{c.service || '—'}</span>
                    )}
                  </td>
                  <td style={cell}>
                    <StatusPill value={c.patient_status} />
                    <IssueBadge
                      codes={c.issues}
                      catalogue={issueTypes}
                      onOpen={() => setRejectFor(c)}
                    />
                  </td>

                  <td style={{
                    ...cell, ...S.stickyRight, textAlign: 'right', whiteSpace: 'nowrap',
                    boxShadow: `inset 1px 0 0 ${BRAND.line}`,
                  }}>
                    <span style={{ display: 'inline-flex', gap: 3, justifyContent: 'flex-end' }}>
                      {/* Edit, not view — this screen is where a claim gets worked
                          on, and an eye implies read-only. Opens the full claim
                          INLINE, underneath this row: an officer works a month in
                          sequence, and a dialog would mean dismiss-scroll-find-
                          your-place on every one. Clicking again collapses it. */}
                      <button
                        type="button"
                        aria-label={`${editing ? 'Close' : 'Open'} the claim for ${c.name || c.member_no}`}
                        aria-expanded={editing}
                        title={editing
                          ? 'Close this claim'
                          : `Open this claim · GH₵ ${money(c.amount)}`}
                        onClick={() => (editing ? closeEditor() : openEditor(rowKey))}
                        style={{
                          ...S.iconBtn(editing ? '#fff' : BRAND.navy),
                          background: editing ? BRAND.navy : 'none',
                          borderColor: editing ? BRAND.navy : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (editing) return;
                          e.currentTarget.style.background = BRAND.navySoft;
                          e.currentTarget.style.borderColor = BRAND.navyLight;
                        }}
                        onMouseLeave={(e) => {
                          if (editing) return;
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <SquarePen size={15} />
                      </button>
                      {/*  THIS claim on its own, as NHIA XML. Named by id, so the
                           server ignores the month/specialty filters the screen is
                           showing — a single claim is exported because it was asked
                           for, not because it also matches the current view. */}
                      <button
                        type="button"
                        aria-label={`Export the claim for ${c.name || c.member_no} as XML`}
                        title="Export just this claim as NHIA XML"
                        onClick={() => runExport({
                          //  Tagged with the model — see the note on selectedIds.
                          //  A bare id here would be matched against the imported
                          //  table whatever this row actually is.
                          ids: [`${c.source}:${c.id}`],
                          attendance: 'all', kind: 'hospital',
                        })}
                        style={S.iconBtn(BRAND.greenDark)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = BRAND.greenSoft;
                          e.currentTarget.style.borderColor = `${BRAND.greenDark}44`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <Download size={15} />
                      </button>
                      {/*  DELETES, rather than ticking the row and leaving the
                           officer to find Bulk Delete. It asks first, names the
                           patient in the question, and says what it does NOT
                           remove — the ward records stay. See deleteOne.

                           Disabled while a delete is in flight so a double-click
                           cannot fire the confirmation twice. `deletingId` is the
                           id, not a boolean, so only the row being deleted goes
                           quiet — the rest of the table stays usable. */}
                      <button
                        type="button"
                        aria-label={`Delete the claim for ${c.name || c.member_no}`}
                        title={c.source === 'facility'
                          ? 'Delete this claim — the ward records are kept'
                          : 'Delete this claim and its imported rows'}
                        disabled={deletingId === c.id}
                        onClick={() => deleteOne(c)}
                        style={{
                          ...S.iconBtn(BRAND.danger),
                          ...(deletingId === c.id
                            ? { opacity: 0.45, cursor: 'wait' } : null),
                        }}
                        onMouseEnter={(e) => {
                          if (deletingId === c.id) return;
                          e.currentTarget.style.background = '#FBE9E9';
                          e.currentTarget.style.borderColor = '#F0C4C4';
                        }}
                        onMouseLeave={(e) => {
                          if (deletingId === c.id) return;
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </span>
                  </td>
                </tr>
              ), editing && (
                // The editor, spanning every column. `editing &&` yields false on
                // the closed rows and React drops those, so flatMap gives one row
                // per claim and two only for the open one.
                <tr key={`${rowKey}-editor`} data-editor-row={rowKey}>
                  <td colSpan={14} style={{
                    padding: 0, borderBottom: `2px solid ${BRAND.navy}`,
                    borderTop: `2px solid ${BRAND.navy}`, background: BRAND.canvas,
                  }}>
                    <ClaimEditor
                      claimId={c.id}
                      source={c.source}
                      patientName={c.name}
                      onClose={closeEditor}
                      onNotReady={notReady}
                      //  Every write in the panel hands the re-read claim up, so
                      //  this row — its REJECT badge included — follows what was
                      //  just saved. The prop existed and was never passed, which
                      //  is why a corrected claim stayed flagged.
                      onChanged={applyFreshClaim}
                      // Fired once the panel has its data and therefore its real
                      // height. Without this the scroll above stays clamped at the
                      // placeholder's height and the editor lands mid-table.
                      onReady={() => scrollOpenEditor('smooth')}
                      // Step through the month without closing the panel.
                      // Where the editor's own sticky bar must sit: directly under
                      // the table's sticky column header.
                      stickyTop={headerH}
                      position={{ index: openIndex + 1, total: orderedKeys.length }}
                      onPrev={openIndex > 0 ? () => step(-1) : null}
                      onNext={openIndex >= 0 && openIndex < orderedKeys.length - 1
                        ? () => step(1) : null}
                    />
                  </td>
                </tr>
              )];
            })}
          </tbody>
        </table>
      </div>

      {/*  The pager. It says which rows are on screen out of how many MATCH, and
           how many the month holds when a filter is narrowing it — the line that
           used to live here said only the second of those. */}
      {!loading && !error && visible.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 10, marginTop: 9,
        }}>
          <div style={S.hint}>
            Showing <strong>{(pageStart + 1).toLocaleString()}</strong>–
            <strong>{Math.min(pageStart + pageSize, visible.length).toLocaleString()}</strong>
            {' '}of {visible.length.toLocaleString()} matching
            {visible.length !== claims.length
              && ` · ${claims.length.toLocaleString()} in this month`}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ ...S.hint, display: 'flex', alignItems: 'center', gap: 6 }}>
              Rows
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{
                  padding: '4px 6px', borderRadius: 6, border: `1px solid ${BRAND.line}`,
                  fontSize: 12.5, color: BRAND.ink, background: BRAND.surface,
                }}>
                {[50, 100, 200, 500].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>

            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {[
                  ['« First', () => setPage(1), pageSafe > 1],
                  ['‹ Prev', () => setPage((p) => Math.max(1, p - 1)), pageSafe > 1],
                ].map(([label, onClick, enabled]) => (
                  <button key={label} type="button" onClick={onClick} disabled={!enabled}
                    style={{
                      padding: '4px 9px', borderRadius: 6, fontSize: 12.5,
                      border: `1px solid ${BRAND.line}`,
                      background: enabled ? BRAND.surface : BRAND.canvas,
                      color: enabled ? BRAND.ink : BRAND.inkMuted,
                      cursor: enabled ? 'pointer' : 'default',
                    }}>{label}</button>
                ))}
                {/*  ── JUMPING TO A PAGE ──────────────────────────────────
                     This was plain text, so with 73 pages the only route to
                     page 40 was thirty-nine presses of Next — and no route
                     back to a page you had just left.

                     A select rather than a typed box or a row of numbered
                     buttons, and not because it is the least code: the
                     IDENTICAL control is already six pixels to the left, on
                     Rows. Same widget, same arrow, nothing new to learn on a
                     screen people already know.

                     One page means no chooser — the whole pager is already
                     hidden in that case, but stating it keeps the two in step
                     if that ever changes. */}
                <label style={{ ...S.hint, display: 'flex', alignItems: 'center',
                  gap: 5, padding: '0 2px' }}>
                  Page
                  <select
                    value={pageSafe}
                    onChange={(e) => setPage(Number(e.target.value))}
                    //  Named for a screen reader, which otherwise announces only
                    //  the number — "73" tells nobody what it selects.
                    aria-label={`Page ${pageSafe} of ${pageCount}, choose a page`}
                    title={`Jump to any of the ${pageCount.toLocaleString()} pages`}
                    style={{
                      padding: '4px 6px', borderRadius: 6,
                      border: `1px solid ${BRAND.line}`,
                      fontSize: 12.5, color: BRAND.ink, background: BRAND.surface,
                      //  Keeps the footer from reflowing as the page number grows
                      //  from 1 to 145 — the buttons either side would otherwise
                      //  shift under the pointer mid-click.
                      minWidth: 58,
                      //  Digits align in the list, so scanning it is reading a
                      //  column rather than ragged text.
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    {/*  Built on render rather than memoised: pageCount is small
                         even at the 50-row setting (145 on the biggest month
                         here), and a memo keyed on it would be the more
                         expensive of the two. */}
                    {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n.toLocaleString()}</option>
                    ))}
                  </select>
                  of {pageCount.toLocaleString()}
                </label>
                {[
                  ['Next ›', () => setPage((p) => Math.min(pageCount, p + 1)), pageSafe < pageCount],
                  ['Last »', () => setPage(pageCount), pageSafe < pageCount],
                ].map(([label, onClick, enabled]) => (
                  <button key={label} type="button" onClick={onClick} disabled={!enabled}
                    style={{
                      padding: '4px 9px', borderRadius: 6, fontSize: 12.5,
                      border: `1px solid ${BRAND.line}`,
                      background: enabled ? BRAND.surface : BRAND.canvas,
                      color: enabled ? BRAND.ink : BRAND.inkMuted,
                      cursor: enabled ? 'pointer' : 'default',
                    }}>{label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {exportOpen && (
        <ExportDialog
          facilityLabel={facilityLabel}
          facility={facility}
          monthLabel={`${String(monthData?.month || '').toUpperCase()} ${selectedYear}`}
          //  The same specialty codes and counts as the pills above the table, so
          //  the export is filtered with the numbers already on screen.
          specialties={stats.specialties || {}}
          stats={stats}
          //  THE TICKED ROWS. `chosen` is `visible` narrowed to the selection, so
          //  it already honours every filter on the row above — tick the OPDC06A
          //  breakdown and this is exactly those claims. Ids only; the dialog has
          //  no use for the rows themselves.
          //  ── TAGGED WITH THEIR MODEL, AND THAT IS NOT COSMETIC ──────────────
          //  These were bare ids, and the export matches them with
          //  `claims.id IN (...)` — the IMPORTED table. A typed-in claim's row id
          //  is also a plain integer, so a hospital claim numbered 540 was sent
          //  indistinguishably from imported claim 540 and would have exported
          //  THAT patient's claim into an NHIA submission. On the month that
          //  raised this, none of the 617 happened to collide, so it answered "no
          //  claims match" rather than producing a wrong file — which is luck.
          selectedIds={chosen.map((c) => `${c.source}:${c.id}`)}
          onClose={() => setExportOpen(false)}
          onRun={(opts) => { setExportOpen(false); runExport(opts); }}
        />
      )}

      {/*  Why the badge on that row is red. The rules are COMPUTED, not stored —
           see the header of RejectReasonDialog for what that means for what it
           can show. Mounted only while open so each opening re-asks the rules;
           a claim repaired since the list loaded should come back clean. */}
      <RejectReasonDialog
        open={!!rejectFor}
        claim={rejectFor}
        onClose={() => setRejectFor(null)}
        //  The dialog's own fetch is a fresh, authoritative evaluation of this
        //  claim, so it feeds the row through the same patch the editor uses.
        //  Clicking the badge on a claim already corrected therefore clears it.
        onResolved={applyFreshClaim}
        //  Straight from "why" to fixing it, without hunting for the row again.
        onOpenClaim={(c) => openEditor(`${c.source}-${c.id}`)}
      />

      {/*  ── THE ERROR REPORT ─────────────────────────────────────────────────
           Fed `visible` and `issueTypes` — the rows on screen and the meaning of
           their badges. Nothing is fetched: the report groups what the table has
           already been given, so a printout cannot contradict the screen it came
           from.

           The scope strings are the words from the heading above, passed down
           rather than rebuilt, for the same reason: a printed sheet that names a
           different facility or month from the screen is worse than one that names
           none. */}
      <ClaimsErrorReport
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        claims={visible}
        issueTypes={issueTypes}
        //  The PER-CLAIM gap catalogue. Every row already carries `gaps`; this
        //  gives those keys their titles, so the report can list "No medication ·
        //  735 claims" beside the rules instead of ignoring it.
        gapTypes={gapTypes}
        //  What the whole batch is missing — counted by the server over the month,
        //  not derived from the rows. Three facts that are true of nearly every
        //  claim (no investigations, no priced amount) belong in a sentence, not in
        //  a findings table where they would add tens of thousands of rows.
        //
        //  THIS IS THE MONTH'S FIGURE, and `claims` above is what the screen has
        //  filtered to. The section says so in its own words — "of N claims in this
        //  batch" — rather than borrowing the report's filtered count, which would
        //  have printed "16,138 of 3,725".
        gaps={stats?.gaps || null}
        scope={{
          facility: facilitiesInView.length === 1
            ? (facilitiesInView[0].name || facilityLabel)
            : (facilitiesInView.length > 1
              ? `${facilitiesInView.length} facilities`
              : facilityLabel),
          period: `${MONTH_TITLE(monthData?.month)} ${selectedYear}`,
          scheme: schemeLabel,
          //  Only when one is in force. "the whole period" is said by the report
          //  itself when this is absent, rather than being pushed up here.
          dates: dateRange.from || dateRange.to
            ? (dateRange.from && dateRange.to
              ? `${dateRange.from} → ${dateRange.to}`
              : (dateRange.from ? `from ${dateRange.from}` : `up to ${dateRange.to}`))
            : null,
          search: q.trim() || null,
          //  THE PILL ROW COUNTS TOO. `visible` is already narrowed by the
          //  OPD/IPD tab and the specialty and service chips, so a report run with
          //  IPD selected describes 321 claims and not 3,725. Left unsaid, its
          //  totals look like the month's and the figure gets quoted as one.
          narrowing: [
            typeTab === 'ipd' ? 'in-patient only'
              : typeTab === 'opd' ? 'out-patient only' : null,
            specialty !== 'all' ? `specialty ${specialty}` : null,
            serviceCode !== 'all' ? `service ${serviceCode}` : null,
          ].filter(Boolean).join(' · ') || null,
        }}
      />

      {/*  Every medication in the month that has no dose unit, listed once each
           rather than once per claim. Scoped exactly like the list behind it, so a
           fix cannot reach outside the month on screen. */}
      <DoseUnitFixDialog
        open={doseFixOpen}
        scope={{
          year: selectedYear,
          month: monthData?.month,
          facility,
          insurance_type: insurances?.length ? insurances.join(',') : 'all',
        }}
        onClose={() => setDoseFixOpen(false)}
        //  Reload after a write: those claims have lost their REJECT badge and the
        //  gap counts in the filter have moved with them.
        onDone={() => { load(); }}
      />

      {/*  The clinical review. Scoped exactly like the list behind it, so a run
           reads the month on screen and nothing else. Mounted unconditionally —
           it keeps a running job's id in sessionStorage, so closing it mid-run
           and reopening finds the same job rather than starting a second. */}
      <ClinicalReviewDialog
        open={aiOpen}
        scope={{
          year: selectedYear,
          month: monthData?.month,
          facility,
          insurance: insurances?.length ? insurances.join(',') : 'all',
        }}
        monthLabel={`${monthData?.month || ''} ${selectedYear || ''}`.trim()}
        reviewed={aiReviewed}
        flagged={aiFlagged}
        total={claims.length}
        onClose={() => setAiOpen(false)}
        //  A finished run has put new answers on the month; the badges and the
        //  button's own count come from the list, so it has to be re-read.
        onDone={() => { load(); }}
        onShowFlagged={() => { setAiOnly(true); setAiOpen(false); setPage(1); }}
      />

      {/*  Setting the G-DRG on admission diagnoses that carry an out-patient one.
           Mounted only while open — it previews on mount, and a dialog that
           re-previews every time the month reloads behind it would throw away the
           officer's half-finished answers. */}
      {recodeOpen && (
        <GdrgRecodeDialog
          scope={{
            year: selectedYear,
            month: monthData?.month,
            facility,
            insurance_type: insurances?.length ? insurances.join(',') : 'all',
          }}
          onClose={() => setRecodeOpen(false)}
          //  Those claims have lost their REJECT badge and the issue counts have
          //  moved with them, so the month is re-read.
          onDone={() => { load(); }}
        />
      )}

      {repairPlan && (
        <RepairDialog
          plan={repairPlan}
          //  The rows already on screen, so the preview can name the PATIENT beside
          //  each claim id. The repair endpoint returns ids because that is what it
          //  acts on; a table of bare numbers is not something anyone can check.
          rowsById={new Map(claims.map((c) => [String(c.id), c]))}
          monthLabel={`${MONTH_TITLE(monthData?.month)} ${selectedYear}`}
          busy={repairBusy}
          result={repairResult}
          issueTypes={issueTypes}
          onConfirm={repairConfirm}
          onTreatAsOpd={treatAsOutpatient}
          onClose={() => { setRepairPlan(null); setRepairResult(null); }}
        />
      )}

      {vetOpen && (
        <MultiVet
          claims={chosen}
          year={selectedYear}
          month={monthData?.month}
          facility={facility}
          insurances={insurances}
          //  The active advanced filter. Override replaces what these claims were
          //  FOUND by — you filtered on "diagnosis = K27" to get this selection, so
          //  K27 is what "replace it" means, and asking for it again inside the
          //  dialog would be asking for something already on screen.
          filter={adv}
          onClose={() => setVetOpen(false)}
          //  Reload after a run: the claims behind this panel now carry different
          //  rows, and their service codes may have moved with the principal.
          //  The dialog stays open and the selection stays put — vetting the same
          //  claims again for a different section is the normal next step, and
          //  closing meant re-filtering and re-picking every one of them.
          onDone={() => { load(); }}
        />
      )}

      {advOpen && (
        <AdvancedFilter
          draft={advDraft}
          onChange={setAdvDraft}
          onClose={() => setAdvOpen(false)}
          onSearch={() => { setAdv(advDraft); setAdvOpen(false); }}
          onClear={() => { setAdvDraft(EMPTY_ADV); setAdv(EMPTY_ADV); setAdvOpen(false); }}
          //  Closes the filter and opens the fix. Two dialogs stacked on each
          //  other is two things to dismiss, and the filter has nothing to say
          //  while the medications are being worked through.
          onFixDoseUnits={() => { setAdvOpen(false); setDoseFixOpen(true); }}
          //  ── Fix all, on a consistency tile ──────────────────────────────
          //  No selection is involved: the tile counted every claim in the month
          //  carrying that rule, most of them on pages nobody has opened, so the
          //  ids are resolved on the server from the same query the count came
          //  from. Sends a DRY RUN and hands the plan to the same RepairDialog the
          //  table's Repair button uses — the officer reads what would happen to
          //  each claim and confirms a second time before anything is written.
          onFixIssue={(code) => { setAdvOpen(false); repairIssue(code); }}
          //  The NHIA format rules. Same gesture from the officer's side, a
          //  different engine behind it — see NHIA_FIX_BY_RULE and fixNhiaIssue.
          onFixNhia={(code, fixKey) => { setAdvOpen(false); fixNhiaIssue(code, fixKey); }}
          //  The other half of ipd_on_opd_service: the claims Fix all reports as
          //  ambiguous, answered once per diagnosis instead of once per claim.
          onRecodeGdrg={() => { setAdvOpen(false); setRecodeOpen(true); }}
          monthLabel={`${MONTH_TITLE(monthData?.month)} ${selectedYear}`}
          total={stats.all}
          //  The facet lists are scoped exactly like the list behind the dialog, so
          //  their counts reconcile with it rather than describing a different month.
          year={selectedYear}
          month={monthData?.month}
          facility={facility}
          insurances={insurances}
        />
      )}
    </div>
  );
}

// ── Advanced Filter ─────────────────────────────────────────────────────────
//  Filters the month on what is INSIDE its claims, not on their headers. The
//  header fields — month, scheme, facility — are already on the page; these three
//  read the claim's children, which is the question the page could not ask:
//  "which of these 1,095 claims carries Malaria?"
//
//  It is a DIALOG rather than an inline row because it is going to grow. Claims
//  with no G-DRG, claims missing a principal diagnosis, tariff outliers — each is
//  another line here, and a filter bar that grows without bound eats the table it
//  is meant to filter.
//
//  Nothing runs until Search. Editing a draft and committing on one click is what
//  keeps a four-field form from firing four queries over a thousand claims.
// ── Multi Vet ───────────────────────────────────────────────────────────────
//  The claim editor's own work — diagnoses, procedures, lab/scan, medications —
//  applied to every ticked claim at once. Not a status workflow: "vet" here means
//  the same editing you do on one claim, done to forty.
//
//  Every write on the server goes through the SAME functions the single-claim
//  editor calls, so each claim's totals and principal stay correct. That is why
//  this is slower than a bulk INSERT and why it is right.
//
//  Breathing room above and below every dialog on this screen, in PIXELS.
//
//  Not a vh value: on a 740px laptop viewport 6vh is 44px, so a "gap" expressed
//  that way is smallest exactly where it is needed most. Both dialogs also cap
//  their height at `100vh - 2 * this`, so a tall one scrolls inside itself rather
//  than growing until it touches the window edge.
const DIALOG_GAP = 80;

//  The heading over a group of filter panels. Groups exist so a new filter has an
//  obvious home — "was it billed, or is it about the visit?" — instead of being
//  appended to a flat list of boxes that gets longer every release.
//  The heading over each group of filters. At 9.5px in #94A3B8 these were the
//  faintest thing in a dialog they are supposed to organise — the four sections
//  ran together and the checks below them looked like one undifferentiated
//  block. Bigger, darker, and with a rule across the row so a section visibly
//  starts somewhere.
//  A rule UNDER a heading separates it from its own section as much as from the
//  one above, so the four groups still ran together. A short accent bar to the
//  LEFT of the words attaches the heading to what follows it, and the hairline
//  now runs beside the label rather than beneath it — the same section marker the
//  admin setup screens use, so the two parts of the app read as one product.
const GROUP_LABEL = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: .8, textTransform: 'uppercase',
  color: BRAND.inkSoft,
  display: 'flex', alignItems: 'center', gap: 9,
  paddingTop: 4,
};
//  The bar and the trailing rule, as elements rather than borders, so the
//  heading can sit in a grid cell without stretching either of them.
const GroupHeading = ({ children }) => (
  <>
    <span aria-hidden="true" style={{
      width: 3, height: 13, borderRadius: 2, flexShrink: 0,
      background: BRAND.green,
    }} />
    <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
    <span aria-hidden="true" style={{ flex: 1, height: 1, background: BRAND.line }} />
  </>
);

//  PREVIEW IS NOT OPTIONAL. Apply stays out of reach until the server has said what
//  would happen — and for Override it also says how many existing rows would be
//  DESTROYED, which is the number that decides whether you press it.
// ── Export NHIA XML ─────────────────────────────────────────────────────────
//  A real dialog rather than Swal's `html` option. Swal styles anything given the
//  swal2-input class with its own width and margins, which is why the first
//  version put Attendance and Facility type on one line and looked broken.
//
//  The FACILITY is not a choice here. It is whichever one the screen is already
//  scoped to, shown so it is unambiguous — a submission that quietly mixed
//  facilities is the one error this must not be able to produce.
function ExportDialog({
  facilityLabel, facility, monthLabel, specialties, stats, onClose, onRun,
  //  The claims ticked on the table behind this dialog. Ids only — the dialog has
  //  no use for the rows.
  selectedIds = [],
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [attendance, setAttendance] = useState('all');
  const [kind, setKind] = useState('hospital');
  const [picked, setPicked] = useState([]);        // specialty codes; empty = all

  //  ── WHICH CLAIMS: THE MONTH, OR THE ONES TICKED ────────────────────────────
  //
  //  "I can select the claims I want, and when I click export it should export
  //  only those claims, not the whole entire thing."
  //
  //  It exported the filtered month and said so only in a comment in the source.
  //  Somebody who had just ticked 3,064 rows got a file of 3,725, and nothing on
  //  the screen had told them that would happen.
  //
  //  DEFAULTS TO THE SELECTION when there is one, because ticking rows and then
  //  pressing Export is a statement of intent — and the alternative is one click
  //  away and labelled. With nothing ticked there is no choice to make and the
  //  control is not shown at all.
  const hasPick = selectedIds.length > 0;
  const [scope, setScope] = useState(hasPick ? 'picked' : 'month');
  //  A selection made or cleared while this is open would otherwise leave the
  //  dialog offering a scope that no longer exists.
  useEffect(() => { setScope(selectedIds.length ? 'picked' : 'month'); },
    [selectedIds.length]);
  const onlyPicked = scope === 'picked' && hasPick;

  //  A date range and a specialty selection, gone on a stray click. Not any more.
  const { panelRef, backdropProps } = useDialogGuard();

  //  ── BY VOLUME, NOT ALPHABETICALLY ──────────────────────────────────────────
  //  OPDC is 4,515 of 7,237 claims and ASUR is 126. Sorted A–Z, the specialty that
  //  IS the month sat sixth in a wall of near-identical chips while OOBG (1 claim)
  //  came fourth. The list is read to find the big ones, so it is ordered by the
  //  number that makes one big. Ties fall back to the code so the order is stable.
  const codes = useMemo(() => Object.keys(specialties || {})
    .sort((a, b) => (Number(specialties[b] || 0) - Number(specialties[a] || 0))
      || a.localeCompare(b)), [specialties]);

  const badDates = !!(from && to && from > to);
  const allFacilities = String(facility ?? 'all').toLowerCase() === 'all';
  const hasDates = !!(from || to);

  //  ── WHAT THIS EXPORT WILL ACTUALLY CONTAIN ─────────────────────────────────
  //  The one thing the form could not say. Four controls each narrow the
  //  submission and the officer was left to hold the arithmetic in their head —
  //  on a screen whose whole purpose is producing a file somebody else audits.
  //
  //  HONEST ABOUT WHAT IT CANNOT KNOW. The specialty counts and the attendance
  //  counts are separate totals, not a cross-tab, so their intersection is not
  //  available here. Where both are narrowed the figure is an upper bound and it
  //  says "up to" — a precise-looking number that is wrong is worse than an
  //  approximate one that admits it.
  const estimate = useMemo(() => {
    //  A NAMED SELECTION IS THE ONE CASE THIS CAN BE CERTAIN OF. The claims are
    //  counted, not inferred from two totals that do not cross-tabulate, and the
    //  server matches on the ids and ignores every other filter — so the figure is
    //  exact and the word is "Exporting", not "Up to".
    if (onlyPicked) return { n: selectedIds.length, exact: true };
    const total = Number(stats?.all || 0);
    const byAtt = attendance === 'opd' ? Number(stats?.opd || 0)
      : attendance === 'ipd' ? Number(stats?.ipd || 0)
        : total;
    if (!picked.length) return { n: byAtt, exact: !hasDates };
    const bySpec = picked.reduce((n, c) => n + Number(specialties?.[c] || 0), 0);
    //  Both narrowed: the smaller of the two is the ceiling, and it is a ceiling.
    if (attendance !== 'all') return { n: Math.min(bySpec, byAtt), exact: false };
    return { n: bySpec, exact: !hasDates };
  }, [stats, attendance, picked, specialties, hasDates, onlyPicked, selectedIds.length]);

  const blocked = badDates || allFacilities;

  // ── Tokens ─────────────────────────────────────────────────────────────────
  //  A numbered eyebrow per section, the same grammar the Multi Vet pattern uses.
  //  Four bordered cards with sentence-case labels read as four unrelated
  //  questions; numbering them says they are one form with an order.
  const eyebrow = {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9,
  };
  const step = {
    width: 19, height: 19, borderRadius: 5, display: 'grid', placeItems: 'center',
    fontSize: 10, fontWeight: 800, background: BRAND.head, color: BRAND.inkSoft,
    flexShrink: 0,
  };
  const legend = {
    fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
    textTransform: 'uppercase', color: BRAND.inkSoft,
  };
  const aside = { fontSize: 11.5, color: BRAND.inkFaint, fontWeight: 500 };
  //  ── A CARD THE SERVER WILL IGNORE ──────────────────────────────────────────
  //  With `ids` on the request the server matches those claims and applies none of
  //  the date, attendance or specialty filters — see claimsXmlExport. So while a
  //  ticked selection is the scope, those three cards are turned OFF rather than
  //  left looking live: a control that changes nothing is worse than an absent one,
  //  because the officer reads the file's contents off it.
  //
  //  A `fieldset[disabled]` rather than opacity and pointer-events. It disables
  //  every control inside natively, keyboard included, so the section cannot be
  //  tabbed into and quietly changed behind a greyed-out surface.
  //
  //  The FACILITY TYPE card is deliberately NOT in here: `kind` decides whether
  //  investigations are written into the XML at all, and the server applies it
  //  whatever else the request carries.
  const offCard = (ignored) => ({
    padding: '13px 15px', borderRadius: 11, minWidth: 0, margin: 0,
    background: ignored ? BRAND.canvas : '#fff',
    border: `1px solid ${BRAND.line}`,
    opacity: ignored ? 0.55 : 1,
  });
  const notUsed = onlyPicked ? (
    <span style={{
      marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em',
      textTransform: 'uppercase', color: BRAND.inkFaint, flexShrink: 0,
    }}>Not used</span>
  ) : null;

  const card = {
    padding: '13px 15px', borderRadius: 11, background: '#fff',
    border: `1px solid ${BRAND.line}`, minWidth: 0,
  };
  const box = {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13,
    borderRadius: 8, border: `1px solid ${BRAND.line}`, outline: 'none',
    fontFamily: 'inherit', color: BRAND.ink, background: '#fff',
  };
  const fieldLabel = {
    display: 'block', fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
    textTransform: 'uppercase', color: BRAND.inkFaint, marginBottom: 4,
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Export NHIA XML" {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(15,23,42,.46)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: `${DIALOG_GAP}px 16px`,
      }}>
      <div ref={panelRef} style={{
        width: '100%', maxWidth: 680, background: BRAND.canvas, borderRadius: 14,
        border: `1px solid ${BRAND.line}`,
        //  Two shadows: a hairline that reads as an edge on a light backdrop, and a
        //  long soft one for lift. One large blur alone looks like a smudge.
        boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 28px 70px rgba(15,23,42,.32)',
        maxHeight: `calc(100vh - ${DIALOG_GAP * 2}px)`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/*  ── HEADER ─────────────────────────────────────────────────────────
             The icon sits in a tinted tile rather than loose against the text, and
             the three facts underneath are LABELLED FIGURES rather than one
             dot-separated string. "UPPER EAST REGINAL HOSPITAL · MAY 2026 · 7,237
             claims" is three different kinds of thing punctuated as if they were a
             list of the same kind. */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 18px',
          background: '#fff', borderBottom: `1px solid ${BRAND.line}`, flexShrink: 0,
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 9, display: 'grid',
            placeItems: 'center', flexShrink: 0,
            background: BRAND.greenSoft, color: BRAND.greenDark,
            border: `1px solid ${BRAND.greenDark}22`,
          }}>
            <Download size={16} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 15.5, fontWeight: 800, color: BRAND.ink,
              letterSpacing: '-.01em', lineHeight: 1.3,
            }}>Export NHIA XML</div>
            <div style={{
              display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 5,
              fontSize: 11.5, lineHeight: 1.4,
            }}>
              {[['Facility', facilityLabel], ['Month', monthLabel],
                ['Claims on screen', Number(stats?.all || 0).toLocaleString()]]
                .map(([k, v]) => (
                  <span key={k} style={{ minWidth: 0 }}>
                    <span style={{
                      display: 'block', fontSize: 9, fontWeight: 800,
                      letterSpacing: '.06em', textTransform: 'uppercase',
                      color: BRAND.inkFaint,
                    }}>{k}</span>
                    <span style={{
                      display: 'block', fontWeight: 700, color: BRAND.inkSoft,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', maxWidth: 260,
                    }}>{v || '—'}</span>
                  </span>
                ))}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            border: `1px solid ${BRAND.line}`, background: '#fff', cursor: 'pointer',
            width: 26, height: 26, borderRadius: 7, display: 'grid',
            placeItems: 'center', color: BRAND.inkMuted, lineHeight: 1, flexShrink: 0,
            fontSize: 12,
          }}>✕</button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 11,
          alignContent: 'start' }}>

          {allFacilities && (
            <div style={{
              display: 'flex', gap: 9, padding: '10px 12px', borderRadius: 10,
              fontSize: 12, lineHeight: 1.55, background: '#FDF6EC', color: '#8A5510',
              border: `1px solid ${BRAND.warning}55`,
            }}>
              <ShieldAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                This screen is showing <strong>all facilities</strong>. Pick one at the
                top before exporting — a submission belongs to a single facility.
              </span>
            </div>
          )}

          {/*  ── WHICH CLAIMS ─────────────────────────────────────────────────
               Above everything else, because it decides whether anything below it
               matters. Shown ONLY when rows are ticked: with no selection there is
               no choice to make, and an inert "Just the ones I ticked (0)" would be
               a control that never does anything. */}
          {hasPick && (
            <div style={{
              ...card,
              borderColor: onlyPicked ? `${BRAND.green}55` : BRAND.line,
              background: onlyPicked ? BRAND.greenSoft : '#fff',
            }}>
              <div style={eyebrow}>
                <span style={step}>·</span>
                <span style={legend}>Which claims</span>
              </div>
              <div style={{ display: 'grid', gap: 7 }}>
                {[
                  ['picked',
                    `The ${selectedIds.length.toLocaleString()} claim${selectedIds.length === 1 ? '' : 's'} I ticked`,
                    'Exactly those rows. The dates, attendance and specialty below '
                    + 'do not apply — you have already named the claims.'],
                  ['month',
                    `Everything in ${monthLabel}`,
                    'The whole month for this facility, narrowed by the settings below.'],
                ].map(([key, title, why]) => {
                  const on = scope === key;
                  return (
                    <label key={key}
                      style={{
                        display: 'flex', gap: 9, alignItems: 'flex-start',
                        padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${on ? BRAND.green : BRAND.line}`,
                        background: on ? '#fff' : BRAND.canvas,
                      }}>
                      <input type="radio" name="export-scope" checked={on}
                        onChange={() => setScope(key)}
                        style={{ marginTop: 2, accentColor: BRAND.green, flexShrink: 0 }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{
                          display: 'block', fontSize: 12.5,
                          fontWeight: on ? 800 : 650,
                          color: on ? BRAND.ink : BRAND.inkSoft,
                        }}>{title}</span>
                        <span style={{
                          display: 'block', fontSize: 11.5, lineHeight: 1.55,
                          color: BRAND.inkMuted, marginTop: 2,
                        }}>{why}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/*  ── 1 · DATES ────────────────────────────────────────────────────
               Labelled From and To rather than two identical dd/mm/yyyy boxes with
               a "to" between them. The blank state is the common one — the whole
               month — so it is stated rather than left to be inferred from two
               empty fields. */}
          <fieldset disabled={onlyPicked} style={offCard(onlyPicked)}>
            <div style={eyebrow}>
              <span style={step}>1</span>
              <span style={legend}>Dates</span>
              <span style={aside}>
                {onlyPicked ? 'The ticked claims carry their own dates'
                  : hasDates ? 'Only claims in this range' : `The whole of ${monthLabel}`}
              </span>
              {notUsed}
              {hasDates && (
                <button type="button" onClick={() => { setFrom(''); setTo(''); }}
                  style={{
                    marginLeft: 'auto', border: 0, background: 'none', padding: 0,
                    font: 'inherit', fontSize: 11.5, fontWeight: 700,
                    cursor: 'pointer', color: BRAND.greenDark,
                    textDecoration: 'underline',
                  }}>Whole month</button>
              )}
            </div>
            <div style={{
              display: 'grid', gap: 9,
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            }}>
              <label style={{ minWidth: 0 }}>
                <span style={fieldLabel}>From</span>
                <input type="date" value={from} aria-label="From date"
                  onChange={(e) => setFrom(e.target.value)}
                  style={{ ...box, borderColor: badDates ? BRAND.danger : BRAND.line }} />
              </label>
              <label style={{ minWidth: 0 }}>
                <span style={fieldLabel}>To</span>
                <input type="date" value={to} aria-label="To date"
                  onChange={(e) => setTo(e.target.value)}
                  style={{ ...box, borderColor: badDates ? BRAND.danger : BRAND.line }} />
              </label>
            </div>
            {badDates && (
              <div style={{
                fontSize: 11.5, color: BRAND.danger, marginTop: 7, fontWeight: 600,
              }}>
                The start date is after the end date.
              </div>
            )}
          </fieldset>

          {/*  ── 2 · ATTENDANCE ───────────────────────────────────────────────
               A segmented control, and the count on its own line underneath the
               word. Inline — "Out-patient 5,827" — reads as one long label and the
               three options stop being comparable at a glance, which is the only
               reason to show the counts at all. */}
          <fieldset disabled={onlyPicked} style={offCard(onlyPicked)}>
            <div style={eyebrow}>
              <span style={step}>2</span>
              <span style={legend}>Attendance</span>
              {onlyPicked && (
                <span style={aside}>Whatever the ticked claims are</span>
              )}
              {notUsed}
            </div>
            <div style={{
              display: 'grid', gap: 6,
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            }}>
              {[['all', 'All', stats?.all], ['opd', 'Out-patient', stats?.opd],
                ['ipd', 'In-patient', stats?.ipd]].map(([v, t, n]) => {
                const on = attendance === v;
                return (
                  <button key={v} type="button" onClick={() => setAttendance(v)}
                    aria-pressed={on} style={{
                      display: 'grid', gap: 1, padding: '8px 10px', borderRadius: 9,
                      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                      background: on ? BRAND.greenSoft : '#fff',
                    }}>
                    <span style={{
                      fontSize: 12.5, fontWeight: on ? 800 : 650,
                      color: on ? BRAND.greenDark : BRAND.ink,
                    }}>{t}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: BRAND.inkFaint,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {n == null ? '—' : `${Number(n).toLocaleString()} claims`}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/*  ── 3 · FACILITY TYPE ────────────────────────────────────────────
               Two cards, not a native select. The choice changes what is IN the
               file — a clinic submission omits investigations — and a dropdown
               that has to be opened to see the second option hides a decision
               with consequences behind a click. */}
          <div style={card}>
            <div style={eyebrow}>
              <span style={step}>3</span>
              <span style={legend}>Facility type</span>
              <span style={aside}>Decides whether investigations are included</span>
            </div>
            <div style={{
              display: 'grid', gap: 6,
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}>
              {[['hospital', 'Hospital', 'Investigations are included'],
                ['clinic', 'Clinic', 'Investigations are left out']].map(([v, t, d]) => {
                const on = kind === v;
                return (
                  <button key={v} type="button" onClick={() => setKind(v)}
                    aria-pressed={on} style={{
                      display: 'grid', gap: 2, padding: '9px 11px', borderRadius: 9,
                      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                      background: on ? BRAND.greenSoft : '#fff',
                    }}>
                    <span style={{
                      fontSize: 12.5, fontWeight: on ? 800 : 650,
                      color: on ? BRAND.greenDark : BRAND.ink,
                    }}>{t}</span>
                    <span style={{ fontSize: 11, color: BRAND.inkMuted, lineHeight: 1.45 }}>
                      {d}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/*  ── 4 · SPECIALTIES ──────────────────────────────────────────────
               Ordered by volume, so the specialty that IS the month is first. All
               and None are here because the alternative on sixteen chips is
               sixteen clicks, and "leave everything unticked to get everything" is
               a rule that has to be read rather than seen. */}
          {codes.length > 0 && (
            <fieldset disabled={onlyPicked} style={offCard(onlyPicked)}>
              <div style={eyebrow}>
                <span style={step}>4</span>
                <span style={legend}>Specialties</span>
                <span style={aside}>
                  {onlyPicked ? 'Whichever the ticked claims belong to'
                    : picked.length
                      ? `${picked.length} of ${codes.length} chosen`
                      : `All ${codes.length}`}
                </span>
                {notUsed}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setPicked([])}
                    disabled={!picked.length}
                    style={{
                      border: 0, background: 'none', padding: 0, font: 'inherit',
                      fontSize: 11.5, fontWeight: 700,
                      cursor: picked.length ? 'pointer' : 'default',
                      color: picked.length ? BRAND.greenDark : BRAND.inkFaint,
                      textDecoration: picked.length ? 'underline' : 'none',
                    }}>Everything</button>
                  <button type="button" onClick={() => setPicked(codes)}
                    disabled={picked.length === codes.length}
                    style={{
                      border: 0, background: 'none', padding: 0, font: 'inherit',
                      fontSize: 11.5, fontWeight: 700,
                      cursor: picked.length === codes.length ? 'default' : 'pointer',
                      color: picked.length === codes.length
                        ? BRAND.inkFaint : BRAND.greenDark,
                      textDecoration: picked.length === codes.length ? 'none' : 'underline',
                    }}>Tick all</button>
                </span>
              </div>
              <div style={{
                display: 'grid', gap: 5, maxHeight: 208, overflowY: 'auto',
                gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
              }}>
                {codes.map((code) => {
                  const on = picked.includes(code);
                  const n = Number(specialties[code] || 0);
                  return (
                    <button key={code} type="button"
                      onClick={() => setPicked((p) => (on
                        ? p.filter((x) => x !== code) : [...p, code]))}
                      aria-pressed={on} style={{
                        display: 'flex', alignItems: 'center', gap: 7, minWidth: 0,
                        padding: '6px 9px', borderRadius: 8, cursor: 'pointer',
                        fontFamily: 'inherit', textAlign: 'left',
                        border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                        background: on ? BRAND.greenSoft : '#fff',
                      }}>
                      {/*  A tick box, not a pill that changes colour. Sixteen
                           chips where selection is only a tint is sixteen things
                           to compare against each other to read the state. */}
                      <span style={{
                        flexShrink: 0, width: 13, height: 13, borderRadius: 3,
                        display: 'grid', placeItems: 'center', fontSize: 9,
                        color: '#fff',
                        border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                        background: on ? BRAND.greenDark : '#fff',
                      }}>{on ? '✓' : ''}</span>
                      <span style={{
                        flex: 1, minWidth: 0, fontSize: 12,
                        fontWeight: on ? 800 : 650,
                        color: on ? BRAND.greenDark : BRAND.ink,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>{code}</span>
                      <span style={{
                        flexShrink: 0, fontSize: 11, fontWeight: 700,
                        color: BRAND.inkFaint, fontVariantNumeric: 'tabular-nums',
                      }}>{n.toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
        </div>

        {/*  ── FOOTER: WHAT IS ABOUT TO BE PRODUCED ───────────────────────────
             The count moves onto the button. "Generate XML" is a command;
             "Generate XML · 4,515 claims" is a command with its consequence
             attached, on the last screen before a file goes to NHIA. */}
        <div style={{
          display: 'flex', gap: 10, padding: '12px 16px', alignItems: 'center',
          borderTop: `1px solid ${BRAND.line}`, background: '#fff', flexShrink: 0,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 160, fontSize: 11.5, lineHeight: 1.5 }}>
            {blocked ? (
              <span style={{ color: BRAND.danger, fontWeight: 600 }}>
                {allFacilities ? 'Choose a single facility first.' : 'Fix the dates first.'}
              </span>
            ) : (
              <>
                <span style={{ color: BRAND.inkMuted }}>
                  {estimate.exact ? 'Exporting' : 'Up to'}{' '}
                </span>
                <strong style={{
                  color: BRAND.ink, fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                }}>{estimate.n.toLocaleString()}</strong>
                <span style={{ color: BRAND.inkMuted }}>
                  {' '}claim(s)
                  {/*  Named, so the number can be checked against the choices that
                       produced it rather than taken on trust. */}
                  {attendance !== 'all' && (attendance === 'opd'
                    ? ' · out-patient' : ' · in-patient')}
                  {picked.length ? ` · ${picked.length} specialt${picked.length === 1 ? 'y' : 'ies'}` : ''}
                  {hasDates ? ' · date range' : ''}
                  {kind === 'clinic' ? ' · no investigations' : ''}
                </span>
              </>
            )}
          </div>
          <button type="button" onClick={onClose} style={{
            padding: '9px 15px', borderRadius: 9, cursor: 'pointer', fontSize: 12.5,
            fontWeight: 700, fontFamily: 'inherit',
            border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
          }}>Cancel</button>
          <button type="button"
            disabled={blocked}
            //  `ids` ONLY when the ticked scope is chosen. Sending them always
            //  would make every export a named list and silently retire the
            //  month-wide one; sending them never is the bug this fixes.
            //
            //  The other four are still sent unchanged. The server ignores the
            //  three it must ignore when ids are present, and `kind` it does not —
            //  so the caller does not have to know which is which.
            onClick={() => onRun({
              from, to, attendance, kind, specialties: picked,
              ids: onlyPicked ? selectedIds : undefined,
            })}
            title={allFacilities ? 'Choose a single facility first'
              : badDates ? 'Fix the dates first' : 'Generate the XML'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '10px 18px', borderRadius: 9, fontSize: 12.5, fontWeight: 800,
              fontFamily: 'inherit', border: '1px solid transparent', color: '#fff',
              cursor: blocked ? 'not-allowed' : 'pointer',
              background: blocked ? BRAND.inkFaint : BRAND.greenDark,
              boxShadow: blocked ? 'none' : '0 1px 2px rgba(15,81,50,.28)',
            }}>
            <Download size={14} />
            Generate XML
            {!blocked && (
              <span style={{
                fontWeight: 700, opacity: 0.85, fontVariantNumeric: 'tabular-nums',
              }}>· {estimate.n.toLocaleString()}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Repair ──────────────────────────────────────────────────────────────────
//  A real dialog, for the same reason ExportDialog is one: Swal's `html` option
//  gives you a single column that grows until the button you are looking for is
//  four screens down. This is a preview of up to 200 claims split three ways —
//  the least suitable thing to render as one long paragraph.
//
//  WHAT THE SHAPE IS DOING
//
//    · The three outcomes are TABS, and the tabs are the counts. "47 repairable,
//      125 need a choice, 33 stuck" is the answer to the question the officer
//      actually has, so it is the navigation rather than three paragraphs above a
//      list. Nothing is truncated at 40 rows any more; each pane scrolls in its
//      own bounded box.
//    · The table header STICKS inside that box, so column meaning survives a
//      scroll of 125 rows.
//    · The action bar is pinned to the bottom of the dialog, never in the scroll.
//      A confirm button you have to hunt for is how the wrong one gets pressed.
//    · The result replaces the plan IN PLACE. A second Swal on top of a dialog to
//      say "23 repaired" is a third thing to dismiss.
//
//  RESPONSIVE. On a narrow screen the tabs wrap and the table becomes stacked
//  cards — a four-column table on a phone is a horizontal scrollbar nobody finds.
const REPAIR_TABS = [
  { key: 'would_fix', label: 'Will be repaired',
    tone: { fg: '#1B6631', bg: '#E7F5EC', bd: '#BFE3CC' },
    blurb: 'Settled from the claim’s own rows and the rules: the principal G-DRG '
         + 'written down — the procedure where there is one, otherwise the '
         + 'diagnosis — the specialty put back in step with the service, a billing '
         + 'code re-derived from its ICD-10 in the priced catalogue, and the header '
         + 'fields NHIA rejects on their characters. The ICD-10 itself is never '
         + 'changed, and neither is anything clinical.' },
  { key: 'ambiguous', label: 'Need a G-DRG chosen',
    tone: { fg: '#8A5510', bg: '#FDF2E3', bd: '#F2DCB8' },
    blurb: 'The ICD-10 resolved, but to more than one billable G-DRG at more than '
         + 'one tariff. Picking one is a billing decision, so this will not make it '
         + '— open the claim and set it on the diagnosis row.' },
  //  ── "NOTHING TO REPAIR" IS NOT "CANNOT BE REPAIRED" ──────────────────────
  //  These two were ONE tab, and it read 256 on a month where the real figure was
  //  4: 249 of them carried only med_short_of_stay, a warning NHIA does not reject
  //  and only the drug chart can answer. A panel claiming 256 claims are beyond
  //  help, when nearly all of them are fine, is a panel nobody trusts the genuine
  //  ones from.
  { key: 'advisory', label: 'Nothing to repair',
    tone: { fg: '#42536E', bg: '#F2F6FB', bd: '#DDE5EF' },
    blurb: 'What is flagged on these is a WARNING, not something NHIA rejects — a '
         + 'drug chart shorter than the stay, most often. Worth a look before you '
         + 'submit; nothing here for a repair to write.' },
  { key: 'unfixable', label: 'Cannot be settled',
    tone: { fg: '#A62828', bg: '#FBE9E9', bd: '#F0C4C4' },
    blurb: 'These carry an ERROR NHIA rejects that the claim’s own rows cannot '
         + 'answer — no billable G-DRG anywhere on it, or a membership number that '
         + 'has to come from the card. Open them; a repair would be guessing.' },
  { key: 'clean', label: 'Already correct',
    tone: { fg: '#5A6B87', bg: '#EEF1F6', bd: '#DDE5EF' },
    blurb: 'These already agree with their own rows. Nothing will be written to them.' },
];

//  One G-DRG moving to another. Used in every pane, so a code change reads the
//  same way wherever it appears.
const CodeMove = ({ from, to }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
    <span style={{ ...S.mono, color: BRAND.inkMuted, textDecoration: 'line-through' }}>
      {from || '—'}
    </span>
    <span style={{ color: BRAND.inkFaint }}>→</span>
    <span style={{ ...S.mono, fontWeight: 800, color: BRAND.greenDark }}>{to}</span>
  </span>
);

//  The same move, for a value that is a SENTENCE rather than a code. CodeMove is
//  monospaced and `nowrap`, which is right for OBGY → MEDI and wrong for
//  "Obstetrics and Gynaecology" → "Adult Medicine" — that one has to wrap or it
//  pushes the preview table sideways.
const TextMove = ({ from, to }) => (
  <span style={{ fontSize: 11.5, lineHeight: 1.5 }}>
    <span style={{ color: BRAND.inkMuted, textDecoration: 'line-through' }}>
      {from || '—'}
    </span>
    <span style={{ color: BRAND.inkFaint }}> → </span>
    <span style={{ fontWeight: 700, color: BRAND.greenDark }}>{to}</span>
  </span>
);

// ── What a repaired FIELD is called, in words ───────────────────────────────
//
//  These are database column names and the preview shows them to the officer who
//  is being asked to authorise the write. It was a six-deep ternary in the JSX
//  and anything unlisted fell through to the raw name — so the specialty
//  DESCRIPTION repair, added when Repair was taught to fix the specialty,
//  rendered as "specialty_gdrg_name".
//
//  A map, so adding a repairable field is one line here and the fallback is
//  visible rather than buried at the end of a chain.
const REPAIR_FIELD_LABEL = {
  procedure_is_principal: 'principal G-DRG — the procedure',
  is_principal: 'principal G-DRG — the diagnosis',
  member_no: 'membership no.',
  other_names: 'other names',
  specialty_gdrg_code: 'specialty',
  specialty_gdrg_name: 'specialty description',
};

//  Which fields show their BEFORE and AFTER rather than a reason. A row id
//  moving from null to 9626 means nothing to anyone; a membership number, a
//  specialty and a description are all values the officer recognises.
const REPAIR_FIELD_SHOWS_MOVE = new Set([
  'member_no', 'other_names', 'specialty_gdrg_name',
]);

// ── The faults a claim can settle from its own rows ─────────────────────────
//
//  ASKED FOR: "there are claims that you know that the principal G-DRG is MEDI.
//  For instance this MEDI28A — automatically the specialty code must be MEDI...
//  add it to the auto fix, auto repair where we can just click and fix them all
//  at once. The moment you load up the claims, you show it at the top."
//
//  These two rules carry their own answer: claimRules works out the specialty
//  from the G-DRG the claim is BILLED ON and puts it in `issue.suggested.to`.
//  There is nothing to choose between, which is what makes a bulk fix safe and a
//  banner honest.
//
//  Both are WARNINGS, which is why they needed naming here at all — the Repair
//  button's own count includes any claim with any issue, but a banner that
//  offered to fix "everything wrong with 652 claims" would be promising something
//  the repair cannot deliver. This is the subset it CAN.
const AUTO_FIX_ISSUES = new Set([
  'specialty_not_the_service', 'specialty_not_a_group',
]);

// ── The medication review, inside the repair preview ─────────────────────────
//  WHY THIS IS A PANEL AND NOT A SENTENCE
//
//  It was one amber paragraph: "10 of these admissions do not have medication
//  covering the whole stay", followed by a run-on line of names. Everything in it
//  was true and none of it was actionable — the officer could read that ten claims
//  were questionable and then had exactly two choices, repair all ten or cancel
//  all seventeen.
//
//  The warning is ABOUT the medication, so the medication is what has to be
//  reachable. Each claim opens to show its actual chart — which days carried
//  something and how much — because "1 day of drugs on a 2-night stay" is
//  sometimes a charting gap and sometimes a patient sent home on treatment, and
//  the two are identical as a number.
//
//  THREE THINGS CAN BE DONE WITH A ROW, and they are the three real answers:
//
//    leave it ticked   the service is wrong, repair it, sort the chart later
//    UNTICK it         hold it back from this run and come to it separately
//    treat as OPD      the opposite conclusion — this was never an admission,
//                      it was detention. Collapses the stay to the visit date,
//                      which makes ZOOM02A correct instead of wrong.
//
//  That last one is why the panel exists at all. A two-night stay with one day of
//  drugs billed as detention is EITHER a mis-coded admission or a correctly-coded
//  detention with the wrong dates, and the software cannot tell which. It can only
//  put both readings in front of the person who can.
function MedicationReview({ rows, nameOf, held, onHeld, onTreatAsOpd, busy }) {
  const [open, setOpen] = useState(null);      // which claim's chart is showing
  const [working, setWorking] = useState(null);

  const none = rows.filter((r) => r.medication.kind === 'none');
  const partial = rows.filter((r) => r.medication.kind !== 'none');

  const toggle = (id) => onHeld((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const setAll = (hold) => onHeld(hold
    ? new Set(rows.map((r) => Number(r.id)))
    : new Set());

  const heldCount = rows.filter((r) => held.has(Number(r.id))).length;

  const Group = ({ title, tone, list, blurb }) => (list.length === 0 ? null : (
    <div style={{ marginTop: 8 }}>
      <div style={{
        fontSize: 10, fontWeight: 800, letterSpacing: .4, textTransform: 'uppercase',
        color: tone, marginBottom: 4,
      }}>{title} · {list.length}</div>
      <div style={{ fontSize: 11, color: '#8A5510', marginBottom: 5, lineHeight: 1.5 }}>
        {blurb}
      </div>
      <div style={{ border: '1px solid #F2DCB8', borderRadius: 8, overflow: 'hidden' }}>
        {list.map((r, i) => {
          const id = Number(r.id);
          const m = r.medication;
          const isHeld = held.has(id);
          const isOpen = open === id;
          return (
            <div key={id} style={{
              borderTop: i ? '1px solid #F2DCB8' : 0,
              background: isHeld ? '#FFF' : 'rgba(255,255,255,.55)',
              opacity: isHeld ? 0.72 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px' }}>
                {/*  Ticked means "repair it". Unticking HOLDS it back — the plan
                     still lists it, the write skips it. */}
                <input type="checkbox" checked={!isHeld} disabled={busy}
                  onChange={() => toggle(id)}
                  aria-label={`Repair ${nameOf(id) || id}`}
                  style={{ accentColor: BRAND.greenDark, width: 14, height: 14, flexShrink: 0 }} />
                <button type="button" onClick={() => setOpen(isOpen ? null : id)}
                  aria-expanded={isOpen}
                  style={{
                    flex: 1, minWidth: 0, textAlign: 'left', border: 0, background: 'none',
                    font: 'inherit', cursor: 'pointer', padding: 0, color: BRAND.ink,
                  }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>
                    {nameOf(id) || `Claim ${id}`}
                  </span>
                  <span style={{ fontSize: 11, color: '#8A5510', marginLeft: 7 }}>
                    {m.nights} night{m.nights === 1 ? '' : 's'} ·{' '}
                    {m.med_lines
                      ? `${m.med_days} day${m.med_days === 1 ? '' : 's'} of drugs`
                      : 'no drugs recorded'}
                  </span>
                  <span style={{ fontSize: 10.5, color: BRAND.inkFaint, marginLeft: 7 }}>
                    {isOpen ? 'hide chart' : 'show chart'}
                  </span>
                </button>
                {/*  The other reading of the same claim. Not a repair — the
                     opposite of one. */}
                <button type="button" disabled={busy || working === id}
                  onClick={async () => {
                    setWorking(id);
                    try { await onTreatAsOpd(r); } finally { setWorking(null); }
                  }}
                  title={'This was detention, not an admission: collapse the stay to the '
                       + 'visit date so the out-patient code is correct. The claim leaves '
                       + 'this list.'}
                  style={{
                    flexShrink: 0, padding: '3px 8px', borderRadius: 6, font: 'inherit',
                    fontSize: 10.5, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer',
                    background: '#fff', border: '1px solid #C9AE86', color: '#6B4109',
                  }}>
                  {working === id ? 'Working…' : 'Treat as out-patient'}
                </button>
              </div>

              {/*  The chart. The evidence behind the count. */}
              {/*  ── EVERYTHING THE DECISION NEEDS, IN ONE PLACE ──────────────
                   The officer is being asked whether this was really an admission
                   or a detention with the wrong dates. That is a clinical call and
                   it cannot be made from a line count — "1 day of drugs" reads
                   very differently under FALSE LABOUR than under SEVERE MALARIA.

                   So: what the patient was treated FOR, then what they were
                   ACTUALLY GIVEN and on which day. Both, or the panel is asking
                   for a judgement while withholding what it rests on.  */}
              {isOpen && (
                <div style={{
                  padding: '9px 11px 11px 31px', borderTop: '1px dashed #F2DCB8',
                  background: 'rgba(255,255,255,.75)', display: 'grid', gap: 9,
                }}>
                  {/* ── What they were treated for ─────────────────────────── */}
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: .4,
                      textTransform: 'uppercase', color: BRAND.inkFaint, marginBottom: 3 }}>
                      Diagnosis
                    </div>
                    {(m.diagnoses || []).length === 0 ? (
                      <div style={{ fontSize: 11.5, color: BRAND.danger }}>
                        No diagnosis on this claim.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 2 }}>
                        {m.diagnoses.map((d, k) => (
                          <div key={`${d.icd10}-${k}`} style={{ fontSize: 11.5, color: BRAND.ink }}>
                            <code style={{ fontSize: 11, fontWeight: 700 }}>{d.icd10 || '—'}</code>
                            <span style={{ marginLeft: 7 }}>{d.description || '—'}</span>
                            {d.gdrg && (
                              <code style={{ fontSize: 10.5, marginLeft: 7, color: BRAND.inkMuted }}>
                                {d.gdrg}
                              </code>
                            )}
                            {d.principal && (
                              <span style={{ fontSize: 9.5, fontWeight: 800, marginLeft: 6,
                                color: BRAND.greenDark }}>PRINCIPAL</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── What they were actually given, and when ────────────── */}
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: .4,
                      textTransform: 'uppercase', color: BRAND.inkFaint, marginBottom: 3 }}>
                      Medication · {m.admission || '—'} to {m.discharge || '—'}
                      {' '}({m.nights} night{m.nights === 1 ? '' : 's'})
                    </div>
                    {(m.days_detail || []).length === 0 ? (
                      <div style={{ fontSize: 11.5, color: BRAND.danger }}>
                        Nothing was dispensed on this claim at all.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 3 }}>
                        {m.days_detail.map((d) => (
                          <div key={d.date} style={{ fontSize: 11.5, color: BRAND.ink,
                            display: 'flex', gap: 8, alignItems: 'baseline' }}>
                            <code style={{ fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                              {d.date}
                            </code>
                            <span style={{ color: BRAND.inkMuted, flexShrink: 0, fontSize: 10.5 }}>
                              {d.lines} line{d.lines === 1 ? '' : 's'}
                            </span>
                            <span style={{ minWidth: 0, color: BRAND.inkSoft }}>
                              {d.drugs || '—'}
                            </span>
                          </div>
                        ))}
                        {/*  Which days of the stay carried NOTHING. The gap is the
                             whole point of the warning, and reading it off a list
                             of dates is work the screen can do. */}
                        {m.nights > 0 && (
                          <div style={{ fontSize: 10.5, color: '#8A5510', marginTop: 2 }}>
                            {m.days_detail.length} of the stay&apos;s days carried
                            something; NHIA would expect closer to {m.nights}.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  ));

  return (
    <div style={{
      padding: '11px 16px 13px', background: '#FDF7EC',
      borderBottom: '1px solid #F2DCB8',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <AlertTriangle size={15} style={{ color: '#8A5510', flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 750, color: '#6B4109' }}>
            {rows.length} of these admissions do not have medication covering the
            whole stay
          </div>
          <div style={{ fontSize: 11.5, color: '#8A5510', marginTop: 2, lineHeight: 1.55 }}>
            Repairing the service makes them a proper admission, and NHIA expects
            something to have been given on each day of one. Open one to see its
            chart. Untick any you want to hold back — the rest still repair.
          </div>
        </div>
        <button type="button" onClick={() => setAll(heldCount !== rows.length)}
          disabled={busy}
          style={{
            flexShrink: 0, padding: '3px 9px', borderRadius: 6, font: 'inherit',
            fontSize: 10.5, fontWeight: 700, cursor: 'pointer',
            background: '#fff', border: '1px solid #C9AE86', color: '#6B4109',
          }}>
          {heldCount === rows.length ? 'Repair all of them' : 'Hold all back'}
        </button>
      </div>

      <Group title="No medication at all" tone={BRAND.danger} list={none}
        blurb="A stay with no treatment recorded. Either the chart was never entered, or the patient was not really admitted." />
      <Group title="Chart stops short of the stay" tone="#8A5510" list={partial}
        blurb="Something was given, but not on every day of the stay." />

      {heldCount > 0 && (
        <div style={{
          marginTop: 8, fontSize: 11.5, fontWeight: 700, color: '#6B4109',
        }}>
          {heldCount} held back — {rows.length - heldCount} of these will still be repaired.
        </div>
      )}
    </div>
  );
}

function RepairDialog({ plan, rowsById, monthLabel, busy, result, onClose, onConfirm,
  onTreatAsOpd,
  //  The rule catalogue the claims list already carries — title and severity per
  //  code. Passed in rather than kept here so the dialog holds no second copy of
  //  the rule names; a facility that downgrades a rule sees it move, not two
  //  screens disagreeing about what it is called.
  issueTypes = [] }) {
  //  `plan?.results || []` built a NEW empty array on every render whenever the
  //  plan had none, so the memo below saw a changed dependency each time and
  //  re-grouped the whole result set on every keystroke and every tab click.
  //  Memoised on the raw reference — which is either the server's array or a
  //  stable `undefined` — so the fallback is created once.
  const results = React.useMemo(() => plan?.results || [], [plan?.results]);
  //  The backdrop used to close this whenever it was not busy, which included the
  //  whole time the officer was reading the plan and deciding.
  const { panelRef, backdropProps } = useDialogGuard();
  //  ── ONE BUCKET PER TAB, DERIVED FROM THE TABS ────────────────────────────
  //
  //  THIS IS WHY THE DIALOG OPENED WHITE. The buckets were written out by hand as
  //  `{ would_fix, ambiguous, unfixable, clean }` — four — while REPAIR_TABS has
  //  FIVE. `advisory` was added to the tab list when the repair learned to tell
  //  "nothing to repair" from "cannot be repaired", and this line was not.
  //
  //  So `grouped.advisory` was undefined, and the tab strip below does
  //  `REPAIR_TABS.map((t) => grouped[t.key].length)` over every tab, unconditionally.
  //  Reading `.length` of undefined threw during render, React unmounted the tree,
  //  and the whole page went blank — every time the preview opened, not sometimes.
  //
  //  Derived from REPAIR_TABS now, so a sixth tab cannot reintroduce it: adding a
  //  tab adds its bucket.
  const grouped = React.useMemo(() => {
    const g = {};
    for (const t of REPAIR_TABS) g[t.key] = [];
    //  ── AND NOTHING IS SILENTLY DROPPED ────────────────────────────────────
    //  The old `if (g[r.status])` discarded any status without a bucket, which is
    //  how 'advisory' rows disappeared even before they crashed anything. The
    //  server also returns 'missing' — an id with no imported claim behind it —
    //  which has no tab of its own and would vanish the same way.
    //
    //  An outcome the officer cannot see is worse than one filed under a heading
    //  that is slightly too broad, so anything unrecognised goes to "Cannot be
    //  settled", which is true of all of them, and each row carries the server's
    //  own `reason` so it explains itself.
    //  The spillover is looked up rather than named, so renaming the 'unfixable'
    //  tab cannot put an `undefined.push` back where the `undefined.length` was.
    const spill = g.unfixable || g[(REPAIR_TABS[0] || {}).key] || [];
    for (const r of results) (g[r.status] || spill).push(r);
    return g;
  }, [results]);

  //  Opens on the pane that has something to act on. Landing on an empty tab
  //  because it happens to be first is how a dialog looks broken.
  const firstWithRows = REPAIR_TABS.find((t) => grouped[t.key].length)?.key || 'would_fix';
  const [tab, setTab] = useState(firstWithRows);
  const active = REPAIR_TABS.find((t) => t.key === tab) || REPAIR_TABS[0];
  const rows = grouped[tab] || [];
  const willFix = grouped.would_fix.length;

  //  ── Claims whose drugs do not cover the stay ──────────────────────────────
  //  A second-order effect of this repair, and the reason it is shown BEFORE the
  //  confirm rather than after the write.
  //
  //  Fixing "billed on an out-patient service" turns a claim the file called
  //  detention into what it really is: a two-night admission. That correction is
  //  right. But it hands NHIA a different question — a patient admitted for two
  //  days is expected to have been given something on each of them, and a two-day
  //  admission carrying one day of drugs reads as either a short stay billed long
  //  or a drug chart nobody finished entering.
  //
  //  NOT A BLOCK. The service is wrong either way; refusing would leave the claim
  //  in a state NHIA rejects outright to avoid one it merely queries. So it is
  //  said plainly, it is countable, and Repair stays enabled.
  const thin = grouped.would_fix.filter((r) => r.medication);

  //  Claims the officer has un-ticked in the review below: the plan CAN fix them,
  //  but the drug chart does not cover the stay and they want to look first. Held
  //  back from the write, never removed from the plan — the dialog stays honest
  //  about what it checked as against what it wrote.
  const [held, setHeld] = useState(() => new Set());

  //  A new plan is a new question. Carrying holds across runs would silently skip
  //  claims on the second pass for a reason taken from the first.
  useEffect(() => { setHeld(new Set()); }, [plan]);

  const willWrite = willFix - [...held].filter(
    (id) => grouped.would_fix.some((r) => Number(r.id) === id)).length;

  const nameOf = (id) => rowsById.get(String(id))?.name || null;

  const th = {
    position: 'sticky', top: 0, zIndex: 1, background: '#F8FAFC',
    padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800,
    letterSpacing: '.04em', textTransform: 'uppercase', color: BRAND.inkFaint,
    borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
  };
  const td = {
    padding: '7px 10px', fontSize: 12, borderBottom: `1px solid ${BRAND.line}`,
    verticalAlign: 'top',
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Repair claims" {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(15,23,42,.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: `${DIALOG_GAP}px 16px`,
      }}>
      <div ref={panelRef} style={{
        width: '100%', maxWidth: 900, background: '#fff', borderRadius: 12,
        border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.28)',
        maxHeight: `calc(100vh - ${DIALOG_GAP * 2}px)`,
        display: 'flex', flexDirection: 'column',
      }}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px',
          borderBottom: `1px solid ${BRAND.line}`,
        }}>
          <Wrench size={16} style={{ color: BRAND.greenDark, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
              {result ? 'Repair complete' : 'Repair claims'}
            </div>
            <div style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
              {monthLabel} · {results.length.toLocaleString()} claim(s) checked
              {!result && ' · nothing has been written yet'}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" style={{
            border: 0, background: 'none', cursor: busy ? 'not-allowed' : 'pointer',
            padding: 4, color: BRAND.inkMuted, lineHeight: 1, fontSize: 15,
          }}>✕</button>
        </div>

        {/* ── The outcome, once it has run ──────────────────────────────── */}
        {result ? (
          <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 10,
            alignContent: 'start', background: '#F8FAFC' }}>
            <div style={{
              padding: '12px 14px', borderRadius: 9, fontSize: 13,
              background: result.failed.length ? '#FDF2E3' : '#E7F5EC',
              color: result.failed.length ? '#8A5510' : '#1B6631',
              border: `1px solid ${result.failed.length ? '#F2DCB8' : '#BFE3CC'}`,
            }}>
              <b style={{ fontSize: 15 }}>{result.fixed.length.toLocaleString()}</b> claim(s)
              repaired.
              {result.failed.length > 0 && (
                <> {result.failed.length} failed: {result.failed.map((r) => r.id).join(', ')}.</>
              )}
            </div>
            {/*  What the repair did NOT clear. "23 repaired" beside a row whose
                 badge is still red is how a screen loses its officer.  */}
            <div style={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.6 }}>
              {result.left.length
                ? `${result.left.length} of them still have something outstanding — `
                  + 'open those to see what.'
                : 'Nothing outstanding on any of them.'}
            </div>
          </div>
        ) : (
          <>
            {/* ── The counts, which are also the tabs ───────────────────── */}
            <div style={{
              display: 'grid', gap: 8, padding: '12px 16px',
              borderBottom: `1px solid ${BRAND.line}`, background: '#F8FAFC',
              //  auto-fit so four tiles sit in a row on a laptop and stack two-up
              //  on a phone without a breakpoint to maintain.
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            }}>
              {REPAIR_TABS.map((t) => {
                const n = grouped[t.key].length;
                const on = tab === t.key;
                return (
                  <button key={t.key} type="button"
                    onClick={() => setTab(t.key)}
                    aria-pressed={on}
                    disabled={!n}
                    style={{
                      textAlign: 'left', font: 'inherit', cursor: n ? 'pointer' : 'default',
                      padding: '9px 11px', borderRadius: 9,
                      background: on ? t.tone.bg : '#fff',
                      border: `1px solid ${on ? t.tone.bd : BRAND.line}`,
                      boxShadow: on ? `inset 3px 0 0 ${t.tone.fg}` : 'none',
                      opacity: n ? 1 : .55,
                    }}>
                    <div style={{
                      fontSize: 19, fontWeight: 800, lineHeight: 1.1,
                      color: n ? t.tone.fg : BRAND.inkFaint,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{n.toLocaleString()}</div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, marginTop: 2,
                      color: on ? t.tone.fg : BRAND.inkMuted,
                    }}>{t.label}</div>
                  </button>
                );
              })}
            </div>

            {/*  ── ONE SCROLL REGION FROM HERE TO THE FOOTER ─────────────────
                 The blurb, the medication review and the claims table all live
                 inside it.

                 They were three siblings of the flex column instead, each sized by
                 its content, and the review panel is tall — ten claims in two
                 groups. It pushed the table down, the table's own `flex: 1` fought
                 it, and the FOOTER WENT OFF THE BOTTOM OF THE CARD: the officer
                 could read a warning about ten claims and had no Repair button to
                 act on it with, which is worse than not showing the warning.

                 With the middle as one scroller the footer is pinned by
                 construction — it cannot be pushed anywhere, however tall the
                 review grows.  */}
            <div style={{ flex: 1, minHeight: 160, overflow: 'auto' }}>
              {/* ── What this pane means ──────────────────────────────────── */}
              <div style={{
                padding: '10px 16px', fontSize: 12, lineHeight: 1.6,
                color: BRAND.inkMuted, borderBottom: `1px solid ${BRAND.line}`,
              }}>
                {active.blurb}
              </div>

            {/*  ── CHECK THE DRUG CHART BEFORE YOU CONFIRM ─────────────────
                 Only on the pane that is about to be written, and only when
                 there is something to say.

                 This repair turns a claim the file called detention into what it
                 really is: a stay of N nights. That is the right correction and
                 it is not in question here. What IS in question is what it
                 exposes — a two-day admission carrying one day of drugs reads to
                 NHIA as either a short stay billed long, or a chart nobody
                 finished entering. Either way it is better found now than in a
                 query three weeks after submission.

                 A WARNING, NOT A GATE. Repair stays enabled. The service is
                 wrong whether or not the chart is thin, and refusing would leave
                 the claim in a state NHIA rejects outright in order to avoid one
                 it merely asks about.  */}
            {tab === 'would_fix' && thin.length > 0 && (
              <MedicationReview
                rows={thin}
                nameOf={nameOf}
                held={held}
                onHeld={setHeld}
                onTreatAsOpd={onTreatAsOpd}
                busy={busy}
              />
            )}

            {/*  The rows. No scroller of its own any more — the region above is
                 the one scroller, and nesting a second here is what let the review
                 grow unbounded and push the footer off the card. */}
            <div>
              {rows.length === 0 ? (
                <div style={{ padding: '28px 16px', textAlign: 'center',
                  fontSize: 12.5, color: BRAND.inkFaint }}>
                  No claims in this group.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Claim</th>
                      <th style={th}>Patient</th>
                      {tab === 'would_fix' && <th style={th}>Specialty</th>}
                      <th style={th}>
                        {tab === 'would_fix' ? 'What is re-coded'
                          : tab === 'ambiguous' ? 'The choice to make'
                            : tab === 'unfixable' ? 'Why not' : 'Status'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const recodes = (r.changes || []).filter((c) => c.field === 'gdrg_code');
                      const other = (r.changes || []).filter((c) => c.field !== 'gdrg_code');
                      return (
                        <tr key={r.id}>
                          <td style={{ ...td, ...S.mono, fontWeight: 700 }}>{r.id}</td>
                          <td style={{ ...td, color: BRAND.ink }}>
                            {nameOf(r.id) || <span style={{ color: BRAND.inkFaint }}>—</span>}
                          </td>

                          {tab === 'would_fix' && (
                            <td style={td}>
                              {r.before.specialty_code !== r.after.specialty_code
                                ? <CodeMove from={r.before.specialty_code} to={r.after.specialty_code} />
                                : <span style={{ color: BRAND.inkFaint }}>unchanged</span>}
                            </td>
                          )}

                          <td style={td}>
                            {tab === 'would_fix' && (
                              <div style={{ display: 'grid', gap: 3 }}>
                                {recodes.map((c) => (
                                  <div key={c.row_id} style={{ display: 'flex', gap: 7,
                                    alignItems: 'baseline', flexWrap: 'wrap' }}>
                                    <span style={{ ...S.mono, fontWeight: 700, color: BRAND.ink }}>
                                      {c.icd10 || c.description}
                                    </span>
                                    <CodeMove from={c.from} to={c.to} />
                                    {/*  Where the match came from. An officer
                                         checking 47 rows needs to know which ones
                                         leaned on a NAME rather than a code —
                                         those are the ones worth a second look. */}
                                    {c.matchedOn && c.matchedOn !== 'icd10' && (
                                      <span style={{ fontSize: 10, fontWeight: 700,
                                        color: '#8A5510', background: '#FDF2E3',
                                        border: '1px solid #F2DCB8', borderRadius: 4,
                                        padding: '0 5px' }}>
                                        {c.matchedOn === 'name' ? 'matched on name' : 'matched on stem'}
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {/*  ── EVERY OTHER KIND OF CHANGE, IN WORDS ──
                                     This printed the raw field names — a row whose
                                     repair was to write the principal down read
                                     "is_principal", which is a column, not a
                                     sentence, and told the officer being asked to
                                     authorise it nothing at all. It matters more
                                     now: writing the principal down is the
                                     commonest repair on the month, 165 of 172.

                                     Rendered whether or not there are re-codes,
                                     because a claim can have both and the re-code
                                     alone was hiding the rest. */}
                                {other.map((c, i) => (
                                  <div key={`${c.field}-${i}`} style={{ display: 'flex',
                                    gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 11, fontWeight: 700,
                                      color: BRAND.inkSoft }}>
                                      {REPAIR_FIELD_LABEL[c.field] || c.field}
                                    </span>
                                    {/*  A before/after only where the VALUE moves.
                                         "null → 9626" is a row id and means nothing
                                         to anyone; the name beside it is the fact. */}
                                    {REPAIR_FIELD_SHOWS_MOVE.has(c.field)
                                      ? (c.field === 'specialty_gdrg_name'
                                        ? <TextMove from={c.from} to={c.to} />
                                        : <CodeMove from={c.from} to={c.to} />)
                                      : c.named
                                        ? <span style={{ color: BRAND.ink, fontSize: 11.5 }}>
                                          {c.named}
                                        </span>
                                        : null}
                                  </div>
                                ))}
                                {!recodes.length && !other.length && (
                                  <span style={{ color: BRAND.inkFaint }}>—</span>
                                )}
                              </div>
                            )}

                            {tab === 'ambiguous' && (
                              <div style={{ display: 'grid', gap: 4 }}>
                                {(r.choices || []).map((ch) => (
                                  <div key={ch.row_id}>
                                    <span style={{ ...S.mono, fontWeight: 700, color: BRAND.ink }}>
                                      {ch.icd10 || ch.description}
                                    </span>
                                    <span style={{ color: BRAND.inkMuted }}> — </span>
                                    {ch.candidates.map((c, i) => (
                                      <span key={c.gdrg_code}>
                                        {i > 0 && <span style={{ color: BRAND.inkFaint }}> / </span>}
                                        <span style={{ ...S.mono, fontWeight: 700 }}>{c.gdrg_code}</span>
                                        <span style={{ color: BRAND.inkMuted, fontSize: 11 }}>
                                          {' '}({Number(c.tariff || 0).toFixed(2)})
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}

                            {tab === 'unfixable' && (
                              <div style={{ color: BRAND.inkMuted }}>
                                {(r.unmatched || []).length
                                  ? (r.unmatched || []).map((u) => (
                                    <span key={u.row_id} style={{ marginRight: 8 }}>
                                      <span style={{ ...S.mono, color: BRAND.ink }}>
                                        {u.icd10 || u.description}
                                      </span>
                                      <span style={{ fontSize: 11 }}> not in the catalogue</span>
                                    </span>
                                  ))
                                  : r.reason}
                              </div>
                            )}

                            {/*  ── WHAT THE WARNING ACTUALLY IS ────────────
                                 Named, not summarised. "Nothing to repair" is the
                                 verdict; the officer still has to decide whether a
                                 short drug chart on THIS claim is the discharge-
                                 morning dose or a drug given and never billed, and
                                 they cannot decide that from the word "warning".

                                 Titles come from the catalogue the server sent with
                                 the list, so this holds no second copy of them. */}
                            {tab === 'advisory' && (
                              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                {(r.issues || []).length
                                  ? (r.issues || []).map((code) => (
                                    <span key={code} style={{
                                      fontSize: 10.5, fontWeight: 700, color: BRAND.inkSoft,
                                      background: BRAND.head, borderRadius: 4,
                                      border: `1px solid ${BRAND.line}`, padding: '1px 6px',
                                    }}>
                                      {(issueTypes.find((t) => t.code === code) || {}).name
                                        || code}
                                    </span>
                                  ))
                                  : <span style={{ color: BRAND.inkMuted }}>{r.reason}</span>}
                              </div>
                            )}

                            {tab === 'clean' && (
                              <span style={{ color: BRAND.inkMuted }}>
                                {r.reason || 'Agrees with its own rows.'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            </div>{/* /the one scroll region */}
          </>
        )}

        {/* ── Actions, pinned. Never inside the scroll ──────────────────── */}
        <div style={{
          display: 'flex', gap: 8, padding: '12px 16px', alignItems: 'center',
          flexWrap: 'wrap', borderTop: `1px solid ${BRAND.line}`, background: '#fff',
        }}>
          <div style={{ flex: 1, minWidth: 180, fontSize: 11.5, color: BRAND.inkMuted }}>
            {result
              ? 'Written. The list behind this dialog has been refreshed.'
              : willFix
                ? <><b style={{ color: BRAND.danger }}>This cannot be undone.</b>{' '}
                  Only the {willFix.toLocaleString()} in the first group are written to.</>
                : 'Nothing in this selection can be repaired automatically.'}
          </div>
          <button type="button" onClick={onClose} disabled={busy} style={{
            padding: '9px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
            fontFamily: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
            border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkMuted,
          }}>{result ? 'Done' : 'Cancel'}</button>
          {/*  The count follows the ticks. Holding four back and still reading
               "Repair 10" would be the button lying about what it is about to do,
               on the one screen where that matters most.

               NOTE: this comment sits OUTSIDE `{!result && (…)}` on purpose. After
               `&&` the parentheses hold a single EXPRESSION, and `{/* … *\/}` there
               is a second one — a parse error, not a comment. Inside a child slot
               it would be fine, which is what makes it easy to get wrong. */}
          {!result && (
            <button type="button" onClick={() => onConfirm(held)}
              disabled={busy || !willWrite}
              title={willWrite ? `Repair ${willWrite} claim(s)` : 'Every claim here is held back'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                fontFamily: 'inherit', border: '1px solid transparent', color: '#fff',
                cursor: busy || !willWrite ? 'not-allowed' : 'pointer',
                background: busy || !willWrite ? BRAND.inkFaint : BRAND.greenDark,
              }}>
              <Wrench size={14} />
              {busy ? 'Repairing…' : `Repair ${willWrite.toLocaleString()}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

//  The consistency rules a claim can be repaired out of USING ITS OWN ROWS, and
//  therefore the ones that can carry a "Fix all". Each resolves to the same act:
//  make the claim's real principal its principal — the procedure where one was
//  done, the diagnosis where none was — and let the service follow it.
//
//  Kept here rather than inferred from `fixable` on the facet, because the facet
//  is a COUNT and does not carry the per-claim verdict. A tile offering Fix all on
//  a rule that cannot be fixed from the claim is worse than no button.
const MV_REPAIRABLE = new Set([
  'ipd_on_opd_service',
  'no_principal_dx',
  'no_principal_multi_gdrg',
]);

//  ── THE NHIA RULES THAT CARRY A FIX ────────────────────────────────────────
//
//  A SECOND family of repairable rules, with a different engine behind it. The
//  three above are settled from a claim's own rows through Multi Vet; these are
//  format corrections to a header field, applied by /claims/nhia-report/fix.
//
//  They are listed rather than inferred for the same reason MV_REPAIRABLE is: the
//  facet is a COUNT and carries no per-claim verdict, and a tile offering Fix all
//  on a rule that cannot be fixed is worse than a tile with no button. Every rule
//  NHIA reported that CANNOT be corrected mechanically — the four G-DRG
//  eligibility rules, prescribing level, the code format — is deliberately absent.
//
//  Maps our rule code to the fix that answers it. The two names differ because the
//  rule is what the claim fails and the fix is what is done about it.
const NHIA_FIX_BY_RULE = new Map([
  //  A specialty code that is not one of the thirteen MDC groups — D001, OPD0, OOBG,
  //  ODPC — corrected to the group the claim's own service belongs to. A specialty that
  //  IS a real group never reaches this: the rule tests membership, so the 138 claims
  //  whose specialty legitimately differs from their service are never offered up.
  ['specialty_not_a_group', 'invalid_specialty_code'],
  ['nhia_member_no_charset', 'invalid_member_no_charset'],
  ['nhia_other_names_charset', 'invalid_other_names'],
  ['nhia_pharmacy_no_medicines', 'pharmacy_without_medicines'],
]);

// ── The pattern's building blocks ───────────────────────────────────────────
//  One numbered row: the step's number, its label, and its choices on the same
//  line. A grid rather than flex so every step's label column is the same width
//  and the choices line up down the card — with each step laying out its own
//  label the rows would be ragged, which reads as three unrelated filters rather
//  than as one sequence.
//
//  The NUMBERS ARE INFORMATION. This is a real sequence: step 1 decides which
//  catalogue applies, step 2 narrows within it, step 3 decides which tariff band is
//  correct. Numbering an unordered set of filters would be decoration; numbering
//  this one tells the officer the order they have to think in.
function Step({ n, label, children, block = false }) {
  return (
    <div style={{
      //  `minmax(0, 1fr)` on the last track, not `1fr`. A bare `1fr` is
      //  `minmax(auto, 1fr)`, whose minimum is the content's min-content width — so
      //  a long row of nowrap pills makes the track overflow its container instead
      //  of wrapping. This is the same one-word fix as the minWidth: 0 below.
      display: 'grid', gridTemplateColumns: '22px minmax(96px, auto) minmax(0, 1fr)',
      gap: 10, alignItems: 'start',
    }}>
      <span style={{
        width: 22, height: 22, borderRadius: 6, display: 'grid', placeItems: 'center',
        fontSize: 11, fontWeight: 800, fontFamily: 'inherit', marginTop: 1,
        background: BRAND.greenSoft, color: BRAND.greenDark,
      }}>{n}</span>
      <span style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '.05em', paddingTop: 5,
        textTransform: 'uppercase', color: BRAND.inkSoft, whiteSpace: 'nowrap',
      }}>{label}</span>
      {/*  minWidth: 0 is what makes the wrapping actually happen. A grid track
           defaults to `min-content`, so a row of nowrap pills forces the track wider
           than its container and the overflow has to go somewhere.

           `block` gives the step its cell as a plain block instead of a wrapping
           flex row — for a step whose content is its own layout, like step 3's two
           columns. Nesting a grid inside a wrapping flex item inside a grid track was
           three layout systems deep for one row of controls, and it is where step 3
           kept getting lost. */}
      <div style={block
        ? { minWidth: 0 }
        : { display: 'flex', gap: 6, flexWrap: 'wrap', minWidth: 0, alignItems: 'center' }}>
        {children}
      </div>
    </div>
  );
}

//  One choice. Small on purpose — the filters are the top of the panel and the
//  space below them belongs to the routine and the claims it will be applied to.
function Pill({ on, onClick, title, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} title={title}
      style={{
        padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: on ? 800 : 600,
        border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
        background: on ? BRAND.greenDark : '#fff',
        color: on ? '#fff' : BRAND.inkSoft,
        whiteSpace: 'nowrap',
      }}>{children}</button>
  );
}

//  The age boxes. Narrow on purpose — an age is two digits, and a full-width input
//  beside seven shortcut pills would make the typed range look like the primary way
//  in when it is the exception.
const ageBox = {
  width: 62, padding: '4px 8px', borderRadius: 7, fontFamily: 'inherit',
  fontSize: 12.5, border: `1px solid ${BRAND.line}`, background: '#fff',
  color: BRAND.ink,
};

//  Step 3 holds two questions on one line, so each needs its own quiet marker.
const subLabel = {
  fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
  textTransform: 'uppercase', color: BRAND.inkFaint, marginRight: 2,
};

// ── The four catalogues, and what a pick from each becomes ──────────────────
//  Tables rather than a switch in the render, so adding a fifth kind is four
//  entries and no new branch — and so the SHAPE of a pick lives next to the
//  catalogue it came from rather than three screens away from it.
const CATALOGUE_URL = {
  diagnosis: claimStoreDiagnosisTariff,
  procedure: claimStoreProcedureTariff,
  investigation: claimStoreInvestigationTariff,
  medication: claimStoreMedicationCatalogue,
};
const CATALOGUE_HINT = {
  diagnosis: 'Search name, ICD-10 or G-DRG…',
  procedure: 'Search procedure or G-DRG…',
  investigation: 'Search test name or code…',
  medication: 'Search drug name or code…',
};
const CATALOGUE_ROW = {
  //  Both codes on a diagnosis: the ICD-10 says what the patient has, the G-DRG says
  //  what it is billed under, and vetting needs to see the pair.
  diagnosis: (o) => [o.icd10 || o.code, o.gdrg].filter(Boolean).join('  ·  '),
  procedure: (o) => [o.gdrg || o.code, o.icd10].filter(Boolean).join('  ·  '),
  investigation: (o) => o.code || o.gdrg || '—',
  //  A drug's strength and form are what tell two Paracetamols apart, and the
  //  catalogue carries both.
  medication: (o) => [o.code, o.strength, o.dosage_form].filter(Boolean).join('  ·  '),
};

//  A catalogue row, shaped for the writer that will save it. Each kind's fields are
//  the ones its own store function reads — saveDiagnosis wants an icd10_code,
//  saveMedication wants a dose unit — so the basket carries what the endpoint needs
//  rather than a generic row the server has to guess at.
function shapePick(kind, o) {
  const base = {
    kind,
    code: o.gdrg || o.code,
    name: o.name || null,
    tariff: o.tariff == null || o.tariff === '' ? null : Number(o.tariff),
  };
  if (kind === 'diagnosis') {
    return { ...base, icd10_code: o.icd10 || o.code || null, gdrg_code: o.gdrg || null };
  }
  if (kind === 'procedure') {
    return { ...base, gdrg_code: o.gdrg || o.code || null, icd10_code: o.icd10 || null };
  }
  if (kind === 'investigation') {
    return { ...base, code: o.code || o.gdrg, kind_of: o.kind || 'lab' };
  }
  //  MEDICATION IS A PRESCRIPTION, not a code. The catalogue gives the drug and
  //  whatever strength it has recorded; the dose, frequency, duration, route and
  //  quantity are prescribed and have to be typed — which is why a medication lands
  //  in the basket INCOMPLETE and the basket row asks for the rest. Applying a drug
  //  with no dose unit is the thing NHIA refuses outright.
  return {
    ...base,
    code: o.code,
    generic_name: o.name || null,
    dosage_form: o.dosage_form || null,
    strength: o.strength || null,
    //  Pre-filled from the catalogue where it knows — the same courtesy the
    //  single-claim editor does, and the reason most rows need only a quantity.
    dose_unit: o.dose_unit || o.strength || '',
    dose: '', frequency: '', duration: '', route: '', quantity: '',
    //  Whether a QUANTITY can be worked out at all. A tablet, capsule or ampoule is
    //  counted; a suspension is dispensed as a volume, and multiplying doses for one
    //  produces a number that means nothing. Read from the catalogue row — the same
    //  flag the single-claim editor reads — with a fallback on the dosage form for a
    //  catalogue that does not carry it.
    countable: o.countable != null
      ? !!o.countable
      : /tablet|capsule|caplet|ampoule|vial|suppositor|pessar|sachet|lozenge/i
        .test(String(o.dosage_form || '')),
    //  Set the moment a quantity is typed, so the suggestion stops overwriting it.
    quantityTyped: false,
  };
}

//  ── WHAT MAKES TWO ENTRIES THE SAME ENTRY ───────────────────────────────────
//  Not the code. This let two rows of "Abnormal uterine and vaginal bleeding" into
//  one basket — same diagnosis, N93.9, under OBGY23 and OBGY26 — because the basket
//  was keyed on the G-DRG and those differ. Applied, that writes one condition to a
//  claim twice under two prices, which is a duplicate NHIA refuses.
//
//  So identity is what each STORE WRITER treats as unique, and nothing else:
//
//    diagnosis      the ICD-10. saveDiagnosis says it outright — one diagnosis per
//                   ICD-10, and "the G-DRG is deliberately NOT part of the
//                   identity: the same condition billed under two G-DRGs is still
//                   one diagnosis, and the claim is billed on one of them".
//    procedure      the G-DRG. That IS the procedure.
//    lab / drug     the catalogue code.
//
//  Used in two places that must agree: the basket refuses a second one, and the
//  picker marks the row IN so it cannot be clicked again. Keying those differently
//  is how a row looks addable and then silently is not.
function identityOf(it) {
  if (!it) return '';
  const up = (v) => String(v || '').trim().toUpperCase();
  if (it.kind === 'diagnosis') return `diagnosis:${up(it.icd10_code || it.icd10 || it.code)}`;
  if (it.kind === 'procedure') return `procedure:${up(it.gdrg_code || it.gdrg || it.code)}`;
  return `${it.kind}:${up(it.code)}`;
}

//  The same identity, for a raw CATALOGUE row that has no `kind` yet.
function identityOfPick(kind, o) {
  return identityOf({ kind, ...o, code: o.gdrg || o.code });
}

//  ── IS THIS PICK THE SAME ENTRY THAT IS ALREADY THERE? ──────────────────────
//  A DIFFERENT QUESTION FROM identityOf, and it has to be, which is why it is its
//  own function rather than a flag on that one.
//
//  identityOf answers "may these two sit on one claim together" — and for a
//  diagnosis the answer turns on the ICD-10 alone, because a claim carrying one
//  condition under two G-DRGs is billed twice for it and NHIA refuses that. The
//  basket and the picker are right to use it.
//
//  THIS answers "would replacing that with this change anything", and the ICD-10
//  alone gets it wrong. The catalogue holds Malaria as OPDC06A for a consultation
//  and MEDI28A for an admission — one ICD-10, two billing codes — and an upload
//  put the out-patient one on admissions. Mapping Malaria onto Malaria is then not
//  a no-op: it is the correction, and the only thing that moves is the G-DRG.
//
//  So the comparison is the NAME AND THE CODE, in the sense that matters to the
//  writer: the identifying code AND the billing code beside it. Same both ->
//  refuse, because deleting the row and writing it back identical is churn on
//  every claim and risks the original's own details. Either different -> allow.
//
//  `wasGdrgs` is what the TICKED CLAIMS actually carry, which can be more than one
//  value. A pick matching only some of them is still a change for the rest, so it
//  is refused only when every claim already has exactly it.
function isSameEntry(kind, pick, wasCode, wasGdrgs) {
  const up = (v) => String(v || '').trim().toUpperCase();
  if (!pick) return false;
  //  For everything except a diagnosis the entry's own code IS the billing code,
  //  so identity already answers this and there is nothing further to compare.
  if (kind !== 'diagnosis') return identityOf(pick) === identityOf({ kind, code: wasCode });

  const sameIcd = up(pick.icd10_code || pick.icd10 || pick.code) === up(wasCode);
  if (!sameIcd) return false;
  const want = up(pick.gdrg_code || pick.gdrg);
  const have = (wasGdrgs || []).map(up).filter(Boolean);
  //  The claims do not say what they are billed under — an older row with a null
  //  G-DRG. Nothing to compare, so fall back to the ICD-10 and let the officer
  //  through: refusing on an unknown would block the very repair that fills it in.
  if (!have.length || !want) return false;
  //  Same only if EVERY ticked claim already carries exactly this code.
  return have.length === 1 && have[0] === want;
}

//  Everything a medication needs before it can be written, in the order the
//  single-claim editor asks for it. Mirrors RX_REQUIRED there; the store validates
//  the same list, so a row that satisfies this satisfies the write.
const MV_RX_FIELDS = [
  ['dose_unit', 'Dose unit', '500 mg'],
  ['dose', 'Dose', '500 mg x 1'],
  ['frequency', 'Frequency', '3 daily'],
  ['duration', 'Duration', '5'],
  ['route', 'Route', 'Oral'],
  ['quantity', 'Quantity', '15'],
];

// ── A MEDICATION IS A PRESCRIPTION ──────────────────────────────────────────
//
//  A code alone cannot be written: NHIA refuses a medicine line with no dose unit, and
//  the store validates dose, frequency, duration, route and quantity the same way the
//  single-claim editor does. So a drug arrives incomplete and asks for the rest here,
//  rather than being applied to 153 claims as a line that will be rejected on every one.
//
//  ONE COMPONENT, TWO USES. The basket adds a drug; an update REPLACES one — and a
//  replacement needs every one of these six fields for exactly the same reason. Two
//  copies of this block would have drifted inside a month, and the half that drifted
//  would be the one that produced rejects.
//
//  `onChange` receives the WHOLE next row. The caller decides where it lives — an entry
//  in the basket array, or the `to` side of a mapping — and this component never needs
//  to know which.
function MvRx({ v, vocab, suggest, onChange, onRemove, removeLabel }) {
  //  Frequency and route come from the CONTROLLED LISTS the store validates against —
  //  the same vocabulary the claim editor uses — so a chosen value cannot be something
  //  the write will refuse.
  const apply = (f, val) => {
    const next = { ...v, [f]: val };
    //  ── QUANTITY IS CALCULATED, NOT TYPED ──────────────────────────────────
    //  units-per-dose × doses-per-day × days. "500 mg x 2" three times daily for five
    //  days is 30 tablets, and nobody should be doing that in their head across 23
    //  claims.
    //
    //  A TYPED QUANTITY WINS. Once a number has been entered by hand the suggestion
    //  stops overwriting it — the pharmacy dispensed a pack of 20, and the arithmetic
    //  does not get to argue.
    if (f === 'quantity') { onChange({ ...next, quantityTyped: true }); return; }
    if (['dose', 'frequency', 'duration'].includes(f) && !v.quantityTyped) {
      const s = suggest(next);
      if (s !== null) { onChange({ ...next, quantity: s, quantityAuto: true }); return; }
    }
    onChange(next);
  };

  return (
    <div style={{
      padding: '2px 14px 10px 14px', display: 'grid', gap: 6,
      gridTemplateColumns: 'repeat(auto-fit, minmax(126px, 1fr))',
      background: BRAND.canvas,
    }}>
      {MV_RX_FIELDS.map(([f, fl, ph]) => {
        const bad = !String(v[f] ?? '').trim();
        const list = f === 'frequency' ? vocab.frequencies
          : f === 'route' ? vocab.routes : null;
        return (
          <label key={f} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '.05em',
              textTransform: 'uppercase',
              color: bad ? BRAND.danger : BRAND.inkFaint,
            }}>
              {fl}
              {/*  Says the number was WORKED OUT. Without it a calculated 30 looks
                   identical to a typed 30, and the officer cannot tell which they are
                   responsible for. Typing over it clears the label, because then
                   they are. */}
              {f === 'quantity' && v.quantityAuto && !v.quantityTyped && (
                <span style={{
                  color: BRAND.greenDark, fontWeight: 800, marginLeft: 5,
                }}>calculated</span>
              )}
              {/*  And why it cannot be, when it cannot. ONLY COUNTABLE FORMS have a
                   countable quantity; a suspension is dispensed as a volume, and
                   multiplying doses for it produces a number that means nothing. So a
                   blank quantity on a syrup is not an oversight. */}
              {f === 'quantity' && v.countable === false && (
                <span style={{
                  color: BRAND.inkFaint, fontWeight: 700, marginLeft: 5,
                  textTransform: 'none', letterSpacing: 0,
                }}>— {v.dosage_form || 'not counted'}, enter it</span>
              )}
            </span>
            {list && list.length ? (
              <select value={v[f] || ''} onChange={(e) => apply(f, e.target.value)}
                style={{
                  padding: '5px 7px', borderRadius: 6, font: 'inherit',
                  fontSize: 11.5, minWidth: 0, background: '#fff',
                  border: `1px solid ${bad ? BRAND.danger : BRAND.line}`,
                  color: BRAND.ink,
                }}>
                <option value="">— {fl.toLowerCase()} —</option>
                {/*  These are OBJECTS — { value, label, perDay } — not strings.
                     Rendered as strings they came out as [object Object] in both
                     dropdowns. */}
                {list.map((opt) => {
                  const val = typeof opt === 'string' ? opt : (opt.value ?? '');
                  const txt = typeof opt === 'string' ? opt : (opt.label ?? opt.value ?? '');
                  return <option key={val} value={val}>{txt}</option>;
                })}
              </select>
            ) : (
              <input value={v[f] || ''} placeholder={ph}
                onChange={(e) => apply(f, e.target.value)}
                style={{
                  padding: '5px 7px', borderRadius: 6, font: 'inherit',
                  fontSize: 11.5, minWidth: 0, background: '#fff',
                  border: `1px solid ${bad ? BRAND.danger : BRAND.line}`,
                  color: BRAND.ink,
                }} />
            )}
          </label>
        );
      })}

      {/*  ── A REMOVE THAT CAN BE FOUND ──────────────────────────────────────
           There is a ✕ on the row above, but it is small, grey, and at the far right
           — nowhere near the six fields being filled in, which is where the decision
           to drop a drug actually gets made. So the prescription block carries its
           own, labelled, at the end of the fields it belongs to.

           Both remove the same row through the same function. Two ways to reach one
           action is fine; one way that is hard to see is not. */}
      {onRemove && (
        <label style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '.05em',
            textTransform: 'uppercase', color: 'transparent',
          }}>&nbsp;</span>
          <button type="button" onClick={onRemove} title={removeLabel}
            style={{
              display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 5,
              padding: '5px 9px', borderRadius: 6, cursor: 'pointer',
              font: 'inherit', fontSize: 11.5, fontWeight: 700,
              border: `1px solid ${BRAND.danger}55`,
              background: BRAND.dangerSoft, color: BRAND.danger,
            }}>
            <Trash2 size={12} /> Remove
          </button>
        </label>
      )}
    </div>
  );
}

// ── One catalogue picker, for the composer ──────────────────────────────────
//  Searches the PRICED CATALOGUE — what a claim could be given — as against
//  MvFilterBox above, which searches what the month's claims already contain. Two
//  lists that look alike and answer opposite questions, so they are two components
//  rather than one with a flag: a picker that quietly switched sources would put
//  catalogue codes in a filter or filter codes in a basket.
//
//  Fetched by AGE BAND. The catalogue is banded — MEDI is adult medicine, PAED is
//  paediatrics — and the endpoint already narrows on it, which is what stops an adult
//  tariff being offered for a group of children. The server refuses that combination
//  anyway; this stops it being offered in the first place.
function MvCatalogue({ url, params, label, hint, onPick, chosen, rowLabel, kind }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [note, setNote] = useState(null);
  //  Imported for this scheme and NOT offerable — no G-DRG, or no code — so it
  //  cannot carry a claim line. Kept separate from `rows` so it can never be
  //  picked, and shown so that "72 tests" can be read against the 85 that were
  //  actually imported instead of looking like all of them.
  const [held, setHeld] = useState([]);
  const [showHeld, setShowHeld] = useState(false);

  const key = JSON.stringify(params || {});
  useEffect(() => {
    let alive = true;
    setRows(null);
    setHeld([]);
    setShowHeld(false);
    cachedGet(url, params)
      .then((data) => {
        if (!alive) return;
        setRows(data?.options || []);
        setHeld(data?.unbillable || []);
        setNote(data?.note || null);
      })
      .catch(() => { if (alive) { setRows([]); setNote('The list could not be loaded.'); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, key]);

  const term = q.trim().toLowerCase();
  const matches = (o) =>
    `${o.code || ''} ${o.icd10 || ''} ${o.name || ''} ${o.gdrg || ''}`
      .toLowerCase().includes(term);
  //  Only searched, never shown whole: the priced diagnosis catalogue runs to 1,404
  //  rows, and a list nobody scrolls is a list that should be typed into. Capped at
  //  60 rendered because a picker is read a few rows at a time.
  const shown = useMemo(() => {
    const all = rows || [];
    if (!term) return all.slice(0, 60);
    return all.filter(matches).slice(0, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, term]);
  //  The held-back rows are searchable too. An officer looking for a test that
  //  exists has to be able to FIND it and see why it cannot be used — that is the
  //  whole point of showing them.
  const heldShown = useMemo(() => {
    const all = held || [];
    return (term ? all.filter(matches) : all).slice(0, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held, term]);

  return (
    <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={hint}
        aria-label={`Search ${label.toLowerCase()}`}
        style={{
          padding: '8px 11px', borderRadius: 8, font: 'inherit', fontSize: 12.5,
          border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.ink,
          minWidth: 0,
        }} />
      {note && (
        <div style={{ fontSize: 11.5, color: BRAND.caution || '#8A5510' }}>{note}</div>
      )}
      <div style={{
        maxHeight: 230, overflowY: 'auto', border: `1px solid ${BRAND.line}`,
        borderRadius: 8, minWidth: 0,
      }}>
        {rows === null && (
          <div style={{ padding: '14px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
            Loading the catalogue…
          </div>
        )}
        {/*  "Nothing here" and the held-back list are SIBLINGS, not branches of one
             ternary. They were a ternary, which meant that when every imported row
             was unbillable the officer saw "Nothing in this catalogue" and no
             explanation — the exact case the held-back list exists for. */}
        {rows !== null && shown.length === 0 && (
          <div style={{ padding: '14px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
            {term ? `No match for “${q.trim()}”.`
              : held.length ? 'Nothing here can be billed — see below.'
                : 'Nothing in this catalogue.'}
          </div>
        )}
        {rows !== null && shown.map((o) => {
          const code = String(o.gdrg || o.code || '');
          //  Matched on IDENTITY, not on the code shown. Two G-DRGs for one ICD-10
          //  are one diagnosis, so once either is in the basket both rows read IN —
          //  otherwise the second looks addable and the basket refuses it.
          const on = chosen.some((b) => identityOf(b) === identityOfPick(kind, o));
          return (
            <button key={o.id || code} type="button" disabled={on}
              onClick={() => onPick(o)}
              title={on ? 'Already in the basket' : 'Add to the basket'}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%',
                textAlign: 'left', border: 0, font: 'inherit', minWidth: 0,
                cursor: on ? 'default' : 'pointer',
                padding: '7px 11px', borderBottom: `1px solid ${BRAND.head}`,
                background: on ? BRAND.greenSoft : '#fff',
                opacity: on ? 0.7 : 1,
              }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  display: 'block', fontSize: 12, fontWeight: 650, color: BRAND.ink,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{o.name || code}</span>
                <span style={{
                  display: 'block', fontSize: 10.5, color: BRAND.inkFaint, ...S.mono,
                }}>{rowLabel ? rowLabel(o) : code}</span>
              </span>
              {/*  The TARIFF, on every row. Applying a G-DRG to 21 claims is applying
                   a price to 21 claims, and the number should be on screen when the
                   choice is made rather than found in the total afterwards. */}
              {o.tariff != null && o.tariff !== '' && (
                <span style={{
                  flexShrink: 0, fontSize: 11, fontWeight: 700, color: BRAND.inkSoft,
                  fontVariantNumeric: 'tabular-nums',
                }}>{Number(o.tariff).toFixed(2)}</span>
              )}
              {on && (
                <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 800,
                  color: BRAND.greenDark }}>IN</span>
              )}
            </button>
          );
        })}
        {/*  ── IMPORTED, BUT NOT BILLABLE ─────────────────────────────────────
             Folded away by default and counted in the header, because these are
             not choices — they are an explanation. What they replace is worse: the
             rows simply were not in the answer, so a test an officer knew had been
             imported was missing with nothing to say why, and the picker looked
             like it had failed to load half the catalogue. */}
        {rows !== null && held.length > 0 && (
          <>
            <button type="button" onClick={() => setShowHeld((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                textAlign: 'left', border: 0, font: 'inherit', cursor: 'pointer',
                padding: '7px 11px', background: '#FDF3E3',
                borderTop: `1px solid ${BRAND.line}`, color: '#8A5510',
                fontSize: 11, fontWeight: 750,
              }}>
              <span style={{ flex: 1 }}>
                {held.length} more imported, not billable
                {term ? ` · ${heldShown.length} match “${q.trim()}”` : ''}
              </span>
              <span>{showHeld ? '−' : '+'}</span>
            </button>
            {showHeld && heldShown.map((o) => (
              <div key={`held-${o.id || o.code || o.name}`}
                title={o.why || 'Cannot be billed'}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 9,
                  padding: '7px 11px', borderBottom: `1px solid ${BRAND.head}`,
                  background: BRAND.canvas || '#F7F8FA', minWidth: 0,
                }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: 'block', fontSize: 12, fontWeight: 600,
                    color: BRAND.inkMuted, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{o.name || '—'}</span>
                  <span style={{
                    display: 'block', fontSize: 10.5, color: BRAND.inkFaint,
                  }}>{o.why || 'Cannot be billed'}</span>
                </span>
                <span style={{
                  flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: '#8A5510',
                  background: '#FDF3E3', borderRadius: 4, padding: '1px 5px',
                }}>NO CODE</span>
              </div>
            ))}
            {showHeld && term && heldShown.length === 0 && (
              <div style={{ padding: '10px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
                None of them match “{q.trim()}”.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── One clinical filter box ─────────────────────────────────────────────────
//  A searchable list of what the MONTH actually contains, with a claim count on
//  every row. Four of these sit side by side — diagnosis, procedure, lab, drug —
//  because they answer one question between them: what is on the claim?
//
//  SEARCHABLE, not a dropdown: 829 diagnoses is not a list anyone scrolls, and the
//  facet rows carry a name AND a code, so typing "malaria", "B54" or the G-DRG all
//  have to find it.
//
//  Multi-select, so "malaria OR typhoid" is one pattern rather than two runs.
function MvFilterBox({ n, label, hint, items, chosen, onSet, rowLabel }) {
  const [q, setQ] = useState('');
  const list = items || [];
  const term = q.trim().toLowerCase();

  const shown = useMemo(() => {
    const hit = term
      ? list.filter((r) => `${r.code || ''} ${r.name || ''} ${r.icd10 || ''}`
        .toLowerCase().includes(term))
      : list;
    //  Chosen rows first, always. Picking one and then typing something else must
    //  not make the pick disappear — that is how a filter gets applied twice.
    const on = hit.filter((r) => chosen.includes(String(r.code)));
    const off = hit.filter((r) => !chosen.includes(String(r.code)));
    return [...on, ...off];
  }, [list, term, chosen]);

  const toggle = (code) => onSet(chosen.includes(code)
    ? chosen.filter((c) => c !== code)
    : [...chosen, code]);

  return (
    <div style={{
      border: `1px solid ${chosen.length ? BRAND.greenDark : BRAND.line}`,
      borderRadius: 10, background: '#fff', display: 'flex', flexDirection: 'column',
      minWidth: 0, overflow: 'hidden',
    }}>
      <div style={{
        padding: '9px 11px', borderBottom: `1px solid ${BRAND.line}`,
        display: 'flex', alignItems: 'center', gap: 8,
        background: chosen.length ? BRAND.greenSoft : '#fff',
      }}>
        <span style={{
          width: 20, height: 20, borderRadius: 6, display: 'grid', placeItems: 'center',
          fontSize: 10.5, fontWeight: 800, flexShrink: 0,
          background: chosen.length ? BRAND.greenDark : BRAND.head,
          color: chosen.length ? '#fff' : BRAND.inkFaint,
        }}>{n}</span>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
          textTransform: 'uppercase',
          color: chosen.length ? BRAND.greenDark : BRAND.inkSoft,
        }}>{label}</span>
        {chosen.length > 0 && (
          <button type="button" onClick={() => onSet([])}
            title={`Clear ${label.toLowerCase()}`}
            style={{
              border: 0, background: 'none', cursor: 'pointer', font: 'inherit',
              fontSize: 11, color: BRAND.greenDark, textDecoration: 'underline',
            }}>{chosen.length} · clear</button>
        )}
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={hint}
        aria-label={`Search ${label.toLowerCase()}`}
        style={{
          border: 0, borderBottom: `1px solid ${BRAND.line}`, padding: '8px 11px',
          font: 'inherit', fontSize: 12, background: '#fff', color: BRAND.ink,
          outline: 'none', minWidth: 0,
        }} />

      {/*  Fills whatever height the box was given, rather than a fixed 176px. All
           four are grid items in one row so they are the same height as each other
           by construction — the point of the fixed height was to line them up, and
           the grid already does that.

           `minHeight: 0` on a flex child that scrolls: without it the child is sized
           by its content, the box grows past its track, and the scrollbar never
           appears. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {list.length === 0 ? (
          <div style={{ padding: '14px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
            Nothing in this month.
          </div>
        ) : shown.length === 0 ? (
          <div style={{ padding: '14px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
            No match for “{q.trim()}”.
          </div>
        ) : shown.slice(0, 250).map((r) => {
          const code = String(r.code);
          const on = chosen.includes(code);
          return (
            <button key={code} type="button" onClick={() => toggle(code)}
              aria-pressed={on}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
                textAlign: 'left', border: 0, cursor: 'pointer', font: 'inherit',
                padding: '6px 11px', borderBottom: `1px solid ${BRAND.head}`,
                background: on ? BRAND.greenSoft : '#fff', minWidth: 0,
              }}>
              <span style={{
                flexShrink: 0, width: 13, height: 13, borderRadius: 3, marginTop: 2,
                display: 'grid', placeItems: 'center', fontSize: 9, color: '#fff',
                border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                background: on ? BRAND.greenDark : '#fff',
              }}>{on ? '✓' : ''}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                {/*  The NAME leads. A code is what you type; a name is what you
                     recognise, and this list is read far more often than typed. */}
                <span style={{
                  display: 'block', fontSize: 12, fontWeight: on ? 750 : 600,
                  color: BRAND.ink, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{r.name || code}</span>
                <span style={{
                  display: 'block', fontSize: 10.5, color: BRAND.inkFaint,
                  ...S.mono,
                }}>{rowLabel ? rowLabel(r) : code}</span>
              </span>
              <span style={{
                flexShrink: 0, fontSize: 10.5, fontWeight: 700, marginTop: 1,
                color: on ? BRAND.greenDark : BRAND.inkFaint,
                fontVariantNumeric: 'tabular-nums',
              }}>{Number(r.claims || 0).toLocaleString()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MV_SECTIONS = [
  { key: 'diagnosis',     label: 'Diagnosis',     facet: 'diagnoses' },
  { key: 'procedure',     label: 'Procedure',     facet: 'procedures' },
  //  Keyed on the code where there is one and on the name otherwise, matching the
  //  facet — most of the investigation catalogue has no G-DRG, so a code-only key
  //  would hide the majority of real tests.
  { key: 'investigation', label: 'Lab / Scan',    facet: 'investigations' },
  { key: 'medication',    label: 'Medication',    facet: 'medications' },
];

//  ── THREE OPERATIONS ────────────────────────────────────────────────────────
//  There was a fourth, Update, and it is gone. It could not change a CODE — the
//  code was the match key, so "update I10" found the I10 row and wrote I10 back
//  over it — and every claim that did not already carry the code came back
//  `skipped`. That is the reported "it updated some and not others": nothing was
//  broken, the operation just never meant what its name implied. Override now does
//  what people were reaching for Update to do.
const MV_OPS = [
  { key: 'add',      label: 'Add',
    hint: 'Add the entry you choose below to every selected claim. A claim that already has it is left as it is, never doubled.' },
  { key: 'override', label: 'Override',
    hint: 'Replace what you filtered these claims by with the entry you choose below. A claim that does not carry it simply gets the new one.' },
  { key: 'delete',   label: 'Delete',
    hint: 'Remove the entry you choose below from every selected claim. Nothing is added.' },
];

//  Which advanced-filter field backs each section. Override replaces what the
//  claims were FILTERED by, so this is the link between the dashboard's filter and
//  the target of the run.
//
//  Lab / Scan has no entry on purpose: the advanced filter has no investigation
//  field, so there is nothing to replace and Override is not offered there.
const MV_FILTER_KEY = {
  diagnosis: 'diagnosis',
  procedure: 'procedure',
  medication: 'medication',
};

//  ── THE FOUR ACTS ───────────────────────────────────────────────────────────
//
//  Add / Update / Override said almost nothing on their own: same length, same shape,
//  and three near-synonyms in English. What separates them is WHAT THEY TOUCH and
//  WHETHER THEY REMOVE ANYTHING — so each carries that as a second line.
//
//  Declared here so the cards, the hint under them and the composer's own headings all
//  read from one place. Three copies of "Override / Delete" would drift, and the one
//  that drifted would be the one describing the operation that destroys data.
const MV_ACTIONS = [
  {
    key: 'add',
    label: 'Add',
    what: 'Diagnosis · Procedure · Lab · Medication',
    hint: 'Every claim in the group gains it. One that already has it is left as it '
        + 'is, never doubled.',
  },
  {
    key: 'update',
    label: 'Update by matching',
    what: 'This entry becomes that one',
    hint: 'Pick the entries these patients already carry, then map each to its '
        + 'replacement. A claim that does not carry one is left alone — it is not '
        + 'given the replacement.',
  },
  {
    key: 'override',
    label: 'Override / Delete',
    what: 'Replace it — or take it off',
    hint: 'The same matching as Update, and one thing more: an entry mapped to '
        + 'NOTHING is removed from every claim carrying it. That is the delete.',
  },
  {
    key: 'service',
    label: 'Change the service',
    what: 'This service becomes that one',
    hint: 'The G-DRG the visit is billed on — what "Select Service" sets on one claim. '
        + 'Changing the service changes that G-DRG, because they are the same thing. '
        + 'Mapped, because a group can hold several different services and "change it '
        + 'to X" means something different for each. Claims with no service at all are '
        + 'their own row.',
  },
  {
    key: 'attendance',
    label: 'Type of attendance',
    what: 'Emergency · Chronic · Antenatal · Postnatal',
    hint: 'Sets the NHIA type of attendance on every ticked claim at once. A claim '
        + 'that already carries the code is left alone.',
  },
  {
    key: 'template',
    label: 'Apply a template',
    what: 'A whole condition template at once',
    hint: 'The condition, its labs, its drugs and its procedures together, from the '
        + 'Condition Templates library. Every claim is checked against ITS OWN patient '
        + 'first — the real date of birth, gender and attendance — and the ones it does '
        + 'not suit are named and skipped rather than stopping the run.',
  },
];

//  ── WHICH ACTS PICK FROM THE FOUR CATALOGUES ────────────────────────────────
//  Add, Update and Override all build a basket out of the diagnosis / procedure / lab /
//  drug lists. The service has its own single catalogue, the attendance is a header
//  field, and a template is chosen from the template library — none of the three has
//  anything to search for in those four.
//
//  A SET, like MV_NEEDS_TARGET below, and for exactly the same reason: this was written
//  as `action !== 'attendance' && action !== 'service'`, so the next act added would have
//  shown an officer four scrolling catalogues it had no use for and nothing to say why.
const MV_USES_CATALOGUES = new Set(['add', 'update', 'override']);

//  ── AND WHICH ACT CARRIES ITS OWN APPLY ─────────────────────────────────────
//  The whole-picture card holds the one Apply for every act that writes through
//  /claims/multivet/batch. A template does not: it writes through
//  /claims/template-apply, whose answer is a verdict PER CLAIM rather than a tally per
//  basket, so it brings its own card and its own button. Named here so the card's gate
//  reads as a rule rather than as one more clause in an OR-chain.
const MV_OWN_APPLY = new Set(['template']);

//  ── WHICH ACTS NEED SOMETHING TICKED FIRST ──────────────────────────────────
//  Update and Override act on entries the patients ALREADY have, picked in step 2, so
//  there is nothing to open until something is ticked. Add, the service and the
//  attendance go straight through.
//
//  A SET, not a hand-written condition. That condition has been the same bug three
//  times: an act was added, the list of names was not updated, and the officer chose it
//  and had nothing to press. Everything not named here goes through by default, so the
//  next act added is reachable without anyone remembering this line.
const MV_NEEDS_TARGET = new Set(['update', 'override']);

//  What step 2/3 is called, per act. One place, so the heading cannot say "Add what" on
//  a screen that changes a service.
const MV_STEP_LABEL = {
  add: 'Add what',
  update: 'Change it to',
  override: 'Replace it, or remove it',
  service: 'Which service',
  attendance: 'Which attendance',
  template: 'Which template',
};

//  And what the button through to the composer says. Same reason: three hand-written
//  ternaries on `action` were three places to forget a new act.
const MV_OPEN_LABEL = {
  add: 'Choose what to add',
  update: 'Choose what to apply',
  override: 'Choose what to apply',
  service: 'Choose the services',
  attendance: 'Choose the attendance',
  template: 'Choose the template',
};

//  The four NHIA codes, with the claim editor's own wording so the two screens agree.
//  claimStore.ATTENDANCE_CODES is the same list on the server side, and readAttendance
//  refuses anything outside it.
const MV_ATTENDANCE = [
  ['EAE', 'Emergency / Acute'],
  //  CFU, not CHR — see ATTENDANCE_CODES in claimStore.js. Keyed on the code, so
  //  a stale key here shows the bare code where the words belong.
  ['CFU', 'Chronic follow-ups'],
  ['ANC', 'Antenatal'],
  ['PNC', 'Postnatal'],
];

//  The group's contents are keyed by the plural the selection endpoint uses; the
//  writers are keyed by the singular. One place to cross between them, because a
//  mapping row has to know both — which list it came out of, and which catalogue can
//  replace it.
const MV_TARGET_KIND = {
  diagnoses: 'diagnosis',
  procedures: 'procedure',
  investigations: 'investigation',
  medications: 'medication',
};

//  'diagnoses:B54' → { facet, kind, code }. The code can contain a colon on nothing
//  in this catalogue, but splitting on the FIRST one costs nothing and removes the
//  question.
const mvParseTarget = (key) => {
  const s = String(key || '');
  const at = s.indexOf(':');
  if (at < 0) return null;
  const facet = s.slice(0, at);
  const kind = MV_TARGET_KIND[facet];
  return kind ? { facet, kind, code: s.slice(at + 1) } : null;
};

//  ── ONE RUN, AS MANY PASSES AS IT TAKES ─────────────────────────────────────
//
//  /claims/multivet/batch refuses more than MAX_CLAIMS = 200 claims in a request,
//  and that limit is right: each claim is a read plus one write per basket entry, so
//  200 is already tens of seconds and 1,894 would be a request nobody's proxy keeps
//  open.
//
//  But 200 is a limit on a REQUEST, not on the day's work. "Apply to everyone I
//  ticked" is one act to the officer, so the screen splits it into passes and
//  reports the total. Sequential, never parallel: ten concurrent passes would open
//  ten connections and hold ten transactions against the same month.
const MV_RUN_CHUNK = 200;

const mvChunk = (list, size = MV_RUN_CHUNK) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

//  Folds one pass's answer into the running total.
//
//  THE PASSES ARE DISJOINT — each holds claims no other pass holds — which is what
//  makes plain addition correct here. Rejects, warnings, claims touched and rows
//  written all sum. `wouldTouch` sums for the same reason: it counts claims, and no
//  claim is in two passes.
//
//  Conflicts do NOT sum. A basket entry that does not suit the patients is one fact
//  about that entry, reported by every pass that sees it, so they are unioned on
//  kind+code. That matters for more than tidiness: the guard has to consider the
//  WHOLE selection before the first write, or a mixed cohort could pass its
//  all-adult passes, write to them, and only refuse when it reached the children.
function mvMergePass(agg, data) {
  agg.passes += 1;
  agg.claims += Number(data?.claims || 0);
  agg.wouldTouch += Number(data?.wouldTouch || 0);
  agg.claimsTouched += Number(data?.claimsTouched || 0);
  agg.writes += Number(data?.writes || 0);
  agg.failed += Number(data?.failed || 0);

  for (const c of data?.conflicts || []) {
    const key = `${c.kind}:${String(c.code).toUpperCase()}`;
    if (!agg.conflictSeen.has(key)) {
      agg.conflictSeen.add(key);
      agg.conflicts.push(c);
    }
  }

  for (const k of data?.perKind || []) {
    const at = agg.perKind.get(k.kind) || {
      kind: k.kind, label: k.label, codes: k.codes,
      wouldChange: 0, alreadyHad: 0, changed: 0, failed: 0,
    };
    at.wouldChange += Number(k.wouldChange || 0);
    at.alreadyHad += Number(k.alreadyHad || 0);
    at.changed += Number(k.changed || 0);
    at.failed += Number(k.failed || 0);
    agg.perKind.set(k.kind, at);
  }

  //  The mapped changes, one line per PAIR — keyed on kind AND the entry being
  //  replaced, so two rows changing different diagnoses stay two rows. Collapsing them
  //  by kind would report "diagnosis: 34" across three pairs and lose which pair found
  //  how many patients, which is the number the officer mapped them by.
  for (const s of data?.perSwap || []) {
    const key = `${s.kind}:${String(s.from).toUpperCase()}`;
    const at = agg.perSwap.get(key) || {
      kind: s.kind, label: s.label, from: s.from, to: s.to, toName: s.toName,
      wouldChange: 0, notCarried: 0, merged: 0, changed: 0, failed: 0,
    };
    at.wouldChange += Number(s.wouldChange || 0);
    at.notCarried += Number(s.notCarried || 0);
    at.merged += Number(s.merged || 0);
    at.changed += Number(s.changed || 0);
    at.failed += Number(s.failed || 0);
    agg.perSwap.set(key, at);
  }

  //  The service changes, one line per mapping — keyed on the service being REPLACED,
  //  because two rows changing different services stay two rows. `(none)` is its own
  //  key: the claims with no service are a cohort, not a missing value.
  //
  //  This was missed when the attendance merge was written, and on a group over 200
  //  claims — which is every group this was asked for — the whole service report was
  //  dropped: `mvSettle` returned no perService at all, so the footer and the run's own
  //  summary lost it. The merge and the settle have to move together.
  for (const s of data?.perService || []) {
    const key = s.from === null || s.from === undefined ? '(none)' : String(s.from).toUpperCase();
    const at = agg.perService.get(key) || {
      from: s.from ?? null, to: s.to,
      wouldChange: 0, notCarried: 0, changed: 0, failed: 0, blocked: 0,
    };
    at.wouldChange += Number(s.wouldChange || 0);
    at.notCarried += Number(s.notCarried || 0);
    at.changed += Number(s.changed || 0);
    at.failed += Number(s.failed || 0);
    at.blocked += Number(s.blocked || 0);
    agg.perService.set(key, at);
  }

  //  The attendance, summed the same way. One code for the whole run, so only its
  //  counts move — and they sum because the passes hold different claims.
  if (data?.perAttendance) {
    const a = data.perAttendance;
    const at = agg.perAttendance || {
      code: a.code, label: a.label,
      wouldChange: 0, alreadyRight: 0, changed: 0, failed: 0,
    };
    at.wouldChange += Number(a.wouldChange || 0);
    at.alreadyRight += Number(a.alreadyRight || 0);
    at.changed += Number(a.changed || 0);
    at.failed += Number(a.failed || 0);
    agg.perAttendance = at;
  }

  //  A dry run reports `issues` — the starting position. A real one reports
  //  `issues.before` and `issues.after`. Both are summed into the same shape so the
  //  screen reads one pair of numbers whether it ran once or ten times.
  const tally = (into, from) => {
    if (!from) return into;
    const t = into || { rejects: 0, warnings: 0, clean: 0 };
    return {
      rejects: t.rejects + Number(from.rejects || 0),
      warnings: t.warnings + Number(from.warnings || 0),
      clean: t.clean + Number(from.clean || 0),
    };
  };
  if (data?.issues && data.issues.before) {
    agg.before = tally(agg.before, data.issues.before);
    agg.after = tally(agg.after, data.issues.after);
  } else if (data?.issues) {
    agg.before = tally(agg.before, data.issues);
  }
  return agg;
}

const mvAgg = () => ({
  passes: 0, claims: 0, wouldTouch: 0, claimsTouched: 0, writes: 0, failed: 0,
  conflicts: [], conflictSeen: new Set(), perKind: new Map(), perSwap: new Map(),
  perAttendance: null, perService: new Map(),
  before: null, after: null,
});

//  The aggregate in the shape the screen and a single pass already share, so nothing
//  downstream has to know whether it took one request or ten.
const mvSettle = (agg) => ({
  ok: true,
  passes: agg.passes,
  claims: agg.claims,
  wouldTouch: agg.wouldTouch,
  claimsTouched: agg.claimsTouched,
  writes: agg.writes,
  failed: agg.failed,
  conflicts: agg.conflicts,
  perKind: [...agg.perKind.values()],
  perSwap: [...agg.perSwap.values()],
  perAttendance: agg.perAttendance,
  perService: [...agg.perService.values()],
  issues: agg.before ? { before: agg.before, after: agg.after } : null,
});

//  ── THE ROUTINE'S NAME IS THE PATTERN THAT BUILT IT ─────────────────────────
//
//  Nobody should be asked to type a name for something the screen already knows.
//  "Name this routine" with an empty box and a placeholder is a question the officer
//  cannot answer better than the pattern can: the pattern IS the name —
//  out-patient, male, under one, malaria.
//
//  `summary` is patternSummary — the same array of chips shown in the dialog header
//  and folded into the pattern card. Using it rather than re-deriving from the
//  pattern object means the saved name is exactly the sentence that was on screen
//  when it was saved, and the two cannot drift into disagreeing.
//
//  DETERMINISTIC ON PURPOSE. The same pattern and basket produce the same name, and
//  the endpoint keys on (name, facility) — so running the same routine again updates
//  one row instead of leaving a shelf of near-identical copies.
const MV_NAME_MAX = 155;   // the column is VARCHAR(160)

const mvRoutineName = (summary, basket) => {
  const bits = (summary || []).filter(Boolean).map(String);

  //  A pattern of nothing but "Out-patient" is a name that would collide with every
  //  other out-patient routine, so what the routine GIVES is added to tell them
  //  apart. Only then: when the pattern names a diagnosis, that is already the
  //  clearest thing about it and the basket would only make the line longer.
  //
  //  Two words is where "narrow enough to name itself" begins — attendance plus one
  //  more decision.
  if (bits.length < 2 && (basket || []).length) {
    const first = basket[0].name || basket[0].code;
    bits.push(basket.length === 1 ? first : `${first} +${basket.length - 1}`);
  }

  const name = bits.join(' · ').replace(/\s+/g, ' ').trim();
  if (!name) return '';
  //  Cut on a separator rather than mid-word, so a truncated name still reads as a
  //  list of parameters instead of a sentence that stops.
  if (name.length <= MV_NAME_MAX) return name;
  const cut = name.slice(0, MV_NAME_MAX);
  const at = cut.lastIndexOf(' · ');
  return (at > 20 ? cut.slice(0, at) : cut.trim()) + ' …';
};

// ── Which scheme the composer prices on ─────────────────────────────────────
//  A tariff belongs to ONE insurer. The same diagnosis, procedure, test and drug
//  cost different amounts under different schemes, and the four catalogues are
//  keyed on the scheme for exactly that reason.
//
//  This used to be the string 'NHIS', hardcoded in two places. It happens to be
//  right today — all 27,003 claims in this database carry scheme_name 'NHIS' — and
//  it is wrong the moment one does not: the officer would be offered NHIS rows at
//  NHIS rates for another insurer's claim, with nothing on screen to say so. The
//  facility already prices ten other schemes in lab_prices.
//
//  So it comes from the schemes ticked on the month card. `insurances` is [] when
//  none are ticked, which means "every scheme in the month" — and that is not a
//  tariff the composer can use, because there is no single correct price. In that
//  case NOTHING is offered and the card says which button to press, rather than
//  quietly pricing everything on one insurer's book.
const composerScheme = (insurances) =>
  (Array.isArray(insurances) && insurances.length === 1 ? insurances[0] : null);

function MultiVet({ claims, year, month, facility, insurances, filter, onClose, onDone }) {
  // ── WHERE THE GROUP COMES FROM ─────────────────────────────────────────────
  //
  //  TWO WAYS IN, and the officer picks by what they did before pressing the
  //  button — not by answering a question here.
  //
  //    SELECTION  rows were ticked in the table. That IS the group. The dialog
  //               opens straight on those claims with every one already ticked,
  //               and the pattern is not asked for at all.
  //
  //    PATTERN    nothing was ticked. Describe a group — this age band, this
  //               diagnosis — and the dialog goes and finds it.
  //
  //  The pattern was previously the only way in, and it ignored the ticks
  //  entirely. An officer who had already narrowed the month in the Advanced
  //  Filter, picked a specialty and ticked fifteen patients pressed Multi Vet and
  //  landed on an empty pattern — their fifteen were counted on the button and
  //  then thrown away, and the only way forward was to describe the same group
  //  again in different words and hope it came back the same. That is the
  //  behaviour this removes.
  //
  //  Read ONCE, at open, through a lazy initialiser: the dialog is mounted fresh
  //  each time it is opened, and re-reading `claims` afterwards would flip the
  //  mode under the officer when the table reloads behind the dialog.
  const preselected = useMemo(
    () => (claims || []).filter((c) => c.source === 'imported'),
    [claims]
  );

  //  This dialog holds more unwritten work than anything else in the workspace —
  //  the pattern, the ticked claims, the basket, the mappings — and a click on the
  //  backdrop used to discard all of it. It does not close any more.
  const { panelRef, backdropProps } = useDialogGuard();
  const [source, setSource] = useState(() => (preselected.length ? 'selection' : 'pattern'));
  //  How many were ticked when it opened, kept for the wording even after the
  //  officer narrows the group further.
  const [openedWith] = useState(() => preselected.length);

  //  NOTHING pre-chosen and nothing pre-expanded. Landing with Diagnosis already
  //  active picked the user's target for them on a screen that edits many claims
  //  at once — the one place a wrong default is worst.
  const [section, setSection] = useState(null);
  const [operation, setOperation] = useState('add');
  const [picked, setPicked] = useState([]);          // [{code, name}]
  const [q, setQ] = useState('');
  const [facets, setFacets] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [result, setResult] = useState(null);      // stays after a run; dialog does not close
  const [sel, setSel] = useState(null);            // what the chosen claims already contain
  //  Accepting the first-day-of-visit rule for in-patient medication writes.
  //  Reset whenever the section or operation changes, so it can never carry over.
  const [ipAck, setIpAck] = useState(false);
  const [selOpen, setSelOpen] = useState(false);   // folded; the header says how many
  const [openClaim, setOpenClaim] = useState(null);
  //  { frequencies, routes } — fetched once, only when medication is the section.
  //  { frequencies, routes, byCode } — byCode is the catalogue keyed on drug code,
  //  which is what lets a picked medication arrive with its dose unit already in
  //  place instead of blank.
  const [rxVocab, setRxVocab] = useState({ frequencies: [], routes: [], byCode: new Map() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  //  How far through a multi-pass run we are — null when there is nothing running or
  //  when the run fits in a single pass. See MV_RUN_CHUNK.
  const [progress, setProgress] = useState(null);

  //  ── WHICH RUN'S ANSWER COUNTS, AND WHICH RUN OWNS `busy` ──────────────────
  //  Two separate questions, and conflating them deadlocks the screen.
  //
  //  `runToken` says whose RESULT is still wanted: it is bumped whenever the basket
  //  or the ticks change, and a pass that comes back holding an older token throws
  //  its answer away rather than enabling Apply against a plan for something else.
  //
  //  `activeRun` says who clears the spinner. It stays with the run that set `busy`,
  //  even after that run has been superseded — otherwise an invalidation would leave
  //  `busy` true with nobody left to turn it off, and the dialog would sit on
  //  "Checking…" for the rest of the session.
  const runToken = useRef(0);
  const activeRun = useRef(0);

  //  Only imported claims have the child tables these writers touch. A typed-in
  //  claim in the selection is reported rather than carried silently into the run.
  //
  //  DECLARED HERE, ABOVE THE ROUTINE STATE, and that position is load-bearing.
  //  `const` bindings are in a temporal dead zone until their declaration runs, and
  //  checkBasket below lists `ids` in its dependency array — which is evaluated
  //  during render, not when the callback fires. With this pair further down the
  //  component the whole dialog threw "Cannot access 'ids' before initialization"
  //  on mount. Same class of fault as the note above orderedKeys in ClaimsMenu.
  const ids = claims.filter((c) => c.source === 'imported').map((c) => c.id);
  const typedIn = claims.length - ids.length;

  //  ── THE PATTERN ───────────────────────────────────────────────────────────
  //  Multi Vet used to work on whatever was TICKED in the table behind it, which
  //  made it a bulk editor: the officer did the finding, the dialog did the writing.
  //
  //  A pattern is the other way round. You describe a group — this age band, this
  //  gender, later this diagnosis or procedure — and the dialog goes and finds them.
  //  That is what makes it vetting rather than editing: the same treatment applies
  //  to everyone who matches, so the group is the unit of work, not the tick-box.
  //
  //  Built up one filter at a time. Age band and gender first because they are the
  //  ones that decide which TARIFF is correct — a child and an adult with the same
  //  diagnosis are billed under different G-DRGs, so narrowing by them is what makes
  //  a single routine safe to apply to the whole group.
  //  MultiVet's own table cell style. NOT the `cell` used by the claims table —
  //  that one is declared inside ClaimsMenu and is invisible here, which is exactly
  //  what tests/frontend-imports.test.js caught: a JSX file happily parses with an
  //  out-of-scope identifier and only fails at render.
  const mvCell = {
    padding: '8px 12px', fontSize: 12.5, color: BRAND.ink,
    borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
  };

  //  Age is a FROM/TO pair, not a band key. Three fixed bands could not answer
  //  "the 5s to the 9s" or "over 70", and a vetting pattern is only as useful as the
  //  groups it can describe — the bands below are shortcuts INTO these two numbers,
  //  the same relationship the Advanced Filter's age panel has.
  const [pattern, setPattern] = useState({
    //  ── NO "ALL" ON THE ATTENDANCE ──────────────────────────────────────────
    //  A routine is written for one setting or the other. An out-patient claim is
    //  billed on its service and has no stay; an admission is billed on a diagnosis
    //  or a procedure and needs drugs across its days. There is no treatment pattern
    //  that is correct for both, so a group spanning them is a group no single
    //  routine can be applied to — and offering "All" invited exactly that.
    //
    //  Defaults to out-patient because that is the bulk of a month: 1,894 of 2,383
    //  on the data in front of us.
    type: 'opd', specialty: 'all', ageFrom: '', ageTo: '', gender: 'all',
    //  ── LENGTH OF STAY, IN DAYS ─────────────────────────────────────────────
    //  In-patient only, and it is what makes a common treatment pattern safe to
    //  apply: a three-day admission needs three days of drugs. Grouping a one-night
    //  stay with a week-long one and giving them the same regimen produces a claim
    //  NHIA queries either way — too much on one, too little on the other.
    //
    //  '' means any length. Counted INCLUSIVELY, so the 1st to the 3rd is 3 days,
    //  matching med_short_of_stay and procedureDates.
    stayDays: '',
    //  ── What is ON the claim ────────────────────────────────────────────────
    //  Arrays, because "malaria OR typhoid" is a real group to vet, and each maps
    //  to a filter claimStore.CHILD_FILTERS already supports — so no new server
    //  parameter is invented for any of them.
    //
    //  Ordered diagnosis → procedure → lab → medication throughout: that is the
    //  order a claim is BUILT in, so it is the order an officer reads one in.
    diagnosis: [], procedure: [], investigation: [], medication: [],
    //  ── HOW the four boxes below match ──────────────────────────────────────
    //  'any'  the claim carries at least one of what is ticked, and may carry
    //         anything else besides. "Who had malaria" — including the patient who
    //         also had typhoid.
    //  'only' the claim carries one of them and NOTHING ELSE of that kind. "Who
    //         had malaria and only malaria" — which is the group a single regimen
    //         is actually safe for, because the patient who also had typhoid needs
    //         more than the routine provides.
    match: 'any',
  });
  //  What the SERVER returned for steps 1 and 3. Specialty is narrowed from this in
  //  the browser — see `found` below — so the chips can carry live counts.
  //
  //  In SELECTION mode this starts as the ticked rows and the search below never
  //  runs, so the group is exactly what was ticked. The rows are the same shape
  //  either way — both come from /claimsdetails — so everything downstream
  //  (the contents fetch, the specialty chips, the composer) is unchanged.
  const [rawFound, setRawFound] = useState(
    () => (preselected.length ? preselected : null));
  const [finding, setFinding] = useState(false);
  //  The pattern folds away once it is set. Describing the group is a minute's work;
  //  reading the claims and applying to them is the rest of the session, so the
  //  filters should not keep a third of the panel for the whole of it.
  const [patternOpen, setPatternOpen] = useState(true);
  //  'pattern' | 'claims'. Two views taking turns in one frame rather than two
  //  dialogs — and the pattern is set ASIDE, not unmounted, so coming Back finds
  //  every filter as it was left. A pattern is a dozen small decisions; rebuilding
  //  it would make going Back something to avoid.
  //  'pattern' | 'claims' | 'compose'. Three pages in one dialog, in the order the
  //  work happens: describe the group, choose who and what act, then build the thing
  //  being applied. Each is set aside rather than unmounted, so Back finds every
  //  earlier decision as it was left.
  //
  //  Compose is its own page because the four catalogue pickers it holds are the same
  //  controls the single-claim editor uses, and those do not fit beside a table —
  //  which is exactly what the right-hand column had become.
  //  Opens on the CLAIMS when the group came from the table. There is no pattern to
  //  describe, so a pattern page would be a step that exists only to be skipped.
  const [view, setView] = useState(() => (preselected.length ? 'claims' : 'pattern'));
  //  One step back, never a jump. compose → claims → pattern, so the way out is the
  //  reverse of the way in and Back always means the same thing.
  //
  //  From the claims view in SELECTION mode there is no pattern behind it, so Back
  //  means "describe a group instead" — it switches the source, which starts the
  //  search. Anything else would either dead-end or silently drop the selection.
  const goBack = () => {
    if (view === 'claims' && source === 'selection') {
      setSource('pattern');
      setRawFound(null);
      setView('pattern');
      return;
    }
    setView((v) => (v === 'compose' ? 'claims' : 'pattern'));
  };
  //  NOTE: the `contents` fetch, kidsOf and groupStats live BELOW `found`, not here.
  //  They name it in a dependency array, which is evaluated during render, so placing
  //  them above its declaration is a temporal-dead-zone ReferenceError on mount —
  //  "Cannot access 'found' before initialization". Third time in this component.
  //  17 specialties is two ragged rows. The common ones are shown and the tail is
  //  behind a count, so the row stays one line and nothing is unreachable.
  const [allSpecs, setAllSpecs] = useState(false);

  //  ONE definition of where childhood ends, and it is not written here. The
  //  boundary is claimRules.ADULT_FROM = 12, which the tariff catalogue is banded
  //  by; a second copy of "12" in this file is how a picker and a price list come to
  //  disagree about what a child is. Expressed as the age BOUNDS the server already
  //  accepts, so no new parameter is invented either.
  //  ── The age shortcuts ─────────────────────────────────────────────────────
  //  Each one just fills in the two boxes, so anything they can express by hand is
  //  still reachable and these are only the common cases. The same list the
  //  Advanced Filter offers, so a pattern built here and a filter built there mean
  //  the same thing by "Child".
  //
  //  'Child (under 12)' and 'Adult (12+)' are the two that matter most: 12 is
  //  claimRules.ADULT_FROM, the boundary the tariff catalogue itself is banded by,
  //  which is what makes them the bands a ROUTINE can safely be built against.
  const AGE_BANDS = [
    { label: 'Under 1', from: '', to: '0' },
    { label: '1–4', from: '1', to: '4' },
    { label: '5–11', from: '5', to: '11' },
    { label: 'Child (under 12)', from: '', to: '11' },
    { label: 'Adult (12+)', from: '12', to: '' },
    { label: '18–59', from: '18', to: '59' },
    { label: '60 and over', from: '60', to: '' },
  ];
  //  ── NO "NOT RECORDED" ─────────────────────────────────────────────────────
  //  I argued for it: a claim with no gender is a real cohort NHIA queries, and a
  //  picker offering only male and female makes those the one group nobody can find.
  //  That is true of a SEARCH and wrong here.
  //
  //  This screen exists to apply a treatment routine, and a claim with no gender
  //  recorded is not a claim to prescribe against — it is one to go and complete
  //  first. Grouping those patients to give them a shared regimen would be building
  //  on a record that is not finished. Finding them is the Advanced Filter's job,
  //  where the gender filter still offers all three.
  const GENDERS = [
    { key: 'all', label: 'Any' },
    { key: 'M', label: 'Male' },
    { key: 'F', label: 'Female' },
  ];

  //  Runs itself as the pattern changes, debounced. A "Find" button would make the
  //  count something you have to ask for, and the count IS the feedback — it is how
  //  you know the pattern says what you meant.
  useEffect(() => {
    //  SELECTION mode has its group already — the rows that were ticked. Running
    //  the pattern search here would overwrite them with whatever an empty pattern
    //  matches, which is the whole month, and the officer's fifteen would be gone
    //  a beat after the dialog opened.
    if (source === 'selection') { setFinding(false); return undefined; }
    let alive = true;
    setFinding(true);
    const t = setTimeout(() => {
      axios.get(claimsdetails, {
        params: {
          year, month, facility,
          insurance_type: insurances?.length ? insurances.join(',') : 'all',
          //  Attendance is a HEADER column, so the server can narrow on it.
          //  Always sent: there is no 'all' any more, so the attendance is
          //  never absent from the question.
          type: pattern.type,
          //  Sent as typed. A blank box is dropped rather than sent empty, so "from
          //  5, no upper bound" is a bound the server sees and an empty pair is no
          //  age filter at all — never "from birth", which Number('') === 0 would
          //  have made it.
          ageFrom: String(pattern.ageFrom).trim() !== '' ? pattern.ageFrom : undefined,
          ageTo: String(pattern.ageTo).trim() !== '' ? pattern.ageTo : undefined,
          gender: pattern.gender !== 'all' ? pattern.gender : undefined,
          //  In-patient only — an out-patient claim has no stay to be N days long.
          stayDays: pattern.type === 'ipd' && String(pattern.stayDays).trim() !== ''
            ? pattern.stayDays : undefined,
          //  Comma-joined codes. Several in one box means "any of these".
          diagnosis: pattern.diagnosis.length ? pattern.diagnosis.join(',') : undefined,
          procedure: pattern.procedure.length ? pattern.procedure.join(',') : undefined,
          investigation: pattern.investigation.length
            ? pattern.investigation.join(',') : undefined,
          medication: pattern.medication.length ? pattern.medication.join(',') : undefined,
          matchMode: pattern.match !== 'any' ? pattern.match : undefined,
          //  Specialty is deliberately NOT sent. The endpoint has no parameter for
          //  it — the claims list narrows its own specialty pills in the browser —
          //  so this does the same, which also lets the chips carry live counts
          //  instead of being a fixed list of codes that may not be in the month.
        },
      })
        .then(({ data }) => {
          if (!alive) return;
          //  Imported claims only. The writers this dialog drives touch the
          //  normalised child tables; a typed-in claim keeps its diagnoses
          //  elsewhere and cannot be vetted this way, so it is left out of the
          //  match rather than counted and then silently skipped.
          setRawFound((data?.claims || []).filter((c) => c.source === 'imported'));
        })
        .catch(() => { if (alive) setRawFound([]); })
        .finally(() => { if (alive) setFinding(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern.type, pattern.stayDays, pattern.ageFrom, pattern.ageTo, pattern.gender,
      //  Stringified: these are arrays, and a new array with the same contents is a
      //  new reference every render, which would re-fetch forever.
      pattern.diagnosis.join(','), pattern.procedure.join(','),
      pattern.investigation.join(','), pattern.medication.join(','), pattern.match,
      //  `source` is in here so that switching from the selection to a pattern
      //  actually runs the search — without it the effect would not re-fire and the
      //  pattern page would sit on an empty group.
      source,
      year, month, facility]);

  //  ── Step 2's own vocabulary ───────────────────────────────────────────────
  //  Built from what steps 1 and 3 actually returned, so a specialty with nothing
  //  in it is not offered at all. A fixed row of OPDC / MEDI / PAED / OBGY would
  //  offer chips that find nothing, which reads as a broken filter rather than as
  //  an empty month.
  //
  //  Normalised to 'UNSPECIFIED' rather than dropped: claims with no specialty are a
  //  real group and one somebody has to deal with.
  const specialties = useMemo(() => {
    const counts = new Map();
    for (const c of rawFound || []) {
      const k = String(c.attendance || '').trim().toUpperCase() || 'UNSPECIFIED';
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [rawFound]);

  //  Step 2 applied. Kept separate from `rawFound` so the chips above can still
  //  show every specialty's count while one of them is selected — a count that
  //  dropped to zero on everything you did not pick would be describing the
  //  selection rather than the month.
  const found = useMemo(() => {
    if (rawFound === null) return null;
    if (pattern.specialty === 'all') return rawFound;
    return rawFound.filter((c) =>
      (String(c.attendance || '').trim().toUpperCase() || 'UNSPECIFIED') === pattern.specialty);
  }, [rawFound, pattern.specialty]);

  //  ── The pattern as a sentence ─────────────────────────────────────────────
  //  What the header shows once the steps are folded away. Only the parts that are
  //  SET — an unset step contributes nothing rather than the word "All", because a
  //  summary reading "All · All · Any age · Any" says nothing and takes a line to
  //  say it.
  const patternSummary = useMemo(() => {
    const bits = [];
    //  Always in the summary, because it is always chosen.
    bits.push(pattern.type === 'ipd' ? 'In-patient' : 'Out-patient');
    if (pattern.specialty !== 'all') {
      bits.push(pattern.specialty === 'UNSPECIFIED' ? 'Unspecified' : pattern.specialty);
    }
    //  Right after the attendance it belongs to, so the folded summary reads
    //  "In-patient · 3 days" rather than putting them at opposite ends.
    if (pattern.type === 'ipd' && String(pattern.stayDays).trim() !== '') {
      const d = Number(pattern.stayDays);
      bits.push(`${d} day${d === 1 ? '' : 's'}`);
    }
    const f = String(pattern.ageFrom).trim();
    const t = String(pattern.ageTo).trim();
    if (f || t) {
      //  Named where a shortcut matches, so the summary reads "Adult (12+)" rather
      //  than "12+ years" — the officer picked a band, so the band is what they
      //  should be shown back.
      const band = AGE_BANDS.find((b) => b.from === f && b.to === t);
      bits.push(band ? band.label
        : (f && t ? `${f}–${t} years` : f ? `${f}+ years` : `under ${Number(t) + 1} years`));
    }
    if (pattern.gender !== 'all') {
      bits.push((GENDERS.find((g) => g.key === pattern.gender) || {}).label);
    }
    //  Card 2's filters belong in this summary too. They live in a separate card, so
    //  scrolling past it is easy — and a pattern narrowed to one drug that does not
    //  say so is the one most likely to be applied to the wrong group.
    //
    //  Named, not counted: "Malaria" is what was picked; "1 diagnosis" is a fact
    //  about the picking.
    const named = (key, list) => {
      const codes = pattern[key];
      if (!codes.length) return;
      const nameOfCode = (code) => {
        const r = (list || []).find((x) => String(x.code) === String(code));
        return (r && r.name) || code;
      };
      bits.push(codes.length === 1
        ? nameOfCode(codes[0])
        : `${nameOfCode(codes[0])} +${codes.length - 1}`);
    };
    //  Said before the items it qualifies, so the folded summary reads
    //  "Only · Malaria" rather than leaving the mode at the far end of the line.
    //  Absent when it is 'any', which is the default and needs no announcing.
    if (pattern.match !== 'any'
      && (pattern.diagnosis.length || pattern.procedure.length
        || pattern.investigation.length || pattern.medication.length)) {
      bits.push({ all: 'All of', only: 'Only', exactly: 'Exactly' }[pattern.match]);
    }
    named('diagnosis', facets?.diagnoses);
    named('procedure', facets?.procedures);
    named('investigation', facets?.investigations);
    named('medication', facets?.medications);

    return bits.filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern, facets]);

  //  What each found claim already CONTAINS, keyed by id. One request for the whole
  //  group — the selection endpoint answers in five queries whatever the size, so
  //  this is far cheaper than a detail fetch per row, and it carries the items
  //  themselves rather than only counts. That is what lets a row both show "Dx 2"
  //  and expand to say which two.
  const [contents, setContents] = useState(null);
  const [openRows, setOpenRows] = useState(() => new Set());

  //  Fetched when the group is OPENED, not while the pattern is being built — the
  //  counts are only read on the claims view, and re-requesting them on every
  //  keystroke in a filter box would be a request per keystroke for nothing.
  useEffect(() => {
    if (view !== 'claims' || !found || found.length === 0) return undefined;
    let alive = true;
    setContents(null);
    setOpenRows(new Set());
    //  ── FETCHED IN CHUNKS OF 200 ────────────────────────────────────────────
    //  The selection endpoint caps at MAX_CLAIMS = 200 and silently returns the
    //  first 200. On a 1,272-patient group that left 1,072 rows with no data — and
    //  because "no data" and "no diagnosis" both render as 0, the totals reported
    //  1,072 patients with No diagnosis beside a Diagnoses total of 288. Every one
    //  of those numbers was wrong, and the alarming one was wrong in the direction
    //  that gets acted on.
    //
    //  Chunked rather than capped, so the answer covers the whole group. Seven
    //  requests for 1,272 claims, sequential so a big group cannot open seven
    //  connections at once.
    const ids = found.map((c) => Number(c.id));
    (async () => {
      const m = new Map();
      try {
        for (let i = 0; i < ids.length; i += 200) {
          const { data } = await axios.post(claimsMultiVetSelection,
            { claimIds: ids.slice(i, i + 200) });
          if (!alive) return;
          for (const c of (data?.claims || [])) m.set(Number(c.id), c);
          //  Published as each chunk lands, so the first rows fill in immediately
          //  rather than the table sitting on dashes until the last request returns.
          setContents(new Map(m));
        }
      } catch {
        //  Whatever arrived stays; the rest read as unknown rather than as empty.
        if (alive) setContents(new Map(m));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, found]);

  //  The four child lists, whatever the endpoint calls them. Read defensively
  //  because the selection payload nests them under `children` on one path and flat
  //  on another, and a missing array must read as "none" rather than throwing on a
  //  table of 200 rows.
  const kidsOf = (rec, key) => (rec
    ? ((rec[key] || (rec.children && rec.children[key])) || [])
    : []);

  //  ── WHAT TO DO WITH THEM ──────────────────────────────────────────────────
  //  'add' | 'update' | 'override'. Update and Override act on something the
  //  patients ALREADY have, so their pickers offer what is present in this group
  //  rather than the whole catalogue — you cannot update a diagnosis nobody has.
  const [action, setAction] = useState(null);
  const [target, setTarget] = useState([]);      // ['diagnoses:B54', …]

  //  ── THE MAPPING: THIS BECOMES THAT ────────────────────────────────────────
  //
  //  An update is PAIRS, not two lists. "Replace these three diagnoses with these
  //  three diagnoses" has no single meaning once there is more than one of either —
  //  and the officer maps them by hand precisely because only they know which becomes
  //  which. So each ticked entry gets its own replacement.
  //
  //  Keyed on the same 'diagnoses:B54' string the ticks use, so a target that is
  //  un-ticked takes its mapping with it and cannot come back mapped to something
  //  chosen for a different row.
  const [mapping, setMapping] = useState({});    // { 'diagnoses:B54': {kind, code, …} }
  //  The NHIA type of attendance to put on every ticked claim. '' means not chosen.
  //  A header field with exactly one value per claim, so it is SET rather than mapped —
  //  "replace this attendance with that one" has nothing to map.
  const [attendance, setAttendance] = useState('');

  //  ── THE SERVICE, MAPPED ───────────────────────────────────────────────────
  //  { 'OPDC06A': 'ZOOM04A', '(none)': 'OPDC06A' } — the service a claim carries now,
  //  to the one it should carry. Mapped rather than set, because a group of out-patient
  //  claims can hold half a dozen services and "change it to X" is a different
  //  instruction for each of them.
  //
  //  '(none)' is its own row: the claims carrying no service at all, which is the
  //  Unspecified group and the reason this operation was asked for.
  const [serviceTo, setServiceTo] = useState({});
  //  Which service row the next pick from the list fills, same idea as activeTarget.
  const [activeService, setActiveService] = useState(null);
  //  The out-patient / in-patient service catalogue, from the same endpoint the
  //  single-claim editor's "Select Service" reads — so the options offered here are
  //  exactly the ones that editor would accept.
  const [serviceOptions, setServiceOptions] = useState([]);
  const [serviceNote, setServiceNote] = useState(null);
  //  Which row the next pick fills. Without it, a click in the diagnosis box on a
  //  screen with three unmapped diagnoses has three possible meanings.
  const [activeTarget, setActiveTarget] = useState(null);

  //  ── FINDING ONE ENTRY AMONG THOUSANDS ─────────────────────────────────────
  //  A month of 4,511 claims carries 1,317 distinct diagnoses. The list showed all
  //  four kinds stacked, every row of every one, which is a column nobody can navigate
  //  and several thousand DOM nodes to build before it can even be scrolled.
  //
  //  So: one kind at a time, and a search within it. `ticked` is a fifth view rather
  //  than a sixth control — after filtering to Medication, the diagnoses already chosen
  //  are off screen, and "what have I picked so far" is the question that follows.
  const [targetKind, setTargetKind] = useState('diagnoses');
  const [targetQ, setTargetQ] = useState('');

  //  ── TICK AN ENTRY, SEE WHO HAS IT ─────────────────────────────────────────
  //  "Change of Catheter — 2 of 4,511" is a number you cannot act on. WHICH two, and
  //  what do their claims look like? That question came up the moment the entry was
  //  ticked, and the answer was already on screen — buried on page one of two hundred
  //  and twenty-three.
  //
  //  A VIEW FILTER, and nothing more. It narrows the table; it does NOT change who the
  //  run writes to. That distinction is the whole safety of it: an update only touches
  //  claims carrying the entry anyway — the swap skips the rest and says so — so
  //  filtering the table changes what you can see and not what will happen. Making it
  //  alter the ticked patients would silently drop the other mapped entries' claims out
  //  of the same run.
  const [focusTarget, setFocusTarget] = useState(null);   // 'diagnoses:B54' | null
  //  The table's own scroller. Moving rows to the top is no help to somebody who is
  //  four hundred rows down — the block would arrive off screen above them.
  const rowsScrollRef = useRef(null);

  //  ── WHICH OF THE GROUP TO ACT ON ──────────────────────────────────────────
  //  The pattern finds a group; this is the part of it being worked on. They are not
  //  the same thing: a pattern of 42 is often the right group to LOOK at and the
  //  wrong one to write to in one pass — half of them today, the rest after a
  //  check.
  //
  //  Named `chosenIds`, not `picked`: `picked` is already the section picker's
  //  selection in this same component, and two selections one word apart is how the
  //  wrong one gets read.
  //
  //  Everything starts ticked, so the default is what the panel did before this
  //  existed — narrowing is the deliberate act, not selecting.
  const [chosenIds, setChosenIds] = useState(() => new Set());
  //  A new group is a new selection. Carrying ticks across patterns would apply a
  //  routine to claims chosen against a different question.
  useEffect(() => {
    setChosenIds(new Set((found || []).map((c) => Number(c.id))));
  }, [found]);

  const foundIds = useMemo(() => (found || []).map((c) => Number(c.id)), [found]);
  const chosenCount = chosenIds.size;

  //  ── WHO THE RUN WRITES TO ─────────────────────────────────────────────────
  //
  //  THE BUG THIS EXISTS TO FIX. The composer's dry run and its Apply both read
  //  `ids` — the rows TICKED IN THE TABLE BEHIND THE DIALOG, which is how Multi Vet
  //  chose its claims before the pattern existed. Multi Vet is now opened with
  //  nothing ticked, on purpose, so `ids` was empty on every real use: checkBasket
  //  returned at its own guard, `batchPlan` stayed null, and the footer sat on
  //  "Checking what this would do…" with Apply greyed out forever. Nothing was
  //  broken behind the button; the button was never reachable.
  //
  //  The target is the patients ticked IN THE PATTERN. Taken in the table's own
  //  order — not the Set's — so "the first 10" writes to the first ten rows as
  //  displayed, and intersected with the group so a tick left over from a previous
  //  pattern can never be written to.
  const targetIds = useMemo(
    () => foundIds.filter((id) => chosenIds.has(id)),
    [foundIds, chosenIds]
  );
  //  Stable across renders, for dependency arrays — a fresh array every render would
  //  re-fire the dry run forever.
  const targetKey = targetIds.join(',');
  const targetCount = targetIds.length;

  //  Quick ways to take part of the group. `first`/`last` read the table's own order,
  //  so "the last 10" means the last 10 rows as displayed rather than by some id
  //  ordering nobody can see.
  const takeNone = () => setChosenIds(new Set());
  const takeAll = () => setChosenIds(new Set(foundIds));
  const takeFirst = (n) => setChosenIds(new Set(foundIds.slice(0, Math.max(0, n))));
  const takeLast = (n) => setChosenIds(new Set(n > 0 ? foundIds.slice(-n) : []));
  const [takeN, setTakeN] = useState('');

  //  Everything the group carries, per kind, with how many patients hold each. Built
  //  from the contents already fetched for the table — no extra request — so the
  //  list is exactly what is on these claims and the counts say how much of the
  //  group each entry would affect.
  //
  //  Keyed by kind AND code, because the same code can appear under two kinds and
  //  a bare code would collapse them into one selection.
  const present = useMemo(() => {
    const kinds = ['diagnoses', 'procedures', 'investigations', 'medications'];
    const out = {};
    for (const k of kinds) out[k] = new Map();
    //  ── THIS RETURNS ARRAYS, ALWAYS ─────────────────────────────────────────
    //  The Maps are working storage; the tally below converts them at the end. This
    //  early return did NOT, so while the group's contents were still loading every
    //  consumer got a Map where it expected an array.
    //
    //  It went unnoticed because the two readers at the time both happened to survive
    //  it: `rows.length` on a Map is undefined, which is falsy, so the claims list
    //  rendered nothing and looked like it was waiting. The mapping rows call `.find`,
    //  which is not a method on a Map, and the whole dialog threw.
    //
    //  There was a second, quieter casualty: the effect below iterates these to drop
    //  targets that no longer exist. Iterating a Map yields [key, value] PAIRS, so
    //  `r.code` was undefined and every ticked entry was filtered away — the selection
    //  silently emptied itself on any render where contents had not arrived.
    if (!contents) {
      for (const k of kinds) out[k] = [];
      return out;
    }
    //  Built from the SELECTED claims only. Offering an entry that belongs to a
    //  patient who is not ticked would put it in front of the officer as something
    //  the run could change, when the run will never touch it — and the "10 of 42"
    //  beside it would be counting people the action excludes.
    for (const [id, rec] of contents.entries()) {
      if (!chosenIds.has(Number(id))) continue;
      for (const k of kinds) {
        //  Once per CLAIM, not once per row: a drug given on four days is one
        //  patient carrying it, and counting rows would say four.
        const seen = new Set();
        for (const r of kidsOf(rec, k)) {
          const code = String(r.code || r.name || '').trim();
          if (!code || seen.has(code)) continue;
          seen.add(code);
          const cur = out[k].get(code)
            || { code, name: r.name || null, patients: 0, gdrgs: new Set() };
          cur.patients += 1;
          if (!cur.name && r.name) cur.name = r.name;
          //  ── THE SECOND CODE A DIAGNOSIS CARRIES ─────────────────────────
          //  Which G-DRG these claims are billing this ICD-10 under. Collected
          //  because "Malaria" alone cannot say: the catalogue holds it as
          //  OPDC06A for a consultation and MEDI28A for an admission, and telling
          //  those apart is the difference between a no-op and the correction.
          //
          //  A SET, not one value: the ticked claims can legitimately disagree —
          //  some Malaria on OPDC06A, some already on MEDI28A — and collapsing
          //  that to the first one seen would make the guard's answer depend on
          //  which claim happened to be read first.
          if (r.gdrg) cur.gdrgs.add(String(r.gdrg).trim().toUpperCase());
          out[k].set(code, cur);
        }
      }
    }
    for (const k of kinds) {
      //  The Set becomes a sorted array on the way out — everything downstream
      //  reads these as plain data, and a Set in a prop is a Set nobody can
      //  compare, serialise or render.
      out[k] = [...out[k].values()]
        .map((r) => ({ ...r, gdrgs: [...r.gdrgs].sort() }))
        .sort((a, b) => b.patients - a.patients);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contents, chosenIds]);

  //  ── THE FOUR KINDS, AS TABS ───────────────────────────────────────────────
  //  Built here rather than in the render so the counts, the default and the search
  //  all read the same numbers. A kind nobody in the group has is still LISTED, with a
  //  zero — hiding it made the tabs move about as the ticked patients changed, and a
  //  control that relocates under the cursor is worse than one that is greyed.
  const TARGET_TABS = [
    ['diagnoses', 'Diagnosis'],
    ['procedures', 'Procedure'],
    ['investigations', 'Lab / Scan'],
    ['medications', 'Medication'],
  ];

  //  Land on a kind the group actually carries. Defaulting to Diagnosis on a group with
  //  no diagnoses shows an empty list beside three tabs that have rows.
  useEffect(() => {
    if (!contents) return;
    const rows = present[targetKind];
    if (Array.isArray(rows) && rows.length) return;
    const first = TARGET_TABS.find(([k]) => (present[k] || []).length);
    if (first) setTargetKind(first[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contents, present, targetKind]);

  //  What step 2 actually shows: one kind, narrowed by the search, or every ticked
  //  entry across all four. Matched on NAME AND CODE — an officer types "malaria", and
  //  another types "B54", and both are how these are known.
  const TARGET_SHOWN = 60;
  const targetList = useMemo(() => {
    const q = targetQ.trim().toLowerCase();
    const hit = (r) => !q || `${r.code} ${r.name || ''}`.toLowerCase().includes(q);
    if (targetKind === 'ticked') {
      //  Across every kind, in the order they were ticked — that is the order they
      //  appear as mapping rows in the composer, so the two read the same.
      const out = [];
      for (const key of target) {
        const p = mvParseTarget(key);
        if (!p) continue;
        const rows = Array.isArray(present[p.facet]) ? present[p.facet] : [];
        const row = rows.find((r) => String(r.code) === p.code)
          || { code: p.code, name: p.code, patients: null };
        if (hit(row)) out.push({ ...row, facet: p.facet, key });
      }
      return { rows: out, total: out.length };
    }
    const rows = (Array.isArray(present[targetKind]) ? present[targetKind] : [])
      .filter((r) => hit(r))
      .map((r) => ({ ...r, facet: targetKind, key: `${targetKind}:${r.code}` }));
    return { rows, total: rows.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [present, targetKind, targetQ, target]);

  //  ── THE ROWS, WITH THE CARRIERS BROUGHT TO THE TOP ────────────────────────
  //
  //  NOT A FILTER. Nothing is removed — the claims carrying the focused entry are moved
  //  to the front, together, so "these are those patients" can be read at a glance with
  //  the rest of the group still underneath.
  //
  //  That difference matters. Ten claims out of 4,511 are unfindable but they are not
  //  the only thing on the officer's mind: the group is still the group, the totals
  //  still describe all of it, and scrolling past the ten reaches everybody else.
  //  Hiding 4,501 rows to show ten answers a narrower question than the one asked.
  //
  //  It also makes the 200-row DOM cap stop mattering for this: the ten are at the top,
  //  so they are always rendered, however deep in the group they sit.
  //
  //  Read from `contents`, which is fetched for EVERY claim in the pattern rather than
  //  only the two hundred rendered — so an entry two claims carry finds both.
  const focusRow = focusTarget ? mvParseTarget(focusTarget) : null;
  const { tableRows, focusCount } = useMemo(() => {
    const all = found || [];
    if (!focusRow || !contents) return { tableRows: all, focusCount: 0 };
    const carries = (c) => {
      const rec = contents.get(Number(c.id));
      if (!rec) return false;
      return kidsOf(rec, focusRow.facet)
        .some((r) => String(r.code || r.name || '').trim() === focusRow.code);
    };
    const hit = [];
    const rest = [];
    //  Stable: each half keeps the group's own order, so the rows do not shuffle among
    //  themselves as well as moving.
    for (const c of all) (carries(c) ? hit : rest).push(c);
    return { tableRows: hit.concat(rest), focusCount: hit.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, contents, focusTarget]);

  //  The focused entry's own name, for the line above the table. Its code is not enough
  //  — "ZOOM04A" says nothing about what is being looked at.
  const focusName = useMemo(() => {
    if (!focusRow) return null;
    const rows = Array.isArray(present[focusRow.facet]) ? present[focusRow.facet] : [];
    const hit = rows.find((r) => String(r.code) === focusRow.code);
    return hit?.name || focusRow.code;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, present]);

  //  A new group is a new question. Carrying a focus across patterns would reorder the
  //  new table by an entry the new group may not even contain.
  useEffect(() => { setFocusTarget(null); }, [found]);

  //  ── AND TAKE THE OFFICER THERE ────────────────────────────────────────────
  //  Bringing rows to the top is no use to somebody scrolled four hundred rows down:
  //  the block lands above them, off screen, and the table looks unchanged. So the
  //  scroller goes back to the top whenever the focus changes.
  //
  //  Two frames, not one: React commits the reordered rows in the first callback's
  //  frame, and setting scrollTop before that paint is setting it on the old list.
  useEffect(() => {
    if (!focusTarget) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (rowsScrollRef.current) rowsScrollRef.current.scrollTop = 0;
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [focusTarget]);

  //  A target chosen and then filtered away must not stay selected — it would be
  //  applied to a group that no longer contains it.
  useEffect(() => {
    if (!target.length) return;
    //  ── NOT WHILE THE CONTENTS ARE UNKNOWN ──────────────────────────────────
    //  `present` is empty both when the group genuinely carries nothing and when the
    //  contents have not arrived yet. Only the first is a reason to drop a tick.
    //  Without this guard, every refetch — which is exactly what a successful run
    //  triggers — emptied the selection and the mapping with it, mid-session, with no
    //  message.
    if (!contents) return;
    const live = new Set();
    for (const [k, rows] of Object.entries(present)) {
      for (const r of (Array.isArray(rows) ? rows : [])) live.add(`${k}:${r.code}`);
    }
    const kept = target.filter((t) => live.has(t));
    if (kept.length !== target.length) {
      setTarget(kept);
      //  And the mapping goes with whatever left, or a replacement stays attached to
      //  an entry the group no longer has.
      setMapping((m) => {
        const next = {};
        for (const k of kept) if (m[k]) next[k] = m[k];
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [present, contents]);

  //  ── WHAT THESE CLAIMS ALREADY HAVE ────────────────────────────────────────
  //  The pattern's own clinical filters, as a fixed list. If the group was found by
  //  "diagnosis = Malaria", then every patient in it HAS malaria — that is not a
  //  guess, it is the definition of the group, and it belongs at the top of the
  //  composer as the structure being built on.
  //
  //  FIXED, with no remove control. Taking one away here would not change the
  //  claims, it would change the GROUP — which is done on the pattern page, behind
  //  Back. A ✕ that silently re-ran the search from a page about writing would be
  //  the same control meaning two different things.
  //
  //  Names come from the facets already loaded for the pattern's own boxes, so this
  //  costs no request.
  const patternItems = useMemo(() => {
    const kinds = [
      ['diagnosis', 'Diagnosis', facets?.diagnoses],
      ['procedure', 'Procedure', facets?.procedures],
      ['investigation', 'Lab / Scan', facets?.investigations],
      ['medication', 'Medication', facets?.medications],
    ];
    const out = [];
    for (const [key, label, list] of kinds) {
      for (const code of (pattern[key] || [])) {
        const row = (list || []).find((r) => String(r.code) === String(code));
        out.push({ kind: key, label, code, name: (row && row.name) || null });
      }
    }
    return out;
  }, [pattern, facets]);

  //  ── The group, in totals ──────────────────────────────────────────────────
  //  What the whole pattern contains, under the table. A group is chosen so ONE
  //  treatment can be applied to all of it, and these totals are how you tell
  //  whether that is true yet: 13 patients with 13 diagnoses and 0 medications is a
  //  group waiting for a regimen; 13 and 117 is one that already has one.
  const groupStats = useMemo(() => {
    //  THE WHOLE GROUP, always. Focusing an entry reorders the table; it removes
    //  nothing, so every one of these totals still describes every claim in the pattern
    //  and none of them should move when the focus does.
    const rows = found || [];
    const s = {
      claims: rows.length, ipd: 0, opd: 0,
      dx: 0, pr: 0, inv: 0, med: 0,
      noDx: 0, noMed: 0, amount: 0,
      //  How many of the group the contents are actually known for. The child
      //  totals below describe THESE, not the whole group — so the strip can say so
      //  instead of implying a count it does not have.
      known: 0,
    };
    for (const c of rows) {
      if (String(c.type || '').toUpperCase() === 'IPD') s.ipd += 1; else s.opd += 1;
      s.amount += Number(c.amount || 0);
      const rec = contents ? contents.get(Number(c.id)) : null;
      //  A claim whose contents have not arrived is SKIPPED, not counted as empty.
      //  Counting it reported 1,072 patients with no diagnosis on a group where the
      //  data for 1,072 of them had simply not been fetched.
      if (!rec) continue;
      s.known += 1;
      const d = kidsOf(rec, 'diagnoses').length;
      const p = kidsOf(rec, 'procedures').length;
      const i = kidsOf(rec, 'investigations').length;
      const m = kidsOf(rec, 'medications').length;
      s.dx += d; s.pr += p; s.inv += i; s.med += m;
      if (d === 0) s.noDx += 1;
      if (m === 0) s.noMed += 1;
    }
    return s;
  }, [found, contents]);

  //  A specialty that stops existing when steps 1 or 3 change must not stay
  //  selected — the count would read 0 with no way to see why.
  useEffect(() => {
    if (pattern.specialty === 'all') return;
    if (!specialties.some(([k]) => k === pattern.specialty)) {
      setPattern((p) => ({ ...p, specialty: 'all' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialties]);

  //  ── THE BASKET ────────────────────────────────────────────────────────────
  //  Treatment is a ROUTINE: a diagnosis, its labs, its drugs, sometimes a
  //  procedure. They belong together and none of them means much alone.
  //
  //  The old screen worked one SECTION at a time, so applying a malaria routine to
  //  214 claims meant running this dialog FOUR times — re-picking the cohort,
  //  re-reading a preview and re-confirming on each pass. Four chances to select
  //  the wrong claims, and four chances to forget the fourth item. The basket is one
  //  pass over mixed kinds: what every claim in the group should HAVE.
  //
  //  There was a `mode` beside this — `routine` for the basket, `single` for
  //  Add/Override/Delete on one section. The single panel is no longer rendered and
  //  `mode` was never set to anything but 'routine', so the flag is gone. What it
  //  gated is not: the dry run's guard read `mode !== 'routine'`, which was harmless
  //  only because the value could not change.
  const [basket, setBasket] = useState([]);          // [{ kind, code, name, ... }]
  //  Medications in the basket that are not yet a complete prescription. Every one
  //  of the six fields the store validates has to be filled, or the line is refused
  //  on every claim it is written to.
  const rxIncomplete = basket.filter((b) => b.kind === 'medication'
    && MV_RX_FIELDS.some(([f]) => !String(b[f] ?? '').trim()));

  const [routines, setRoutines] = useState([]);
  const [batchPlan, setBatchPlan] = useState(null);  // the dry run for the basket
  const [savingRoutine, setSavingRoutine] = useState(false);

  //  The saved routines for this facility. Fetched once — a routine library changes
  //  when somebody saves one, and that path refreshes it itself.
  useEffect(() => {
    let alive = true;
    axios.get(claimsMultiVetRoutines, { params: { facility } })
      .then(({ data }) => { if (alive) setRoutines(data?.routines || []); })
      .catch(() => { if (alive) setRoutines([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  Moving what is picked into the basket. Deliberately a step of its own rather
  //  than adding on selection: the medication validation below runs against
  //  `picked`, so a drug reaches the basket only once its dose, frequency and
  //  quantity are complete. An incomplete item in a saved routine would be a
  //  mistake that repeats every month.
  const addToBasket = () => {
    if (!section || !picked.length) return;
    setBasket((b) => {
      const seen = new Set(b.map((x) => `${x.kind}:${String(x.code).toUpperCase()}`));
      const next = [...b];
      for (const p of picked) {
        const key = `${section}:${String(p.code).toUpperCase()}`;
        if (!seen.has(key)) { next.push({ ...p, kind: section }); seen.add(key); }
      }
      return next;
    });
    setPicked([]);
    setBatchPlan(null);
  };

  const dropFromBasket = (kind, code) => {
    setBasket((b) => b.filter(
      (x) => !(x.kind === kind && String(x.code) === String(code))));
    setBatchPlan(null);
  };

  //  Loading a routine replaces the basket outright rather than merging. Merging
  //  two routines is not a thing anyone means to do, and a half-merged basket is
  //  the kind of state nobody notices until it has been applied.
  //  Nothing calls this at the moment: the shelf that did was taken off this panel and
  //  will be rebuilt somewhere of its own. Kept rather than deleted because it is the
  //  half of the routine feature that is hard to get right — a load REPLACES the basket
  //  outright, and rediscovering why would be the whole argument again.
  // eslint-disable-next-line no-unused-vars
  const loadRoutine = (r) => {
    setBasket(r.items || []);
    setPicked([]);
    setSection(null);
    setBatchPlan(null);
    setError(null);
  };

  //  ── THE MAPPING, RESOLVED ─────────────────────────────────────────────────
  //  Every ticked entry as a row: where it came from, what it is called, how many of
  //  the ticked patients carry it, and what it has been mapped to.
  //
  //  DECLARED ABOVE checkBasket, which names `replacements` in its dependency array —
  //  evaluated during render, so a declaration below it throws on mount. Third time
  //  this component has taught that lesson.
  const targetRows = useMemo(() => target.map((key) => {
    const p = mvParseTarget(key);
    if (!p) return null;
    //  Array.isArray rather than `|| []`. The fallback only catches a MISSING facet;
    //  it happily passes through anything truthy, which is how a Map reached `.find`
    //  here and threw on mount. A row that cannot be found still produces a mapping
    //  row named by its code — the entry is ticked, so it is being changed whether or
    //  not the group's contents have arrived to describe it.
    const rows = Array.isArray(present[p.facet]) ? present[p.facet] : [];
    const row = rows.find((r) => String(r.code) === p.code);
    //  ── ONE SLOT, TWO ANSWERS ───────────────────────────────────────────────
    //  A mapping row is either "becomes THIS" or "goes". `{ remove: true }` is the
    //  second, stored in the same slot so a row can only ever hold one of them — the
    //  officer cannot pick a replacement AND mark it for deletion.
    const picked = mapping[key] || null;
    const drop = !!(picked && picked.remove);
    return {
      key, ...p,
      name: row?.name || p.code,
      patients: row?.patients ?? null,
      //  What the ticked claims are billing this entry under, so "is the pick the
      //  same as what is already there" can be asked of the CODE as well as the
      //  name. Empty for the kinds whose own code is the billing code.
      gdrgs: row?.gdrgs || [],
      to: drop ? null : picked,
      drop,
    };
  }).filter(Boolean), [target, present, mapping]);

  //  A row with nothing chosen yet — neither a replacement nor a deletion. Apply waits
  //  for these: a half-mapped update would change some of what was ticked and quietly
  //  leave the rest, which reads as the run having failed on those claims rather than
  //  as a row never filled in.
  const unmapped = targetRows.filter((r) => !r.to && !r.drop);

  //  A replacement drug needs the same six fields the store validates, for the same
  //  reason a new one does — it is a written row either way.
  const swapRxIncomplete = targetRows.filter((r) => r.to && r.kind === 'medication'
    && MV_RX_FIELDS.some(([f]) => !String(r.to[f] ?? '').trim()));

  //  Landing on the composer with rows to map and nothing selected means the first
  //  pick has no destination and silently becomes an addition instead. So the first
  //  unmapped row is armed on arrival, and again whenever the last one is filled and
  //  another is un-ticked back into existence.
  useEffect(() => {
    if (view !== 'compose') return;
    if (activeTarget && targetRows.some((r) => r.key === activeTarget)) return;
    const next = targetRows.find((r) => !r.to);
    setActiveTarget(next ? next.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, targetRows, activeTarget]);

  //  What goes on the wire. Complete pairs, and — under Override only — rows marked to
  //  be REMOVED, which carry no replacement on purpose. `remove` is explicit rather
  //  than inferred from a missing `to`, because the server must be able to tell "delete
  //  it" from "the officer never finished choosing", and guessing wrong deletes.
  const replacements = useMemo(() => targetRows
    .filter((r) => r.to || r.drop)
    .map((r) => (r.drop
      ? { kind: r.kind, from: r.code, remove: true }
      : { kind: r.kind, from: r.code, to: r.to })), [targetRows]);
  const replacementKey = replacements
    .map((r) => `${r.kind}:${r.from}>${r.remove ? '(removed)' : r.to.code}`).join('|');

  //  ── WHAT SERVICES THIS GROUP CARRIES ──────────────────────────────────────
  //  Built from `found` rather than from `contents`: the service is a HEADER field and
  //  every row in the claims list already carries it, so there is nothing to fetch. A
  //  claim with none is counted under '(none)' — its own row, not an omission.
  const serviceRows = useMemo(() => {
    const byCode = new Map();
    for (const c of found || []) {
      if (!chosenIds.has(Number(c.id))) continue;
      const code = String(c.service_code || '').trim().toUpperCase() || null;
      const key = code === null ? '(none)' : code;
      const at = byCode.get(key) || {
        key, code, name: code ? (c.service || null) : null, patients: 0,
      };
      at.patients += 1;
      if (!at.name && c.service) at.name = c.service;
      byCode.set(key, at);
    }
    return [...byCode.values()]
      .sort((a, b) => b.patients - a.patients)
      .map((r) => ({ ...r, to: serviceTo[r.key] || null }));
  }, [found, chosenIds, serviceTo]);

  //  Only the rows the officer actually mapped travel. A group of six services where
  //  two were mapped is a legitimate run: the other four are left as they are.
  const serviceMap = useMemo(() => serviceRows
    .filter((r) => r.to)
    //  The LABEL travels with the code, so the server's guards can name the service in a
    //  refusal. "OPDC06A cannot be given to male patients" tells nobody why.
    .map((r) => ({ from: r.code, to: r.to.code, toName: r.to.label || null })),
  [serviceRows]);
  const serviceKey = serviceMap.map((m) => `${m.from || '(none)'}>${m.to}`).join('|');

  //  ── WHO IS IN THIS GROUP, IN THE TWO WAYS A SERVICE CARES ─────────────────
  //  General OPD - Adult is OPDC06A and the Child one is OPDC06C; antenatal cannot be
  //  given to a man. Read from the CLAIMS — the pattern's age and gender steps are both
  //  optional, so the only trustworthy answer is the rows themselves.
  const cohort = useMemo(() => {
    let child = 0;
    let adult = 0;
    let male = 0;
    let female = 0;
    for (const c of found || []) {
      if (!chosenIds.has(Number(c.id))) continue;
      //  Age at the VISIT, not today — a claim from March is billed on the age the
      //  patient was in March, and an infant born in August crosses no boundary but a
      //  twelve-year-old does.
      const dob = c.dob ? new Date(c.dob) : null;
      const at = c.visit_date || c.date || null;
      const on = at ? new Date(at) : new Date(c.dob || 0);
      if (dob && !Number.isNaN(dob.getTime()) && !Number.isNaN(on.getTime())) {
        const years = (on - dob) / (365.25 * 86400000);
        if (years >= 12) adult += 1; else child += 1;
      }
      const g = String(c.gender || '').trim().toUpperCase().slice(0, 1);
      if (g === 'M') male += 1; else if (g === 'F') female += 1;
    }
    return {
      child, adult, male, female,
      //  'adult' | 'child' | 'mixed' | null. Mixed is the case with no safe single
      //  answer, and it is common: a group filtered only by specialty holds both.
      band: (adult && child) ? 'mixed' : (adult ? 'adult' : (child ? 'child' : null)),
      hasMale: male > 0,
    };
  }, [found, chosenIds]);

  //  The catalogue, from the endpoint the single-claim editor uses. Keyed on the
  //  pattern's attendance AND on the cohort's age band, so an out-patient group of
  //  children is offered the Child services — the same narrowing that editor does per
  //  claim, applied to the group.
  //
  //  A MIXED group is sent no age at all: there is no age that is right for both, and
  //  narrowing to one would quietly hide the services the other half needs. The list
  //  stays whole and the server's band guard refuses the wrong choice, which is the
  //  honest order — offer everything, refuse what cannot apply, say why.
  useEffect(() => {
    if (view !== 'compose' || action !== 'service') return undefined;
    let alive = true;
    cachedGet(claimStoreServices, {
      attendance: pattern.type === 'ipd' ? 'inpatient' : 'outpatient',
      age: cohort.band === 'adult' ? 30 : cohort.band === 'child' ? 5 : undefined,
    })
      .then((res) => {
        if (!alive) return;
        setServiceOptions(res?.options || []);
        setServiceNote(res?.note || null);
      })
      .catch(() => {
        if (!alive) return;
        setServiceOptions([]);
        setServiceNote('The service list could not be loaded.');
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, action, pattern.type, cohort.band]);

  //  Arm the first unmapped service row on arrival, for the same reason the mapping
  //  rows are armed: the first pick otherwise has no destination.
  useEffect(() => {
    if (view !== 'compose' || action !== 'service') return;
    if (activeService && serviceRows.some((r) => r.key === activeService)) return;
    const next = serviceRows.find((r) => !r.to);
    setActiveService(next ? next.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, action, serviceRows, activeService]);

  //  ── SAVING A ROUTINE ──────────────────────────────────────────────────────
  //
  //  The name is not asked for. It is the pattern — see mvRoutineName. Every detail a
  //  name could carry has already been set on this screen, and an empty box beside a
  //  placeholder reading "e.g. Malaria — Child" asked the officer to summarise their
  //  own work in words the screen could have written better.
  //
  //  The cohort travels with it because a routine is only correct for the patients it
  //  was written for: "Malaria · 1–4" names a paediatric tariff, and a shelf that
  //  cannot say who a routine is for is a shelf that invites applying it to anybody.
  //
  //  DECLARED HERE, above applyBasket, because applyBasket saves the routine itself
  //  once the run succeeds. Same reason `ids` sits at the top of this component.
  const routineName = useMemo(
    () => mvRoutineName(patternSummary, basket),
    [patternSummary, basket]
  );

  //  One write, used by the Save button and by the quiet save after a run.
  //
  //    `premium`  is only ever raised here — the endpoint keeps the higher of the two
  //               with GREATEST — so applying a promoted routine cannot demote it.
  //    `applied`  is sent by the RUN and by nothing else. It is what puts a routine on
  //               the shelf, so it has to mean the basket went onto real claims.
  //
  //  Returns what the server says became of it, not just the name: the Save button
  //  needs to tell the officer whether they will see this again, and that is the
  //  endpoint's rule to state rather than the screen's to guess.
  const putRoutine = useCallback(async (opts = {}) => {
    if (!basket.length || !routineName) return null;
    const { data } = await axios.post(claimsMultiVetRoutines, {
      name: routineName,
      items: basket,
      //  The PATTERN, not the dashboard's advanced filter. The pattern is how these
      //  claims were found, so it is what says who the routine is for.
      cohort: { ...pattern, summary: patternSummary },
      facility: facility || null,
      premium: opts.premium === true,
      applied: opts.applied === true,
      actor: (() => {
        try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || undefined; }
        catch { return undefined; }
      })(),
    });
    const list = await axios.get(claimsMultiVetRoutines, { params: { facility } });
    setRoutines(list.data?.routines || []);
    return { name: routineName, onShelf: data?.onShelf !== false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket, routineName, pattern, patternSummary, facility]);

  //  The dry run for a basket. Same guarantee as the single-section path: nothing
  //  is written until the server has said what would happen.
  //
  //  Chunked at MV_RUN_CHUNK, because the whole selection is the question. Answering
  //  it for the first 200 and calling that the plan would put a number on screen that
  //  describes part of the run and reads as all of it.
  const checkBasket = useCallback(async () => {
    //  Either half is a run on its own: a basket with no mapped changes, or an update
    //  with an empty basket. This guard used to demand a basket, so an officer who only
    //  mapped changes got the permanent "Checking…" all over again.
    if ((!basket.length && !replacements.length && !attendance && !serviceMap.length) || !targetIds.length) return;
    //  Every run carries a token. The basket can change while ten passes are in
    //  flight, and a stale answer landing on `batchPlan` would enable Apply against
    //  a plan for a basket that no longer exists.
    const token = (runToken.current += 1);
    activeRun.current = token;
    const stale = () => token !== runToken.current;

    setBusy(true); setError(null); setBatchPlan(null);
    const passes = mvChunk(targetIds);
    const agg = mvAgg();
    try {
      for (let i = 0; i < passes.length; i += 1) {
        const { data } = await axios.post(claimsMultiVetBatch, {
          claimIds: passes[i], items: basket, replacements,
          attendance: attendance || undefined, serviceMap,
          dryRun: true,
        });
        if (stale()) return;
        mvMergePass(agg, data);
        //  Progress only while there is more than one pass — "checked 200 of 200" is
        //  noise, and the footer already says what it is doing.
        if (passes.length > 1) {
          setProgress({
            phase: 'check', done: agg.claims, total: targetIds.length,
            pass: i + 1, passes: passes.length,
          });
        }
      }
      if (stale()) return;
      setBatchPlan(mvSettle(agg));
    } catch (e) {
      if (!stale()) setError(e?.response?.data?.error || e.message);
    } finally {
      if (activeRun.current === token) { setBusy(false); setProgress(null); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket, targetKey, replacementKey, attendance, serviceKey]);

  //  Runs itself once the basket is non-empty, for the same reason the
  //  single-section preview does: a greyed-out Apply beside a complete form reads
  //  as a broken dialog rather than as a step outstanding.
  //
  //  `targetKey` is in here as well as the basket: changing WHO is ticked changes
  //  the answer just as much as changing WHAT is being applied, and a plan for 42
  //  claims sitting above an Apply button aimed at 300 is the worst version of this
  //  screen. checkBasket clears the plan itself, so a re-tick re-checks.
  useEffect(() => {
    if ((!basket.length && !replacements.length && !attendance && !serviceMap.length) || !targetCount) return undefined;
    if (batchPlan || busy || error) return undefined;
    const t = setTimeout(() => { checkBasket(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket, targetKey, replacementKey, attendance, serviceKey, targetCount, batchPlan, busy, error]);

  //  A change to the MAPPING or to the attendance invalidates the plan the same way a
  //  change to the basket does — they are the other halves of what would be written.
  useEffect(() => {
    runToken.current += 1;
    setBatchPlan(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replacementKey, attendance, serviceKey]);

  //  A change to the ticks invalidates the plan that was built for the old ones.
  //  Separate from the effect above so it fires even while a plan is on screen — that
  //  is exactly the case it exists for.
  //  `busy` is deliberately NOT cleared here — see activeRun. The run in flight still
  //  owns the spinner and will clear it when it lands; forcing it false would let the
  //  effect above start a second run on top of the first.
  useEffect(() => {
    runToken.current += 1;          // abandon the answer any pass in flight brings back
    setBatchPlan(null);
    setProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  //  ── THE WRITE ─────────────────────────────────────────────────────────────
  //  Everything above this point has written nothing. This is the one function on
  //  the composer that changes a claim, and it applies the whole basket to EVERY
  //  ticked patient — in as many passes as the endpoint's per-request limit needs.
  const applyBasket = async () => {
    if (!batchPlan || !targetIds.length) return;
    if (!basket.length && !replacements.length && !attendance && !serviceMap.length) return;

    //  Refused here as well as by the server. The band guard is checked against the
    //  whole selection during the dry run, so by the time Apply is pressed the answer
    //  is already known and pressing on would write to the passes that happen to
    //  agree before stopping at the one that does not.
    if ((batchPlan.conflicts || []).length) {
      await Swal.fire({
        icon: 'error', title: 'These entries do not suit the patients',
        html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
            + batchPlan.conflicts.map((c) => `<div>${c.why}</div>`).join('')
            + '</div>',
      });
      return;
    }

    const passes = mvChunk(targetIds);
    const esc = (v) => String(v == null ? '' : v).replace(/</g, '&lt;');
    const ok = await Swal.fire({
      //  A service change re-prices the claim, so it gets the same warning icon a
      //  removal does rather than the neutral question mark an addition gets.
      icon: (replacements.length || serviceMap.length) ? 'warning' : 'question',
      title: replacements.length
        //  A run that REMOVES rows says so in its title. Counted separately from the
        //  replacements, because "removes 3" is the sentence that should stop somebody
        //  who did not mean to.
        ? (() => {
          const gone = targetRows.filter((r) => r.drop).length;
          const swapped = replacements.length - gone;
          const bits = [];
          if (swapped) bits.push(`change ${swapped} entr${swapped === 1 ? 'y' : 'ies'}`);
          if (gone) bits.push(`REMOVE ${gone}`);
          if (basket.length) bits.push(`add ${basket.length}`);
          if (serviceMap.length) bits.push(`re-price ${serviceMap.length} service(s)`);
          if (attendance) bits.push('set the attendance');
          const s = bits.join(', ');
          return `${s.charAt(0).toUpperCase()}${s.slice(1)} on ${targetIds.length} claim(s)?`;
        })()
        : serviceMap.length && !basket.length
          //  Named for what it is. Changing the service changes the G-DRG the claim is
          //  priced on, and "apply 0 items" would have been the alternative.
          ? `Change the service on ${targetIds.length} claim(s)?`
          : attendance && !basket.length
            ? `Set the type of attendance on ${targetIds.length} claim(s)?`
            : `Apply ${basket.length} item(s) to ${targetIds.length} claim(s)?`,
      html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
          //  ── WHAT GOES, NAMED ────────────────────────────────────────────
          //  Each pair spelled out. "3 changes" is a fact about the run; "Malaria
          //  becomes Typhoid fever" is the thing to check before it happens, and it is
          //  the only chance to catch a row mapped to the wrong replacement.
          + (replacements.length
            ? `<div style="font-size:11px;font-weight:800;letter-spacing:.05em;`
              + `text-transform:uppercase;color:#B42318;margin-bottom:3px">`
              + `${targetRows.some((r) => r.to) ? 'Replacing' : 'Removing'}</div>`
              + targetRows.filter((r) => r.to || r.drop).map((r) =>
                `<div><b>${esc(r.name)}</b> <span style="color:#94A3B8">${esc(r.code)}</span>`
                //  A removal is named as one. "→ null" is what an unlabelled delete
                //  looks like, and this is the last screen before the rows go.
                + (r.drop
                  ? ' → <b style="color:#B42318">removed, nothing replaces it</b>'
                  : ` → <b>${esc(r.to.name || r.to.code)}</b>`)
                + (r.patients != null
                  ? ` <span style="color:#64748B">· on the ${r.patients} carrying it</span>`
                  : '')
                + '</div>').join('')
              + ((basket.length || attendance) ? '<div style="height:8px"></div>' : '')
            : '')
          //  ── THE SERVICE CHANGES, NAMED ──────────────────────────────────
          //  Each mapping spelled out with the count it reaches. On a normalised claim
          //  the service is the G-DRG the claim is PRICED on, so this is the line that
          //  changes what the claim is worth — it should not be a number.
          + (serviceMap.length
            ? `<div style="font-size:11px;font-weight:800;letter-spacing:.05em;`
              + `text-transform:uppercase;color:#B42318;margin-bottom:3px">Service</div>`
              + serviceRows.filter((r) => r.to).map((r) =>
                `<div>${r.code
                  ? `<b>${esc(r.name || r.code)}</b> <span style="color:#94A3B8">`
                    + `${esc(r.code)}</span>`
                  : '<b>Claims with no service</b>'}`
                + ` → <b>${esc(r.to.label || r.to.code)}</b>`
                + ` <span style="color:#64748B">· ${r.patients} claim(s)</span></div>`).join('')
              + ((basket.length || attendance) ? '<div style="height:8px"></div>' : '')
            : '')
          + (attendance
            ? `<div style="font-size:11px;font-weight:800;letter-spacing:.05em;`
              + `text-transform:uppercase;color:#64748B;margin-bottom:3px">`
              + 'Type of attendance</div>'
              + `<div>Every ticked claim becomes <b>`
              + `${esc((MV_ATTENDANCE.find(([c]) => c === attendance) || [, attendance])[1])}`
              + `</b> <span style="color:#94A3B8">${esc(attendance)}</span></div>`
              + (basket.length ? '<div style="height:8px"></div>' : '')
            : '')
          + (basket.length
            ? (replacements.length
              ? `<div style="font-size:11px;font-weight:800;letter-spacing:.05em;`
                + `text-transform:uppercase;color:#64748B;margin-bottom:3px">Adding</div>`
              : '')
              + basket.map((b) => `<div><b>${MV_SECTIONS.find((s) => s.key === b.kind)?.label
                  || b.kind}</b> — ${esc(b.name || b.code)}</div>`).join('')
            : '')
          + `<div style="margin-top:10px">${basket.length
            ? 'A claim that already has an item keeps the one it has; nothing is doubled. '
            : ''}${replacements.length
            ? 'A claim that does not carry an entry being replaced is left alone — it is '
              + 'not given the replacement. This cannot be undone.'
            : 'Nothing is removed.'}</div>`
          //  Said before it starts, not discovered while it runs. A 1,894-claim basket
          //  is ten passes and minutes of writing, and an officer who does not know
          //  that closes the tab halfway through.
          + (passes.length > 1
            ? `<div style="margin-top:10px"><b>${targetIds.length} claims runs in `
              + `${passes.length} passes of up to ${MV_RUN_CHUNK}.</b> Leave this open `
              + 'until it finishes — a pass that has already run stays applied.</div>'
            : '')
          + '</div>',
      showCancelButton: true,
      confirmButtonText: `Apply to ${targetIds.length}`,
      cancelButtonText: 'Cancel',
      //  A run that destroys rows defaults to Cancel. Every other run here only adds,
      //  so this is the one place the safe button should be the one under the cursor.
      focusCancel: replacements.length > 0,
    });
    if (!ok.isConfirmed) return;

    const token = (runToken.current += 1);
    activeRun.current = token;
    const actor = (() => {
      try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || undefined; }
      catch { return undefined; }
    })();

    setBusy(true); setError(null);
    const agg = mvAgg();
    //  Held outside the loop so a pass that throws halfway still reports what the
    //  earlier passes wrote. Those rows are saved; saying nothing about them would be
    //  the one report worse than a failure.
    let stopped = null;
    try {
      for (let i = 0; i < passes.length; i += 1) {
        if (passes.length > 1) {
          setProgress({
            phase: 'apply', done: agg.claims, total: targetIds.length,
            pass: i + 1, passes: passes.length,
          });
        }
        try {
          const { data } = await axios.post(claimsMultiVetBatch, {
            claimIds: passes[i], items: basket, replacements,
            attendance: attendance || undefined, serviceMap,
            dryRun: false, actor,
          });
          mvMergePass(agg, data);
        } catch (e) {
          stopped = e?.response?.data?.error || e.message;
          break;
        }
      }

      const out = mvSettle(agg);

      //  ── A BASKET THAT WAS APPLIED IS A ROUTINE, WHETHER OR NOT ANYONE SAID SO ──
      //
      //  Saved without being asked. A basket that has been run on 400 claims is a
      //  standard by definition — it is what this facility does for this group — and
      //  losing it because nobody pressed Save means rebuilding it by hand next month.
      //
      //  THIS IS WHAT PUTS IT ON THE SHELF. `applied: true` is sent from here and from
      //  nowhere else, because being applied is the evidence: it went onto real claims
      //  and the officer stood behind it.
      //
      //  Safe to do quietly for two reasons: the name is derived from the pattern, so
      //  the same work saves over one row rather than adding another, and this save
      //  never sets premium, so it cannot promote anything on its own.
      //
      //  BEST EFFORT. The claims are already written; a shelf that failed to update
      //  must not turn a successful run into an error message.
      let saved = null;
      try { saved = await putRoutine({ premium: false, applied: true }); }
      catch (e) { console.warn('[multivet] routine not saved:', e.message); }

      //  ── THE NUMBER THE OFFICER CAME FOR ──────────────────────────────────
      //  Not "214 changed" — that is a fact about the write. "198 rejects became
      //  4" is the day's work, and it is the only figure that says whether running
      //  the basket was worth doing.
      const iss = out.issues;
      const moved = iss ? iss.before.rejects - iss.after.rejects : null;
      await Swal.fire({
        icon: (stopped || out.failed) ? 'warning' : 'success',
        title: stopped
          ? `Stopped after ${out.claimsTouched || 0} claim(s)`
          : (iss && moved > 0
            ? `${moved} claim(s) will no longer be rejected`
            : `${out.claimsTouched || 0} claim(s) vetted`),
        html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
            + (iss
              ? `<div><b>Would be rejected</b> — `
                + `${iss.before.rejects} → <b>${iss.after.rejects}</b></div>`
                + `<div><b>Worth checking</b> — `
                + `${iss.before.warnings} → ${iss.after.warnings}</div>`
                + `<div style="margin-top:8px">`
              : '<div>')
            + `${out.claimsTouched || 0} claim(s) touched, `
            + `${out.writes || 0} row(s) written`
            + (out.failed ? `, <b>${out.failed} could not be saved</b>` : '')
            + (out.passes > 1 ? `, over ${out.passes} pass(es)` : '')
            + '.</div>'
            + (stopped
              ? `<div style="margin-top:10px;color:#B42318"><b>It did not finish.</b> `
                + `${stopped}<br>What the passes above wrote is saved. Re-check and run `
                + 'again — a claim that already has an entry is left alone, so nothing '
                + 'is doubled.</div>'
              : '')
            //  Said, not silent. A save the officer did not ask for and cannot see is
            //  a surprise the next time the shelf has something on it.
            + (saved
              ? `<div style="margin-top:10px;padding-top:9px;border-top:1px solid #E2E8F0;`
                + `font-size:12px;color:#475569">Kept as a routine — `
                + `<b>${saved.name.replace(/</g, '&lt;')}</b>. It is on the shelf beside `
                + 'the patients now that it has been applied. Use <b>Save as routine</b> '
                + 'to make it premium.</div>'
              : '')
            + '</div>',
      });
      //  The plan described claims that have since changed, so it is dropped rather
      //  than left on screen beside an Apply button that would re-run it.
      setBatchPlan(null);
      reloadSelection();
      onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      if (activeRun.current === token) { setBusy(false); setProgress(null); }
    }
  };

  const saveRoutine = async () => {
    if (!basket.length) return;
    //  ── THE ONE QUESTION LEFT WORTH ASKING ──────────────────────────────────
    //  Not what to call it — whether it belongs on the shelf. A premium routine is
    //  offered beside the patients on every future group that fits, so promoting one
    //  is a decision about what the officer wants put in front of them, and that is
    //  not derivable from anything on screen.
    const res = await Swal.fire({
      icon: 'question',
      title: 'Make this a premium routine?',
      html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
          + `<div style="font-size:11px;font-weight:800;letter-spacing:.05em;`
          + `text-transform:uppercase;color:#64748B">Saving as</div>`
          + `<div style="font-weight:800;color:#0F172A;margin:2px 0 10px">`
          + `${routineName.replace(/</g, '&lt;')}</div>`
          + `<div><b>Premium</b> puts it on the shelf beside the patients straight `
          + `away — before it has ever been run — and keeps it at the top. That is how `
          + `you set the routines this facility means to use.</div>`
          //  The other answer, said plainly. A routine that is neither applied nor
          //  premium is a draft, and the shelf does not carry drafts — so the officer
          //  has to be told that here, not left to notice it is missing.
          + `<div style="margin-top:8px">Without it, the basket is kept but stays off `
          + `the shelf until you apply it.</div></div>`,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes, premium',
      denyButtonText: 'Just keep it',
      cancelButtonText: 'Cancel',
    });
    if (res.isDismissed) return;          // Cancel, or the escape key

    setSavingRoutine(true);
    try {
      const out = await putRoutine({ premium: res.isConfirmed });
      Swal.fire({
        icon: 'success',
        title: res.isConfirmed ? 'Saved as premium' : 'Routine kept',
        //  Straight from the server's own answer. Whether it is on the shelf is the
        //  endpoint's rule — applied OR premium — and the screen should not keep a
        //  second copy of it to disagree with.
        text: out?.onShelf
          ? 'It is on the shelf beside the patients, at the top.'
          : 'It is saved. It joins the shelf the first time you apply it.',
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Could not save it',
        text: e?.response?.data?.error || e.message });
    } finally { setSavingRoutine(false); }
  };


  //  The same lists the Advanced Filter offers, so you vet with the month's own
  //  vocabulary rather than typing a remembered code.
  //  ── The four boxes follow the pattern ─────────────────────────────────────
  //  Re-fetched whenever steps 1–3 change, so the diagnosis, procedure, lab and
  //  drug lists offer only what the CURRENT cohort contains — and their counts are
  //  counts of that cohort, not of the month.
  //
  //  Fetched once before: 829 diagnoses on offer for a group of four claims, and
  //  every count on the list wrong for the group being looked at.
  //
  //  The child filters are deliberately NOT sent. They would narrow their own boxes
  //  — pick a diagnosis and that box collapses to the one row you picked — which is
  //  the standard facet trap. Cross-narrowing needs each list scoped by the other
  //  three rather than by all four, and that is a change of its own.
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      axios.get(claimsDetailsFacets, {
        params: {
          year, month, facility,
          insurance_type: insurances?.length ? insurances.join(',') : 'all',
          //  Always sent: there is no 'all' any more, so the attendance is
          //  never absent from the question.
          type: pattern.type,
          specialty: pattern.specialty !== 'all' ? pattern.specialty : undefined,
          ageFrom: String(pattern.ageFrom).trim() !== '' ? pattern.ageFrom : undefined,
          ageTo: String(pattern.ageTo).trim() !== '' ? pattern.ageTo : undefined,
          gender: pattern.gender !== 'all' ? pattern.gender : undefined,
          stayDays: pattern.type === 'ipd' && String(pattern.stayDays).trim() !== ''
            ? pattern.stayDays : undefined,
        },
      })
        .then(({ data }) => { if (alive) setFacets(data); })
        .catch(() => { if (alive) setFacets({}); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern.type, pattern.specialty, pattern.ageFrom, pattern.ageTo, pattern.gender,
      pattern.stayDays, year, month, facility]);

  //  A prescription needs a frequency and a route from the controlled lists, not
  //  free text — the store validates against them. Loaded lazily: only the
  //  medication section can use it.
  useEffect(() => {
    //  Loaded once the composer is open, because that is where a
    //  prescription is now written. It was keyed on the old section picker, which
    //  this screen no longer has — so the frequency and route lists came back empty
    //  and both dropdowns had nothing to offer.
    if (view !== 'compose' || rxVocab.frequencies.length) return;
    let alive = true;
    //  The same scheme the pickers use. The frequency and route lists do not vary
    //  by insurer, but `byCode` — the dose units a picked drug arrives with — does,
    //  and reading it from one scheme's book while the pickers read another's is
    //  how a drug gets prefilled with the wrong strength.
    axios.get(claimStoreMedicationCatalogue,
      { params: { scheme: composerScheme(insurances) || undefined } })
      .then(({ data }) => {
        if (!alive) return;
        //  Keyed on code, which is what the pick-list offers. 239 catalogue rows
        //  have no code and simply never match — those drugs arrive blank and the
        //  officer types the unit, which is the honest outcome.
        const byCode = new Map();
        for (const o of data?.options || []) {
          if (o.code && !byCode.has(o.code)) byCode.set(String(o.code).trim(), o);
        }
        setRxVocab({
          frequencies: data?.frequencies || [], routes: data?.routes || [], byCode,
        });
      })
      .catch(() => {
        if (alive) setRxVocab({ frequencies: [], routes: [], byCode: new Map() });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, insurances]);

  //  What the selection already holds. Re-read after every run, because a run is
  //  exactly what makes the previous answer wrong.
  const reloadSelection = useCallback(() => {
    if (!ids.length) { setSel([]); return; }
    axios.post(claimsMultiVetSelection, { claimIds: ids })
      .then(({ data }) => setSel(data.claims || []))
      .catch(() => setSel([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  useEffect(() => { reloadSelection(); }, [reloadSelection]);

  //  Any change to what would be applied invalidates the preview. Leaving a stale
  //  one on screen next to an enabled Apply is how the wrong edit gets committed.
  const pickedKey = picked.map((p) => p.code).join(',');
  useEffect(() => {
    setPreview(null); setError(null); setIpAck(false);
  }, [section, operation, pickedKey]);

  const spec = MV_SECTIONS.find((s) => s.key === section);
  const list = (spec?.facet && facets ? facets[spec.facet] : null) || [];
  const shown = list
    .filter((it) => !q.trim()
      || `${it.code} ${it.name || ''}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 150);

  //  ── The same prescription arithmetic the single-claim editor uses ──────────
  //  doses per day x days x units per dose, rounded UP, and ONLY for a countable
  //  form: a 7-day syrup course is 1 bottle, not 21 doses. A suggestion, never an
  //  authority — it stops the moment a number is typed.
  const perDayOf = (freq) => rxVocab.frequencies.find((f) => f.value === freq)?.perDay || null;
  const unitsPerDose = (dose) => {
    //  "500 mg x 2" — the number after the x is how many units make one dose.
    const m = String(dose || '').match(/x\s*([\d.]+)\s*$/i);
    return m ? Number(m[1]) : 1;
  };
  const suggestQty = (v) => {
    if (!v.countable) return null;
    const per = perDayOf(v.frequency);
    const days = parseInt(String(v.duration || '').replace(/[^\d]/g, ''), 10);
    if (!per || !Number.isFinite(days) || days <= 0) return null;
    const q = Math.ceil(unitsPerDose(v.dose) * per * days);
    return Number.isFinite(q) && q > 0 ? String(q) : null;
  };

  const toggle = (it) => setPicked((p) => {
    if (p.some((x) => x.code === it.code)) return p.filter((x) => x.code !== it.code);
    if (section !== 'medication') return [...p, { code: it.code, name: it.name || it.code }];
    //  Resolved from the catalogue on pick, exactly as the single-claim editor
    //  does it: the dose unit arrives filled in, and the dose starts as one unit
    //  per dose ("500 mg x 1") for the officer to change.
    const opt = rxVocab.byCode.get(String(it.code).trim());
    const du = opt?.dose_unit || '';
    return [...p, {
      code: it.code, name: it.name || it.code,
      dose_unit: du,
      dose: du ? `${du} x 1` : '',
      frequency: '', duration: '', route: '', quantity: '',
      //  Whether the quantity is arithmetic for this drug, and whether a human
      //  has overridden it.
      countable: !!opt?.countable,
      quantityTyped: false,
    }];
  });

  const patchPick = (code, key, value) => setPicked((p) => p.map((x) => {
    if (x.code !== code) return x;
    const next = { ...x, [key]: value };
    if (key === 'quantity') return { ...next, quantityTyped: true };
    //  Re-suggested after any change to the three inputs it depends on, unless a
    //  number was typed — then it is left alone.
    if (['dose', 'frequency', 'duration'].includes(key) && !x.quantityTyped) {
      const s = suggestQty(next);
      if (s !== null) return { ...next, quantity: s, quantityAuto: true };
    }
    return next;
  }));

  //  ── WHAT OVERRIDE REPLACES ────────────────────────────────────────────────
  //  The codes on the dashboard's advanced filter for this section. You filtered
  //  "diagnosis = K27" to find these 27 claims, so K27 is what "replace it" means.
  //
  //  Read live from the filter rather than captured when the dialog opened: the
  //  dialog stays open across runs, and a stale target would replace the wrong code
  //  on the second pass.
  const targetCodes = useMemo(() => {
    const key = MV_FILTER_KEY[section];
    if (!key || !filter) return [];
    const v = filter[key];
    return Array.isArray(v) ? v.filter(Boolean) : (v ? [v] : []);
  }, [filter, section]);

  //  Override is only offered when there IS something to replace. Without a target
  //  it would have to fall back to the old meaning — wipe the section — which is
  //  the destructive behaviour this rework removes, so it is refused instead.
  const canOverride = targetCodes.length > 0;

  //  Switching to a section the filter says nothing about — Lab / Scan, or a
  //  section that was not filtered on — must not leave Override selected. It would
  //  sit there active and refused by the server on Apply, which reads as the dialog
  //  being broken rather than as the operation not applying here.
  useEffect(() => {
    if (operation === 'override' && !canOverride) { setOperation('add'); setPreview(null); }
  }, [canOverride, operation]);

  //  Details are only meaningful when rows are being WRITTEN. A delete needs the
  //  code and nothing else, so it must not be blocked on a dose.
  const writes = operation !== 'delete';
  const RX_REQUIRED = [
    ['dose_unit', 'Dose unit'], ['dose', 'Dose'], ['frequency', 'Frequency'],
    ['duration', 'Duration'], ['route', 'Route'], ['quantity', 'Quantity'],
  ];
  //  The NHIA shape: a quantity, a space, then the unit. Mirrors DOSE_UNIT_RE in
  //  claimStore.js — checked here so it is caught before a 200-claim run starts,
  //  and there so it cannot be bypassed.
  const DOSE_UNIT_RE = /^\d+(?:[.,]\d+)?\s+\S.*$/;
  const badUnit = (v) => !DOSE_UNIT_RE.test(String(v.dose_unit || '').trim());

  //  ── IN-PATIENT CLAIMS AND THE DISPENSING DATE ─────────────────────────────
  //  A medication row carries the DAY it was dispensed, and claimStore refuses a
  //  day outside the visit. Multi Vet sends no date, so the store falls back to the
  //  FIRST day of the visit — correct for an out-patient claim, which has exactly
  //  one day, and wrong for an admission, where a 14-day course would land every
  //  drug on day one.
  //
  //  Multi Vet cannot resolve this by asking for a date: the selected claims have
  //  DIFFERENT visits, so no single date is valid across them. So the rule is named
  //  and has to be accepted deliberately, rather than applied silently.
  const inpatient = (sel || []).filter(
    (cl) => /inpatient|ipd/i.test(String(cl.attendance || '')));
  const rxDateRisk = section === 'medication' && writes && inpatient.length > 0;
  const rxDateBlocked = rxDateRisk && !ipAck;

  const rxMissing = (section === 'medication' && writes)
    ? picked.filter((v) => badUnit(v) || RX_REQUIRED.some(([k]) => !String(v[k] ?? '').trim()
        || (k === 'quantity' && !(Number(v.quantity) > 0))))
    : [];

  const runPreview = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(claimsMultiVetPreview, {
        section, operation, claimIds: ids, values: picked,
        //  What override replaces. Sent on every call rather than only for
        //  override, so the server sees the same payload it will be applied with.
        target: targetCodes,
      });
      setPreview(data);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  //  THE PREVIEW RUNS ITSELF once the form is complete and valid.
  //
  //  Apply has always required a preview — nothing here writes without the server
  //  first saying what would happen. But leaving that preview to a manual Check
  //  meant a fully filled prescription still faced a greyed-out Apply with no
  //  visible reason, which reads as the dialog being broken rather than as a step
  //  being outstanding. Running it automatically keeps the guarantee and removes
  //  the dead end; Check remains, for re-running it deliberately.
  //
  //  Cannot loop: it fires only while `preview` is null, and setting it stops the
  //  condition from holding. Debounced so typing a quantity does not fire a request
  //  per keystroke.
  useEffect(() => {
    if (!picked.length || preview || busy || error) return undefined;
    if (rxMissing.length || rxDateBlocked) return undefined;
    if (!ids.length) return undefined;
    const t = setTimeout(() => { runPreview(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedKey, JSON.stringify(picked), preview, busy, error, rxMissing.length,
      section, operation, ids.length]);

  const apply = async () => {
    const c = preview?.counts;
    //  Override names what it destroys and asks again. It is the only operation
    //  here that removes data the officer did not choose.
    if (operation === 'override') {
      //  Names the two codes rather than a row count. "Deletes 3 existing rows" was
      //  true of the old wipe-the-section override and told nobody WHICH; now there
      //  is exactly one thing going and one thing arriving, so it can be said
      //  plainly and checked before it happens.
      const ok = await Swal.fire({
        icon: 'warning',
        title: `Replace ${targetCodes.join(', ')} with ${picked.map((p) => p.code).join(', ')}?`,
        text: `On ${c.change} claim(s). ${c.destroys} row(s) carry `
            + `${targetCodes.join(', ')} and will be replaced; the rest of each `
            + 'claim is untouched. This cannot be undone.',
        showCancelButton: true, confirmButtonText: `Replace on ${c.change}`,
        cancelButtonText: 'Keep', focusCancel: true,
      });
      if (!ok.isConfirmed) return;
    }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(claimsMultiVet, {
        section, operation, claimIds: ids, values: picked,
        target: targetCodes,
        actor: (() => {
          try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || undefined; }
          catch { return undefined; }
        })(),
      });
      const s = data.summary || {};
      //  One plain sentence. The detail is on the banner that stays in the dialog,
      //  so this does not need to repeat it.
      await Swal.fire({
        icon: s.failed ? 'warning' : 'success',
        title: `${s.changed || 0} claim(s) vetted`,
        text: s.failed ? `${s.failed} could not be saved.` : 'Nothing else was changed.',
        confirmButtonText: 'OK',
      });
      setResult(s);
      //  Deliberately NOT closing: vetting the same selection again — a procedure
      //  after a diagnosis — is the normal next step, not the exception.
      setPreview(null);
      setPicked([]);
      reloadSelection();   // the claims below now hold different rows
      onDone();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const c = preview?.counts;
  //  An incomplete prescription blocks BOTH buttons, not just Apply — a preview
  //  built from a half-filled row would report a change it should not make.
  const canApply = c && c.change > 0 && !preview.spansFacilities && !busy
    && rxMissing.length === 0 && !rxDateBlocked;

  return (
    <div role="dialog" aria-modal="true" aria-label="Multi Vet" {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(15,23,42,.42)',
        //  See DIALOG_GAP — centred and capped, the same as the Advanced Filter.
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: `${DIALOG_GAP}px 16px`,
      }}>
      <div ref={panelRef} style={{
        //  BIGGER. 1120 was a compromise from when this held one section's picker;
        //  it now holds a basket, a routine library and a claims list, and 1120 put
        //  all three in a column narrower than the table behind it.
        //  A little bigger again, and TALLER: the shell is what the new layout gets
        //  designed inside, so it should be the size that layout will actually have
        //  rather than something that grows later and reflows the design.
        width: '100%', maxWidth: 1560, minHeight: 'min(86vh, 900px)',
        background: '#fff', borderRadius: 14,
        border: `1px solid ${BRAND.line}`,
        boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 32px 80px rgba(15,23,42,.34)',
        maxHeight: `calc(100vh - ${DIALOG_GAP * 2}px)`,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/*  ── Header ──────────────────────────────────────────────────────────
             A navy band, the same one the ICT import dialog and the Claims
             dialogs use. It was a bordered white strip, which read as an
             unstyled panel rather than as part of the product. */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 13, padding: '17px 20px',
          background: 'linear-gradient(135deg, #123A6D 0%, #0B2452 100%)',
          color: '#fff', flexShrink: 0, position: 'relative', overflow: 'hidden',
        }}>
          {/*  The same green glow the other brand dialogs carry, so the family
               reads as one. Decorative only — behind the content, no pointer. */}
          <span aria-hidden="true" style={{
            position: 'absolute', top: -80, right: -60, width: 230, height: 230,
            borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(46,158,79,.30) 0%, transparent 65%)',
          }} />
          {/*  ── BACK, IN PLACE OF THE ICON ────────────────────────────────────
               It replaces the badge rather than sitting beside it: on the claims
               view the icon is decoration and Back is the most likely next action,
               so the most reachable spot in the dialog should hold whichever of the
               two is useful. */}
          {view !== 'pattern' ? (
            <button type="button" onClick={goBack}
              title={view === 'compose' ? 'Back to the patients' : 'Back to the pattern'}
              style={{
                flexShrink: 0, width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
                display: 'grid', placeItems: 'center', position: 'relative',
                background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.34)',
                color: '#fff',
              }}>
              <ChevronLeft size={19} />
            </button>
          ) : (
            <span style={{
              flexShrink: 0, width: 38, height: 38, borderRadius: 10,
              display: 'grid', placeItems: 'center', position: 'relative',
              background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)',
            }}>
              <Layers size={18} />
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <div style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: '.13em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,.55)',
            }}>{view === 'compose' ? 'Multi Vet · the group · what to apply'
                : view === 'claims'
                  ? (source === 'selection'
                    ? 'Multi Vet · the claims you selected'
                    : 'Multi Vet · the group')
                  : 'Claims · batch vetting'}</div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.01em' }}>
              {view === 'claims'
                //  The pattern, as the title. On this view the group IS the subject,
                //  and repeating "Multi Vet" would waste the one line that could say
                //  which claims are on screen.
                //
                //  In SELECTION mode there is no pattern to show, and showing one
                //  would be a lie — these claims were chosen by hand, not matched.
                //  So the title says how they got here.
                ? (source === 'selection'
                  ? `${openedWith.toLocaleString()} claim(s) you picked in the table`
                  : (patternSummary.length
                    ? patternSummary.join(' · ')
                    : `Every claim in ${month || 'the month'}`))
                : 'Multi Vet'}
            </div>
            <div style={{
              fontSize: 12.5, color: 'rgba(255,255,255,.78)', marginTop: 3,
              lineHeight: 1.5, maxWidth: '76ch',
            }}>
              {view === 'compose'
                ? <>Build what goes on the <strong>{chosenCount.toLocaleString()}</strong> ticked
                    claim(s). Nothing is written until you apply it.</>
                : view === 'claims'
                ? (source === 'selection'
                  ? <>All <strong>{(found || []).length.toLocaleString()}</strong> are
                      ticked and ready — choose what to do, or untick any you did not
                      mean.{' '}
                      <button type="button" onClick={goBack}
                        style={{
                          border: 0, background: 'none', padding: 0, font: 'inherit',
                          fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                          color: '#fff', textDecoration: 'underline',
                        }}>
                        Find a group by pattern instead
                      </button></>
                  : <><strong>{(found || []).length.toLocaleString()}</strong> patient(s) in
                      this group. Tick who to work on, then choose what to do.</>)
                : 'Describe the group you want to vet below, then open it. Nothing is '
                  + 'written until you say so.'}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            flexShrink: 0, position: 'relative', width: 30, height: 30,
            borderRadius: 9, cursor: 'pointer', fontSize: 13, lineHeight: 1,
            border: '1px solid rgba(255,255,255,.22)',
            background: 'rgba(255,255,255,.14)', color: '#fff',
          }}>✕</button>
        </div>

        {/*  -- EMPTY CANVAS -------------------------------------------------
             Everything that was in this panel has been taken out on purpose. The
             sections, the operation buttons, the picker, the basket, the routine
             chips, the claims fold and the commit bar are all gone from the render.

             WHAT IS STILL HERE, deliberately: the component's state and its
             handlers, above this return, and both server endpoints behind them.
             The basket, the saved routines, the age-band guard and the
             before/after reject counts are all wired and tested  -  so the new
             layout can reach them without any of that being rebuilt.

             What goes here next is a design decision, not a code one. */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: 22, background: BRAND.canvas,
          //  A flex COLUMN, not a grid of auto rows. The pattern card takes the
          //  height it needs and the four boxes take everything left over — so the
          //  lists you pick from grow with the dialog instead of staying 176px tall
          //  inside a 900px panel.
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>

          {/*  ══ TWO VIEWS, ONE DIALOG ════════════════════════════════════════
               Describing a group and working through it are different jobs, and
               they wanted the same amount of room — so they take turns in the same
               frame rather than being two dialogs or one crowded panel.

               `view` slides between them. The pattern is not unmounted so much as
               set aside: coming Back finds every filter exactly as it was left,
               which matters because a pattern is a dozen small decisions and
               rebuilding it would make going Back something to avoid. */}
          {view === 'pattern' && (
            <>
          {/*  ══ THE PATTERN ══════════════════════════════════════════════════
               Describe the group; the dialog finds it. Filters stack downwards and
               narrow as you go, so the count at the bottom of the card is always
               the answer to everything set above it. */}
          {/*  NO `overflow: hidden` on this card, deliberately.
               It had it, for rounded corners, and it cost two rounds of debugging:
               anything that overflowed was silently CUT AWAY instead of showing.
               Step 3 vanished twice that way — nothing looked broken, there was no
               scrollbar, the step simply was not there.
               A clipped layout bug is invisible; an overflowing one is obvious. The
               header button carries its own top radius so the corners still look
               right without the card hiding its own content. */}
          <div style={{
            background: '#fff', border: `1px solid ${BRAND.line}`, borderRadius: 12,
          }}>
            {/*  ── The header, which is also the fold and the answer ──────────
                 Three jobs in one row, and they belong together: what this card is,
                 what it currently says, and how many claims that comes to. The
                 count used to sit in a separate strip at the bottom — so folding the
                 card away took the answer with it, which is the one thing that must
                 survive being folded. */}
            <button type="button" onClick={() => setPatternOpen((v) => !v)}
              aria-expanded={patternOpen}
              style={{
                width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer',
                padding: '12px 16px', border: 0, borderBottom: patternOpen
                  ? `1px solid ${BRAND.line}` : 0,
                background: patternOpen ? '#fff' : BRAND.canvas,
                display: 'flex', alignItems: 'center', gap: 11,
                //  Its own radius, because the card no longer clips. Bottom corners
                //  too when folded, since it is then the whole card.
                borderRadius: patternOpen ? '12px 12px 0 0' : 12,
              }}>
              <span style={{ color: BRAND.inkFaint, flexShrink: 0, display: 'flex' }}>
                {patternOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </span>
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: '.13em',
                textTransform: 'uppercase', color: BRAND.greenDark, flexShrink: 0,
              }}>Pattern</span>

              <span style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
                gap: 6, flexWrap: 'wrap',
              }}>
                {patternSummary.length === 0
                  ? <span style={{ fontSize: 12.5, color: BRAND.inkMuted }}>
                      {patternOpen
                        ? 'Describe the claims you want to vet. Each step narrows the group.'
                        : 'Every claim in the month — open to narrow it.'}
                    </span>
                  //  The set filters, as chips, so the fold does not hide what it is
                  //  filtering by. Shown open as well: the steps below say the same
                  //  thing spread over three rows, and one line is easier to check.
                  : patternSummary.map((b) => (
                    <span key={b} style={{
                      fontSize: 11.5, fontWeight: 700, padding: '2px 9px',
                      borderRadius: 999, background: BRAND.greenSoft,
                      color: BRAND.greenDark, whiteSpace: 'nowrap',
                    }}>{b}</span>
                  ))}
              </span>

              {/*  The answer, always visible — open or folded. */}
              <span style={{ flexShrink: 0, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                {finding
                  ? <span style={{ color: BRAND.inkFaint }}>Finding…</span>
                  : found === null ? null
                    : found.length === 0
                      ? <span style={{ color: BRAND.inkFaint }}>no matches</span>
                      : <>
                          <strong style={{ fontSize: 16, color: BRAND.greenDark }}>
                            {found.length.toLocaleString()}
                          </strong>
                          <span style={{ color: BRAND.inkMuted }}> claims</span>
                        </>}
              </span>
            </button>

            {/*  ── NUMBERED STEPS ────────────────────────────────────────────
                 The numbers are not decoration: this IS a sequence. Step 1 decides
                 which catalogue applies at all, step 2 narrows to a specialty
                 within it, and step 3 decides which TARIFF BAND is correct — the
                 same diagnosis is priced differently for a child and an adult. Each
                 step's options are what remain after the ones above it.

                 Compact on purpose. Small pills on one row per step, and age and
                 gender sharing a line, because the space below this belongs to the
                 routine and the claims — not to the filters that found them. */}
            <div style={{
              padding: patternOpen ? '14px 16px' : 0,
              display: patternOpen ? 'grid' : 'none', gap: 12,
            }}>

              <Step n={1} label="Type of service">
                {/*  Two choices, and one of them is always taken. No "All": there is
                     no treatment routine that is correct for an out-patient
                     attendance and an admission at once, so a group spanning both is
                     one nothing can be applied to. */}
                {[['opd', 'Out-patient'], ['ipd', 'In-patient']]
                  .map(([k, label]) => (
                    <Pill key={k} on={pattern.type === k}
                      //  Leaving in-patient drops the stay length with it. A stay
                      //  filter left set on an out-patient pattern would silently
                      //  find nothing — an OPD claim has no stay to be N days long.
                      onClick={() => setPattern((p) => ({
                        ...p, type: k, stayDays: k === 'ipd' ? p.stayDays : '',
                      }))}>
                      {label}
                    </Pill>
                  ))}

                {/*  ── LENGTH OF STAY ──────────────────────────────────────────
                     In-patient only, and it appears the moment In-patient is
                     chosen — a stay length is meaningless on an out-patient claim,
                     so it is not offered there rather than being offered and
                     ignored.

                     This is the filter that makes a COMMON treatment pattern safe.
                     A three-day admission needs three days of drugs; putting a
                     one-night stay and a week-long one in the same group and giving
                     them the same regimen produces a claim NHIA queries either way
                     — too much on one, too little on the other. Grouping by stay
                     length is how one routine becomes correct for everybody in it. */}
                {pattern.type === 'ipd' && (
                  <>
                    <span style={{
                      width: 1, alignSelf: 'stretch', minHeight: 22,
                      background: BRAND.line, margin: '0 5px',
                    }} />
                    <span style={subLabel}>Stay</span>
                    <Pill on={String(pattern.stayDays).trim() === ''}
                      onClick={() => setPattern((p) => ({ ...p, stayDays: '' }))}>
                      Any
                    </Pill>
                    {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                      <Pill key={d} on={String(pattern.stayDays) === String(d)}
                        title={`Admissions of exactly ${d} day${d === 1 ? '' : 's'}`}
                        //  A second click clears it, like the age shortcuts.
                        onClick={() => setPattern((p) => ({
                          ...p, stayDays: String(p.stayDays) === String(d) ? '' : String(d),
                        }))}>
                        {d} day{d === 1 ? '' : 's'}
                      </Pill>
                    ))}
                    {/*  Longer stays exist and are rarer, so they are typed rather
                         than given seven more pills nobody clicks. */}
                    <input type="number" min="1" max="400" placeholder="more"
                      value={[1, 2, 3, 4, 5, 6, 7].includes(Number(pattern.stayDays))
                        ? '' : pattern.stayDays}
                      onChange={(e) => setPattern((p) => ({ ...p, stayDays: e.target.value }))}
                      aria-label="Length of stay in days"
                      style={{ ...ageBox, width: 66 }} />
                  </>
                )}
              </Step>

              {/*  Built from the claims steps 1 and 3 returned, so a specialty with
                   nothing in it is never offered. The counts stay put while one is
                   selected — a count that zeroed everything you did not pick would
                   describe the selection rather than the month. */}
              <Step n={2} label="Specialty">
                <Pill on={pattern.specialty === 'all'}
                  onClick={() => setPattern((p) => ({ ...p, specialty: 'all' }))}>
                  All
                </Pill>
                {/*  Ordered by claim count, so the specialties a month is actually
                     made of lead the row. The tail is behind a count rather than
                     wrapping into a second ragged line — but a SELECTED specialty is
                     always rendered, or picking ORTH and then collapsing the row
                     would hide the thing the pattern is filtering by. */}
                {(allSpecs ? specialties : specialties.slice(0, 10))
                  .concat(
                    !allSpecs && pattern.specialty !== 'all'
                      && !specialties.slice(0, 10).some(([k]) => k === pattern.specialty)
                      ? specialties.filter(([k]) => k === pattern.specialty)
                      : []
                  )
                  .map(([code, n]) => (
                    <Pill key={code} on={pattern.specialty === code}
                      onClick={() => setPattern((p) => ({ ...p, specialty: code }))}>
                      {code === 'UNSPECIFIED' ? 'Unspecified' : code}
                      <span style={{
                        marginLeft: 6, fontSize: 10.5, fontWeight: 700,
                        opacity: pattern.specialty === code ? .85 : .5,
                      }}>{n.toLocaleString()}</span>
                    </Pill>
                  ))}
                {specialties.length > 10 && (
                  <button type="button" onClick={() => setAllSpecs((v) => !v)}
                    style={{
                      border: 0, background: 'none', cursor: 'pointer', font: 'inherit',
                      fontSize: 11.5, color: BRAND.greenDark, textDecoration: 'underline',
                      padding: '0 4px',
                    }}>
                    {allSpecs ? 'fewer' : `+${specialties.length - 10} more`}
                  </button>
                )}
                {rawFound !== null && specialties.length === 0 && (
                  <span style={{ fontSize: 12, color: BRAND.inkFaint }}>
                    Nothing to narrow — step 1 found no claims.
                  </span>
                )}
              </Step>

              {/*  ── Step 3: the patient, both halves on ONE line ──────────────
                   Age and gender were stacked. They are one question — who was
                   treated — and stacking them cost a whole row of the panel for no
                   gain. Side by side, wrapping to two rows only when the dialog is
                   genuinely too narrow to hold them. */}
              {/*  Age carries its own boxes AND the shortcuts, so any group can be
                   described — "the 5s to the 9s", "over 70" — and the bands are
                   only the common cases. Gender rides the same wrapping row, so on
                   a wide dialog the whole patient question is one line and on a
                   narrow one it becomes two rather than being cut off. */}
              {/*  ── Step 3, in TWO COLUMNS ────────────────────────────────────
                   IT WAS INVISIBLE. Age and gender shared one flex row — two number
                   inputs, seven age shortcuts, a clear link, a label and four
                   gender pills. On this dialog that is wider than the card, the row
                   did not wrap, and the card's `overflow: hidden` clipped the whole
                   step away. No scrollbar, no clue it was there: the worst kind of
                   layout bug, because nothing looks broken.

                   A GRID cannot do that. Two tracks with a minimum width and
                   `minWidth: 0` on each: they sit side by side while there is room
                   and stack when there is not, and neither can push past the card.
                   Age on the left with its own wrapping chip row, gender on the
                   right. */}
              <Step n={3} label="The patient" block>
                <div style={{
                  display: 'grid', gap: '10px 20px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  alignItems: 'start',
                }}>
                  {/*  ── Age: the boxes, then the shortcuts under them ────────── */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap',
                      marginBottom: 6,
                    }}>
                      <span style={subLabel}>Age</span>
                      <span style={{ fontSize: 12, color: BRAND.inkMuted }}>from</span>
                      <input type="number" min="0" max="130" placeholder="any"
                        value={pattern.ageFrom}
                        onChange={(e) => setPattern((p) => ({ ...p, ageFrom: e.target.value }))}
                        aria-label="Age from"
                        style={ageBox} />
                      <span style={{ fontSize: 12, color: BRAND.inkMuted }}>to</span>
                      <input type="number" min="0" max="130" placeholder="any"
                        value={pattern.ageTo}
                        onChange={(e) => setPattern((p) => ({ ...p, ageTo: e.target.value }))}
                        aria-label="Age to"
                        style={ageBox} />
                      <span style={{ fontSize: 11.5, color: BRAND.inkFaint }}>years</span>
                      {(String(pattern.ageFrom).trim() !== ''
                        || String(pattern.ageTo).trim() !== '') && (
                        <button type="button"
                          onClick={() => setPattern((p) => ({ ...p, ageFrom: '', ageTo: '' }))}
                          style={{
                            border: 0, background: 'none', cursor: 'pointer', padding: '0 4px',
                            font: 'inherit', fontSize: 11.5, color: BRAND.inkFaint,
                            textDecoration: 'underline',
                          }}>clear</button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
                      {AGE_BANDS.map((b) => {
                        //  A shortcut is "on" when the boxes hold its numbers —
                        //  however they got there. Typing 12 and leaving the upper
                        //  bound blank lights up Adult (12+), which tells the officer
                        //  their hand-typed range is a band the tariff book knows.
                        const on = String(pattern.ageFrom) === b.from
                          && String(pattern.ageTo) === b.to;
                        return (
                          <Pill key={b.label} on={on}
                            //  A second click clears it, so a shortcut can undo
                            //  itself without hunting for a reset.
                            onClick={() => setPattern((p) => (on
                              ? { ...p, ageFrom: '', ageTo: '' }
                              : { ...p, ageFrom: b.from, ageTo: b.to }))}>
                            {b.label}
                          </Pill>
                        );
                      })}
                    </div>
                  </div>

                  {/*  ── Gender, on the right ─────────────────────────────────── */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ marginBottom: 6 }}>
                      <span style={subLabel}>Gender</span>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
                      {GENDERS.map((g) => (
                        <Pill key={g.key} on={pattern.gender === g.key}
                          onClick={() => setPattern((p) => ({ ...p, gender: g.key }))}>
                          {g.label}
                        </Pill>
                      ))}
                    </div>
                  </div>
                </div>
              </Step>

              {/*  Diagnosis and procedure become steps 4 and 5, in the same shape:
                   <Step n={4} label="Diagnosis"> and a row of pills. */}
            </div>

            {/*  The count that used to live down here is now in the header, so
                 folding the card does not take the answer with it. */}
          </div>

          {/*  ══ CARD 2 — WHAT IS ON THE CLAIM ════════════════════════════════
               Card 1 asks who the patient was; this asks what was done for them.
               Four boxes on one row because between them they are one question, and
               in the order a claim is BUILT: diagnosis, then the procedure it led
               to, then the tests, then the drugs. That is the order an officer reads
               a claim in, so it is the order they are offered here.

               Every box narrows the same count in card 1's header, and every row
               carries how many claims in the month contain it — so a filter that
               would find nothing says so before it is picked. */}
          {/*  ── HOW THE FOUR BOXES MATCH ─────────────────────────────────────
               A different question from WHAT is ticked, so it gets its own control
               above them rather than being a fifth thing inside one box.

               "Any of these" finds the patient who had malaria — including the one
               who also had typhoid. "Only these" finds the patient who had malaria
               and nothing else. That second group is the one a single routine is
               actually safe for: a patient who also had typhoid needs more than the
               group's regimen provides, so putting them in it under-treats them on
               paper and gets the claim queried. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: BRAND.inkSoft,
            }}>Match</span>
            {/*  Ticking two diagnoses can mean three different things and they find
                 different patients. ALL is the combination case — "spondylitis AND
                 malaria at the same time" — which a single IN cannot express,
                 because IN is satisfied by one match however many codes are listed.

                 ALL and ONLY are independent questions, so EXACTLY is offered as
                 its own choice rather than left as something to discover: has every
                 one of them, and has nothing else. That is the tightest group a
                 fixed routine can be applied to. */}
            {[
              ['any', 'Any of these',
                'At least one of what is ticked. The patient may have other diagnoses, procedures, labs or drugs as well.'],
              ['all', 'All of these',
                'EVERY one of what is ticked, together on the same claim. Other entries are still allowed.'],
              ['only', 'Only these',
                'Nothing outside what is ticked — but not necessarily all of it.'],
              ['exactly', 'Exactly these',
                'Every one of them and nothing else. The tightest group, and the safest for one shared routine.'],
            ].map(([k, label, hint]) => (
              <Pill key={k} on={pattern.match === k} title={hint}
                onClick={() => setPattern((p) => ({ ...p, match: k }))}>
                {label}
              </Pill>
            ))}
            <span style={{ fontSize: 11.5, color: BRAND.inkFaint, minWidth: 0 }}>
              {{
                any: 'Patients holding at least one of what you tick below.',
                all: 'Patients holding every one of them together — others allowed.',
                only: 'Patients whose record holds nothing beyond what you tick.',
                exactly: 'Patients holding every one of them and nothing else.',
              }[pattern.match]}
            </span>
          </div>

          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(268px, 1fr))',
            //  Takes the rest of the panel. `minHeight: 0` is what lets it actually
            //  shrink to fit rather than being sized by its content and pushing the
            //  footer off — the same default that lost step 3 twice.
            flex: 1, minHeight: 240,
          }}>
            <MvFilterBox n={4} label="Diagnosis"
              hint="Search name, ICD-10 or G-DRG…"
              items={facets?.diagnoses}
              chosen={pattern.diagnosis}
              onSet={(v) => setPattern((p) => ({ ...p, diagnosis: v }))}
              //  The diagnosis list carries BOTH codes, because they answer
              //  different questions: the ICD-10 says what the patient has, the
              //  G-DRG says what it is billed under. Vetting needs both on screen.
              rowLabel={(r) => [r.icd10 || r.code, r.gdrg].filter(Boolean).join('  ·  ')} />

            <MvFilterBox n={5} label="Procedure"
              hint="Search procedure or G-DRG…"
              items={facets?.procedures}
              chosen={pattern.procedure}
              onSet={(v) => setPattern((p) => ({ ...p, procedure: v }))} />

            {/*  Lab before drugs — tests are ordered before treatment. */}
            <MvFilterBox n={6} label="Lab / Scan"
              hint="Search test name or code…"
              items={facets?.investigations}
              chosen={pattern.investigation}
              onSet={(v) => setPattern((p) => ({ ...p, investigation: v }))} />

            <MvFilterBox n={7} label="Medication"
              hint="Search drug name or code…"
              items={facets?.medications}
              chosen={pattern.medication}
              onSet={(v) => setPattern((p) => ({ ...p, medication: v }))} />
          </div>

          {/*  ══ WHERE THE CLAIMS TABLE WAS ═══════════════════════════════════
               A 200-row table of matched claims used to sit here. It has been taken
               out on purpose: it competed with the four filter boxes for the panel's
               height and won, so the lists you actually pick from were 176px tall
               while the list you only read was the size of the dialog.

               The count is on the pattern header and the claims themselves get their
               own form, opened from the footer. */}
            </>
          )}

          {/*  ══ COMPOSE — WHAT GOES ON THE TICKED CLAIMS ═════════════════════
               The full width, because the four pickers here are the single-claim
               editor's own controls and they need the room that editor gives them.

               A basket, not one item: a routine is a diagnosis AND its labs AND its
               drugs, and building it in one pass is the whole reason this screen
               exists. The endpoints behind it already take a basket and are tested —
               see /claims/multivet/batch — so this page fills it and applies once. */}
          {view === 'compose' && (
            <>
            {/*  ── THE BASKET, ABOVE THE PICKERS ───────────────────────────────
                 What has been chosen so far, and the one Apply. Above rather than
                 below, because it is the thing being built and the pickers are how
                 you feed it — and because on a page of four scrolling lists a
                 footer strip would be the first thing off the bottom.

                 It goes through the same dry-run-then-apply path the endpoint
                 enforces: nothing is written until the server has said what would
                 happen, including the age-band conflicts it refuses outright. */}
            {/*  ══ THE SERVICE, MAPPED ═════════════════════════════════════════
                 Left: what the group carries now, one row per distinct service, with
                 how many claims hold it. Right: the catalogue, filtered by the
                 pattern's attendance — the same list "Select Service" offers on one
                 claim.

                 Two columns rather than the four-catalogue grid, because there is one
                 catalogue here and the thing being mapped has to sit beside it. */}
            {action === 'service' && (
              <div style={{
                display: 'grid', gap: 14, flexShrink: 0, minWidth: 0,
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              }}>
                {/*  ── WHAT THEY CARRY NOW ─────────────────────────────────── */}
                <div style={{
                  background: '#fff', border: `1px solid ${BRAND.line}`, borderRadius: 12,
                  padding: 16, minWidth: 0,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
                    textTransform: 'uppercase', color: BRAND.inkSoft, marginBottom: 6,
                  }}>The services in this group</div>
                  <div style={{
                    fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.6, marginBottom: 10,
                  }}>
                    Click one, then pick what it becomes. A service left unmapped is left
                    alone.
                  </div>
                  <div style={{ display: 'grid', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
                    {serviceRows.length === 0 ? (
                      <span style={{ fontSize: 12, color: BRAND.inkFaint }}>
                        No claims are ticked.
                      </span>
                    ) : serviceRows.map((r) => {
                      const on = activeService === r.key;
                      return (
                        <div key={r.key} role="button" tabIndex={0}
                          onClick={() => setActiveService(on ? null : r.key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault(); setActiveService(on ? null : r.key);
                            }
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
                            padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
                            border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                            borderLeft: `3px solid ${on ? BRAND.greenDark : 'transparent'}`,
                            background: on ? BRAND.greenSoft : '#fff',
                          }}>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{
                              display: 'block', fontSize: 12, fontWeight: 700,
                              color: r.code ? BRAND.ink : (BRAND.caution || '#8A5510'),
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {/*  A claim with no service is a real cohort, not a gap in
                                   the data — it is the Unspecified group, and naming it
                                   plainly is what makes it selectable. */}
                              {r.code ? (r.name || r.code) : 'No service set'}
                            </span>
                            <span style={{
                              display: 'block', fontSize: 10.5, color: BRAND.inkFaint,
                              ...S.mono,
                            }}>{r.code || '—'}</span>
                          </span>
                          {r.to && (
                            <>
                              <ArrowRight size={13} style={{ flexShrink: 0 }}
                                color={BRAND.greenDark} />
                              <span style={{
                                flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 750,
                                color: BRAND.greenDark, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>{r.to.label || r.to.code}</span>
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setServiceTo((m) => {
                                    const n = { ...m }; delete n[r.key]; return n;
                                  });
                                  setActiveService(r.key);
                                  setBatchPlan(null);
                                }}
                                aria-label={`Do not change ${r.name || r.code || 'this service'}`}
                                style={{
                                  flexShrink: 0, border: 0, background: 'none',
                                  cursor: 'pointer', color: BRAND.inkFaint, padding: 2,
                                  lineHeight: 1,
                                }}>✕</button>
                            </>
                          )}
                          <span style={{
                            flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                            whiteSpace: 'nowrap',
                            color: r.patients === targetCount
                              ? BRAND.greenDark : (BRAND.caution || '#8A5510'),
                          }}>
                            {r.patients === targetCount
                              ? 'all' : `${r.patients} of ${targetCount}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/*  ── AND WHAT IT BECOMES ─────────────────────────────────── */}
                <div style={{
                  background: '#fff', borderRadius: 12, padding: 16, minWidth: 0,
                  border: `1px solid ${activeService ? BRAND.greenDark : BRAND.line}`,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
                    textTransform: 'uppercase', color: BRAND.inkSoft, marginBottom: 6,
                  }}>
                    {pattern.type === 'ipd' ? 'In-patient services' : 'Out-patient services'}
                  </div>
                  <div style={{
                    fontSize: 12, lineHeight: 1.6, marginBottom: 10,
                    color: activeService ? BRAND.greenDark : BRAND.inkMuted,
                    fontWeight: activeService ? 600 : 400,
                  }}>
                    {activeService
                      ? (() => {
                        const r = serviceRows.find((x) => x.key === activeService);
                        return <>Choosing what <b>{r?.code
                          ? (r.name || r.code) : 'the claims with no service'}</b> becomes
                          {r?.patients != null && (
                            <span style={{ color: BRAND.inkMuted, fontWeight: 400 }}>
                              {' '}· {r.patients} claim(s)
                            </span>
                          )}</>;
                      })()
                      : 'Click a service on the left first.'}
                  </div>
                  {/*  ── WHO IS IN THE GROUP, SO THE LIST MAKES SENSE ──────────
                       A list narrowed to Child services with nothing saying why looks
                       like a short catalogue. And on a MIXED group nothing is narrowed,
                       which is the case the officer most needs to know about — that is
                       where "General OPD - Adult" is one click from an infant. */}
                  <div style={{
                    fontSize: 11.5, lineHeight: 1.55, marginBottom: 8,
                    padding: '6px 9px', borderRadius: 7,
                    background: cohort.band === 'mixed' ? BRAND.dangerSoft : BRAND.canvas,
                    color: cohort.band === 'mixed' ? BRAND.danger : BRAND.inkMuted,
                    border: `1px solid ${cohort.band === 'mixed'
                      ? `${BRAND.danger}44` : BRAND.line}`,
                  }}>
                    {cohort.band === 'mixed'
                      ? <><b>{cohort.adult} adult and {cohort.child} child claim(s)</b> —
                          so no age narrows this list. An Adult service cannot be applied
                          to the children, and it will be refused rather than written.
                          Split the pattern by age to work on one band at a time.</>
                      : cohort.band === 'child'
                        ? <>All {cohort.child} claim(s) are under 12 — showing the Child
                            services.</>
                        : cohort.band === 'adult'
                          ? <>All {cohort.adult} claim(s) are 12 or over — showing the
                              Adult services.</>
                          : <>No dates of birth on these claims, so the age band is
                              unknown and the list is not narrowed.</>}
                    {cohort.hasMale && (
                      <>{' '}<b>{cohort.male} male claim(s)</b> — antenatal, postnatal and
                        obstetric services are refused on this group.</>
                    )}
                  </div>
                  {serviceNote && (
                    <div style={{
                      fontSize: 11.5, color: BRAND.caution || '#8A5510', marginBottom: 8,
                    }}>{serviceNote}</div>
                  )}
                  <div style={{ display: 'grid', gap: 3, maxHeight: 300, overflowY: 'auto' }}>
                    {serviceOptions.length === 0 ? (
                      <span style={{ fontSize: 12, color: BRAND.inkFaint }}>
                        Loading the service list…
                      </span>
                    ) : serviceOptions.map((o) => {
                      const code = String(o.code || '').toUpperCase();
                      const active = serviceRows.find((x) => x.key === activeService);
                      //  The service a row already carries is not something it can
                      //  become. Offering it would delete the row and write it back.
                      const same = active && active.code === code;
                      //  ── WHAT THIS GROUP CANNOT BE GIVEN ────────────────────
                      //  Marked here and refused at the server, which is the pair this
                      //  system uses everywhere: the screen stops it being offered, the
                      //  endpoint stops it being written. Neither on its own is enough —
                      //  a disabled button is a courtesy, and a refusal after the click
                      //  is a wasted decision.
                      const femaleOnly = /^(ANC|PNC|OBGY)/i.test(code)
                        || /antenatal|postnatal|post-natal|delivery|matern|obstetric|gyna?ecolog/i
                          .test(String(o.label || ''));
                      const wrongSex = femaleOnly && cohort.hasMale;
                      //  The A/C suffix is the age band — OPDC06A adult, OPDC06C child —
                      //  the same suffix claimRules.ageBandOf reads.
                      const codeBand = /^[A-Z]{2,5}\d{1,3}A$/.test(code) ? 'adult'
                        : /^[A-Z]{2,5}\d{1,3}C$/.test(code) ? 'child' : null;
                      const wrongBand = codeBand !== null && cohort.band !== null
                        && cohort.band !== 'mixed' && codeBand !== cohort.band;
                      //  On a MIXED group an age-specific service is wrong for part of
                      //  it whichever way it is chosen, so both are refused rather than
                      //  one arbitrarily allowed.
                      const mixedBand = codeBand !== null && cohort.band === 'mixed';
                      const barred = wrongSex || wrongBand || mixedBand;
                      const why = wrongSex
                        ? `This group includes ${cohort.male} male claim(s) — an `
                          + 'antenatal, postnatal or obstetric service cannot be applied '
                          + 'to them.'
                        : mixedBand
                          ? `This group holds ${cohort.adult} adult and ${cohort.child} `
                            + 'child claim(s). Split it by age band first.'
                          : wrongBand
                            ? `Every claim in this group is ${cohort.band === 'child'
                              ? 'under 12' : '12 or over'}, and this is the `
                              + `${codeBand} service.`
                            : null;
                      return (
                        <button key={code} type="button"
                          disabled={!activeService || same || barred}
                          title={why
                            || (same ? 'These claims already carry this service'
                              : !activeService ? 'Click a service on the left first' : undefined)}
                          onClick={() => {
                            setServiceTo((m) => ({
                              ...m, [activeService]: { code, label: o.label || code },
                            }));
                            setBatchPlan(null);
                            const next = serviceRows.find(
                              (x) => x.key !== activeService && !x.to);
                            setActiveService(next ? next.key : null);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
                            textAlign: 'left', font: 'inherit', fontSize: 12,
                            padding: '6px 9px', borderRadius: 7,
                            cursor: (activeService && !same && !barred)
                              ? 'pointer' : 'not-allowed',
                            border: `1px solid ${barred ? `${BRAND.danger}33` : BRAND.line}`,
                            background: (same || barred) ? BRAND.canvas : '#fff',
                            color: (activeService && !same && !barred)
                              ? BRAND.ink : BRAND.inkFaint,
                          }}>
                          <span style={{
                            flex: 1, minWidth: 0, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            textDecoration: barred ? 'line-through' : 'none',
                          }}>{o.label || code}</span>
                          {/*  Why it is struck through, on the row itself. A greyed
                               option with the reason only in a tooltip is a dead end for
                               anyone who does not hover. */}
                          {barred && (
                            <span style={{
                              flexShrink: 0, fontSize: 9.5, fontWeight: 800,
                              letterSpacing: '.04em', textTransform: 'uppercase',
                              color: BRAND.danger, whiteSpace: 'nowrap',
                            }}>
                              {wrongSex ? 'female only'
                                : mixedBand ? 'mixed ages'
                                  : codeBand === 'adult' ? 'adult only' : 'child only'}
                            </span>
                          )}
                          <span style={{
                            flexShrink: 0, fontSize: 10, color: BRAND.inkFaint, ...S.mono,
                          }}>{code}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/*  ── TYPE OF ATTENDANCE ──────────────────────────────────────────
                 Its own card, above the picture, because it is not one of the four
                 catalogues: it is a HEADER field with exactly one value per claim, so
                 it is chosen rather than searched for and mapped.

                 Shown only when that is the act. Four more buttons on a screen already
                 holding a basket and a mapping would be four more things to rule out. */}
            {/*  ══ A WHOLE TEMPLATE, ONTO EVERY CLAIM IT SUITS ═════════════════
                 Its own component and its own Apply, because its answer is a VERDICT
                 PER CLAIM rather than a tally for a basket — a group of 300 will hold
                 claims a child template suits and claims it does not, and the officer
                 needs to know which. See MV_OWN_APPLY, and the header of that file for
                 the four rules it keeps from the shared path.

                 `targetIds`, not `ids`: the ticked rows of the PATTERN, which is the bug
                 the comment above targetIds records — `ids` is the table behind the
                 dialog and is empty on every real use. */}
            {action === 'template' && (
              <MultiVetTemplate
                claimIds={targetIds}
                //  Same pair the basket's own apply calls: re-read the selection so the
                //  rows show their new service codes, and tell the parent so the month
                //  behind the dialog is refreshed too.
                onApplied={() => { reloadSelection(); onDone(); }} />
            )}

            {action === 'attendance' && (
              <div style={{
                background: '#fff', border: `1px solid ${BRAND.line}`, borderRadius: 12,
                padding: 16, flexShrink: 0, minWidth: 0,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
                  textTransform: 'uppercase', color: BRAND.inkSoft, marginBottom: 6,
                }}>Type of attendance</div>
                <div style={{
                  fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.6, marginBottom: 10,
                }}>
                  One code, on all {targetCount.toLocaleString()} ticked claim(s). A claim
                  that already carries it is left alone and counted as already right.
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {MV_ATTENDANCE.map(([code, label]) => {
                    const on = attendance === code;
                    return (
                      <button key={code} type="button" aria-pressed={on}
                        onClick={() => { setAttendance(on ? '' : code); setBatchPlan(null); }}
                        style={{
                          display: 'inline-flex', alignItems: 'baseline', gap: 6,
                          padding: '7px 13px', borderRadius: 8, font: 'inherit',
                          fontSize: 12.5, fontWeight: on ? 800 : 650, cursor: 'pointer',
                          border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                          background: on ? BRAND.greenDark : '#fff',
                          color: on ? '#fff' : BRAND.ink,
                        }}>
                        {label}
                        <span style={{
                          fontSize: 10, fontWeight: 800, opacity: on ? 0.8 : 0.55,
                          ...S.mono,
                        }}>{code}</span>
                      </button>
                    );
                  })}
                </div>
                {/*  ── NO CAVEAT HERE. IT WAS REMOVED ON PURPOSE ────────────────
                     There was a panel warning that an antenatal G-DRG cannot carry
                     Emergency/Acute. It appeared whichever of the four was chosen, so
                     on the other three it was a lecture about a case the officer had
                     not asked about — and on this screen, which exists to apply the
                     code to a group already narrowed by the pattern, the whole note
                     was in the wrong place.

                     Whether a service and an attendance contradict each other is a
                     RULE about a claim, and rules live in claimRules.js where the
                     badges and the repair panel read them: one place, applied to every
                     claim however it was edited, rather than a paragraph on the one
                     screen that happens to set the field. Nothing here refuses a
                     combination — the officer picks the code and it is applied.

                     See the footer for what a run would actually do: it reports how
                     many of the ticked claims would change and how many already carry
                     the code, which is information rather than advice. */}
              </div>
            )}

            {/*  ── THE APPLY BUTTON LIVES IN HERE ──────────────────────────────
                 So this gate decides whether the run can be applied AT ALL, and it
                 listed only the basket, the pattern's own items and the mapping. An
                 attendance-only run has none of those: the card never rendered, and the
                 officer picked Emergency/Acute, looked at four catalogue lists they did
                 not need, and had nothing to press. Every kind of work a run can carry
                 has to be in this condition.

                 EXCEPT an act that brings its own — see MV_OWN_APPLY. A template writes
                 through a different endpoint whose answer is a verdict per claim, so it
                 has its own card and its own button; rendering this one beside it would
                 put two Apply buttons on the page, and the dead one would be the one
                 wired to the basket nobody filled. */}
            {!MV_OWN_APPLY.has(action)
              && (basket.length > 0 || patternItems.length > 0 || targetRows.length > 0
              || replacements.length > 0 || !!attendance || serviceMap.length > 0) && (
              <div style={{
                background: '#fff', border: `1px solid ${BRAND.greenDark}55`,
                borderRadius: 12, flexShrink: 0,
              }}>
                <div style={{
                  padding: '9px 14px', background: BRAND.greenSoft,
                  borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center',
                  gap: 9, fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
                  textTransform: 'uppercase', color: BRAND.greenDark,
                }}>
                  <span>
                    The whole picture
                    {targetRows.length > 0 && ` · changing ${targetRows.length}`}
                    {basket.length > 0 && ` · adding ${basket.length}`}
                    {serviceMap.length > 0 && ` · service ${serviceMap.length}`}
                    {attendance && ` · attendance ${attendance}`}
                  </span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700 }}>
                    on {chosenCount.toLocaleString()} claim(s)
                  </span>
                </div>

                {/*  ── WHAT THEY ALREADY HAVE ────────────────────────────────────
                     The pattern's own filters. If the group was found by "diagnosis
                     = Malaria" then every patient in it has malaria — that is the
                     definition of the group, not a guess — so it sits above what is
                     being added, and the two read as one structure.

                     NO REMOVE. Dropping one of these would not change the claims, it
                     would change the GROUP, which is done on the pattern page behind
                     Back. A ✕ here that silently re-ran the search would be the same
                     control meaning two different things. Locked, and it says so. */}
                {patternItems.length > 0 && (
                  <>
                    <div style={{
                      padding: '7px 14px 5px', borderTop: `1px solid ${BRAND.line}`,
                      background: BRAND.canvas, display: 'flex', alignItems: 'baseline',
                      gap: 8,
                    }}>
                      <span style={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                        textTransform: 'uppercase', color: BRAND.inkFaint,
                      }}>Already on these claims</span>
                      <span style={{ fontSize: 11, color: BRAND.inkFaint }}>
                        from the pattern — change it with Back
                      </span>
                    </div>
                    {patternItems.map((it) => (
                      <div key={`${it.kind}-${it.code}`} style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        padding: '6px 14px', background: BRAND.canvas,
                        fontSize: 12.5, minWidth: 0, color: BRAND.inkSoft,
                      }}>
                        <span style={{
                          flexShrink: 0, minWidth: 70, fontSize: 9.5, fontWeight: 800,
                          letterSpacing: '.05em', textTransform: 'uppercase',
                          color: BRAND.inkFaint,
                        }}>{it.label}</span>
                        <code style={{ fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                          {it.code}
                        </code>
                        <span style={{
                          flex: 1, minWidth: 0, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{it.name || '—'}</span>
                        {/*  A lock rather than a disabled ✕: a greyed-out remove
                             invites clicking and then explains nothing. */}
                        <span style={{
                          flexShrink: 0, fontSize: 9.5, fontWeight: 800,
                          letterSpacing: '.05em', textTransform: 'uppercase',
                          color: BRAND.inkFaint,
                        }}>fixed</span>
                      </div>
                    ))}
                  </>
                )}

                {/*  ── THE SERVICE CHANGES, IN THE PICTURE ─────────────────────
                     One row per mapped service, so the card holding Apply shows what
                     Apply will do rather than being empty on a service run. */}
                {serviceMap.length > 0 && serviceRows.filter((r) => r.to).map((r) => (
                  <div key={`svc-${r.key}`} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '7px 14px',
                    borderTop: `1px solid ${BRAND.line}`, fontSize: 12.5, minWidth: 0,
                  }}>
                    <span style={{
                      flexShrink: 0, minWidth: 67, fontSize: 9.5, fontWeight: 800,
                      letterSpacing: '.05em', textTransform: 'uppercase',
                      color: BRAND.inkFaint,
                    }}>service</span>
                    <span style={{
                      flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', color: BRAND.inkFaint,
                      textDecoration: r.code ? 'line-through' : 'none',
                    }}>
                      {r.code
                        ? <><code style={{ fontSize: 11, fontWeight: 700 }}>{r.code}</code>
                            {'  '}{r.name || ''}</>
                        : 'no service set'}
                    </span>
                    <ArrowRight size={13} style={{ flexShrink: 0 }} color={BRAND.greenDark} />
                    <span style={{
                      flex: 1, minWidth: 0, fontWeight: 750, color: BRAND.greenDark,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      <code style={{ fontSize: 11, fontWeight: 700 }}>{r.to.code}</code>
                      {'  '}{r.to.label || ''}
                    </span>
                    <span style={{
                      flexShrink: 0, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
                      color: BRAND.inkMuted,
                    }}>{r.patients} claim(s)</span>
                    <button type="button"
                      onClick={() => {
                        setServiceTo((m) => {
                          const n = { ...m }; delete n[r.key]; return n;
                        });
                        setBatchPlan(null);
                      }}
                      aria-label={`Do not change ${r.name || r.code || 'this service'}`}
                      style={{
                        flexShrink: 0, border: 0, background: 'none', cursor: 'pointer',
                        color: BRAND.inkFaint, padding: 2, lineHeight: 1,
                      }}>✕</button>
                  </div>
                ))}

                {/*  ── THE ATTENDANCE, IN THE PICTURE ──────────────────────────
                     A card whose heading says "the whole picture" and then shows
                     nothing is worse than no card. This is the one row an
                     attendance-only run has, so it belongs in the same list as the
                     basket and the mapping rather than only in the picker above. */}
                {attendance && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px',
                    borderTop: `1px solid ${BRAND.line}`, fontSize: 12.5, minWidth: 0,
                  }}>
                    <span style={{
                      flexShrink: 0, minWidth: 67, fontSize: 9.5, fontWeight: 800,
                      letterSpacing: '.05em', textTransform: 'uppercase',
                      color: BRAND.inkFaint,
                    }}>attendance</span>
                    <code style={{ fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                      {attendance}
                    </code>
                    <span style={{
                      flex: 1, minWidth: 0, fontWeight: 700, color: BRAND.greenDark,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {(MV_ATTENDANCE.find(([c]) => c === attendance) || [, attendance])[1]}
                      <span style={{ fontWeight: 500, color: BRAND.inkMuted }}>
                        {' '}— on every ticked claim that does not already carry it
                      </span>
                    </span>
                    <button type="button"
                      onClick={() => { setAttendance(''); setBatchPlan(null); }}
                      aria-label="Do not set the type of attendance"
                      style={{
                        flexShrink: 0, border: 0, background: 'none', cursor: 'pointer',
                        color: BRAND.inkFaint, padding: 2, lineHeight: 1,
                      }}>✕</button>
                  </div>
                )}

                {/*  ══ THE MAPPING: THIS BECOMES THAT ═══════════════════════════
                     One row per entry ticked on the patients page. Each maps to its
                     OWN replacement, picked from the catalogue below — because
                     "replace these three diagnoses with these three" has no single
                     meaning, and the officer is the only one who knows which becomes
                     which.

                     The row being filled is highlighted and the catalogue card says
                     whose replacement it is taking. Without that, a click in the
                     diagnosis box on a screen with three unmapped diagnoses has three
                     possible meanings and picks one silently. */}
                {targetRows.length > 0 && (
                  <>
                    <div style={{
                      padding: '7px 14px 5px', borderTop: `1px solid ${BRAND.line}`,
                      display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                    }}>
                      <span style={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                        textTransform: 'uppercase', color: BRAND.danger,
                      }}>Changing</span>
                      <span style={{ fontSize: 11, color: BRAND.inkFaint }}>
                        {unmapped.length
                          ? `pick a replacement for ${unmapped.length} more below`
                          : 'the old entry is removed, the new one written in its place'}
                      </span>
                    </div>
                    {targetRows.map((r) => {
                      const on = activeTarget === r.key;
                      return (
                        <div key={r.key}>
                          <div
                            role="button" tabIndex={0}
                            onClick={() => setActiveTarget(on ? null : r.key)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); setActiveTarget(on ? null : r.key);
                              }
                            }}
                            title={r.to
                              ? 'Click to choose a different replacement'
                              : `Click, then pick a ${r.kind} below`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 9,
                              padding: '7px 14px', cursor: 'pointer',
                              borderTop: `1px solid ${BRAND.line}`,
                              borderLeft: `3px solid ${on ? BRAND.danger : 'transparent'}`,
                              background: on ? BRAND.dangerSoft : '#fff',
                              fontSize: 12.5, minWidth: 0,
                            }}>
                            <span style={{
                              flexShrink: 0, minWidth: 67, fontSize: 9.5, fontWeight: 800,
                              letterSpacing: '.05em', textTransform: 'uppercase',
                              color: BRAND.inkFaint,
                            }}>{r.kind}</span>

                            {/*  What goes. Struck through once a replacement is
                                 chosen, because at that point it is leaving. */}
                            <span style={{
                              flex: 1, minWidth: 0, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              color: r.to ? BRAND.inkFaint : BRAND.ink,
                              textDecoration: r.to ? 'line-through' : 'none',
                            }}>
                              <code style={{ fontSize: 11, fontWeight: 700 }}>{r.code}</code>
                              {'  '}{r.name}
                            </span>

                            <ArrowRight size={13} style={{ flexShrink: 0 }}
                              color={r.to ? BRAND.greenDark : BRAND.inkFaint} />

                            {/*  What arrives, or an invitation. A blank slot that says
                                 nothing is a row nobody knows is unfinished. */}
                            <span style={{
                              flex: 1, minWidth: 0, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontWeight: (r.to || r.drop) ? 750 : 600,
                              color: r.drop ? BRAND.danger
                                : r.to ? BRAND.greenDark
                                  : (on ? BRAND.danger : BRAND.inkFaint),
                            }}>
                              {r.drop
                                ? 'removed — nothing takes its place'
                                : r.to
                                  ? <>
                                      <code style={{ fontSize: 11, fontWeight: 700 }}>
                                        {r.to.code}
                                      </code>{'  '}{r.to.name || ''}
                                    </>
                                  : (on ? `Pick a ${r.kind} below →` : 'nothing chosen yet')}
                            </span>

                            {/*  ── OVERRIDE WITH NOTHING IS A DELETE ───────────
                                 Offered only under Override, because that is the act
                                 that says it removes things — the card in step 1 reads
                                 "Override / Delete". Under Update the same button would
                                 be a delete hidden inside an operation whose whole
                                 promise is that it swaps. */}
                            {action === 'override' && (
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMapping((m) => (r.drop
                                    ? (() => { const n = { ...m }; delete n[r.key]; return n; })()
                                    : { ...m, [r.key]: { remove: true } }));
                                  setBatchPlan(null);
                                  //  Marking a row for deletion finishes it, so the
                                  //  next unfinished one takes the arming.
                                  if (!r.drop && on) {
                                    const next = targetRows.find(
                                      (x) => x.key !== r.key && !x.to && !x.drop);
                                    setActiveTarget(next ? next.key : null);
                                  }
                                }}
                                title={r.drop
                                  ? 'Keep it, and choose a replacement instead'
                                  : `Take ${r.name} off every claim carrying it`}
                                style={{
                                  flexShrink: 0, padding: '3px 8px', borderRadius: 6,
                                  font: 'inherit', fontSize: 10.5, fontWeight: 700,
                                  cursor: 'pointer', whiteSpace: 'nowrap',
                                  border: `1px solid ${r.drop ? BRAND.danger : `${BRAND.danger}55`}`,
                                  background: r.drop ? BRAND.danger : BRAND.dangerSoft,
                                  color: r.drop ? '#fff' : BRAND.danger,
                                }}>
                                {r.drop ? 'Keep it' : 'Remove it'}
                              </button>
                            )}

                            {/*  How many of the ticked patients carry the entry being
                                 replaced. This is the number that separates a shared
                                 entry from one patient's outlier, and it is the reason
                                 to map by hand rather than in bulk. */}
                            {r.patients != null && (
                              <span style={{
                                flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                                whiteSpace: 'nowrap',
                                color: r.patients === targetCount
                                  ? BRAND.greenDark : (BRAND.caution || '#8A5510'),
                              }}>
                                {r.patients === targetCount
                                  ? 'all' : `${r.patients} of ${targetCount}`}
                              </span>
                            )}

                            {/*  Un-ticks the entry entirely — the mapping goes with it,
                                 because a replacement chosen for a row that is no
                                 longer being changed is not a thing to keep. */}
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTarget((t) => t.filter((k) => k !== r.key));
                                setMapping((m) => {
                                  const next = { ...m }; delete next[r.key]; return next;
                                });
                                if (on) setActiveTarget(null);
                              }}
                              aria-label={`Stop changing ${r.name}`}
                              style={{
                                flexShrink: 0, border: 0, background: 'none',
                                cursor: 'pointer', color: BRAND.inkFaint, padding: 2,
                                lineHeight: 1,
                              }}>✕</button>
                          </div>

                          {/*  A replacement DRUG is a prescription, the same as a new
                               one — it is a written row either way. Same component,
                               same six fields, same calculated quantity. */}
                          {r.to && r.kind === 'medication' && (
                            <MvRx v={r.to} vocab={rxVocab} suggest={suggestQty}
                              onChange={(next) => setMapping((m) => ({ ...m, [r.key]: next }))}
                              onRemove={() => {
                                setMapping((m) => {
                                  const n = { ...m }; delete n[r.key]; return n;
                                });
                                setActiveTarget(r.key);
                              }}
                              removeLabel={`Choose a different replacement for ${r.name}`} />
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/*  ── AND WHAT IS BEING ADDED ─────────────────────────────────
                     Removable, because none of it is written yet. */}
                {basket.length > 0 && (
                  <div style={{
                    padding: '7px 14px 5px', borderTop: `1px solid ${BRAND.line}`,
                    display: 'flex', alignItems: 'baseline', gap: 8,
                  }}>
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: BRAND.greenDark,
                    }}>Adding</span>
                    <span style={{ fontSize: 11, color: BRAND.inkFaint }}>
                      nothing is written until you apply
                    </span>
                  </div>
                )}
                <div style={{ display: 'grid' }}>
                  {basket.map((b) => (
                    <div key={`${b.kind}-${b.code}`}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 9, padding: '7px 14px',
                      borderTop: `1px solid ${BRAND.line}`, fontSize: 12.5, minWidth: 0,
                    }}>
                      <span style={{
                        flexShrink: 0, minWidth: 70, fontSize: 9.5, fontWeight: 800,
                        letterSpacing: '.05em', textTransform: 'uppercase',
                        color: BRAND.inkFaint,
                      }}>{b.kind}</span>
                      <code style={{ fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {b.code}
                      </code>
                      <span style={{
                        flex: 1, minWidth: 0, color: BRAND.inkSoft, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{b.name || '—'}</span>
                      {b.tariff != null && (
                        <span style={{
                          flexShrink: 0, fontSize: 11.5, color: BRAND.inkSoft,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{Number(b.tariff).toFixed(2)}</span>
                      )}
                      <button type="button"
                        onClick={() => { dropFromBasket(b.kind, b.code); }}
                        aria-label={`Remove ${b.name || b.code}`}
                        style={{
                          flexShrink: 0, border: 0, background: 'none', cursor: 'pointer',
                          color: BRAND.inkFaint, padding: 2, lineHeight: 1,
                        }}>✕</button>
                    </div>

                    {/*  A medication is a prescription — see MvRx, which the update
                         mapping below shares. Same six fields, same arithmetic, one
                         component: a replacement drug needs every one of them for
                         exactly the reason a new one does, and two copies of that
                         block would have drifted inside a month. */}
                    {b.kind === 'medication' && (
                      <MvRx v={b} vocab={rxVocab} suggest={suggestQty}
                        onChange={(next) => setBasket((rows) => rows.map((r) => (
                          r.kind === b.kind && r.code === b.code ? next : r)))}
                        onRemove={() => dropFromBasket(b.kind, b.code)}
                        removeLabel={`Remove ${b.name || b.code} from the basket`} />
                    )}
                    </div>
                  ))}
                </div>

                {/*  The guard, and the count, before the write. Both come from the
                     server's dry run — the same one the endpoint requires. */}
                {batchPlan?.conflicts?.length > 0 && (
                  <div style={{
                    padding: '9px 14px', borderTop: `1px solid ${BRAND.line}`,
                    background: BRAND.dangerSoft, color: BRAND.danger,
                    fontSize: 11.5, lineHeight: 1.6,
                  }}>
                    {batchPlan.conflicts.map((c) => (
                      <div key={`${c.kind}-${c.code}`}>{c.why}</div>
                    ))}
                  </div>
                )}

                <div style={{
                  display: 'flex', gap: 9, alignItems: 'center', padding: '10px 14px',
                  borderTop: `1px solid ${BRAND.line}`, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: BRAND.inkMuted }}>
                    {/*  The one line that says why Apply is not available. It read
                         "Add or remove entries, then apply" whatever the reason —
                         including while a prescription was half-written, which is the
                         case where the officer most needs telling what is missing.

                         An earlier attempt at this went in through a shell replace
                         that silently matched nothing, so the line never changed.
                         Worth the note: a Replace that reports success because it
                         found zero occurrences is indistinguishable from one that
                         worked. */}
                    {/*  A multi-pass run says which pass it is on. "Checking…" for
                         ninety seconds on a 1,894-claim group is indistinguishable
                         from the stuck spinner this footer used to show. */}
                    {progress
                      ? `${progress.phase === 'apply' ? 'Applying' : 'Checking'} — pass `
                        + `${progress.pass} of ${progress.passes}, `
                        + `${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} claim(s) so far…`
                      : busy
                        ? (batchPlan ? 'Applying…' : 'Checking…')
                        : !targetCount
                          ? 'No patients are ticked. Go back and tick who this applies to.'
                          //  An unfinished MAPPING is named before an empty basket:
                          //  a half-mapped update is a job in progress, and telling
                          //  the officer to "pick something to add" in the middle of
                          //  one is an instruction for a different task.
                          : unmapped.length
                            ? `Pick what ${unmapped[0].name} becomes — ${unmapped.length} `
                              + `entr${unmapped.length === 1 ? 'y' : 'ies'} still to map.`
                            : swapRxIncomplete.length
                              ? `Finish the prescription replacing ${swapRxIncomplete[0].name} — every field is required.`
                              : (basket.length === 0 && replacements.length === 0 && !attendance
                                && serviceMap.length === 0)
                                ? (action === 'attendance'
                                  ? 'Pick the type of attendance above.'
                                  : action === 'service'
                                    ? 'Click a service above, then pick what it becomes.'
                                    : 'Pick something below to add to these claims.')
                                : rxIncomplete.length
                                  ? `Finish the prescription for ${rxIncomplete[0].name || rxIncomplete[0].code} — every field is required.`
                                  : batchPlan
                                    //  Worded for what the run actually is. "The rest
                                    //  already have these" reads oddly about a single
                                    //  header field, where the right sentence is that
                                    //  they already carry the code.
                                    ? `${batchPlan.wouldTouch ?? 0} of ${targetCount} claim(s) would change`
                                      + (batchPlan.perAttendance && !basket.length
                                        && !replacements.length
                                        ? ` — ${(batchPlan.perAttendance.alreadyRight || 0)
                                          .toLocaleString()} already carry ${batchPlan.perAttendance.code}.`
                                        : ' — the rest already have these.')
                                    : 'Checking what this would do…'}
                  </div>
                  {/*  The name is not asked for — it is the pattern. Shown on the
                       button's own tooltip so it is visible BEFORE the click rather
                       than only inside the dialog, and so there is no moment where the
                       officer wonders what it will be called. */}
                  <button type="button" onClick={saveRoutine}
                    disabled={busy || savingRoutine || !basket.length || !routineName}
                    title={routineName
                      ? `Save as "${routineName}" — you will be asked whether it is premium`
                      : 'Pick something first'}
                    style={{
                      padding: '8px 13px', borderRadius: 8, fontSize: 12,
                      cursor: basket.length && routineName ? 'pointer' : 'not-allowed',
                      fontWeight: 700, fontFamily: 'inherit', background: '#fff',
                      border: `1px solid ${BRAND.line}`, color: BRAND.inkSoft,
                    }}>{savingRoutine ? 'Saving…' : 'Save as routine'}</button>
                  {/*  ── A HALF-WRITTEN PRESCRIPTION CANNOT BE APPLIED ──────────
                       NHIA refuses a medicine line with no dose unit and the store
                       validates the other five the same way the single-claim editor
                       does. Applying an incomplete drug to 153 claims would be 153
                       lines rejected — so Apply waits, and the empty fields are
                       already outlined in red above. */}
                  {(() => {
                    //  One expression, read twice — by `disabled` and by the styling.
                    //  They were two copies of the same condition, which is how a
                    //  button that looks enabled and does nothing gets built.
                    const ready = !busy && !!batchPlan && targetCount > 0
                      && (basket.length > 0 || replacements.length > 0 || !!attendance
                        || serviceMap.length > 0)
                      && (batchPlan.conflicts || []).length === 0
                      && rxIncomplete.length === 0
                      //  A HALF-MAPPED UPDATE CANNOT BE APPLIED. It would change some
                      //  of what was ticked and quietly leave the rest, which reads as
                      //  the run having failed on those claims rather than as a row
                      //  never filled in.
                      && unmapped.length === 0 && swapRxIncomplete.length === 0;
                    return (
                      <button type="button" onClick={applyBasket} disabled={!ready}
                        title={unmapped.length
                          ? `Pick what ${unmapped[0].name} becomes`
                          : swapRxIncomplete.length
                            ? `Finish the prescription replacing ${swapRxIncomplete[0].name}`
                            : rxIncomplete.length
                              ? `Finish the prescription for ${rxIncomplete[0].name || rxIncomplete[0].code}`
                              : !targetCount
                                ? 'Go back to the patients and tick who this applies to'
                                : undefined}
                        style={{
                          padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                          fontFamily: 'inherit', border: '1px solid transparent', color: '#fff',
                          cursor: ready ? 'pointer' : 'not-allowed',
                          background: ready ? BRAND.greenDark : BRAND.inkFaint,
                        }}>
                        {busy ? 'Working…' : `Apply to ${targetCount.toLocaleString()}`}
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}

            {/*  ── THE FOUR CATALOGUES, WHEN THEY ARE PART OF THE ACT ──────────
                 Hidden on an attendance run. The type of attendance is a header field:
                 there is nothing to search for and nothing to pick from a catalogue, so
                 four scrolling lists of diagnoses and drugs were four things to rule out
                 before finding the one control that mattered — and the officer reported
                 exactly that, not knowing how to go about it. */}
            {MV_USES_CATALOGUES.has(action) && (
            <div style={{
              flex: 1, minHeight: 0, display: 'grid', gap: 14,
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              alignContent: 'start',
            }}>
              {[
                ['diagnosis', 'Diagnosis', 'Search the priced catalogue by name, ICD-10 or G-DRG.'],
                ['procedure', 'Procedure', 'Search the procedure catalogue by name or G-DRG.'],
                ['investigation', 'Lab / Scan', 'Search the investigation catalogue.'],
                ['medication', 'Medication', 'Search the drug catalogue, then set dose, frequency, duration, route and quantity.'],
              ].map(([k, label, hint], i) => {
                //  ── WHERE THE NEXT PICK IN THIS BOX GOES ───────────────────
                //  Stated before the click, not discovered after it. A card whose
                //  picks silently become a replacement on one screen and an addition
                //  on the next is the same control meaning two different things.
                const act = activeTarget ? mvParseTarget(activeTarget) : null;
                const fillsRow = act && act.kind === k
                  ? targetRows.find((r) => r.key === activeTarget)
                  : null;
                //  A row of this kind still waiting, so the card can offer to take it
                //  rather than leaving the officer to find it above.
                const waiting = targetRows.find((r) => r.kind === k && !r.to);
                return (
                <div key={k} style={{
                  background: '#fff', borderRadius: 12, padding: 16, minWidth: 0,
                  border: `1px solid ${fillsRow ? BRAND.danger : BRAND.line}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: 6, display: 'grid',
                      placeItems: 'center', fontSize: 10.5, fontWeight: 800,
                      flexShrink: 0,
                      background: fillsRow ? BRAND.danger : BRAND.head,
                      color: fillsRow ? '#fff' : BRAND.inkFaint,
                    }}>{i + 1}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 800, letterSpacing: '.05em',
                      textTransform: 'uppercase', color: BRAND.inkSoft,
                    }}>{label}</span>
                  </div>

                  {/*  Replaces the generic hint when this box is filling a mapping
                       row: what the pick is FOR matters more than how to search. */}
                  {fillsRow ? (
                    <div style={{
                      fontSize: 12, lineHeight: 1.6, color: BRAND.danger, fontWeight: 600,
                    }}>
                      Choosing what <b>{fillsRow.name}</b> becomes
                      {fillsRow.patients != null && (
                        <span style={{ color: BRAND.inkMuted, fontWeight: 500 }}>
                          {' '}· on the {fillsRow.patients} claim(s) carrying it
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.6 }}>
                      {hint}
                      {waiting && (
                        <>
                          {' '}
                          <button type="button"
                            onClick={() => setActiveTarget(waiting.key)}
                            style={{
                              border: 0, background: 'none', padding: 0, font: 'inherit',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              color: BRAND.danger, textDecoration: 'underline',
                            }}>
                            Replace {waiting.name} instead
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {/*  ── ALL FOUR CATALOGUES ────────────────────────────────────
                       The same endpoints the single-claim editor uses, so the rows
                       and the prices are the ones an officer already recognises —
                       and a code picked here is a code that editor would accept.

                       One component for all four: they differ only in which
                       catalogue they read, what a row is labelled by, and how a pick
                       is shaped for the writer. Four copies of a searchable list
                       would drift within a month. */}
                  {/*  A tariff belongs to one insurer, so the composer cannot
                       price on "every scheme in the month". Said here, with the
                       fix, rather than leaving four empty lists to be interpreted. */}
                  {!composerScheme(insurances) && (
                    <div style={{
                      marginTop: 10, padding: '8px 10px', borderRadius: 8,
                      background: '#FDF3E3', border: '1px solid #B4690E33',
                      fontSize: 11.5, lineHeight: 1.55, color: '#8A5510',
                    }}>
                      {insurances?.length
                        ? <>These claims span <b>{insurances.length} schemes</b>. A tariff
                          belongs to one insurer, so tick just one on the month card to
                          see its catalogue.</>
                        : <>No scheme is selected, so there is no one tariff to price on.
                          Tick the insurance on the month card and its catalogue appears
                          here.</>}
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <MvCatalogue
                      kind={k}
                      url={CATALOGUE_URL[k]}
                      params={{
                        //  From the schemes ticked on the month card, never a
                        //  hardcoded insurer. undefined when more than one scheme
                        //  is in view, which the endpoint answers with an empty
                        //  list and a reason — see composerScheme above.
                        scheme: composerScheme(insurances) || undefined,
                        //  The band the pattern asked for, as an age the endpoint
                        //  understands. Nothing set means no narrowing. Only the
                        //  diagnosis and procedure catalogues are age-banded; the
                        //  others ignore it.
                        age: String(pattern.ageTo).trim() !== '' ? Number(pattern.ageTo)
                          : (String(pattern.ageFrom).trim() !== '' ? Number(pattern.ageFrom)
                            : undefined),
                      }}
                      label={label}
                      hint={CATALOGUE_HINT[k]}
                      chosen={basket.filter((b) => b.kind === k)}
                      rowLabel={CATALOGUE_ROW[k]}
                      onPick={(o) => {
                        const item = shapePick(k, o);
                        const id = identityOf(item);

                        //  ── IS THIS PICK A REPLACEMENT, OR AN ADDITION? ──────
                        //  If a mapping row of THIS kind is waiting, the pick fills
                        //  it. Otherwise it goes in the basket. The card above says
                        //  which, before the click, so the two never have to be
                        //  guessed apart afterwards.
                        const act = activeTarget ? mvParseTarget(activeTarget) : null;
                        if (act && act.kind === k) {
                          //  A replacement identical to what it replaces changes
                          //  nothing and would delete the row and write it back —
                          //  churn on every claim, and a real risk of losing the
                          //  original's own details.
                          //
                          //  Compared on the NAME AND THE CODE — see isSameEntry.
                          //  Identity alone was wrong here: for a diagnosis it is the
                          //  ICD-10 by itself, so mapping Malaria onto Malaria under a
                          //  DIFFERENT G-DRG read as a no-op and was refused. That is
                          //  not a no-op — the catalogue holds Malaria as OPDC06A for
                          //  a consultation and MEDI28A for an admission, and swapping
                          //  one for the other is the whole correction.
                          const row = targetRows.find((r) => r.key === activeTarget);
                          if (isSameEntry(k, item, act.code, row ? row.gdrgs : [])) {
                            notifyClaim({
                              title: `${item.name || item.code} is what is already there`,
                              lead: k === 'diagnosis'
                                ? 'Same diagnosis and same G-DRG, so nothing would '
                                  + 'change. Pick a different G-DRG for it, choose '
                                  + 'another diagnosis, or un-tick the row.'
                                : 'Pick something different, or un-tick the row if it '
                                  + 'should stay as it is.',
                            });
                            return;
                          }
                          setMapping((m) => ({ ...m, [activeTarget]: item }));
                          setBatchPlan(null);
                          //  On to the next row waiting for one. Mapping four entries
                          //  should be four picks, not four picks and four clicks.
                          const next = targetRows.find(
                            (r) => r.key !== activeTarget && !r.to);
                          setActiveTarget(next ? next.key : null);
                          return;
                        }

                        //  ── REFUSED, AND SAID OUT LOUD ────────────────────────
                        //  Two G-DRGs for one ICD-10 are one diagnosis. Dropping the
                        //  click silently would leave the officer clicking a row that
                        //  never lands and no reason why — so it names the entry
                        //  already holding that identity.
                        const clash = basket.find((b) => identityOf(b) === id);
                        if (clash) {
                          notifyClaim({
                            title: `${clash.name || clash.code} is already in the list`,
                            lead: k === 'diagnosis'
                              ? 'One diagnosis per ICD-10. The same condition billed '
                                + 'under two G-DRGs is still one diagnosis, and a claim '
                                + 'is billed on one of them.'
                              : 'It is already being applied to these claims.',
                          });
                          return;
                        }
                        //  And it cannot duplicate what the claims ALREADY have from
                        //  the pattern either — adding the diagnosis the group was
                        //  found by would write it a second time on every claim.
                        const onClaims = patternItems.find((p) => identityOf({
                          kind: p.kind, code: p.code, icd10_code: p.code, gdrg_code: p.code,
                        }) === id);
                        if (onClaims) {
                          notifyClaim({
                            title: `Every claim already has ${onClaims.name || onClaims.code}`,
                            lead: 'It is what this pattern was filtered by — adding it '
                                + 'would write it a second time on every claim.',
                          });
                          return;
                        }
                        setBasket((b) => [...b, item]);
                        setBatchPlan(null);
                      }} />
                  </div>
                </div>
                );
              })}
            </div>
            )}
            </>
          )}

          {/*  ══ THE CLAIMS THIS PATTERN FOUND ════════════════════════════════
               Left: who they are. Right: what will be done to them — reserved
               rather than filled with something temporary.

               A split, not a full-width table, because the two halves are read
               together: you choose a treatment on the right and check it against the
               patients on the left without either leaving the screen. */}
          {view === 'claims' && (
            <div style={{
              flex: 1, minHeight: 0, display: 'grid', gap: 14,
              gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
              alignItems: 'stretch',
            }}>
              {/*  ── LEFT: the patients ──────────────────────────────────────── */}
              <div style={{
                background: '#fff', border: `1px solid ${BRAND.line}`, borderRadius: 12,
                display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
              }}>
                <div style={{
                  padding: '11px 16px', borderBottom: `1px solid ${BRAND.line}`,
                  display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
                }}>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
                    textTransform: 'uppercase', color: BRAND.inkSoft,
                  }}>Patients in this pattern</span>

                  {/*  ── WHY THE ROWS MOVED ────────────────────────────────────
                       A table that silently reorders itself is a table nobody trusts,
                       so the reason sits in its header and the ✕ that undoes it travels
                       with the reason. It says AT THE TOP, not "only" — nothing has
                       been removed, and implying otherwise would be the filter this is
                       deliberately not. */}
                  {focusRow && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '2px 4px 2px 9px', borderRadius: 999, minWidth: 0,
                      background: BRAND.greenSoft,
                      border: `1px solid ${BRAND.greenDark}55`,
                    }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: BRAND.greenDark,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 260,
                      }}>
                        {focusCount === 0
                          ? `nobody here carries ${focusName}`
                          : `${focusCount.toLocaleString()} with ${focusName} at the top`}
                      </span>
                      <button type="button" onClick={() => setFocusTarget(null)}
                        aria-label="Put the group back in its own order"
                        title="Put the group back in its own order"
                        style={{
                          border: 0, background: 'none', cursor: 'pointer', padding: '0 3px',
                          color: BRAND.greenDark, lineHeight: 1, fontSize: 12,
                          flexShrink: 0,
                        }}>✕</button>
                    </span>
                  )}
                  <span style={{
                    fontSize: 12, fontWeight: 800,
                    color: chosenCount === (found || []).length ? BRAND.greenDark : BRAND.caution || '#8A5510',
                    fontVariantNumeric: 'tabular-nums', marginLeft: 'auto',
                  }}>
                    {chosenCount.toLocaleString()} of {(found || []).length.toLocaleString()}
                  </span>
                </div>

                {/*  ── HOW MANY OF THEM ────────────────────────────────────────
                     The pattern finds a group; this takes part of it. They are not
                     the same thing — 42 is often the right group to LOOK at and the
                     wrong one to write to in one pass.

                     Half / first / last are shortcuts into the same tick marks, so
                     anything they express is still reachable by hand and they are
                     only the common cases. They read the TABLE's order, so "the last
                     10" means the last 10 rows as shown rather than by an id order
                     nobody can see. */}
                <div style={{
                  padding: '8px 14px', borderBottom: `1px solid ${BRAND.line}`,
                  background: BRAND.canvas, display: 'flex', gap: 6, flexWrap: 'wrap',
                  alignItems: 'center', flexShrink: 0,
                }}>
                  <span style={subLabel}>Take</span>
                  <Pill on={chosenCount === foundIds.length && foundIds.length > 0}
                    onClick={takeAll}>All</Pill>
                  <Pill on={chosenCount === 0} onClick={takeNone}>None</Pill>
                  <Pill on={false} title="The first half of the list"
                    onClick={() => takeFirst(Math.ceil(foundIds.length / 2))}>
                    Half
                  </Pill>
                  <span style={{ width: 1, alignSelf: 'stretch', minHeight: 18,
                    background: BRAND.line, margin: '0 3px' }} />
                  <input type="number" min="1" max={foundIds.length || 1} placeholder="n"
                    value={takeN}
                    onChange={(e) => setTakeN(e.target.value)}
                    aria-label="How many to take"
                    style={{ ...ageBox, width: 58 }} />
                  <Pill on={false} title="Tick the first n rows"
                    onClick={() => takeFirst(Number(takeN) || 0)}>First</Pill>
                  <Pill on={false} title="Tick the last n rows"
                    onClick={() => takeLast(Number(takeN) || 0)}>Last</Pill>
                  {/*  Says what the right-hand panel will act on, beside the controls
                       that set it — not only over there where it is easy to miss. */}
                  <span style={{ marginLeft: 'auto', fontSize: 11.5,
                    color: chosenCount ? BRAND.inkMuted : BRAND.danger }}>
                    {chosenCount === 0
                      ? 'Nothing ticked — nothing will be changed'
                      : chosenCount === foundIds.length
                        ? 'Every patient in the group'
                        : `${chosenCount.toLocaleString()} ticked`}
                  </span>
                </div>

                {/*  Its own scroller. `minHeight: 0` so it shrinks to the panel
                     rather than being sized by 200 rows and pushing the footer off —
                     the default that has cost this dialog three layout bugs. */}
                <div ref={rowsScrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {/*  Select-all, with an INDETERMINATE state when only some
                             are ticked. A plain checked/unchecked box would show
                             "unchecked" on a half-selected list, which reads as
                             "nothing is selected" — the one thing it must not say
                             when 21 of 42 will be written to. */}
                        <th style={{
                          position: 'sticky', top: 0, zIndex: 1, background: BRAND.head,
                          padding: '8px 6px 8px 12px', width: 30,
                          borderBottom: `1px solid ${BRAND.line}`,
                        }}>
                          <input type="checkbox"
                            checked={foundIds.length > 0 && chosenCount === foundIds.length}
                            ref={(el) => {
                              if (el) el.indeterminate = chosenCount > 0
                                && chosenCount < foundIds.length;
                            }}
                            onChange={() => (chosenCount === foundIds.length
                              ? takeNone() : takeAll())}
                            aria-label="Select every patient in this pattern"
                            style={{ accentColor: BRAND.greenDark, width: 14, height: 14,
                              cursor: 'pointer' }} />
                        </th>
                        {['Folder', 'Name', 'DOB', 'Sex', 'Type', 'Service',
                          'Dx', 'Proc', 'Lab', 'Med', ''].map((h, i) => (
                          <th key={h || 'open'} style={{
                            position: 'sticky', top: 0, zIndex: h ? 1 : 3,
                            background: BRAND.head,
                            textAlign: 'left', padding: '8px 12px', fontSize: 10,
                            fontWeight: 800, letterSpacing: '.05em',
                            textTransform: 'uppercase', color: BRAND.inkFaint,
                            borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
                            //  ── THE CHEVRON COLUMN IS PINNED RIGHT ──────────────
                            //  It was scrolling out of view. On a narrow dialog the
                            //  table scrolls sideways, and the one control on every
                            //  row — the thing that opens the claim — went off the
                            //  edge, so on wide groups there was no visible way to
                            //  see what a patient already has.
                            //
                            //  z-index 3 rather than 1: it has to sit above both the
                            //  sticky header row and the pinned body cells where
                            //  they cross.
                            ...(h === '' ? { right: 0, width: 34, padding: '8px 4px' } : {}),
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/*  200 RENDERED, not 200 acted on. The cap is the DOM; every
                           claim in the pattern is still in the group, and the line
                           below says so — a silently truncated list reads as a
                           smaller pattern than it is. */}
                      {tableRows.slice(0, 200).flatMap((c, rowIdx) => {
                        const id = Number(c.id);
                        const rec = contents ? contents.get(id) : null;
                        //  In the block that was moved to the top. Marked, because a
                        //  reordered table with nothing distinguishing the rows that
                        //  moved is a table that has silently rearranged itself.
                        const inFocus = focusRow !== null && rowIdx < focusCount;
                        const kinds = [
                          ['diagnoses', 'Diagnosis'],
                          ['procedures', 'Procedure'],
                          ['investigations', 'Lab / Scan'],
                          ['medications', 'Medication'],
                        ];
                        const isOpen = openRows.has(id);
                        //  A dash, not a 0, until the counts have arrived. "0" is an
                        //  answer — "this patient has no diagnosis" — and showing it
                        //  before the data lands would be asserting something false
                        //  about every row for a second.
                        //  `—` until THIS claim's contents have arrived, not until
                        //  the first chunk has. A red 0 says "this patient has no
                        //  diagnosis", which is an assertion — and it was being made
                        //  about 1,072 patients whose data had simply not been
                        //  requested yet.
                        const known = !!rec;
                        const num = (k) => (known ? kidsOf(rec, k).length : '—');
                        const cnt = {
                          padding: '8px 10px', fontSize: 12, textAlign: 'center',
                          borderBottom: `1px solid ${BRAND.line}`,
                          fontVariantNumeric: 'tabular-nums',
                        };
                        return [
                          <tr key={`${c.source}-${id}`}
                            style={{ background: chosenIds.has(id) ? BRAND.greenSoft : '#fff' }}>
                            {/*  Ticked rows are tinted, so a partly-selected list is
                                 readable at a glance rather than by inspecting 42
                                 checkboxes.

                                 A row that was BROUGHT UP gets a bar down its left
                                 edge — a second tint would fight the ticked one, and
                                 these are two different facts about the same row. */}
                            <td style={{
                              ...mvCell, padding: '8px 6px 8px 12px',
                              boxShadow: inFocus
                                ? `inset 3px 0 0 ${BRAND.greenDark}` : 'none',
                            }}>
                              <input type="checkbox" checked={chosenIds.has(id)}
                                onChange={() => setChosenIds((s) => {
                                  const n = new Set(s);
                                  if (n.has(id)) n.delete(id); else n.add(id);
                                  return n;
                                })}
                                aria-label={`Include ${c.name || id}`}
                                style={{ accentColor: BRAND.greenDark, width: 14, height: 14,
                                  cursor: 'pointer' }} />
                            </td>
                            <td style={{ ...mvCell, ...S.mono }}>{c.folder_no || '—'}</td>
                            <td style={{ ...mvCell, fontWeight: 650 }}>{c.name || '—'}</td>
                            <td style={{ ...mvCell, color: BRAND.inkMuted, whiteSpace: 'nowrap' }}>
                              {c.dob || '—'}
                            </td>
                            <td style={{ ...mvCell, color: BRAND.inkMuted }}>
                              {String(c.gender || '—').slice(0, 1).toUpperCase()}
                            </td>
                            <td style={{ ...mvCell }}>{c.type || '—'}</td>
                            <td style={{ ...mvCell, color: BRAND.inkMuted }}>
                              {c.service || c.service_code || '—'}
                            </td>
                            {/*  A ZERO IS THE INTERESTING NUMBER. A patient with no
                                 diagnosis or no medication is the one this group
                                 exists to fix, so it is coloured and the rest are
                                 quiet — the eye should land on the gaps. */}
                            {kinds.map(([k]) => {
                              const v = num(k);
                              return (
                                <td key={k} style={{
                                  ...cnt,
                                  color: v === 0 ? BRAND.danger : BRAND.ink,
                                  fontWeight: v === 0 ? 800 : 600,
                                }}>{v}</td>
                              );
                            })}
                            {/*  Pinned to the right edge with the header above it, so
                                 the row's one control is reachable however wide the
                                 table gets. A left border rather than a shadow — it
                                 sits on a white row, and the border reads as the
                                 column edge it is. */}
                            <td style={{
                              ...cnt, padding: '4px 4px', position: 'sticky', right: 0,
                              zIndex: 2, background: '#fff', width: 34,
                              borderLeft: `1px solid ${BRAND.line}`,
                            }}>
                              <button type="button"
                                aria-expanded={isOpen}
                                aria-label={isOpen ? 'Hide what this claim holds'
                                  : 'Show what this claim holds'}
                                onClick={() => setOpenRows((s) => {
                                  const n = new Set(s);
                                  if (n.has(id)) n.delete(id); else n.add(id);
                                  return n;
                                })}
                                style={{
                                  border: 0, background: 'none', cursor: 'pointer',
                                  padding: 3, color: BRAND.inkFaint, display: 'flex',
                                }}>
                                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </td>
                          </tr>,

                          //  ── What is already on this claim ──────────────────
                          //  The reason the arrow exists: "Med 9" tells you there is
                          //  a regimen, not whether it is the right one. Four
                          //  columns so the four kinds can be compared at a glance
                          //  rather than read as one long list.
                          isOpen && (
                            <tr key={`${c.source}-${id}-open`}>
                              <td colSpan={12} style={{
                                padding: '10px 14px 12px 34px',
                                background: BRAND.canvas,
                                borderBottom: `1px solid ${BRAND.line}`,
                              }}>
                                {contents === null ? (
                                  <span style={{ fontSize: 11.5, color: BRAND.inkFaint }}>
                                    Loading what this claim holds…
                                  </span>
                                ) : (
                                  <div style={{
                                    display: 'grid', gap: 12,
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                  }}>
                                    {kinds.map(([k, label]) => {
                                      const rows = kidsOf(rec, k);
                                      return (
                                        <div key={k} style={{ minWidth: 0 }}>
                                          <div style={{
                                            fontSize: 9.5, fontWeight: 800,
                                            letterSpacing: '.06em', textTransform: 'uppercase',
                                            color: BRAND.inkFaint, marginBottom: 3,
                                          }}>{label} · {rows.length}</div>
                                          {rows.length === 0 ? (
                                            <div style={{ fontSize: 11.5, color: BRAND.danger }}>
                                              none
                                            </div>
                                          ) : rows.map((r, i) => (
                                            <div key={`${r.code || r.name}-${i}`} style={{
                                              fontSize: 11.5, color: BRAND.ink,
                                              lineHeight: 1.5, overflow: 'hidden',
                                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={`${r.code || ''} ${r.name || ''}`.trim()}>
                                              <span style={{ ...S.mono, color: BRAND.inkFaint }}>
                                                {r.code || '—'}
                                              </span>
                                              {' '}{r.name || ''}
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ),

                          //  ── WHERE THE MOVED BLOCK ENDS ─────────────────────
                          //  After the last one that was brought up. Without it the
                          //  block runs straight into the rest of the group and the
                          //  officer has to count the left-edge bars to see where
                          //  "these are those patients" stops being true.
                          focusRow && focusCount > 0 && rowIdx === focusCount - 1 && (
                            <tr key="mv-focus-divider">
                              <td colSpan={12} style={{
                                padding: '5px 14px', background: BRAND.canvas,
                                borderTop: `2px solid ${BRAND.greenDark}`,
                                borderBottom: `1px solid ${BRAND.line}`,
                                fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em',
                                textTransform: 'uppercase', color: BRAND.inkFaint,
                              }}>
                                {focusCount === 1
                                  ? `the 1 with ${focusName} — the rest of the group follows`
                                  : `those ${focusCount.toLocaleString()} — the rest of the `
                                    + 'group follows'}
                              </td>
                            </tr>
                          ),
                        ].filter(Boolean);
                      })}
                    </tbody>
                  </table>
                </div>

                {/*  ── THE GROUP, IN TOTALS ─────────────────────────────────────
                     A group is chosen so ONE treatment can be applied to all of it,
                     and these numbers are how you tell whether that is true yet: 13
                     patients with 13 diagnoses and 0 medications is a group waiting
                     for a regimen; 13 and 117 already has one.

                     The two RED figures are the ones worth acting on — patients with
                     no diagnosis at all, and patients with no drugs at all. Those are
                     what this whole screen exists to fix, so they are the only
                     numbers given colour.

                     These describe the WHOLE GROUP and do not move when an entry is
                     focused: focusing reorders the table, it removes nothing. */}
                <div style={{
                  borderTop: `1px solid ${BRAND.line}`, background: BRAND.canvas,
                  padding: '10px 14px', flexShrink: 0, display: 'flex',
                  gap: 16, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 11.5,
                }}>
                  {[
                    ['Patients', groupStats.claims, null],
                    ['OPD', groupStats.opd, null],
                    ['IPD', groupStats.ipd, null],
                    ['Diagnoses', groupStats.dx, null],
                    ['Procedures', groupStats.pr, null],
                    ['Labs', groupStats.inv, null],
                    ['Medicines', groupStats.med, null],
                    ['No diagnosis', groupStats.noDx, BRAND.danger],
                    ['No medicine', groupStats.noMed, BRAND.danger],
                  ].map(([label, v, tone]) => (
                    <span key={label} style={{ whiteSpace: 'nowrap' }}>
                      <strong style={{
                        fontSize: 13.5, fontVariantNumeric: 'tabular-nums',
                        color: tone && v > 0 ? tone : BRAND.ink,
                      }}>
                        {contents === null && label !== 'Patients'
                          && label !== 'OPD' && label !== 'IPD'
                          ? '—' : Number(v).toLocaleString()}
                      </strong>
                      <span style={{ color: BRAND.inkMuted }}> {label}</span>
                    </span>
                  ))}
                  {/*  Says what the child totals cover while they are still arriving,
                       so "288 Diagnoses" beside 1,272 patients cannot be read as the
                       whole group. Silent once every row is in. */}
                  {contents !== null && groupStats.known < groupStats.claims && (
                    <span style={{
                      fontSize: 11, color: BRAND.caution || '#8A5510', whiteSpace: 'nowrap',
                    }}>
                      counted for {groupStats.known.toLocaleString()} of{' '}
                      {groupStats.claims.toLocaleString()} so far…
                    </span>
                  )}
                  {groupStats.amount > 0 && (
                    <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                      <span style={{ color: BRAND.inkMuted }}>Total </span>
                      <strong style={{ fontSize: 13.5, color: BRAND.ink }}>
                        GHS {groupStats.amount.toLocaleString(undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </span>
                  )}
                </div>

                {(found || []).length > 200 && (
                  <div style={{
                    padding: '8px 14px', borderTop: `1px solid ${BRAND.line}`,
                    fontSize: 11, color: BRAND.inkMuted, background: BRAND.canvas,
                    flexShrink: 0,
                  }}>
                    The table shows the first 200 of {(found || []).length.toLocaleString()} —
                    the totals above cover all of them.
                    {focusRow && focusCount > 0
                      && ` The ${focusCount.toLocaleString()} with ${focusName} are at the top.`}
                  </div>
                )}
              </div>

              {/*  ── RIGHT: WHAT TO DO WITH THEM ─────────────────────────────── */}
              <div style={{
                background: '#fff', border: `1px solid ${BRAND.line}`, borderRadius: 12,
                display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
              }}>
                <div style={{
                  padding: '11px 16px', borderBottom: `1px solid ${BRAND.line}`,
                  display: 'flex', alignItems: 'baseline', gap: 9, flexShrink: 0,
                }}>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
                    textTransform: 'uppercase', color: BRAND.greenDark,
                  }}>What to give them</span>
                  <span style={{ fontSize: 12, color: BRAND.inkMuted }}>
                    Applies to the {chosenCount.toLocaleString()} ticked.
                  </span>
                </div>

                <div style={{
                  flex: 1, minHeight: 0, overflowY: 'auto', padding: 16,
                  display: 'grid', gap: 14, alignContent: 'start',
                }}>
                  {/*  THE SHELF OF SAVED ROUTINES USED TO SIT HERE, above step 1.
                       Removed on request — it will live somewhere of its own. The
                       routines are still saved, still premium-ranked, and still
                       fetched into `routines`; nothing on this panel reads them now.

                       It was also the wrong place for it once this list grew: the
                       group below can hold 1,317 distinct diagnoses, and a shelf
                       above it pushed the thing being searched further down the
                       column. */}

                  {/*  ── STEP 1: the act ────────────────────────────────────────
                       Chosen before anything else because it decides what the next
                       step can even offer. Add picks from the CATALOGUE — you are
                       putting something new on the claim. Update and Override act on
                       what the patients ALREADY have, so they pick from this group's
                       own contents: you cannot update a diagnosis nobody has, and
                       offering the catalogue there would invite exactly that. */}
                  <Step n={1} label="Do what" block>
                    {/*  ── FOUR ACTS, EACH SAYING WHAT IT IS ────────────────────
                         Four pills reading Add / Update / Override said almost
                         nothing: they are the same length, the same shape, and three
                         of them are near-synonyms in English. What separates them is
                         WHAT THEY TOUCH and WHETHER THEY REMOVE ANYTHING, and neither
                         fitted on a pill.

                         So each is a card carrying its own second line. Two columns,
                         because four cards in a row in this panel would be four
                         columns of two words each. */}
                    <div style={{
                      display: 'grid', gap: 6, minWidth: 0,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    }}>
                      {MV_ACTIONS.map(({ key, label, what, hint }) => {
                        const on = action === key;
                        return (
                          <button key={key} type="button" aria-pressed={on} title={hint}
                            //  The mapping goes with the targets. A replacement chosen
                            //  for an entry that is no longer being changed is not a
                            //  thing to keep, and keeping it would carry a diagnosis
                            //  picked under Update into an Override run.
                            onClick={() => {
                              setAction(key); setTarget([]);
                              setMapping({}); setActiveTarget(null); setBatchPlan(null);
                              setAttendance(''); setServiceTo({}); setActiveService(null);
                            }}
                            style={{
                              display: 'grid', gap: 2, textAlign: 'left', minWidth: 0,
                              padding: '7px 10px', borderRadius: 8, font: 'inherit',
                              cursor: 'pointer',
                              border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                              background: on ? BRAND.greenSoft : '#fff',
                            }}>
                            <span style={{
                              fontSize: 12, fontWeight: on ? 800 : 700,
                              color: on ? BRAND.greenDark : BRAND.ink,
                            }}>{label}</span>
                            <span style={{
                              fontSize: 10.5, lineHeight: 1.4, color: BRAND.inkMuted,
                            }}>{what}</span>
                          </button>
                        );
                      })}
                    </div>
                    {action && (
                      <div style={{
                        fontSize: 11.5, color: BRAND.inkMuted, marginTop: 6, lineHeight: 1.55,
                      }}>
                        {(MV_ACTIONS.find((a) => a.key === action) || {}).hint}
                      </div>
                    )}
                  </Step>

                  {/*  ── STEP 2: which of THEIR entries ─────────────────────────
                       Only for Update and Override. Built from the contents already
                       fetched for the table on the left, so no extra request — and
                       every row says how many of the group carry it, because that
                       number is how you tell a shared entry from one patient's
                       outlier before changing it for everybody. */}
                  {(action === 'update' || action === 'override') && (
                    <Step n={2} label={action === 'update' ? 'Update which' : 'Replace which'} block>
                      {contents === null ? (
                        <span style={{ fontSize: 12, color: BRAND.inkFaint }}>
                          Reading what these patients have…
                        </span>
                      ) : (
                        <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
                          {/*  ── ONE KIND AT A TIME ──────────────────────────────
                               All four stacked was a column of several thousand rows
                               on a real month — 1,317 diagnoses alone — and no way
                               through it but scrolling. The counts are on the tabs, so
                               choosing one is an informed choice rather than a guess
                               about where the entries are. */}
                          <div style={{
                            display: 'flex', gap: 4, flexWrap: 'wrap', minWidth: 0,
                          }}>
                            {TARGET_TABS.map(([k, label]) => {
                              const n = (present[k] || []).length;
                              const on = targetKind === k;
                              return (
                                <button key={k} type="button" aria-pressed={on}
                                  disabled={!n}
                                  title={n ? `${n} distinct ${label.toLowerCase()} in this group`
                                    : `No ${label.toLowerCase()} on these claims`}
                                  onClick={() => { setTargetKind(k); setTargetQ(''); }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    padding: '4px 9px', borderRadius: 999, font: 'inherit',
                                    fontSize: 11.5, fontWeight: on ? 800 : 650,
                                    cursor: n ? 'pointer' : 'not-allowed',
                                    border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                                    background: on ? BRAND.greenDark : '#fff',
                                    color: on ? '#fff' : (n ? BRAND.inkSoft : BRAND.inkFaint),
                                  }}>
                                  {label}
                                  <span style={{
                                    fontSize: 10, fontWeight: 800,
                                    fontVariantNumeric: 'tabular-nums',
                                    opacity: on ? 0.85 : 0.6,
                                  }}>{n}</span>
                                </button>
                              );
                            })}
                            {/*  ── AND WHAT HAS BEEN PICKED SO FAR ──────────────
                                 A fifth view, not a sixth control. Filtering to
                                 Medication puts the diagnoses already ticked off
                                 screen, and "what have I chosen" is the question that
                                 immediately follows. */}
                            {target.length > 0 && (
                              <button type="button"
                                aria-pressed={targetKind === 'ticked'}
                                onClick={() => {
                                  setTargetKind(targetKind === 'ticked'
                                    ? (TARGET_TABS.find(([k]) => (present[k] || []).length)
                                      || ['diagnoses'])[0]
                                    : 'ticked');
                                  setTargetQ('');
                                }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  padding: '4px 9px', borderRadius: 999, font: 'inherit',
                                  fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
                                  marginLeft: 'auto',
                                  border: `1px solid ${BRAND.greenDark}`,
                                  background: targetKind === 'ticked'
                                    ? BRAND.greenDark : BRAND.greenSoft,
                                  color: targetKind === 'ticked' ? '#fff' : BRAND.greenDark,
                                }}>
                                Chosen
                                <span style={{
                                  fontSize: 10, fontVariantNumeric: 'tabular-nums',
                                }}>{target.length}</span>
                              </button>
                            )}
                          </div>

                          {/*  ── TYPE THE NAME ───────────────────────────────────
                               Matched on the NAME AND THE CODE, because both are how
                               these are known: one officer types "malaria", another
                               types B54, and neither should have to know which this
                               box wants. */}
                          <div style={{ position: 'relative', minWidth: 0 }}>
                            <Search size={13} style={{
                              position: 'absolute', left: 9, top: '50%',
                              transform: 'translateY(-50%)', color: BRAND.inkFaint,
                              pointerEvents: 'none',
                            }} />
                            <input value={targetQ}
                              onChange={(e) => setTargetQ(e.target.value)}
                              placeholder={targetKind === 'ticked'
                                ? 'Search what you have chosen…'
                                : `Search ${(TARGET_TABS.find(([k]) => k === targetKind)
                                  || [, 'entries'])[1].toLowerCase()} by name or code…`}
                              style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '6px 26px 6px 27px', borderRadius: 7,
                                font: 'inherit', fontSize: 12, background: '#fff',
                                border: `1px solid ${BRAND.line}`, color: BRAND.ink,
                              }} />
                            {targetQ && (
                              <button type="button" onClick={() => setTargetQ('')}
                                aria-label="Clear the search"
                                style={{
                                  position: 'absolute', right: 6, top: '50%',
                                  transform: 'translateY(-50%)', border: 0,
                                  background: 'none', cursor: 'pointer', padding: 2,
                                  color: BRAND.inkFaint, lineHeight: 1, fontSize: 12,
                                }}>✕</button>
                            )}
                          </div>

                          {/*  What the search found, or why it found nothing. A list
                               that empties with no message reads as broken. */}
                          {targetList.total === 0 ? (
                            <span style={{ fontSize: 12, color: BRAND.inkFaint }}>
                              {targetQ
                                ? `Nothing here matches "${targetQ}".`
                                : 'These patients carry none of this kind.'}
                            </span>
                          ) : (
                            <>
                              <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                                {/*  Capped. 1,317 buttons is a second of layout every
                                     time a tick changes, and nobody reads past the
                                     first screen anyway — that is what the search is
                                     for. The count below says what is being held
                                     back, because a silent cap reads as "that is all
                                     there is". */}
                                {targetList.rows.slice(0, TARGET_SHOWN).map((r) => {
                                  const key = r.key;
                                  const on = target.includes(key);
                                  //  Carried by every patient, or only some? That is
                                  //  the difference between changing the group's shared
                                  //  entry and changing one person's.
                                  const all = r.patients === chosenCount;
                                  //  Only in the Chosen view, where four kinds are
                                  //  mixed and a drug and a diagnosis otherwise look
                                  //  identical. On a single-kind tab the tab already
                                  //  says it, and repeating it on every row is noise.
                                  const kindLabel = targetKind === 'ticked'
                                    ? (TARGET_TABS.find(([kk]) => kk === r.facet)
                                      || [, ''])[1]
                                    : null;
                                    return (
                                      <button key={key} type="button" aria-pressed={on}
                                        onClick={() => {
                                          setTarget((t) => (t.includes(key)
                                            ? t.filter((x) => x !== key) : [...t, key]));
                                          //  Un-ticking an entry drops whatever it was
                                          //  mapped to — see the Pill above.
                                          if (on) {
                                            setMapping((m) => {
                                              const n = { ...m }; delete n[key]; return n;
                                            });
                                          }
                                          //  ── AND SHOW WHO HAS IT ──────────────
                                          //  Ticking asks "which claims are these?"
                                          //  and the table can answer, so it does.
                                          //  Un-ticking the entry in focus puts the
                                          //  whole group back — a table still filtered
                                          //  by an entry no longer chosen is a filter
                                          //  with nothing on screen explaining it.
                                          setFocusTarget(on
                                            ? (focusTarget === key ? null : focusTarget)
                                            : key);
                                          setBatchPlan(null);
                                        }}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 8,
                                          textAlign: 'left', font: 'inherit', cursor: 'pointer',
                                          padding: '6px 9px', borderRadius: 7, minWidth: 0,
                                          border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                                          background: on ? BRAND.greenSoft : '#fff',
                                        }}>
                                        <span style={{
                                          flexShrink: 0, width: 13, height: 13, borderRadius: 3,
                                          display: 'grid', placeItems: 'center', fontSize: 9,
                                          color: '#fff',
                                          border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                                          background: on ? BRAND.greenDark : '#fff',
                                        }}>{on ? '✓' : ''}</span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                          <span style={{
                                            display: 'block', fontSize: 12,
                                            fontWeight: on ? 750 : 600, color: BRAND.ink,
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}>{r.name || r.code}</span>
                                          <span style={{
                                            display: 'block', fontSize: 10.5,
                                            color: BRAND.inkFaint, ...S.mono,
                                          }}>
                                            {r.code}
                                            {kindLabel && (
                                              <span style={{
                                                //  Out of the monospace the code sits
                                                //  in — there is no S.sans, and the
                                                //  label is prose, not a code.
                                                fontFamily: 'inherit', marginLeft: 7,
                                                fontWeight: 700, fontSize: 9,
                                                textTransform: 'uppercase',
                                                letterSpacing: '.05em',
                                              }}>{kindLabel}</span>
                                            )}
                                          </span>
                                        </span>
                                        <span style={{
                                          flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                                          whiteSpace: 'nowrap',
                                          color: all ? BRAND.greenDark : BRAND.caution || '#8A5510',
                                        }}>
                                          {/*  Counted against the TICKED patients,
                                               not the whole group. "10 of 42" while
                                               only 12 are ticked would be measuring
                                               people the run excludes. */}
                                          {r.patients == null ? '—'
                                            : all ? 'all' : `${r.patients} of ${chosenCount}`}
                                        </span>
                                      </button>
                                    );
                                  })}
                              </div>

                              {/*  ── WHAT IS NOT ON SCREEN ─────────────────────
                                   Said out loud. A cap that keeps quiet reads as "that
                                   is all there is", and on 1,317 diagnoses that is the
                                   difference between a search and a wrong answer. */}
                              {targetList.total > TARGET_SHOWN && (
                                <span style={{
                                  fontSize: 11.5, color: BRAND.inkMuted, lineHeight: 1.5,
                                }}>
                                  Showing {TARGET_SHOWN} of {targetList.total.toLocaleString()}
                                  {' — type above to find the one you want.'}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </Step>
                  )}

                  {/*  ── The way through to the composer ────────────────────────
                       The four catalogue pickers are the same controls the
                       single-claim editor uses — a searchable diagnosis list, a
                       procedure list, labs, and a prescription with dose, frequency,
                       duration, route and quantity. None of that fits in a column
                       beside a table, which is what this one had become.

                       So it opens its own page. Not a fourth dialog: the same frame,
                       one step further in, with Back returning here and Back again to
                       the pattern. */}
                  {/*  ── THE WAY THROUGH, FOR EVERY ACT ──────────────────────────
                       DERIVED from MV_ACTIONS, not a list of action names. This gate
                       has now been the same bug three times: an act was added, the
                       hand-written condition was not, and the officer chose it and had
                       nothing to press. `needsTarget` is the only real distinction —
                       Update and Override act on entries ticked in step 2 and have
                       nothing to open until something is ticked; the other three go
                       straight through. */}
                  {action && (!MV_NEEDS_TARGET.has(action) || target.length > 0) && (
                    <Step n={MV_NEEDS_TARGET.has(action) ? 3 : 2}
                      label={MV_STEP_LABEL[action] || 'Add what'} block>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.6 }}>
                          {action === 'add'
                            ? <>Pick a diagnosis, procedure, lab or medication from the
                                catalogue to put on the {chosenCount.toLocaleString()}
                                {' '}ticked claim(s).</>
                            : action === 'attendance'
                              ? <>Choose Emergency/Acute, Chronic, Antenatal or Postnatal
                                  for the {chosenCount.toLocaleString()} ticked claim(s).</>
                              : action === 'service'
                                //  Says what it will find on the other side. On a group
                                //  where nobody has a service that is one row — "no
                                //  service set" — and the officer should know that
                                //  before pressing, not be surprised by it.
                                ? <>The next step lists the service(s) these
                                    {' '}{chosenCount.toLocaleString()} claim(s) carry —
                                    one row for each, and one for the claims with none —
                                    and you map each to its replacement.</>
                                : action === 'template'
                                  //  Says what the next step will TELL them, because the
                                  //  answer is per claim: on a group holding both children
                                  //  and adults a child template fits some and not others,
                                  //  and knowing that before pressing is the point.
                                  ? <>Pick a template from the library. Every one of the
                                      {' '}{chosenCount.toLocaleString()} ticked claim(s) is
                                      then checked against its own patient, and the next
                                      step says which can take it and which cannot.</>
                                  : action === 'override'
                                    ? <>Pick what replaces each of the {target.length}
                                        {' '}entr{target.length === 1 ? 'y' : 'ies'} chosen
                                        above — or mark it to be removed.</>
                                    : <>Pick what replaces the {target.length}
                                        {' '}entr{target.length === 1 ? 'y' : 'ies'} chosen above.</>}
                        </div>
                        <button type="button"
                          disabled={chosenCount === 0}
                          title={chosenCount === 0
                            ? 'Tick some patients on the left first'
                            : 'Open the composer'}
                          onClick={() => setView('compose')}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            justifySelf: 'start', padding: '9px 15px', borderRadius: 8,
                            fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit',
                            border: '1px solid transparent', color: '#fff',
                            cursor: chosenCount ? 'pointer' : 'not-allowed',
                            background: chosenCount ? BRAND.greenDark : BRAND.inkFaint,
                          }}>
                          {MV_OPEN_LABEL[action] || 'Choose what to apply'}
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </Step>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 8, padding: '13px 20px', alignItems: 'center',
          borderTop: `1px solid ${BRAND.line}`, background: '#fff', flexShrink: 0,
        }}>
          {/*  The pattern's own count, not the ticked-rows count. Nothing is ticked
               in here any more — the pattern IS the selection. */}
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: BRAND.inkMuted }}>
            {found === null
              ? 'Set a pattern above.'
              : found.length === 0
                ? 'No claims match this pattern yet.'
                : <>
                    <strong style={{ color: BRAND.ink, fontSize: 13 }}>
                      {found.length.toLocaleString()}
                    </strong>
                    {' claim(s) in this pattern'}
                  </>}
          </div>

          {/*  Back, on the claims view. Same slot as Open, because they are the same
               control at two points in one journey — forward when a group has been
               described, back when it needs changing. */}
          {view !== 'pattern' && (
            <button type="button" onClick={goBack}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
                border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
              }}>
              <ChevronLeft size={14} /> {view === 'compose' ? 'Back to the patients' : 'Back to the pattern'}
            </button>
          )}

          {/*  Opens the group in place. It was a note saying the form was next; the
               form is this dialog's other view. */}
          <button type="button"
            disabled={view === 'claims' || !found || found.length === 0}
            title={found && found.length
              ? `Open the ${found.length.toLocaleString()} claims this pattern found`
              : 'No claims to open yet'}
            onClick={() => setView('claims')}
            style={{
              //  Hidden on the claims view — you are already there, and a live
              //  "Open" beside a "Back" is two controls for one place.
              display: view === 'claims' ? 'none' : 'inline-block',
              padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
              fontFamily: 'inherit', border: '1px solid transparent', color: '#fff',
              cursor: found && found.length ? 'pointer' : 'not-allowed',
              background: found && found.length ? BRAND.greenDark : BRAND.inkFaint,
            }}>
            {found && found.length
              ? `Open ${found.length.toLocaleString()} claims`
              : 'Open claims'}
          </button>

          <button type="button" onClick={onClose} style={{
            padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
            fontWeight: 700, fontFamily: 'inherit',
            border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── One foldable filter panel ───────────────────────────────────────────────
//  A header that says what is chosen, and — when unfolded — a search box over the
//  values actually present in this month, each with its claim count. Picking one
//  sets the filter; picking it again clears it, so there is no separate "none"
//  entry to hunt for.
//
//  The list is capped at 200 RENDERED rows with the shortfall stated. 500 entries
//  is what the server returns and every one of them is reachable by typing — but
//  500 DOM rows per panel is what makes a dialog feel broken.
function FilterPanel({
  panel, values = [], onPick, open, onToggle, items, error,
  //  Opens the detail-and-repair dialog for one row. Passed down rather than
  //  owned here because the dialog has to sit above the Advanced Filter, not
  //  inside its scroll area.
  onInspect,
}) {
  const [q, setQ] = useState('');
  const chosen = new Set(values.map(String));
  //  Toggle, so the same click that selects also deselects. There is no separate
  //  "none" entry to find, and clearing the whole panel is the ✕ on the header.
  const toggle = (code) => {
    const next = new Set(chosen);
    if (next.has(String(code))) next.delete(String(code));
    else next.add(String(code));
    onPick([...next]);
  };

  const matches = (items || []).filter((it) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return `${it.code || ''} ${it.name || ''}`.toLowerCase().includes(needle);
  });
  const shown = matches.slice(0, 200);

  return (
    <div style={{
      border: `1px solid ${chosen.size ? BRAND.greenDark : BRAND.line}`,
      borderRadius: 9, background: '#fff', overflow: 'hidden',
    }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '9px 11px', border: 0, cursor: 'pointer', textAlign: 'left',
          font: 'inherit', background: chosen.size ? BRAND.greenSoft : '#F8FAFC',
          borderBottom: open ? `1px solid ${BRAND.line}` : 'none',
        }}>
        {open ? <ChevronUp size={13} style={{ color: BRAND.inkMuted, flexShrink: 0 }} />
          : <ChevronDown size={13} style={{ color: BRAND.inkMuted, flexShrink: 0 }} />}
        <span style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: .4, textTransform: 'uppercase',
          color: chosen.size ? BRAND.greenDark : BRAND.inkSoft, flexShrink: 0,
        }}>{panel.label}</span>
        {/*  Names the picks while they fit and counts them when they do not. One
             tick should read as itself, not as "1 selected". */}
        <span style={{
          flex: 1, minWidth: 0, fontSize: 12, fontWeight: chosen.size ? 800 : 500,
          color: chosen.size ? BRAND.ink : BRAND.inkFaint,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {chosen.size === 0 ? 'any'
            : chosen.size <= 2 ? values.join(', ')
            : `${chosen.size} selected`}
        </span>
        {items && (
          <span style={{ fontSize: 10.5, color: BRAND.inkFaint, flexShrink: 0 }}>
            {items.length}
          </span>
        )}
        {/*  Clears the whole panel without opening it — the common correction. */}
        {chosen.size > 0 && (
          <span role="button" tabIndex={0} aria-label={`Clear ${panel.label}`}
            onClick={(e) => { e.stopPropagation(); onPick([]); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onPick([]); } }}
            style={{ fontSize: 12, color: BRAND.inkMuted, padding: '0 2px', flexShrink: 0 }}>✕</span>
        )}
      </button>

      {open && (
        <div>
          <div style={{ padding: 8, borderBottom: `1px solid ${BRAND.line}` }}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={panel.hint}
              aria-label={`Search ${panel.label}`}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '7px 9px',
                fontSize: 12, borderRadius: 7, fontFamily: 'inherit',
                border: `1px solid ${BRAND.line}`, outline: 'none', color: BRAND.ink,
              }} />
          </div>

          <div style={{ maxHeight: 232, overflowY: 'auto' }}>
            {error && (
              <div style={{ padding: '10px 11px', fontSize: 11.5, color: BRAND.danger }}>
                Could not load the list: {error}
              </div>
            )}
            {!error && items === null && (
              <div style={{ padding: '10px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
                Loading what this month contains…
              </div>
            )}
            {!error && items && items.length === 0 && (
              <div style={{ padding: '10px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
                Nothing recorded in this month.
              </div>
            )}
            {!error && items && items.length > 0 && shown.length === 0 && (
              <div style={{ padding: '10px 11px', fontSize: 11.5, color: BRAND.inkFaint }}>
                Nothing matches “{q.trim()}”.
              </div>
            )}

            {shown.map((it) => {
              const on = chosen.has(String(it.code));
              return (
                <button key={`${it.code}`} type="button"
                  onClick={() => toggle(it.code)}
                  aria-pressed={on}
                  //  `note` explains a code the catalogues cannot name, or one that is
                  //  not a specialty at all — D001 and friends, which came out of the
                  //  imported file's specialtyAttended field. Without it a bare code
                  //  sitting among named ones reads as a twelfth specialty rather than
                  //  as the data fault it is.
                  title={[it.name || it.code, it.note].filter(Boolean).join(' — ')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '6px 11px', border: 0, cursor: 'pointer', textAlign: 'left',
                    font: 'inherit', fontSize: 12,
                    background: on ? BRAND.greenSoft : 'transparent',
                    borderBottom: `1px solid ${BRAND.line}`,
                  }}>
                  {/*  A real tick box. Several of these can be on at once, and a
                       row that only changes shade does not say that. */}
                  <span aria-hidden="true" style={{
                    width: 13, height: 13, borderRadius: 3, flexShrink: 0,
                    display: 'grid', placeItems: 'center',
                    border: `1.5px solid ${on ? BRAND.greenDark : BRAND.inkFaint}`,
                    background: on ? BRAND.greenDark : '#fff',
                    color: '#fff', fontSize: 9, fontWeight: 900, lineHeight: 1,
                  }}>{on ? '✓' : ''}</span>
                  <span style={{
                    fontWeight: 800, color: on ? BRAND.greenDark : BRAND.ink,
                    flexShrink: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 11,
                  }}>{it.code}</span>
                  <span style={{
                    flex: 1, minWidth: 0, color: BRAND.inkSoft,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{it.name || ''}</span>
                  {/*  Claims, not lines. The whole reason to show a number here is
                       "how big is this if I click it". */}
                  <span style={{
                    flexShrink: 0, fontWeight: 800, fontSize: 11,
                    color: on ? BRAND.greenDark : BRAND.inkMuted,
                  }}>{it.claims.toLocaleString()}</span>

                  {/*  ── WHAT IS THIS, REALLY? ─────────────────────────────────
                       Opens everything the database holds for this entry, and lets
                       it be corrected at the root. A <span role="button"> and not a
                       <button>, because this row IS a button and nesting one inside
                       another is invalid — the same reason the ✕ on the header is a
                       span. stopPropagation so inspecting does not also tick the
                       filter, which would apply a filter nobody asked for. */}
                  {panel.entryKind && onInspect && (
                    <span
                      role="button" tabIndex={0}
                      aria-label={`Details for ${it.code}`}
                      title="What this is, and fix it at the root"
                      onClick={(e) => {
                        e.stopPropagation();
                        onInspect({ kind: panel.entryKind, code: it.code, name: it.name });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault(); e.stopPropagation();
                        onInspect({ kind: panel.entryKind, code: it.code, name: it.name });
                      }}
                      style={{
                        flexShrink: 0, width: 17, height: 17, borderRadius: '50%',
                        display: 'grid', placeItems: 'center', cursor: 'pointer',
                        border: `1px solid ${BRAND.line}`, background: '#fff',
                        color: BRAND.inkMuted, fontSize: 10, fontWeight: 800,
                        lineHeight: 1,
                      }}>i</span>
                  )}
                </button>
              );
            })}

            {matches.length > shown.length && (
              <div style={{ padding: '8px 11px', fontSize: 11, color: BRAND.inkFaint }}>
                {(matches.length - shown.length).toLocaleString()} more — type above to
                narrow the list.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AdvancedFilter({
  draft, onChange, onClose, onSearch, onClear, monthLabel, total,
  year, month, facility, insurances,
  //  Called when a check's own Fix action is used. The parent owns the dialogs so
  //  they are not unmounted when this one closes.
  onFixDoseUnits,
  //  (ruleCode, count) => void. Fires the month-wide repair for one consistency
  //  rule. Absent means the tile shows no Fix all, which is what every other
  //  caller of this panel wants.
  onFixIssue,
  //  (ruleCode, fixKey, count) => void. The same idea for the NHIA format rules,
  //  which have a different engine behind them — see NHIA_FIX_BY_RULE.
  onFixNhia,
  //  () => void. Opens the G-DRG re-code dialog, which is the SECOND half of
  //  ipd_on_opd_service: Fix all settles what is unambiguous, this settles the
  //  rest. Absent means the tile shows only Fix all.
  onRecodeGdrg,
}) {
  //  A half-described filter is work too. The backdrop no longer discards it.
  const { panelRef, backdropProps } = useDialogGuard();
  const set = (k, v) => onChange({ ...draft, [k]: v });
  //  Two keys in ONE update. `set` spreads the draft it was rendered with, so
  //  calling it twice in a row applies the second change to the pre-first draft
  //  and silently throws the first away. The age bands set a lower and an upper
  //  bound together, which is exactly that case.
  const setBoth = (patch) => onChange({ ...draft, ...patch });
  const count = ['diagnosis', 'procedure', 'investigation', 'medication', 'service']
    .reduce((n, k) => n + (draft[k]?.length || 0), 0)
    + (draft.type !== 'all' ? 1 : 0);

  //  The entry whose ⓘ was pressed, or null. Held here rather than inside
  //  FilterPanel so the dialog renders as a sibling of this panel and is not
  //  clipped by the panel's own 232px scroll area.
  const [inspect, setInspect] = useState(null);

  //  What is in the month, so each panel is a list to pick from rather than a box
  //  to type a remembered code into. Fetched once when the dialog opens.
  const [facets, setFacets] = useState(null);
  const [facetError, setFacetError] = useState(null);
  //  Bumped after a catalogue repair, which changes both the names in these lists
  //  and the counts beside them. A plain `setFacets(null)` would not do: the fetch
  //  below runs on an empty dependency list, so clearing the state without a
  //  trigger leaves the panels reading "Loading…" for as long as the dialog is open.
  const [facetToken, setFacetToken] = useState(0);
  useEffect(() => {
    let alive = true;
    setFacets(null);
    setFacetError(null);
    axios.get(claimsDetailsFacets, {
      params: {
        year, month, facility,
        insurance_type: insurances?.length ? insurances.join(',') : 'all',
      },
    })
      .then(({ data }) => { if (alive) setFacets(data); })
      .catch((e) => { if (alive) setFacetError(e?.response?.data?.message || e.message); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetToken]);

  //  Has the month's contents arrived? Everything in this dialog is a list of what
  //  is actually IN the month — the four pick panels, both groups of checks, the
  //  attendance counts — so until this resolves there is genuinely nothing to show.
  //
  //  On a remote server that is a second or two, and the dialog spent it looking
  //  BROKEN rather than busy: the panels read "any" with no counts and the two
  //  check groups drew as empty grey bars, which is indistinguishable from "this
  //  month has no problems". A filter that appears to report a clean month while
  //  it is still loading is worse than a slow one.
  const facetsLoading = !facets && !facetError;

  //  Which panel is open. ONE at a time: four open lists of 500 entries is the
  //  scrolling problem this dialog exists to remove. Opening one folds the rest,
  //  which is also what keeps the two-column grid from growing to ten screens.
  //  ALL CLOSED on open. Starting with diagnosis expanded made it the odd one out —
  //  one full-width open list above three collapsed bars — so the dialog looked
  //  broken rather than deliberate. Every panel now presents identically and you
  //  open the one you want.
  const [openPanel, setOpenPanel] = useState(null);

  //  `entryKind` is what the ⓘ on a row opens — the catalogue kind the entry
  //  belongs to, which is also the key catalogueEntry.js is configured by. The
  //  Service panel has none: a service is the claim's own header field, registered
  //  from insured_services, and there is no per-claim child row to repair.
  const PANELS = [
    { key: 'diagnosis',  label: 'Diagnosis',  facet: 'diagnoses',
      entryKind: 'diagnosis',
      hint: 'ICD-10, G-DRG or part of the name' },
    { key: 'procedure',  label: 'Procedure',  facet: 'procedures',
      entryKind: 'procedure',
      hint: 'G-DRG or part of the name' },
    //  Lab / Scan was missing here while the composer's own filter has had it all
    //  along, so a test billed across 1,208 rows could be filtered on one screen
    //  and not the other. The facet has always been served.
    { key: 'investigation', label: 'Lab / Scan', facet: 'investigations',
      entryKind: 'investigation',
      hint: 'Code or part of the test name' },
    { key: 'medication', label: 'Medication', facet: 'medications',
      entryKind: 'medication',
      hint: 'Code or part of the generic name' },
    { key: 'service',    label: 'Service',    facet: 'services',
      hint: 'The specialty G-DRG the claim was registered under', readOnlyPick: true },
  ];

  // ── WHICH SECTION A CHECK BELONGS IN ──────────────────────────────────────
  //  The two groups ask different questions: "what is ABSENT from this claim"
  //  and "what is WRONG with it". The backend does not split them that way — it
  //  splits on how a check is IMPLEMENTED, `gaps` being plain column tests and
  //  `issues` being the ones needing the child tables — and the dialog was
  //  showing that split to the officer, who has no reason to care about it.
  //
  //  So the sections are decided HERE, by what the check MEANS. Nothing about
  //  the rules changes: a check listed under "What is missing" still ticks into
  //  `issues` and is still answered by the same SQL. Only where it appears moves.
  //
  //  "Admission with no principal diagnosis" and "…with several G-DRGs and no
  //  principal" are the clearest cases — a claim with no principal is missing
  //  one — and they were sitting under a heading about being billed wrongly.
  //  The four "Claim has no …" checks and the missing date of birth are the same
  //  kind of statement and move with them.
  //  `no_specialty` and `no_medication_on_admission` are in this list for the same
  //  reason as the rest: they are COMPLETENESS checks — something the claim does
  //  not carry — and belong under "What is missing" rather than under "What is
  //  wrong", where the claim would be accused of an error it has not made. A claim
  //  with no specialty has not got a specialty wrong.
  //
  //  KEPT OUTSIDE THE LITERAL. tests/claims-filter-sections.test.js reads this Set
  //  out of the source as text, and a comment between the entries was parsed as
  //  two more entries — which then failed its own "neither set names a check that
  //  does not exist" assertion.
  const MISSING_ISSUES = new Set([
    'no_principal_dx', 'no_principal_multi_gdrg',
    'no_patient_name', 'no_policy_id', 'no_billable_gdrg', 'no_diagnosis',
    'no_date_of_birth', 'no_specialty', 'no_medication_on_admission',
    //  The out-patient half of the same pair. A completeness check like its twin —
    //  something the claim does not carry — so it reads under "What is missing"
    //  rather than under "What is wrong", where an ordinary OPD consultation would
    //  be accused of a fault it has not committed.
    'no_medication_outpatient',
    //  The claim SAYS it dispensed pharmacy and carries no medicine lines. What is
    //  absent is the medicines, so it reads under "What is missing" beside the
    //  other completeness checks. It sat under "What is wrong" with a REJECTS
    //  badge, which is true of its severity and wrong about its kind — the two are
    //  separate questions, and the section answers the second.
    'nhia_pharmacy_no_medicines',
  ]);
  //  Listed in BOTH lists by the backend — once as a column test, once as a rule
  //  with SQL behind it — so the dialog showed the same check twice, in two
  //  sections, with two different badges. It belongs with the other completeness
  //  checks: a missing dose unit is missing whether the patient was admitted or
  //  not, which is the reason it cannot live under an admissions heading.
  const DUPLICATED_IN_GAPS = new Set(['med_no_dose_unit']);

  const issuesMissing = (facets?.issues || [])
    .filter((g) => MISSING_ISSUES.has(g.code) && !DUPLICATED_IN_GAPS.has(g.code));
  const issuesWrong = (facets?.issues || [])
    .filter((g) => !MISSING_ISSUES.has(g.code) && !DUPLICATED_IN_GAPS.has(g.code));

  //  The "Fix all" button, shared by both issue lists. Two of the checks that
  //  moved up — no principal, and several G-DRGs with no principal — are
  //  repairable, so defining this only on the list they used to be in would have
  //  taken their Fix all away as a side effect of moving them.
  //  Same button, two engines behind it. The style is shared deliberately: from
  //  the officer's side these are one idea — settle every claim carrying this
  //  fault — and giving them two different-looking buttons would suggest a
  //  distinction that only matters inside the code.
  const fixButton = (title, onClick) => (
    <button type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{
        position: 'absolute', right: 7, bottom: 6,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 7px', borderRadius: 6,
        font: 'inherit', fontSize: 10.5, fontWeight: 800,
        cursor: 'pointer', background: '#fff',
        border: `1px solid ${BRAND.greenDark}66`, color: BRAND.greenDark,
      }}>
      <Wrench size={10} /> Fix all
    </button>
  );

  const issueAction = (g) => {
    //  ── SETTING THE G-DRG ON THE DIAGNOSIS ROWS ────────────────────────────
    //  A SECOND action on this one tile, beside Fix all rather than instead of it,
    //  because they finish different halves of the same fault.
    //
    //  Fix all settles the claim's header and every diagnosis row whose ICD-10
    //  resolves to a single admissible code. It then reports the rest as
    //  "ambiguous — open the claim and pick one", which on this month is 233
    //  claims one at a time.
    //
    //  This button is those claims. The ambiguity is per DIAGNOSIS, not per claim,
    //  so 449 rows are 21 questions and "what does Pneumonia become for an adult"
    //  answers 62 claims at once.
    if (g.code === 'ipd_on_opd_service' && onRecodeGdrg) {
      return (
        <>
          {onFixIssue && fixButton(
            `Settle all ${g.claims} from each claim's own rows — a procedure `
            + 'becomes the principal where one was done, otherwise the '
            + 'diagnosis. You see the list before anything is written.',
            () => onFixIssue(g.code, g.claims))}
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onRecodeGdrg(); }}
            title={'For the claims Fix all reports as ambiguous: pick which G-DRG '
              + 'each diagnosis becomes, once, and it is applied to every claim '
              + 'carrying it. The ICD-10 is never changed.'}
            style={{
              position: 'absolute', right: 7, bottom: 26,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 7px', borderRadius: 6,
              font: 'inherit', fontSize: 10.5, fontWeight: 800,
              cursor: 'pointer', background: '#fff',
              border: `1px solid ${BRAND.navy}55`, color: BRAND.navy,
            }}>
            <Wrench size={10} /> Set G-DRGs
          </button>
        </>
      );
    }
    //  Settled from the claim's own rows, through Multi Vet.
    if (MV_REPAIRABLE.has(g.code) && onFixIssue) {
      return fixButton(
        `Settle all ${g.claims} from each claim's own rows — a procedure `
        + 'becomes the principal where one was done, otherwise the '
        + 'diagnosis. You see the list before anything is written.',
        () => onFixIssue(g.code, g.claims));
    }
    //  ── The NHIA format corrections ────────────────────────────────────────
    //  These tiles had no button at all, which is what made the section read as
    //  "here are 48 broken claims and nothing you can do about them" — when three
    //  of the rules are mechanically correctable across the whole month.
    const nhiaFix = NHIA_FIX_BY_RULE.get(g.code);
    if (nhiaFix && onFixNhia) {
      return fixButton(
        `Correct all ${g.claims} claims carrying this fault. You see every `
        + 'change — the old value and the new one — before anything is written.',
        () => onFixNhia(g.code, nhiaFix, g.claims));
    }
    return null;
  };

  //  One removable chip per chosen value, resolved to its NAME where the facet
  //  knows it — "B54" alone does not tell anyone they picked malaria. Derived from
  //  `draft`, so it can never disagree with what Search would send.
  const chips = [
    ...PANELS.flatMap((pan) => (draft[pan.key] || []).map((code) => {
      const hit = (facets?.[pan.facet] || []).find((x) => String(x.code) === String(code));
      const name = hit?.name || '';
      return {
        id: `${pan.key}:${code}`,
        label: name ? `${code} · ${name}` : String(code),
        title: `${pan.label}: ${name ? `${code} — ${name}` : code}`,
        clear: () => set(pan.key, (draft[pan.key] || [])
          .filter((v) => String(v) !== String(code))),
      };
    })),
    ...(draft.gaps || []).map((key) => {
      const hit = (facets?.gaps || []).find((g) => g.code === key);
      return {
        id: `gap:${key}`,
        label: hit?.name || key,
        title: `Missing: ${hit?.name || key}`,
        clear: () => set('gaps', (draft.gaps || []).filter((k) => k !== key)),
      };
    }),
    ...(draft.issues || []).map((key) => {
      const hit = (facets?.issues || []).find((g) => g.code === key);
      return {
        id: `issue:${key}`,
        label: hit?.name || key,
        title: `Wrong: ${hit?.name || key}`,
        clear: () => set('issues', (draft.issues || []).filter((k) => k !== key)),
      };
    }),
    ...((draft.type && draft.type !== 'all')
      ? [{
        id: 'type',
        label: draft.type === 'opd' ? 'Out-patient' : 'In-patient',
        title: 'Attendance',
        clear: () => set('type', 'all'),
      }]
      : []),
    //  Age is ONE chip whichever bounds are set — two chips reading "from 18"
    //  and "to 59" would let you remove half a range and leave the other half
    //  filtering, which is not a thing anyone means to do.
    ...((draft.ageFrom !== '' || draft.ageTo !== '')
      ? [{
        id: 'age',
        label: (() => {
          const f = draft.ageFrom; const t = draft.ageTo;
          if (f !== '' && t !== '') return f === t ? `${f} years old` : `${f}–${t} years`;
          return f !== '' ? `${f} years and over` : `under ${Number(t) + 1} years`;
        })(),
        title: 'Age at the visit',
        clear: () => setBoth({ ageFrom: '', ageTo: '' }),
      }]
      : []),
    //  ONE CHIP FOR THE WHOLE STAY SELECTION, like age and unlike the code filters.
    //  "2 days, 3 days" is one cohort the officer asked for; splitting it into two
    //  removable chips invites removing half a range and leaving the rest filtering.
    ...((draft.stayDays || []).length
      ? [{
        id: 'stay',
        label: draft.stayDays
          .map((c) => (c === '1' ? 'Same day'
            : String(c).endsWith('+') ? `${parseInt(c, 10)} days or longer`
              : `${c} days`))
          .join(', '),
        title: 'Length of admission',
        clear: () => set('stayDays', []),
      }]
      : []),
  ];

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Advanced filter"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(15,23,42,.42)',
        //  CENTRED, not pinned to the top. Anchoring at flex-start meant the only
        //  thing holding the dialog off the top edge was the padding, and this
        //  dialog is tall enough to eat all of it.
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: `${DIALOG_GAP}px 16px`,
      }}>
      <div ref={panelRef}
        style={{
          width: '100%', maxWidth: 980, background: '#fff', borderRadius: 12,
          border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.28)',
          //  Capped below the viewport by twice the gap, so the space above and
          //  below survives however tall the content wants to be — it scrolls
          //  inside instead of pushing against the window edge.
          maxHeight: `calc(100vh - ${DIALOG_GAP * 2}px)`,
          display: 'flex', flexDirection: 'column',
        }}>
        {/*  A navy band, the same one the admin pages and every claim dialog
             wear. The header was white on white with a small green wrench, so the
             dialog opened with no edge and no weight — it read as part of the page
             behind it rather than as a thing that had opened on top. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: '#fff',
          borderRadius: '12px 12px 0 0',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            display: 'grid', placeItems: 'center',
            background: 'rgba(255,255,255,.14)',
          }}>
            <Wrench size={16} style={{ color: '#fff' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }}>
              Advanced filter
            </div>
            <div style={{ fontSize: 11.5, opacity: .82 }}>
              {monthLabel} · {Number(total || 0).toLocaleString()} claims to search
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{
              border: 0, background: 'rgba(255,255,255,.14)', cursor: 'pointer',
              width: 28, height: 28, borderRadius: 7, color: '#fff',
              display: 'grid', placeItems: 'center', fontSize: 13, lineHeight: 1,
              flexShrink: 0,
            }}>✕</button>
        </div>

        {/*  WHAT IS CURRENTLY SET, as removable chips. With four filters you can
             infer it from the panel headers; with a dozen you cannot, and this is
             the row that keeps the dialog readable as filters are added. */}
        {chips.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
            padding: '9px 14px', borderBottom: `1px solid ${BRAND.line}`,
            background: '#FCFDFE',
          }}>
            <span style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: .4,
              textTransform: 'uppercase', color: BRAND.inkFaint, marginRight: 2,
            }}>Filtering by</span>
            {chips.map((ch) => (
              <span key={ch.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 260,
                padding: '3px 5px 3px 8px', borderRadius: 999, fontSize: 11,
                background: BRAND.greenSoft, color: BRAND.greenDark,
                border: `1px solid ${BRAND.greenDark}33`,
              }}>
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }} title={ch.title}>{ch.label}</span>
                <button type="button" onClick={ch.clear} aria-label={`Remove ${ch.label}`}
                  style={{
                    border: 0, background: 'none', cursor: 'pointer', padding: 0,
                    lineHeight: 1, fontSize: 12, color: BRAND.greenDark, opacity: .7,
                  }}>✕</button>
              </span>
            ))}
          </div>
        )}

        {/*  TWO COLUMNS at this width, not three. Three made every list narrow
             enough to truncate the names you pick by — and left two-thirds of the
             row empty whenever one panel was open. An OPEN panel now spans the
             full width; collapsed ones pair up. */}
        <div style={{
          padding: 14, overflowY: 'auto', display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          alignItems: 'start', alignContent: 'start',
        }}>
          {/*  ── Reading the month ─────────────────────────────────────────────
               Says what is happening, ONCE, across the top — rather than four
               panels and two check groups each quietly drawing empty. It names
               the month and the claim count so the wait is attached to a real
               amount of work.  */}
          {facetsLoading && (
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '11px 13px', borderRadius: 9,
              background: BRAND.navySoft,
              border: `1px solid ${BRAND.line}`,
            }}>
              <Loader2 size={16} className="mf-spin"
                style={{ color: BRAND.navy, flexShrink: 0 }} />
              {/*  Declared here rather than in a stylesheet: this dialog is the
                   only thing that spins, and a component carrying its own
                   animation cannot be half-installed. Keyed on the class name so
                   a second render cannot stack a duplicate rule. */}
              <style>{'@keyframes mfspin{to{transform:rotate(360deg)}}'
                + '.mf-spin{animation:mfspin .9s linear infinite}'}</style>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.navy }}>
                  Reading {monthLabel}…
                </div>
                <div style={{ fontSize: 11.5, color: BRAND.inkMuted, lineHeight: 1.5 }}>
                  Working out what {Number(total || 0).toLocaleString()} claim(s) actually
                  contain, so every list below is what is really in the month. The counts
                  appear as soon as it lands.
                </div>
              </div>
            </div>
          )}

          <div style={{ gridColumn: '1 / -1', ...GROUP_LABEL }}>
            <GroupHeading>What was billed</GroupHeading>
          </div>

          {PANELS.map((pan) => (
            //  The wrapper carries the span so FilterPanel stays unaware of the
            //  grid it happens to sit in.
            <div key={pan.key} style={{
              gridColumn: openPanel === pan.key ? '1 / -1' : 'auto', minWidth: 0,
            }}>
              <FilterPanel
                panel={pan}
                values={draft[pan.key] || []}
                onPick={(v) => set(pan.key, v)}
                open={openPanel === pan.key}
                onToggle={() => setOpenPanel(openPanel === pan.key ? null : pan.key)}
                items={facets ? (facets[pan.facet] || []) : null}
                error={facetError}
                onInspect={setInspect}
              />
            </div>
          ))}

          {/*  ── WHAT THIS CODE REALLY IS ────────────────────────────────────
               The ⓘ on any row opens it. Rendered here, outside the grid, because
               it is a modal over the whole screen and not a part of the panel that
               opened it. The month is passed through so the dialog can say "1,550
               of these 2,491 rows are in the month you are looking at" — and so a
               repair can be limited to that month rather than every claim. */}
          {inspect && (
            <CatalogueEntryDialog
              kind={inspect.kind}
              code={inspect.code}
              name={inspect.name}
              scope={{
                year, month, facility,
                insurance_type: insurances?.length ? insurances.join(',') : 'all',
              }}
              onClose={() => setInspect(null)}
              //  The facet counts and the claim rows have both moved, so the lists
              //  on this dialog are stale. Re-read them rather than leaving figures
              //  on screen that describe the database as it was.
              onRepaired={() => setFacetToken((t) => t + 1)}
            />
          )}

          {/*  ── WHO THE PATIENT WAS ────────────────────────────────────────
               Placed directly under "What was billed" because that is how the
               questions actually pair up: a drug, a procedure or a service means
               something different for a two-year-old than for a pensioner, and
               this is the axis that lets you ask about one without the other.

               Age is computed AS OF THE VISIT, never from today. A patient who
               was 4 in January is 5 in December, and a claim has to be judged by
               the age it was billed at. */}
          <div style={{ gridColumn: '1 / -1', ...GROUP_LABEL, marginTop: 2 }}>
            <GroupHeading>Who the patient was</GroupHeading>
          </div>

          <div style={{
            gridColumn: '1 / -1', border: `1px solid ${BRAND.line}`, borderRadius: 10,
            background: BRAND.surface, padding: '12px 14px',
            boxShadow: '0 1px 2px rgba(15,31,61,.05)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: BRAND.ink }}>
                Age at the visit
              </span>

              {[
                ['from', 'ageFrom', 'any'],
                ['to', 'ageTo', 'any'],
              ].map(([word, key, placeholder]) => (
                <label key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 12, color: BRAND.inkSoft,
                }}>
                  {word}
                  <input
                    type="number" min={0} max={130} inputMode="numeric"
                    value={draft[key]}
                    placeholder={placeholder}
                    onChange={(e) => set(key, e.target.value)}
                    aria-label={`Age ${word}`}
                    style={{
                      width: 68, padding: '5px 8px', fontSize: 13,
                      border: `1px solid ${BRAND.line}`, borderRadius: 7,
                      color: BRAND.ink, background: '#fff',
                    }} />
                </label>
              ))}
              <span style={{ fontSize: 12, color: BRAND.inkFaint }}>years</span>

              {(draft.ageFrom !== '' || draft.ageTo !== '') && (
                <button type="button"
                  onClick={() => setBoth({ ageFrom: '', ageTo: '' })}
                  style={{
                    marginLeft: 'auto', border: 0, background: 'none', cursor: 'pointer',
                    fontSize: 12, color: BRAND.inkSoft, textDecoration: 'underline',
                  }}>
                  Clear age
                </button>
              )}
            </div>

            {/*  The bands the data actually has, and the one boundary NHIS
                 itself uses. Measured on this database rather than invented:
                 under-1 908 claims, 1–4 1,077, 5–11 721, 12–17 633, 18–59
                 6,597, 60+ 1,479. Each is a shortcut for the two boxes above,
                 not a separate filter — clicking one fills them in, so what is
                 being asked stays visible. */}
            <div style={{
              display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10,
            }}>
              {[
                ['Under 1', '', '0'],
                ['1–4', '1', '4'],
                ['5–11', '5', '11'],
                ['Child (under 12)', '', '11'],
                ['Adult (12+)', '12', ''],
                ['18–59', '18', '59'],
                ['60 and over', '60', ''],
              ].map(([label, from, to]) => {
                const on = String(draft.ageFrom) === from && String(draft.ageTo) === to;
                return (
                  <button key={label} type="button"
                    onClick={() => {
                      //  A second click on the active band clears it, so the
                      //  shortcut can undo itself without hunting for Clear.
                      //  ONE update: two `set` calls would apply the second to
                      //  the pre-first draft and drop the lower bound.
                      setBoth(on ? { ageFrom: '', ageTo: '' } : { ageFrom: from, ageTo: to });
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                      fontSize: 11.5, fontWeight: on ? 700 : 600,
                      border: `1px solid ${on ? BRAND.green : BRAND.line}`,
                      background: on ? BRAND.green : '#fff',
                      color: on ? '#fff' : BRAND.inkSoft,
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {/*  Said before the search runs, not discovered afterwards. A claim
                 with no date of birth has no age, so it cannot satisfy a range —
                 and 1,092 imported claims here have none. Without this line the
                 count drops and nothing explains why. */}
            {(draft.ageFrom !== '' || draft.ageTo !== '') && (
              <div style={{
                marginTop: 9, fontSize: 11.5, color: BRAND.inkSoft,
                background: '#F8FAFC', border: `1px solid ${BRAND.line}`,
                borderRadius: 7, padding: '6px 9px',
              }}>
                Age is worked out from the date of birth and the visit date, so a
                claim with no date of birth is left out of an age range.
              </div>
            )}
          </div>

          <div style={{ gridColumn: '1 / -1', ...GROUP_LABEL, marginTop: 2 }}>
            <GroupHeading>What is missing</GroupHeading>
          </div>

          {/*  Straight checkboxes, not a searchable list: the set is fixed and
               short, and every one of them is a question you either want asked or
               do not. Ticking several means "any of these problems" — the same OR
               as several diagnoses in one panel. ANDing them would ask for the one
               claim missing everything at once.  */}
          {/*  FLEX-WRAP, not a nested auto-fit grid. As a grid inside a spanning
               grid item this box computed a one-row height, and `overflow: hidden`
               then silently ate four of the seven checkboxes while the next group
               heading drew on top of it. Wrapping flex items cannot collapse that
               way, and nothing here clips.  */}
          <CheckList
            items={facets?.gaps}
            chosen={draft.gaps}
            loaded={!!facets}
            onSet={(v) => set('gaps', v)}
            empty="Completeness checks are unavailable for this month."
            //  ── A check that can be FIXED from here ─────────────────────────
            //  "Medication without a dose unit" is the one gap on this list with a
            //  bulk remedy, because the fault is not per claim: the same drug is
            //  missing its unit on every claim carrying it. Filtering to the 31
            //  claims and opening each was the only route, and it is 31 times the
            //  same correction.
            //
            //  The action sits on the tile because that is where the officer is
            //  standing when they learn the number.
            actionFor={(g) => (g.code === 'med_no_dose_unit' && onFixDoseUnits ? (
              <button type="button"
                onClick={(e) => { e.stopPropagation(); onFixDoseUnits(); }}
                title="List the medications and give each a dose unit — fixes every claim at once"
                style={{
                  position: 'absolute', right: 7, bottom: 6,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 7px', borderRadius: 6,
                  font: 'inherit', fontSize: 10.5, fontWeight: 800,
                  cursor: 'pointer', background: '#fff',
                  border: `1px solid ${BRAND.greenDark}66`, color: BRAND.greenDark,
                }}>
                <Wrench size={10} /> Fix all
              </button>
            ) : null)} />

          {/*  The completeness checks that happen to be implemented as `issues`
               rather than as column tests. A SECOND list in the SAME section, not
               merged into the one above: the two tick into different draft keys
               and the server whitelists them separately, so merging would mean
               working out which key every code belongs to on each change. To the
               officer this reads as one continuous group; the seam is an
               implementation detail and stays invisible. */}
          {issuesMissing.length > 0 && (
            <CheckList
              items={issuesMissing}
              chosen={draft.issues}
              loaded={!!facets}
              onSet={(v) => set('issues', v)}
              empty=""
              actionFor={issueAction} />
          )}

          {/*  What the claim gets WRONG, as against what it is missing. A separate
               group because the two are acted on differently: a claim with no
               medication needs someone to go and find one, while an admission
               billed on the wrong thing can be repaired from the claim's own rows
               — which is what the Repair button on the table does with the rows
               this filter selects.

               The heading no longer says "admissions". It used to, and it was
               wrong for half of what sat under it: the two format rules are not
               about admissions at all, and the "claim has no…" checks that were
               here have moved up to "What is missing", where they belong.

               An out-patient claim is still never counted by the admission rules
               themselves: the OPD catalogue is wide — consultations, ZOOM02
               detention, day-case procedures — and a claim billed on any of them
               is correct. See the header of backend/claimRules.js.  */}
          <div style={{ gridColumn: '1 / -1', ...GROUP_LABEL, marginTop: 2 }}>
            <GroupHeading>What is wrong</GroupHeading>
          </div>

          <CheckList
            items={issuesWrong}
            chosen={draft.issues}
            loaded={!!facets}
            onSet={(v) => set('issues', v)}
            empty="Consistency checks are unavailable for this month."
            //  ── The three that can be settled from the claim's own rows ──────
            //  Same idea as "Fix all" on the dose units, and for the same reason:
            //  the correction is mechanical, it is identical on every flagged
            //  claim, and filtering to 17 claims and opening each is 17 times the
            //  same decision.
            //
            //  These three need no decision at all:
            //
            //    an admission billed on an OPD tariff  →  bill it on its own
            //    an admission with no principal        →  procedure if there is
            //    several G-DRGs and no principal          one, else the diagnosis
            //
            //  That is exactly what claimRules already suggests and what
            //  repairClaims already writes — it takes resolvePrincipal, which
            //  prefers a PROCEDURE over any diagnosis, and falls back to the
            //  diagnosis when no procedure was done. Nothing new is being decided
            //  here; what was missing was a way to run it over the whole list
            //  instead of one claim at a time.
            //
            //  Deliberately NOT offered on the rest. "Claim has no patient name"
            //  cannot be repaired from the claim — the name is not on it — and a
            //  Fix all that quietly did nothing on half the tiles would teach the
            //  officer to distrust the one that works.
            //  NO `disabled` HERE, and the count is `g.claims`.
            //
            //  It was `g.count`, which is not a field CheckList's items have — the
            //  count is `claims`, as `const none = !g.claims` above uses. So
            //  `disabled={!g.count}` read undefined on every tile and greyed out
            //  all of them, including the one showing 17 claims.
            //
            //  The guard was redundant anyway: CheckList already refuses to render
            //  an action for a check with no claims (`none ? null : actionFor(g)`),
            //  so by the time this runs there is always something to fix. Two
            //  places deciding the same thing is how they end up disagreeing.
            //  The same one the moved checks use, defined once above. Two copies
            //  of a button whose whole point is that it behaves identically on
            //  every tile is how the two quietly stop matching.
            actionFor={issueAction} />

          <div style={{ gridColumn: '1 / -1', ...GROUP_LABEL, marginTop: 2 }}>
            <GroupHeading>The visit</GroupHeading>
          </div>

          {/*  Attendance is a HEADER field, so it applies to every claim source and
               its counts come from the data rather than from two hard-coded
               buttons. It stays a row of three because there are only ever three. */}
          <div style={{
            border: `1px solid ${BRAND.line}`, borderRadius: 9, background: '#fff',
            gridColumn: '1 / -1',
          }}>
            <div style={{
              padding: '9px 11px', borderBottom: `1px solid ${BRAND.line}`,
              fontSize: 10.5, fontWeight: 800, letterSpacing: .4,
              textTransform: 'uppercase', color: BRAND.inkSoft,
            }}>Service type / attendance</div>
            {/*  ── GENDER ────────────────────────────────────────────────────
                 A row of three because there are only ever three, exactly like the
                 attendance row above it — and for the same reason it needs no facet
                 query.

                 "Not recorded" is a real cohort, not an absence. NHIA validates on
                 gender and queries a claim without one, so a filter offering only
                 male and female would make those the one group that cannot be
                 found. Multi-select, because "male and not-recorded" is a sensible
                 thing to ask for.

                 It sits with attendance rather than with the code filters because
                 it is a fact about the PATIENT, and — like attendance and age — it
                 works on typed-in claims as well as imported ones. */}
            <div style={{ display: 'flex', gap: 6, padding: '10px 10px 0', flexWrap: 'wrap' }}>
              {[['M', 'Male'], ['F', 'Female'], ['unknown', 'Not recorded']].map(([v, label]) => {
                const on = (draft.gender || []).includes(v);
                return (
                  <button key={v} type="button"
                    aria-pressed={on}
                    onClick={() => set('gender', on
                      ? draft.gender.filter((x) => x !== v)
                      : [...(draft.gender || []), v])}
                    style={{
                      flex: '1 1 110px', padding: '8px 10px', borderRadius: 8,
                      cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                      fontWeight: on ? 800 : 600,
                      border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                      background: on ? BRAND.greenSoft : '#fff',
                      color: on ? BRAND.greenDark : BRAND.inkSoft,
                    }}>{label}</button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 6, padding: 10 }}>
              {[
                ['all', 'All', null],
                ['opd', 'Out-patient', 'opd'],
                ['ipd', 'In-patient', 'ipd'],
              ].map(([value, label, facetCode]) => {
                const on = (draft.type || 'all') === value;
                const hit = facetCode && facets
                  ? (facets.attendance || []).find((a) => a.code === facetCode)
                  : null;
                return (
                  <button key={value} type="button" onClick={() => set('type', value)}
                    aria-pressed={on}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                      fontSize: 12, fontWeight: on ? 800 : 600, fontFamily: 'inherit',
                      border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                      background: on ? BRAND.greenSoft : '#fff',
                      color: on ? BRAND.greenDark : BRAND.inkSoft,
                    }}>
                    {label}
                    {hit && (
                      <span style={{ marginLeft: 5, fontWeight: 800, color: BRAND.inkFaint }}>
                        {hit.claims.toLocaleString()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/*  ── HOW LONG THE ADMISSION RAN ──────────────────────────────────
               "I want to be able to filter only in-patient that stayed for a specific
               number of days, like two days, like three days, like four days, to make
               the search easier."

               IN-PATIENT ONLY, and the card says so. Every out-patient claim is
               exactly one day — that is what out-patient means — so the counts come
               from the admissions alone; a month-wide "Same day" would read 3,423 and
               bury the 19 admissions that never stayed a night.

               THOSE 19 ARE THE POINT. A same-day admission shows the same two dates on
               the table as a two-day one and is indistinguishable there, which is why
               "this filter says In-patient but some of these are out-patient" was the
               reported symptom. Nothing was mislabelled: the claims say in-patient. The
               screen simply had no way to show that some never stayed. */}
          {(facets?.stays || []).length > 0 && (
            <div style={{
              border: `1px solid ${BRAND.line}`, borderRadius: 9, background: '#fff',
              gridColumn: '1 / -1',
            }}>
              <div style={{
                padding: '9px 11px', borderBottom: `1px solid ${BRAND.line}`,
                fontSize: 10.5, fontWeight: 800, letterSpacing: .4,
                textTransform: 'uppercase', color: BRAND.inkSoft,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>Length of admission</span>
                <span style={{
                  fontWeight: 700, letterSpacing: 0, textTransform: 'none',
                  color: BRAND.inkFaint, fontSize: 10.5,
                }}>
                  in-patient claims only — counted inclusively, so the 1st to the 3rd is
                  three days
                </span>
                {(draft.stayDays || []).length > 0 && (
                  <button type="button" onClick={() => set('stayDays', [])}
                    style={{
                      marginLeft: 'auto', border: 'none', background: 'none',
                      color: BRAND.greenDark, cursor: 'pointer', fontSize: 11,
                      fontWeight: 700, fontFamily: 'inherit', letterSpacing: 0,
                      textTransform: 'none',
                    }}>Clear</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, padding: 10, flexWrap: 'wrap' }}>
                {(facets.stays || []).map((s) => {
                  const on = (draft.stayDays || []).includes(s.code);
                  //  Same day is the one worth calling out — it is the cohort that
                  //  looks like an admission on the table and is not one on the ward.
                  const sameDay = s.code === '1';
                  return (
                    <button key={s.code} type="button"
                      aria-pressed={on}
                      title={sameDay
                        ? 'Admitted and discharged on the same date — never spent a '
                          + 'night on the ward'
                        : `Admissions that ran ${s.name.toLowerCase()}`}
                      onClick={() => set('stayDays', on
                        ? draft.stayDays.filter((x) => x !== s.code)
                        : [...(draft.stayDays || []), s.code])}
                      style={{
                        flex: '0 1 auto', minWidth: 104, padding: '8px 10px',
                        borderRadius: 8, cursor: 'pointer', fontSize: 12,
                        fontFamily: 'inherit', fontWeight: on ? 800 : 600,
                        textAlign: 'left',
                        border: `1px solid ${on ? BRAND.greenDark
                          : sameDay ? '#F2DCB8' : BRAND.line}`,
                        background: on ? BRAND.greenSoft : sameDay ? '#FDF7EE' : '#fff',
                        color: on ? BRAND.greenDark : BRAND.inkSoft,
                      }}>
                      {s.name}
                      <span style={{
                        marginLeft: 6, fontWeight: 800, color: BRAND.inkFaint,
                      }}>{s.claims.toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/*  Says what the filter cannot reach. The three child filters read the
               normalised claim tables; a typed-in claim keeps its diagnoses and
               drugs in different tables keyed by patient and date, so the same
               question needs a different query there. Stated rather than left to
               look like "this month has none of those". */}
          {/*  Folded away. It is reference text that is read once and then never
               again, and at full size it was the largest thing in the dialog —
               which is the wrong emphasis when the panels above are the work. */}
          <details style={{ gridColumn: '1 / -1', marginTop: 2 }}>
            <summary style={{
              cursor: 'pointer', fontSize: 11, color: BRAND.inkMuted,
              padding: '2px 2px', listStyle: 'revert',
            }}>
              How these counts and filters behave
            </summary>
            <div style={{
              marginTop: 6, padding: '9px 11px', borderRadius: 8,
              background: '#F2F6FB', border: `1px solid ${BRAND.line}`,
              fontSize: 11.5, color: BRAND.inkSoft, lineHeight: 1.55,
            }}>
              The counts are <strong>claims</strong>, not lines — a drug given on four
              days of one stay is one claim. Diagnosis, procedure, medication and
              service read the <strong>imported</strong> claims; typed-in claims are
              not searched by those yet, because their details live in different
              tables. Attendance applies to both.
            </div>
          </details>
        </div>

        {/*  A tinted footer, so the actions sit on their own ground rather than
             floating at the bottom of the scrolling area with nothing under
             them. */}
        <div style={{
          display: 'flex', gap: 8, padding: '12px 16px', alignItems: 'center',
          borderTop: `1px solid ${BRAND.line}`, background: '#F8FAFC',
          borderRadius: '0 0 12px 12px',
        }}>
          <button type="button" onClick={onClear}
            title="Clear every filter and show the whole month"
            style={{
              padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
              fontWeight: 700, fontFamily: 'inherit',
              border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
            }}>Clear all</button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose}
            style={{
              padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
              fontWeight: 700, fontFamily: 'inherit',
              border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
            }}>Cancel</button>
          <button type="button" onClick={onSearch}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
              fontWeight: 800, fontFamily: 'inherit', border: '1px solid transparent',
              background: BRAND.greenDark, color: '#fff',
            }}>
            <Search size={14} />
            {count ? `Search — ${count} filter${count === 1 ? '' : 's'}` : 'Search'}
          </button>
        </div>
      </div>
    </div>
  );
}
